import React, { useState, useEffect } from 'react';
import { Camera, BarChart3, CheckSquare, FileText, Shield, Settings, Menu, X, ChevronRight, ChevronDown, Trash2, AlertTriangle, Search, Activity, Users, Clock, RefreshCw, Plus, Pencil, Trophy, AlertCircle, Bell, MessageSquare, Send, ArrowLeft } from 'lucide-react';

// ============================================================================
// ROLE-BASED ACCESS CONTROL
// ============================================================================

// Pages always accessible regardless of role (bypass module restriction)
const ALWAYS_ALLOWED_PAGES = ['dashboard', 'modules'];

// Admin-only nav items hidden from non-admin users
const ADMIN_ONLY_NAV = ['tasks', 'reports', 'audit', 'settings'];

// Returns the list of accessible module IDs for the user, or null (= unrestricted/admin)
const getAllowedModules = (user) => {
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return null; // admin always unrestricted
  const perms = user?.permissions;
  if (perms && Object.keys(perms).length > 0) return Object.keys(perms);
  return [];
};

// Returns the permission level for a specific module: 'view' | 'edit' | 'full' | null
const getUserPermission = (user, moduleId) => {
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return 'full';
  return user?.permissions?.[moduleId] || null;
};

const canAccessPage = (user, page) => {
  if (ALWAYS_ALLOWED_PAGES.includes(page)) return true;
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return true;
  const isManager = role === 'manager';
  const isDataOpsManager = role === 'dataops_manager';
  const isDevOpsManager = role === 'devops_manager';
  if ((page === 'tasks' || page === 'reports') && (isManager || isDataOpsManager || isDevOpsManager)) return true;
  if (page === 'audit' && isDevOpsManager) return true;
  const allowed = getAllowedModules(user);
  return !allowed || allowed.includes(page);
};

// ============================================================================
// MODELS (Data Layer)
// ============================================================================

const API_BASE = '/api'; // Cloudflare Workers endpoint

class BaseModel {
  static authHeaders() {
    const token = localStorage.getItem('orbit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static async fetch(endpoint) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { ...this.authHeaders() }
    });
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }

  static async post(endpoint, data) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }
}

class DashboardModel extends BaseModel {
  static async getStats() {
    try { return await this.fetch('/dashboard'); } catch { return { stats: [], activities: [], quickModules: [] }; }
  }
  static async getBurnCharts() {
    try { return await this.fetch('/dashboard/burncharts'); }
    catch { return null; }
  }
}

class ModulesModel extends BaseModel {
  static async getModules() {
    try { return await this.fetch('/modules'); }
    catch { return []; }
  }

  static async getPlanTrackData() {
    try { return await this.fetch('/plantrack'); }
    catch { return { stats: { total: 0, active: 0, completed: 0, onHold: 0, planning: 0 }, projects: [], milestones: [], sprints: [], sprint: null }; }
  }

  static async getAppSettings() {
    try { return await this.fetch('/settings'); }
    catch { return {}; }
  }

  static async saveAppSettings(data) {
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async sendEodSummary(module) {
    try {
      const response = await fetch(`${API_BASE}/eod/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async sendQualityEodSummary(userName) {
    try {
      const response = await fetch(`${API_BASE}/eod/send-quality`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_name: userName })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async getProjectDetail(projectId) {
    try {
      return await this.fetch(`/plantrack/projects/${projectId}`);
    } catch {
      // Fallback mock data for dev
      const allData = await this.getPlanTrackData();
      const project = allData.projects.find(p => p.id === projectId);
      return {
        project,
        tasks: [
          { id: 'TASK-2024-016', title: 'Design REST endpoint contracts', type: 'task', priority: 'P1', status: 'completed', assignee: 'John Developer', estimateHours: 16, storyPoints: 3, progress: 100 },
          { id: 'TASK-2024-017', title: 'Set up API gateway configuration', type: 'task', priority: 'P2', status: 'in_progress', assignee: 'Mike Fullstack', estimateHours: 8, storyPoints: 2, progress: 40 }
        ],
        comments: [
          { id: 1, userName: 'John Developer', content: 'Kicked off the API modernization. Starting with the authentication endpoints first.', createdAt: '2025-01-28T10:00:00' },
          { id: 2, userName: 'Jim Manager', content: 'Make sure we maintain backward compatibility with the existing clients during migration.', createdAt: '2025-01-29T14:00:00' },
          { id: 3, userName: 'Mike Fullstack', content: 'I can help with the gateway config once the endpoint contracts are finalized.', createdAt: '2025-02-01T09:00:00' }
        ],
        updates: [
          { id: 1, userName: 'John Developer', updateType: 'status_change', content: 'Project moved to active development', oldValue: 'planning', newValue: 'active', createdAt: '2025-01-28T08:00:00' },
          { id: 2, userName: 'John Developer', updateType: 'progress', content: 'API design phase completed, moving to implementation', oldValue: null, newValue: '35%', createdAt: '2025-02-04T16:00:00' }
        ],
        milestones: allData.milestones.filter(m => m.projectId === projectId)
      };
    }
  }

  static async createProject(data) {
    try { return await this.post('/plantrack/projects', data); }
    catch { return { success: true, projectId: `PROJ-${String(Date.now()).slice(-6)}` }; }
  }

  static async updateProject(projectId, data) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/projects/${projectId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async addComment(projectId, content) {
    try { return await this.post(`/plantrack/projects/${projectId}/comments`, { content }); }
    catch { return { success: true, id: Date.now() }; }
  }

  static async addUpdate(projectId, content) {
    try { return await this.post(`/plantrack/projects/${projectId}/updates`, { content, updateType: 'general' }); }
    catch { return { success: true, id: Date.now() }; }
  }

  static async postProjectUpdateReport(preparedBy, role) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/post-update-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prepared_by: preparedBy, role })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async createSprint(data) {
    return await this.post('/plantrack/sprints', data);
  }

  static async updateSprint(sprintId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/sprints/${sprintId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return r.json();
    } catch { return { success: false }; }
  }

  static async createProjectTask(projectId, data) {
    try { return await this.post(`/plantrack/projects/${projectId}/tasks`, data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }
  static async deleteProjectTask(projectId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); }
    catch { return { success: false }; }
  }

  static async getLeaderboard(period = 'weekly') {
    try { return await this.fetch(`/leaderboard?period=${period}`); } catch { return { devops: [], dataops: [] }; }
  }

  static async getProjectCharter(projectId) {
    try { return await this.fetch(`/plantrack/projects/${projectId}/charter`); } catch { return {}; }
  }
  static async saveProjectCharter(projectId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/charter`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getRisks(projectId = null) {
    try { return await this.fetch(`/risks${projectId ? `?project_id=${projectId}` : ''}`); } catch { return { risks: [], summary: {} }; }
  }
  static async createRisk(data) {
    try { return await this.post('/risks', data); } catch { return { success: false }; }
  }
  static async updateRisk(id, data) {
    try {
      const r = await fetch(`${API_BASE}/risks/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteRisk(id) {
    try {
      const r = await fetch(`${API_BASE}/risks/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getDevOpsPipeline() {
    try {
      return await this.fetch('/devops');
    } catch {
      return {
        capacity: { total: 160, committed: 156, completed: 46, utilization: 97.5 },
        workload: [
          { name: 'John Developer', assigned: 76, capacity: 40, percentage: 190 },
          { name: 'Sarah Frontend', assigned: 20, capacity: 40, percentage: 50 },
          { name: 'Mike Fullstack', assigned: 16, capacity: 40, percentage: 40 },
          { name: 'Jim Manager', assigned: 0, capacity: 40, percentage: 0 }
        ],
        pipeline: {
          backlog: [
            { id: 'PR3-2024-001', title: 'Settlement Vault API Modernization', type: 'PROJECT', priority: 'P1', assignee: 'John Developer', estimate: 80, storyPoints: 13 },
            { id: 'ADH-2024-012', title: 'Update Monday.com integration webhook', type: 'ADHOC', priority: 'P2', assignee: 'Sarah Frontend', estimate: 8, storyPoints: 3 }
          ],
          ready: [
            { id: 'BUG-2024-007', title: 'Fix calendar sync issues', type: 'BUG', priority: 'P1', assignee: 'Mike Fullstack', estimate: 16, storyPoints: 5 },
            { id: 'BUG-2024-009', title: 'API rate limiting not enforced', type: 'BUG', priority: 'P1', assignee: 'John Developer', estimate: 8, storyPoints: 3 }
          ],
          inProgress: [
            { id: 'PR3-2024-002', title: 'Build Negotiator Worklist Dashboard', type: 'PROJECT', priority: 'P2', assignee: 'John Developer', estimate: 60, storyPoints: 8, progress: 50 },
            { id: 'TASK-2024-017', title: 'Set up API gateway configuration', type: 'task', priority: 'P2', assignee: 'Mike Fullstack', estimate: 8, storyPoints: 2, progress: 40 },
            { id: 'TASK-2024-018', title: 'Create worklist filter components', type: 'task', priority: 'P2', assignee: 'Sarah Frontend', estimate: 12, storyPoints: 3, progress: 60 }
          ],
          review: [
            { id: 'ADH-2024-015', title: 'Azure DevOps API integration', type: 'ADHOC', priority: 'P3', assignee: 'Sarah Frontend', estimate: 12, storyPoints: 5 }
          ],
          done: [
            { id: 'TASK-2024-016', title: 'Design REST endpoint contracts', type: 'task', priority: 'P1', assignee: 'John Developer', estimate: 16, storyPoints: 3, progress: 100 },
            { id: 'TASK-2024-020', title: 'Write unit tests for settlement module', type: 'task', priority: 'P3', assignee: 'Mike Fullstack', estimate: 12, storyPoints: 3, progress: 100 }
          ]
        },
        sprint: { name: 'Sprint 24', number: 24, startDate: '2025-01-20', endDate: '2025-02-02', status: 'active', goal: 'Complete Settlement Vault modernization' },
        ops: { pipelines: [
          { id: 'etl-001', name: 'Daily Users ETL', type: 'ETL', status: 'running', progress: 67, recordsProcessed: 1200000, durationSeconds: 7200, lastRun: '2025-01-31T10:00:00' },
          { id: 'rep-001', name: 'Sales Dashboard Refresh', type: 'Report', status: 'running', progress: 45, recordsProcessed: 3500000, durationSeconds: 5400, lastRun: '2025-01-31T10:30:00' },
          { id: 'etl-002', name: 'Real-Time Events Pipeline', type: 'ETL', status: 'running', progress: 92, recordsProcessed: 8100000, durationSeconds: 1800, lastRun: '2025-01-31T11:30:00' },
          { id: 'dash-001', name: 'Executive KPI Dashboard', type: 'Dashboard', status: 'completed', progress: 100, recordsProcessed: 250000, durationSeconds: 300, lastRun: '2025-01-31T06:00:00' },
          { id: 'pbi-001', name: 'Financial Analysis Report', type: 'Power BI', status: 'completed', progress: 100, recordsProcessed: 1800000, durationSeconds: 1200, lastRun: '2025-01-31T00:00:00' },
          { id: 'etl-003', name: 'Customer Data Sync', type: 'ETL', status: 'failed', progress: 45, recordsProcessed: 500000, durationSeconds: 600, lastRun: '2025-01-31T04:00:00', errorMessage: 'Connection timeout to source DB' }
        ]}
      };
    }
  }

  static async getDevOpsKanban() {
    try {
      return await this.fetch('/devops/kanban');
    } catch {
      // Reuse sprint pipeline data as fallback
      const data = await this.getDevOpsPipeline();
      return { pipeline: data.pipeline };
    }
  }

  static async createDevOpsTask(data) {
    try { return await this.post('/devops/tasks', data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }

  static async createDataOpsTask(data) {
    try { return await this.post('/dataops/tasks', data); }
    catch { return { success: true, taskId: `DOP-${String(Date.now()).slice(-7)}` }; }
  }

  static async updateDevOpsTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/devops/tasks/${taskId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteDevOpsTask(taskId) {
    try {
      const response = await fetch(`${API_BASE}/devops/tasks/${taskId}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getDevOpsTask(taskId) {
    try { return await this.fetch(`/devops/tasks/${taskId}`); }
    catch { return null; }
  }

  // ---- DataOps Model Methods ----

  static async getDataOpsKanban() {
    try {
      return await this.fetch('/dataops/kanban');
    } catch {
      return { open: [], in_progress: [], done: [], total: 0 };
    }
  }

  static async getDataOpsData() {
    try {
      return await this.fetch('/dataops');
    } catch {
      return {
        metrics: { activePipelines: 3, dataProcessed: '19.9TB', reportsGenerated: 3, successRate: '67%' },
        pipelineStatus: [{ status: 'running', count: 3 }, { status: 'completed', count: 2 }, { status: 'failed', count: 1 }],
        powerbiReports: [
          { title: 'Executive KPI Dashboard', icon: '📊', lastUpdated: '2 hours ago', status: 'Published', views: 1234 },
          { title: 'Sales Performance', icon: '💰', lastUpdated: '5 hours ago', status: 'Published', views: 892 }
        ],
        activePipelines: [
          { name: 'Daily Users ETL', type: 'ETL', status: 'running', progress: 67, records: 1200000, started: '2 hours ago' },
          { name: 'Sales Dashboard Refresh', type: 'Report', status: 'running', progress: 45, records: 3500000, started: '90 min ago' }
        ],
        allPipelines: [
          { pipelineId: 'etl-001', pipelineName: 'Daily Users ETL', pipelineType: 'ETL', status: 'running', lastRun: '2025-02-15T10:00:00', durationSeconds: 7200, recordsProcessed: 1200000, progress: 67 },
          { pipelineId: 'rep-001', pipelineName: 'Sales Dashboard Refresh', pipelineType: 'Report', status: 'running', lastRun: '2025-02-15T10:30:00', durationSeconds: 5400, recordsProcessed: 3500000, progress: 45 },
          { pipelineId: 'etl-003', pipelineName: 'Customer Data Sync', pipelineType: 'ETL', status: 'failed', lastRun: '2025-02-15T04:00:00', durationSeconds: 600, recordsProcessed: 500000, progress: 45, errorMessage: 'Connection timeout' }
        ],
        allReports: [
          { reportId: 'pbi-exec-kpi', reportName: 'Executive KPI Dashboard', reportIcon: '📊', category: 'Executive', status: 'Published', lastUpdated: '2025-02-15T10:00:00', viewCount: 1234, isFavorite: 1 },
          { reportId: 'pbi-sales', reportName: 'Sales Performance', reportIcon: '💰', category: 'Sales', status: 'Published', lastUpdated: '2025-02-15T07:00:00', viewCount: 892, isFavorite: 0 }
        ]
      };
    }
  }

  static async getPipeline(pipelineId) {
    try { return await this.fetch(`/dataops/pipelines/${pipelineId}`); }
    catch { return null; }
  }

  static async createPipeline(data) {
    try { return await this.post('/dataops/pipelines', data); }
    catch { return { success: true }; }
  }

  static async updatePipeline(pipelineId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deletePipeline(pipelineId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getReport(reportId) {
    try { return await this.fetch(`/dataops/reports/${reportId}`); }
    catch { return null; }
  }

  static async createReport(data) {
    try { return await this.post('/dataops/reports', data); }
    catch { return { success: true }; }
  }

  static async updateReport(reportId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteReport(reportId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- QMS Model Methods ----

  static async getQualityData(projectId) {
    try { return await this.fetch(`/quality${projectId ? '?projectId=' + projectId : ''}`); }
    catch {
      return { projects: [], project: null, requirements: [], testCases: [], bugs: [], metrics: { requirements: { total: 0, approved: 0, pending: 0 }, testCases: { total: 0, passed: 0, failed: 0, pending: 0 }, bugs: { total: 0, open: 0, inProgress: 0, resolved: 0 }, passRate: 0 } };
    }
  }

  static async enableProjectQa(projectId, data) {
    try { return await this.post(`/quality/projects/${projectId}/enable`, data); } catch { return { success: true }; }
  }

  static async getQaLeads() {
    try { return await this.fetch('/users/qa-leads'); } catch { return []; }
  }

  static async getProjectOverview(projectId) {
    try { return await this.fetch(`/projects/${projectId}/overview`); }
    catch { return null; }
  }

  static async clearData(modules) {
    try {
      const r = await fetch(`${API_BASE}/settings/data`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules })
      });
      return r.json();
    } catch { return { success: false, error: 'Network error' }; }
  }

  // ---- Milestone Engine Model Methods ----
  static async getMilestones(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones${projectId ? `?projectId=${projectId}` : ''}`); } catch { return []; }
  }
  static async getMilestoneDetail(milestoneId) {
    try { return await this.fetch(`/plantrack/milestones/${milestoneId}`); } catch { return null; }
  }
  static async createMilestone(data) {
    try { return await this.post('/plantrack/milestones', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateMilestone(milestoneId, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async completeMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/complete`, { method: 'PUT' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async getMilestoneTemplates() {
    try { return await this.fetch('/plantrack/milestones/templates'); } catch { return []; }
  }
  static async getTemplateItems(templateId) {
    try { return await this.fetch(`/plantrack/milestones/templates/${templateId}/items`); } catch { return []; }
  }
  static async generateMilestonesFromTemplate(data) {
    try { return await this.post('/plantrack/milestones/generate', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async createMilestoneTemplate(data) {
    try { return await this.post('/plantrack/milestones/templates', data); } catch (e) { return { success: false }; }
  }
  static async updateMilestoneTemplate(id, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async deleteMilestoneTemplate(id) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async createTemplateItem(templateId, data) {
    try { return await this.post(`/plantrack/milestones/templates/${templateId}/items`, data); } catch (e) { return { success: false }; }
  }
  static async deleteTemplateItem(templateId, itemId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${templateId}/items/${itemId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async getExecutiveSummary(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones/executive-summary${projectId ? `?projectId=${projectId}` : ''}`); } catch { return { summary: {}, upcomingMilestones: [], delayedMilestones: [] }; }
  }
  static async createMilestoneDependency(data) {
    try { return await this.post('/plantrack/milestones/dependencies', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestoneDependency(predecessorId, successorId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/dependencies/${predecessorId}/${successorId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async linkTaskToMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async unlinkTaskFromMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async calculateCriticalPath(projectId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/critical-path`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }

  static async updateQaProject(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/projects/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateQaProject failed:', e); return { success: false, error: e.message }; }
  }

  static async getReqTypes() {
    try { const r = await fetch(`${API_BASE}/quality/req-types`); return r.json(); } catch { return []; }
  }
  static async createReqType(data) {
    try { return await this.post('/quality/req-types', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateReqType(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/req-types/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteReqType(id) {
    try { const r = await fetch(`${API_BASE}/quality/req-types/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }

  static async getTcModules() {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules`); return r.json(); } catch { return []; }
  }
  static async createTcModule(data) {
    try { return await this.post('/quality/tc-modules', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateTcModule(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteTcModule(id) {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }

  static async createRequirement(data) {
    try { return await this.post('/quality/requirements', data); } catch (e) { console.error('createRequirement failed:', e); return { success: false, error: e.message }; }
  }

  static async updateRequirement(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/requirements/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateRequirement failed:', e); return { success: false, error: e.message }; }
  }
  static async deleteRequirement(id) {
    try { const r = await fetch(`${API_BASE}/quality/requirements/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }
  static async renameReqCategory(projectId, oldCategory, newCategory) {
    try { const r = await fetch(`${API_BASE}/quality/req-categories/rename`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId, oldCategory, newCategory }) }); return r.json(); } catch { return { success: false }; }
  }

  static async createTestCase(data) {
    try { return await this.post('/quality/testcases', data); } catch (e) { console.error('createTestCase failed:', e); return { success: false, error: e.message }; }
  }

  static async updateTestCase(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/testcases/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateTestCase failed:', e); return { success: false, error: e.message }; }
  }
  static async deleteTestCase(id) {
    try { const r = await fetch(`${API_BASE}/quality/testcases/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }

  static async createBug(data) {
    try { return await this.post('/quality/bugs', data); } catch (e) { console.error('createBug failed:', e); return { success: false, error: e.message }; }
  }

  static async getBug(id) {
    try { return await this.fetch(`/quality/bugs/${id}`); } catch { return null; }
  }

  static async updateBug(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/bugs/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateBug failed:', e); return { success: false, error: e.message }; }
  }

  static async getAllBugs(filters = {}) {
    try {
      const p = new URLSearchParams();
      if (filters.projectId) p.set('projectId', filters.projectId);
      if (filters.severity) p.set('severity', filters.severity);
      if (filters.status) p.set('status', filters.status);
      if (filters.search) p.set('search', filters.search);
      if (filters.sort) p.set('sort', filters.sort);
      if (filters.order) p.set('order', filters.order);
      const qs = p.toString();
      return await this.fetch(`/quality/bugs${qs ? '?' + qs : ''}`);
    } catch { return { bugs: [], metrics: {}, total: 0, projects: [] }; }
  }

  static async getBugDetail(id) {
    try { return await this.fetch(`/quality/bugs/${id}/detail`); }
    catch { return null; }
  }

  static async addBugComment(bugId, data) {
    try { return await this.post(`/quality/bugs/${bugId}/comments`, data); }
    catch { return { success: false }; }
  }

  static async deleteBugComment(commentId) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/comments/${commentId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getBugAttachments(bugId) {
    try { return await this.fetch(`/quality/bugs/${bugId}/attachments`); } catch { return []; }
  }
  static async addBugAttachment(bugId, data) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/${bugId}/attachments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteBugAttachment(attachmentId) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/attachments/${attachmentId}`, {
        method: 'DELETE', headers: { ...this.authHeaders() },
      });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getProjectAttachments(projectId) {
    try { return await this.fetch(`/plantrack/projects/${projectId}/attachments`); } catch { return []; }
  }
  static async addProjectAttachment(projectId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/attachments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteProjectAttachment(attachmentId) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/attachments/${attachmentId}`, {
        method: 'DELETE', headers: { ...this.authHeaders() },
      });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- QuickTrack Model Methods ----

  static async getQuickTrackData() {
    try { return await this.fetch('/quicktrack'); }
    catch {
      return {
        tasks: [
          { id: 1, title: 'Sample task', priority: 'medium', status: 'open', category: 'general', assignedTo: 'John Developer', createdBy: 'Jim Manager', dueDate: null, createdAt: new Date().toISOString(), completedAt: null }
        ],
        metrics: { total: 1, open: 1, inProgress: 0, done: 0, overdue: 0 }
      };
    }
  }

  static async createQuickTask(data) {
    try { return await this.post('/quicktrack/tasks', data); }
    catch { return { success: true }; }
  }

  static async updateQuickTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteQuickTask(taskId) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- Vendor Events Model Methods ----

  static async getVendorEventsData(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.vendor) params.set('vendor', filters.vendor);
      if (filters.workflow) params.set('workflow', filters.workflow);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return await this.fetch(`/vendorevents${qs ? '?' + qs : ''}`);
    } catch {
      return { vendors: [], events: [], metrics: { totalFired: 0, totalAccepted: 0, acceptanceRate: 0 } };
    }
  }

  static async createVendor(data) {
    try { return await this.post('/vendorevents/vendors', data); }
    catch { return { success: true }; }
  }

  static async createVendorEvent(data) {
    try { return await this.post('/vendorevents/events', data); }
    catch { return { success: true }; }
  }

  static async updateVendorEvent(eventId, data) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteVendorEvent(eventId) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async receiveVendorWebhook(vendorId, payload) {
    try { return await this.post(`/vendorevents/vendors/${vendorId}/webhook`, payload); }
    catch (e) { return { success: false, error: e.message }; }
  }

  static async updateVendor(vendorId, data) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async deleteVendor(vendorId) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'DELETE' });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  // ---- LearnTrack Model Methods ----

  static async getLearnTrackData() {
    try {
      return await this.fetch('/learntrack');
    } catch {
      return {
        skills: [
          { id: 1, skillName: 'JavaScript', category: 'technical' },
          { id: 2, skillName: 'React', category: 'technical' },
          { id: 3, skillName: 'Python', category: 'technical' }
        ],
        courses: [
          { id: 1, courseName: 'React Fundamentals', difficulty: 'beginner', durationHours: 8, instructor: 'Sarah Frontend', targetGroup: 'developer', status: 'published', badgeIcon: '⚛️', skillName: 'React', enrollmentCount: 2, completedCount: 2 },
          { id: 2, courseName: 'Advanced JavaScript', difficulty: 'advanced', durationHours: 12, instructor: 'Mike Fullstack', targetGroup: 'developer', status: 'published', badgeIcon: '🟨', skillName: 'JavaScript', enrollmentCount: 1, completedCount: 0 }
        ],
        myEnrollments: [
          { id: 1, status: 'completed', progress: 100, courseName: 'React Fundamentals', badgeIcon: '⚛️', difficulty: 'beginner', skillName: 'React', score: 92 },
          { id: 2, status: 'in_progress', progress: 65, courseName: 'Advanced JavaScript', badgeIcon: '🟨', difficulty: 'advanced', skillName: 'JavaScript', score: null }
        ],
        myBadges: [{ id: 1, badgeName: 'React Starter', badgeIcon: '⚛️', description: 'Completed React Fundamentals', awardedAt: '2025-02-01' }],
        teamProgress: [
          { userId: 2, userName: 'John Developer', totalEnrollments: 3, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 },
          { userId: 3, userName: 'Sarah Frontend', totalEnrollments: 2, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 }
        ]
      };
    }
  }

  static async getCourseDetail(courseId) {
    try { return await this.fetch(`/learntrack/courses/${courseId}`); }
    catch { return null; }
  }

  static async createCourse(data) {
    try { return await this.post('/learntrack/courses', data); }
    catch { return { success: true }; }
  }

  static async updateCourse(courseId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteCourse(courseId) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async enrollUser(data) {
    try { return await this.post('/learntrack/enroll', data); }
    catch { return { success: true }; }
  }

  static async updateEnrollment(enrollmentId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/enrollments/${enrollmentId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- TimeBoard Model Methods ----

  static async getTimeBoardData(weekStart) {
    try {
      const params = weekStart ? `?week=${weekStart}` : '';
      return await this.fetch(`/timeboard${params}`);
    } catch {
      const ws = weekStart || '2025-01-27';
      return {
        weekStart: ws,
        myTimesheet: { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
        entries: [
          { id: 19, entryDate: `${ws}`, hours: 8, category: 'project', projectId: 'PROJ-001', description: 'Settlement API implementation', isBillable: 1 },
          { id: 20, entryDate: '2025-01-28', hours: 8, category: 'project', projectId: 'PROJ-001', description: 'REST endpoint development', isBillable: 1 },
          { id: 21, entryDate: '2025-01-29', hours: 6, category: 'project', projectId: 'PROJ-002', description: 'Worklist bulk actions', isBillable: 1 },
          { id: 22, entryDate: '2025-01-29', hours: 2, category: 'meeting', projectId: null, description: 'Sprint retrospective', isBillable: 0 }
        ],
        teamTimesheets: [
          { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
          { id: 5, userId: 3, userName: 'Sarah Frontend', weekStartDate: ws, status: 'draft', totalHours: 16, billableHours: 12, leaveHours: 0 },
          { id: 6, userId: 4, userName: 'Mike Fullstack', weekStartDate: ws, status: 'draft', totalHours: 20, billableHours: 16, leaveHours: 8 },
          { id: 8, userId: 1, userName: 'Jim Manager', weekStartDate: ws, status: 'draft', totalHours: 32, billableHours: 8, leaveHours: 0 }
        ],
        leaveDays: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29' }
        ]
      };
    }
  }

  static async createTimesheetEntry(data) {
    try { return await this.post('/timeboard/entries', data); }
    catch { return { success: true }; }
  }

  static async updateTimesheetEntry(id, data) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteTimesheetEntry(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async submitTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/submit`, { method: 'PUT' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async approveTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/approve`, { method: 'PUT' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async syncCompletedTasks(defaultHours = 1) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/sync-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...ModulesModel.authHeaders() },
        body: JSON.stringify({ defaultHours }),
      });
      return response.json();
    } catch { return { success: false, synced: 0, skippedNoHours: 0 }; }
  }

  // ---- LeaveBoard Model Methods ----

  static async getLeaveBoardData() {
    try { return await this.fetch('/leaveboard'); }
    catch {
      return {
        myBalance: { vacation_total: 15, vacation_used: 5, vacation_remaining: 10, sick_total: 10, sick_used: 2, sick_remaining: 8, personal_total: 5, personal_used: 1, personal_remaining: 4 },
        requests: [
          { id: 1, userId: 2, userName: 'John Developer', leaveType: 'vacation', startDate: '2025-01-06', endDate: '2025-01-10', totalDays: 5, status: 'approved', reason: 'Family vacation', approvedBy: 'Jim Manager', createdAt: '2024-12-15' },
          { id: 4, userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1, status: 'approved', reason: 'Personal appointment', approvedBy: 'Jim Manager', createdAt: '2025-02-10' },
          { id: 5, userId: 3, userName: 'Sarah Frontend', leaveType: 'vacation', startDate: '2025-02-24', endDate: '2025-02-28', totalDays: 5, status: 'pending', reason: 'Spring break vacation', createdAt: '2025-02-01' },
          { id: 3, userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3, status: 'approved', reason: 'Personal trip', approvedBy: 'Jim Manager', createdAt: '2025-01-18' },
          { id: 6, userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2, status: 'approved', reason: 'Flu', approvedBy: 'Jim Manager', createdAt: '2025-01-28' },
          { id: 7, userId: 1, userName: 'Jim Manager', leaveType: 'vacation', startDate: '2025-03-10', endDate: '2025-03-14', totalDays: 5, status: 'pending', reason: 'Annual leave', createdAt: '2025-02-01' }
        ],
        teamBalances: [
          { userName: 'Jim Manager', vacationRemaining: 17, sickRemaining: 9, personalRemaining: 5 },
          { userName: 'John Developer', vacationRemaining: 10, sickRemaining: 8, personalRemaining: 4 },
          { userName: 'Mike Fullstack', vacationRemaining: 11, sickRemaining: 7, personalRemaining: 5 },
          { userName: 'Sarah Frontend', vacationRemaining: 13, sickRemaining: 10, personalRemaining: 3 }
        ],
        upcomingLeave: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2 },
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3 },
          { userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1 }
        ]
      };
    }
  }

  static async createLeaveRequest(data) {
    try { return await this.post('/leaveboard/requests', data); }
    catch { return { success: true }; }
  }

  static async updateLeaveRequest(id, data) {
    try {
      const response = await fetch(`${API_BASE}/leaveboard/requests/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- MeetingBoard Model Methods ----

  static async getMeetingBoardData() {
    try { return await this.fetch('/meetingboard'); }
    catch { return { upcoming: [], past: [], stats: { total: 0, upcoming: 0, completed: 0, cancelled: 0 } }; }
  }

  static async createMeeting(data) {
    try { return await this.post('/meetingboard/meetings', data); }
    catch { return { success: true }; }
  }

  static async updateMeeting(id, data) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: true }; }
  }

  static async deleteMeeting(id) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}`, {
        method: 'DELETE', headers: authHeaders()
      });
      return res.json();
    } catch { return { success: true }; }
  }

  static async saveMeetingNotes(id, data) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}/notes`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  // ---- KnowledgeBoard Model Methods ----

  static async getKnowledgeData(params = {}) {
    try {
      const p = new URLSearchParams();
      if (params.categoryId) p.set('categoryId', params.categoryId);
      if (params.search) p.set('search', params.search);
      if (params.status) p.set('status', params.status);
      const qs = p.toString();
      return await this.fetch(`/knowledge/articles${qs ? '?' + qs : ''}`);
    } catch { return { categories: [], articles: [], metrics: { total: 0, published: 0, drafts: 0, categories: 0 } }; }
  }

  static async getKnowledgeArticle(id) {
    try { return await this.fetch(`/knowledge/articles/${id}`); }
    catch { return null; }
  }

  static async createKnowledgeArticle(data) {
    try { return await this.post('/knowledge/articles', data); }
    catch { return { success: false }; }
  }

  static async updateKnowledgeArticle(id, data) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/articles/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async deleteKnowledgeArticle(id) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/articles/${id}`, {
        method: 'DELETE', headers: authHeaders()
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async createKnowledgeCategory(data) {
    try { return await this.post('/knowledge/categories', data); }
    catch { return { success: false }; }
  }

  static async updateKnowledgeCategory(id, data) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/categories/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async deleteKnowledgeCategory(id) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/categories/${id}`, {
        method: 'DELETE', headers: authHeaders()
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async getArticleVersions(articleId) {
    try { return await this.fetch(`/knowledge/articles/${articleId}/versions`); }
    catch { return []; }
  }

  static async restoreArticleVersion(articleId, versionId) {
    try { return await this.post(`/knowledge/articles/${articleId}/versions/${versionId}/restore`, {}); }
    catch { return { success: false }; }
  }

  // ---- IncidentBoard Model Methods ----

  static async getIncidentsData(params = {}) {
    try {
      const p = new URLSearchParams();
      if (params.severity) p.set('severity', params.severity);
      if (params.status) p.set('status', params.status);
      if (params.team) p.set('team', params.team);
      if (params.search) p.set('search', params.search);
      const qs = p.toString();
      return await this.fetch(`/incidents${qs ? '?' + qs : ''}`);
    } catch { return { incidents: [], metrics: { total: 0, open: 0, critical: 0, resolvedToday: 0, bySeverity: {}, byStatus: {}, byTeam: {} } }; }
  }

  static async getIncident(id) {
    try { return await this.fetch(`/incidents/${id}`); }
    catch { return null; }
  }

  static async createIncident(data) {
    try { return await this.post('/incidents', data); }
    catch { return { success: false }; }
  }

  static async updateIncident(id, data) {
    try {
      const res = await fetch(`${API_BASE}/incidents/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async addIncidentUpdate(incidentId, data) {
    try { return await this.post(`/incidents/${incidentId}/updates`, data); }
    catch { return { success: false }; }
  }

  // ---- AutomationBoard Model Methods ----

  static async getAutomationRules() {
    try { return await this.fetch('/automation/rules'); }
    catch { return []; }
  }

  static async createAutomationRule(data) {
    try { return await this.post('/automation/rules', data); }
    catch { return { success: false }; }
  }

  static async updateAutomationRule(id, data) {
    try {
      const res = await fetch(`${API_BASE}/automation/rules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }

  static async deleteAutomationRule(id) {
    try {
      const res = await fetch(`${API_BASE}/automation/rules/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return res.json();
    } catch { return { success: false }; }
  }

  static async getAutomationLogs() {
    try { return await this.fetch('/automation/logs'); }
    catch { return []; }
  }

  // ---- RequestBoard Model Methods ----

  static async getRequests(filters = {}) {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const qs = p.toString();
      return await this.fetch(`/requests${qs ? '?' + qs : ''}`);
    } catch { return []; }
  }
  static async getRequest(id) {
    try { return await this.fetch(`/requests/${id}`); } catch { return null; }
  }
  static async getRequestStats() {
    try { return await this.fetch('/requests/stats'); }
    catch { return { total: 0, pending: 0, approved: 0, rejected: 0, inprogress: 0, completed: 0, todayCount: 0, byType: [], byDept: [] }; }
  }
  static async createRequest(data) {
    try { return await this.post('/requests', data); } catch { return { success: false }; }
  }
  static async updateRequest(id, data) {
    try {
      const res = await fetch(`${API_BASE}/requests/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }
  static async approveRequest(id, notes = '') {
    try { return await this.post(`/requests/${id}/approve`, { notes }); } catch { return { success: false }; }
  }
  static async rejectRequest(id, notes = '') {
    try { return await this.post(`/requests/${id}/reject`, { notes }); } catch { return { success: false }; }
  }
  static async convertRequest(id, data) {
    try { return await this.post(`/requests/${id}/convert`, data); } catch { return { success: false }; }
  }
  static async getRequestComments(id) {
    try { return await this.fetch(`/requests/${id}/comments`); } catch { return []; }
  }
  static async addRequestComment(id, data) {
    try { return await this.post(`/requests/${id}/comments`, data); } catch { return { success: false }; }
  }
  static async getRequestCategories() {
    try { return await this.fetch('/request-categories'); } catch { return []; }
  }

  // ---- AssetBoard Model Methods ----

  static async getAssets(filters = {}) {
    try {
      const p = new URLSearchParams();
      if (filters.search)      p.set('search', filters.search);
      if (filters.category_id) p.set('category_id', filters.category_id);
      if (filters.status)      p.set('status', filters.status);
      if (filters.location)    p.set('location', filters.location);
      const qs = p.toString();
      return await this.fetch(`/assets${qs ? '?' + qs : ''}`);
    } catch { return []; }
  }
  static async getAsset(id) {
    try { return await this.fetch(`/assets/${id}`); } catch { return null; }
  }
  static async getAssetStats() {
    try { return await this.fetch('/assets/stats'); }
    catch { return { total: 0, assigned: 0, available: 0, maintenance: 0, retired: 0, expiringCount: 0, expiringAssets: [] }; }
  }
  static async createAsset(data) {
    try { return await this.post('/assets', data); } catch { return { success: false }; }
  }
  static async updateAsset(id, data) {
    try {
      const res = await fetch(`${API_BASE}/assets/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }
  static async deleteAsset(id) {
    try {
      const res = await fetch(`${API_BASE}/assets/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return res.json();
    } catch { return { success: false }; }
  }
  static async assignAsset(data) {
    try { return await this.post('/assets/assign', data); } catch { return { success: false }; }
  }
  static async returnAsset(data) {
    try { return await this.post('/assets/return', data); } catch { return { success: false }; }
  }
  static async getAssetHistory(id) {
    try { return await this.fetch(`/assets/${id}/history`); } catch { return []; }
  }
  static async getAssetCategories() {
    try { return await this.fetch('/asset-categories'); } catch { return []; }
  }
  static async createAssetCategory(data) {
    try { return await this.post('/asset-categories', data); } catch { return { success: false }; }
  }

  // ---- Audit Trail Model Methods ----

  static async getAuditLogs(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.module) params.set('module', filters.module);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.search) params.set('search', filters.search);
      if (filters.offset) params.set('offset', filters.offset);
      params.set('limit', filters.limit || 50);
      const qs = params.toString();
      return await this.fetch(`/audit?${qs}`);
    } catch { return { logs: [], hasMore: false, offset: 0 }; }
  }

  static async getAuditStats() {
    try { return await this.fetch('/audit/stats'); }
    catch { return { byModule: [], byAction: [], total: 0, uniqueUsers: 0, today: 0, lastWeek: 0 }; }
  }
  static async getReportsData() {
    try { return await this.fetch('/reports'); }
    catch { return {}; }
  }
  static async getTaskManageData(filters = {}) {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join(',') : v;
      if (val) p.set(k, val);
    });
    try { return await this.fetch(`/tasks/manage?${p}`); }
    catch { return { tasks: [], summary: {}, assignees: [], projects: [] }; }
  }
  static async bulkUpdateTasks(tasks, updates) {
    try {
      const res = await fetch(`${API_BASE}/tasks/manage/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('orbit_token')}` },
        body: JSON.stringify({ tasks, updates }),
      });
      return await res.json();
    } catch { return { success: false }; }
  }

  static async getTeamMembers() {
    try {
      const teams = await this.fetch('/teams');
      return (teams || []).flatMap(team =>
        (team.members || []).map(m => ({ ...m, team_module: team.module, team_name: team.team_name }))
      );
    } catch { return []; }
  }

  static async getUserDirectory() {
    try { return await this.fetch('/users/directory'); }
    catch { return []; }
  }
}

// ============================================================================
// VIEWS (Presentation Components)
// ============================================================================

const NOTIF_ICONS = {
  task_assigned:   { icon: '📋', color: 'text-blue-600' },
  bug_assigned:    { icon: '🐛', color: 'text-red-600' },
  bug_commented:   { icon: '💬', color: 'text-teal-600' },
  comment_replied: { icon: '↩️', color: 'text-indigo-600' },
  meeting_invite:  { icon: '📹', color: 'text-sky-600' },
};

// ============================================================================
// MESSAGING PANEL
// ============================================================================

const MessagingPanel = ({ user, onClose, onUnreadChange, initialUserId, initialUserName }) => {
  const [conversations, setConversations] = useState([]);
  const [activeUserId, setActiveUserId] = useState(initialUserId || null);
  const [activeUserName, setActiveUserName] = useState(initialUserName || '');
  const [thread, setThread] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState('');
  const [otherTyping, setOtherTyping] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const typingTimerRef = React.useRef(null);
  const threadEndRef = React.useRef(null);
  const threadContainerRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const prevThreadLenRef = React.useRef(0);
  const justSentRef = React.useRef(false);

  const EMOJIS = [
    ['😀','😂','😍','🥰','😎','🤔','😭','😡','🥺','😴','🤯','🤩','😏','🙄','😬','🤗','😇','🥳','😤','🤪'],
    ['👍','👎','👏','🙌','🤝','🤜','✌️','🤞','👌','🖐️','💪','🙏','👋','🤙','☝️','👆','🫶','❤️','🔥','💯'],
    ['😆','😅','🤣','😊','😋','😜','🤭','😶','🙃','😑','🫡','🥴','🤤','😮','😲','🫢','🤫','😈','👻','💀'],
    ['🎉','🎊','✨','🌟','💫','⭐','🌈','🎯','🚀','💡','💥','❗','❓','💬','📌','🔔','📣','🏆','🎁','🫠'],
  ];

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) { setMessageText(t => t + emoji); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newText = messageText.slice(0, start) + emoji + messageText.slice(end);
    setMessageText(newText);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + emoji.length, start + emoji.length); }, 0);
  };

  const token = () => localStorage.getItem('orbit_token');
  const ah = () => ({ Authorization: `Bearer ${token()}` });

  const loadConversations = async () => {
    try {
      const res = await fetch('/api/messages/conversations', { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        const convs = data.conversations || [];
        setConversations(convs);
        // Only update parent badge — don't set to 0 if no convs yet (let parent polling handle it)
        if (convs.length > 0) {
          const total = convs.reduce((s, c) => s + (c.unread_count || 0), 0);
          onUnreadChange?.(total);
        }
      }
    } catch { /* ignore */ }
  };

  const loadUserDirectory = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch('/api/users/directory', { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        setAllUsers((data.users || data || []).filter(u => u.user_id !== user?.id));
      }
    } catch { /* ignore */ }
    setLoadingUsers(false);
  };

  const loadThread = async (uid, uname) => {
    setLoadingThread(true);
    setActiveUserId(uid);
    setActiveUserName(uname);
    try {
      const res = await fetch(`/api/messages/thread/${uid}`, { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        setThread(data.messages || []);
      }
      await fetch(`/api/messages/thread/${uid}`, { method: 'PUT', headers: ah() }).catch(() => {});
      loadConversations();
    } catch { /* ignore */ }
    setLoadingThread(false);
  };

  const refreshThread = async (uid) => {
    try {
      const res = await fetch(`/api/messages/thread/${uid}`, { headers: ah() });
      if (res.ok) { const data = await res.json(); setThread(data.messages || []); }
    } catch { /* ignore */ }
  };

  const checkTyping = async (uid) => {
    try {
      const res = await fetch(`/api/messages/typing/${uid}`, { headers: ah() });
      if (res.ok) { const data = await res.json(); setOtherTyping(data.isTyping || false); }
    } catch { /* ignore */ }
  };

  const sendTypingSignal = async (uid) => {
    await fetch(`/api/messages/typing/${uid}`, { method: 'PUT', headers: ah() }).catch(() => {});
  };

  useEffect(() => {
    loadUserDirectory();
    loadConversations();
    // If opened directly to a thread (from message dropdown), fetch the thread immediately
    if (initialUserId) {
      const ah = () => ({ Authorization: `Bearer ${localStorage.getItem('orbit_token')}` });
      fetch(`/api/messages/thread/${initialUserId}`, { headers: ah() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setThread(d.messages || []); })
        .catch(() => {});
      fetch(`/api/messages/thread/${initialUserId}`, { method: 'PUT', headers: ah() }).catch(() => {});
    }
    const iv = setInterval(() => {
      if (activeUserId) { refreshThread(activeUserId); checkTyping(activeUserId); loadConversations(); }
      else loadConversations();
    }, 3000);
    return () => clearInterval(iv);
  }, [activeUserId]);

  useEffect(() => {
    const container = threadContainerRef.current;
    const isInitialLoad = prevThreadLenRef.current === 0 && thread.length > 0;
    const hasNew = thread.length > prevThreadLenRef.current;
    const nearBottom = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight < 120
      : true;

    const scrollToBottom = (instant) => {
      requestAnimationFrame(() => {
        const c = threadContainerRef.current;
        if (c) {
          if (instant) c.scrollTop = c.scrollHeight;
          else threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      });
    };

    if (isInitialLoad || justSentRef.current) {
      scrollToBottom(true);
    } else if (hasNew && nearBottom) {
      scrollToBottom(false);
    }

    prevThreadLenRef.current = thread.length;
    justSentRef.current = false;
  }, [thread]);

  const handleSend = async () => {
    if (!messageText.trim() || !activeUserId || sending) return;
    setSending(true);
    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...ah() },
        body: JSON.stringify({ recipientId: activeUserId, content: messageText.trim() }),
      });
      setMessageText('');
      setShowEmoji(false);
      justSentRef.current = true;
      // Use refreshThread (no loading flash) so container stays visible for scroll measurement
      await refreshThread(activeUserId);
    } catch { /* ignore */ }
    setSending(false);
  };

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return 'just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  // Build a map of unread counts + last message keyed by user id
  const convMap = {};
  conversations.forEach(c => { convMap[c.other_id] = c; });

  const filteredUsers = allUsers.filter(u =>
    !search || u.user_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed bottom-0 right-6 w-[380px] h-[520px] bg-white rounded-t-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white">
        <div className="flex items-center gap-2">
          {activeUserId && (
            <button onClick={() => { setActiveUserId(null); setActiveUserName(''); setThread([]); prevThreadLenRef.current = 0; }} className="p-1 rounded hover:bg-blue-500">
              <ArrowLeft size={16} />
            </button>
          )}
          <MessageSquare size={16} />
          <span className="font-semibold text-sm">{activeUserId ? activeUserName : 'Messages'}</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-blue-500"><X size={16} /></button>
      </div>

      {/* Content area */}
      {!activeUserId ? (
        /* User list */
        <>
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search people..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {loadingUsers ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <Users size={32} className="text-gray-200" />
                <p className="text-sm">No users found</p>
              </div>
            ) : filteredUsers.map(u => {
              const conv = convMap[u.user_id];
              const unread = conv?.unread_count || 0;
              const isActive = u.is_active === 1 || u.is_active === true;
              return (
                <button
                  key={u.user_id}
                  onClick={() => loadThread(u.user_id, u.user_name)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                      {(u.user_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    {/* Active/inactive status dot */}
                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${isActive ? 'bg-green-500' : 'bg-red-400'}`} />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-semibold truncate ${unread > 0 ? 'text-gray-900' : 'text-gray-700'}`}>{u.user_name}</p>
                      {conv && <p className="text-[11px] text-gray-400 flex-shrink-0 ml-2">{fmtTime(conv.last_at)}</p>}
                    </div>
                    {conv ? (
                      <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>{conv.last_message}</p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-0.5">No messages yet</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* Chat thread */
        <>
          <div ref={threadContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {loadingThread ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
            ) : thread.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">No messages yet. Say hi!</div>
            ) : thread.map(msg => {
              const isMe = Number(msg.senderId) === Number(user?.id);
              return (
                <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                  {!isMe && (
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0 mb-0.5">
                      {(activeUserName || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className={`max-w-[72%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${isMe ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none border border-gray-100'}`}>
                    <p className="leading-snug break-words">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-blue-200 text-right' : 'text-gray-400'}`}>{fmtTime(msg.createdAt)}</p>
                  </div>
                </div>
              );
            })}
            {/* Typing indicator */}
            {otherTyping && (
              <div className="flex items-end gap-2 justify-start">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                  {(activeUserName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={threadEndRef} />
          </div>
          {/* Emoji Picker */}
          {showEmoji && (
            <div className="px-3 pb-1 bg-white border-t border-gray-100">
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-2 space-y-1">
                <div className="flex items-center justify-between mb-1 px-1">
                  <span className="text-xs text-gray-400 font-medium">Pick an emoji</span>
                  <button
                    onClick={() => setShowEmoji(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X size={13} />
                  </button>
                </div>
                {EMOJIS.map((row, ri) => (
                  <div key={ri} className="flex flex-wrap gap-0.5">
                    {row.map(em => (
                      <button
                        key={em}
                        onClick={() => insertEmoji(em)}
                        className="w-8 h-8 flex items-center justify-center text-lg rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Input */}
          <div className="px-3 py-3 bg-white border-t border-gray-100 flex items-end gap-2">
            <button
              onClick={() => setShowEmoji(s => !s)}
              className={`p-2 rounded-xl transition-colors flex-shrink-0 ${showEmoji ? 'bg-yellow-100 text-yellow-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              title="Emoji"
            >
              😊
            </button>
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={e => {
                setMessageText(e.target.value);
                if (activeUserId && e.target.value.trim()) {
                  sendTypingSignal(activeUserId);
                  clearTimeout(typingTimerRef.current);
                }
              }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-24 overflow-y-auto"
              style={{ lineHeight: '1.4' }}
            />
            <button
              onClick={handleSend}
              disabled={!messageText.trim() || sending}
              className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <Send size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Header = ({ user, onMenuClick, onLogout, onChangePassword, notifications, unreadCount, onMarkRead, onMarkAllRead, onDismiss, unreadMessages, msgConversations, onOpenMessage, onToggleMessages }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const initials = (user?.name || 'U').charAt(0).toUpperCase();

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return 'just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="bg-blue-600 text-white shadow-md relative z-40">
      <div className="max-w-7xl mx-auto px-4 py-3.5 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} className="lg:hidden p-1">
            <Menu size={22} />
          </button>
          <div className="text-xl font-bold tracking-wide">🛰️ ORBIT</div>
        </div>

        <div className="flex items-center gap-2">
          {/* Messages Icon + Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setMsgOpen(o => !o); setNotifOpen(false); setMenuOpen(false); }}
              className="relative p-2 rounded-lg hover:bg-blue-500 transition-colors"
              title="Messages"
            >
              <MessageSquare size={20} />
              {unreadMessages > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>

            {msgOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setMsgOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={15} className="text-gray-500" />
                      <span className="font-semibold text-gray-800 text-sm">Messages</span>
                      {unreadMessages > 0 && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">{unreadMessages} new</span>
                      )}
                    </div>
                    <button onClick={() => { setMsgOpen(false); onToggleMessages?.(); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      View all
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                    {(!msgConversations || msgConversations.length === 0) ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        <MessageSquare size={24} className="mx-auto mb-2 text-gray-300" />
                        No messages yet
                      </div>
                    ) : msgConversations.map(c => (
                      <div
                        key={c.other_id}
                        className={`flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${c.unread_count > 0 ? 'bg-blue-50' : ''}`}
                        onClick={() => { setMsgOpen(false); onOpenMessage?.(c.other_id, c.other_name); }}
                      >
                        <div className="flex-shrink-0 text-xl leading-none mt-0.5">💬</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${c.unread_count > 0 ? 'text-gray-900' : 'text-gray-600'}`}>
                            New message from {c.other_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug truncate">{c.last_message}</p>
                          <p className="text-[11px] text-gray-400 mt-1">{fmtTime(c.last_at)}</p>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); /* could dismiss later */ }}
                          className="flex-shrink-0 p-1 text-gray-300 hover:text-gray-500 rounded self-start"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(o => !o); setMenuOpen(false); }}
              className="relative p-2 rounded-lg hover:bg-blue-500 transition-colors"
              title="Notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setNotifOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Bell size={15} className="text-gray-500" />
                      <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">{unreadCount} new</span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button onClick={() => { onMarkAllRead?.(); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Mark all read</button>
                    )}
                  </div>

                  <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        <Bell size={24} className="mx-auto mb-2 text-gray-300" />
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map(n => {
                        const style = NOTIF_ICONS[n.type] || { icon: '🔔', color: 'text-gray-500' };
                        return (
                          <div
                            key={n.id}
                            className={`flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${!n.isRead ? 'bg-blue-50' : ''}`}
                            onClick={() => { if (!n.isRead) onMarkRead?.(n.id); }}
                          >
                            <div className="flex-shrink-0 text-lg leading-none mt-0.5">{style.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold ${!n.isRead ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n.message}</p>
                              <p className="text-[11px] text-gray-400 mt-1">{fmtTime(n.createdAt)}</p>
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); onDismiss?.(n.id); }}
                              className="flex-shrink-0 p-1 text-gray-300 hover:text-gray-500 rounded self-start"
                              title="Dismiss"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => { setMenuOpen(o => !o); setNotifOpen(false); }}
              className="flex items-center gap-2.5 hover:bg-blue-500 rounded-lg px-2 py-1.5 transition-colors"
            >
              <span className="hidden sm:inline text-sm opacity-90">{user?.name}</span>
              {user?.avatar ? (
                <img src={user.avatar} alt={user?.name} className="w-8 h-8 rounded-full object-cover border-2 border-blue-300" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center font-bold text-sm border-2 border-blue-300">
                  {initials}
                </div>
              )}
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 min-w-[200px] overflow-hidden z-50">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <p className="font-semibold text-gray-800 text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize mt-0.5">{user?.role || 'User'}</p>
                    {user?.email && <p className="text-xs text-gray-400 mt-0.5 truncate">{user.email}</p>}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { setMenuOpen(false); onChangePassword?.(); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                    >
                      <Shield size={14} className="text-gray-400" /> Change Password
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={() => { setMenuOpen(false); onLogout?.(); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                    >
                      <X size={14} /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RISK REGISTER MODULE
// ============================================================================

// ── Confirm Dialog (singleton) ──────────────────────────────────────────────
const _confirmRef = { show: null };
const requestConfirm = (message, onConfirm) => _confirmRef.show?.(message, onConfirm);

const ConfirmDialog = () => {
  const [state, setState] = useState(null);
  _confirmRef.show = (message, onConfirm) => setState({ message, onConfirm });
  if (!state) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Trash2 size={18} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Confirm Deletion</h3>
              <p className="text-sm text-gray-500">{state.message}</p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setState(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={() => { state.onConfirm(); setState(null); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const RISK_CATEGORIES = ['technical', 'resource', 'schedule', 'budget', 'external', 'security', 'compliance'];
const RISK_LEVELS = ['low', 'medium', 'high'];
const RISK_STATUSES = ['open', 'mitigated', 'accepted', 'closed'];

const riskScoreColor = (score) => {
  if (score >= 6) return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: 'High', dot: 'bg-red-500' };
  if (score >= 3) return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: 'Medium', dot: 'bg-yellow-500' };
  return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', label: 'Low', dot: 'bg-green-500' };
};

const statusColor = (status) => {
  const map = {
    open: 'bg-blue-100 text-blue-700',
    mitigated: 'bg-green-100 text-green-700',
    accepted: 'bg-yellow-100 text-yellow-700',
    closed: 'bg-gray-100 text-gray-600',
  };
  return map[status] || 'bg-gray-100 text-gray-600';
};

const RiskFormModal = ({ risk, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: risk?.title || '',
    description: risk?.description || '',
    category: risk?.category || 'technical',
    likelihood: risk?.likelihood || 'medium',
    impact: risk?.impact || 'medium',
    status: risk?.status || 'open',
    owner: risk?.owner || '',
    mitigation_plan: risk?.mitigation_plan || '',
    contingency_plan: risk?.contingency_plan || '',
    due_date: risk?.due_date || '',
  });
  const [saving, setSaving] = useState(false);

  const score = { low: 1, medium: 2, high: 3 };
  const previewScore = (score[form.likelihood] || 2) * (score[form.impact] || 2);
  const previewColor = riskScoreColor(previewScore);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{risk ? 'Edit Risk' : 'Add New Risk'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood</label>
              <select value={form.likelihood} onChange={e => setForm(f => ({ ...f, likelihood: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
              <select value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${previewColor.bg} ${previewColor.border}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${previewColor.dot}`} />
            <span className={`text-sm font-medium ${previewColor.text}`}>Risk Score: {previewScore} — {previewColor.label} Risk</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation Plan</label>
            <textarea value={form.mitigation_plan} onChange={e => setForm(f => ({ ...f, mitigation_plan: e.target.value }))}
              rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Plan</label>
            <textarea value={form.contingency_plan} onChange={e => setForm(f => ({ ...f, contingency_plan: e.target.value }))}
              rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving…' : risk ? 'Update Risk' : 'Add Risk'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RiskHeatmap = ({ risks }) => {
  const levels = ['low', 'medium', 'high'];
  const grid = {};
  levels.forEach(l => levels.forEach(i => { grid[`${l}-${i}`] = []; }));
  risks.forEach(r => {
    const key = `${r.likelihood}-${r.impact}`;
    if (grid[key]) grid[key].push(r);
  });

  const cellColor = (l, i) => {
    const score = { low: 1, medium: 2, high: 3 };
    const s = score[l] * score[i];
    if (s >= 6) return 'bg-red-100 border-red-200';
    if (s >= 3) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Risk Heatmap</h3>
      <div className="flex gap-2">
        <div className="flex flex-col justify-around pr-2 text-xs text-gray-500 font-medium" style={{ width: 64 }}>
          {['High', 'Medium', 'Low'].map(l => <div key={l} className="text-right">{l}</div>)}
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-3 gap-1.5">
            {['high', 'medium', 'low'].flatMap(likelihood =>
              ['low', 'medium', 'high'].map(impact => {
                const items = grid[`${likelihood}-${impact}`] || [];
                return (
                  <div key={`${likelihood}-${impact}`} className={`border rounded-lg p-2 min-h-[64px] ${cellColor(likelihood, impact)}`}>
                    {items.length > 0
                      ? items.map(r => (
                          <div key={r.id} className="text-xs text-gray-700 truncate mb-0.5" title={r.title}>• {r.title}</div>
                        ))
                      : <div className="text-xs text-gray-300 text-center mt-2">—</div>
                    }
                  </div>
                );
              })
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-1.5">
            {['Low', 'Medium', 'High'].map(l => (
              <div key={l} className="text-center text-xs text-gray-500 font-medium">{l}</div>
            ))}
          </div>
          <div className="text-center text-xs text-gray-400 mt-1">Impact →</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">← Likelihood</div>
    </div>
  );
};

// ============================================================================
// PROJECT CHARTER
// ============================================================================

const emptyCharter = () => ({
  department: '',
  project_purpose: '',
  biz_summary: '', biz_problem: '', biz_solution: '',
  benefits_qty: '', benefits_qlty: '',
  del1_title: 'Deliverable 1', del1_desc: '', del1_criteria: '',
  del2_title: 'Deliverable 2', del2_desc: '', del2_criteria: '',
  del3_title: 'Deliverable 3', del3_desc: '', del3_criteria: '',
  del4_title: 'Deliverable 4', del4_desc: '', del4_criteria: '',
  strategic_alignment: '',
  exclusions: ['', ''],
  obj1_title: '', obj1_alignment: '', obj1_metrics: '',
  obj2_title: '', obj2_alignment: '', obj2_metrics: '',
  obj3_title: '', obj3_alignment: '', obj3_metrics: '',
  start_date: '',
  update_frequency: '',
  total_budget: 'No external budget allocated; project will utilize existing employee time and resources',
  budget_personnel: '', budget_hardware: '', budget_software: '', budget_services: '',
  sponsor: '',
  stakeholder_register: '',
  risk_biz: [{ risk: '', level: 'High' }, { risk: '', level: 'Medium' }],
  mitigating_strategies: '',
  risk_tech: [{ risk: '', level: 'Medium' }],
  risk_register: '',
  team_business: [
    { name: '', role: '', responsibilities: '' },
    { name: '', role: '', responsibilities: '' },
    { name: '', role: '', responsibilities: '' },
  ],
  team_it: [
    { name: 'Anthony Zoblescin', role: 'IT Director', responsibilities: 'Solution Architect' },
    { name: 'Gian Halili', role: 'IT Project Manager', responsibilities: 'Project Manager' },
    { name: 'Karen Jimenez', role: 'DevOps Manager', responsibilities: 'Lead Developer' },
    { name: 'Richard Estrelles', role: 'DataOps Manager', responsibilities: 'Data Architect' },
    { name: 'Rommel Acebedo', role: 'Database Administrator', responsibilities: 'Data Architect' },
    { name: 'Mary Joy Colas', role: 'BI Analyst', responsibilities: 'Data Architect' },
    { name: 'Jerald Tembrevilla', role: 'Fullstack Developer', responsibilities: 'Software Developer' },
    { name: 'Kamil Corpuz', role: 'Fullstack Developer', responsibilities: 'Software Developer' },
  ],
  steering: [
    { name: '', title: '', approval_date: '' },
    { name: '', title: '', approval_date: '' },
    { name: '', title: '', approval_date: '' },
  ],
  tech_infrastructure: 'Existing hosting environment',
  tech_software: '', tech_tools: '',
  int_deps: ['', ''],
  ext_deps: ['', ''],
  implementation_rec: '',
});

const ENGAGEMENT_STYLES = {
  unaware:    { bg: '#f3f4f6', color: '#6b7280', label: 'Unaware' },
  resistant:  { bg: '#fee2e2', color: '#dc2626', label: 'Resistant' },
  neutral:    { bg: '#fef9c3', color: '#ca8a04', label: 'Neutral' },
  supportive: { bg: '#dcfce7', color: '#16a34a', label: 'Supportive' },
  leading:    { bg: '#dbeafe', color: '#2563eb', label: 'Leading' },
};
const INFLUENCE_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };
const INTEREST_LABELS  = { low: 'Low', medium: 'Medium', high: 'High' };
const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', quarterly: 'Quarterly', asneeded: 'As Needed' };

const EMPTY_STAKEHOLDER = { name: '', title: '', organization: '', email: '', role: '', interest: 'medium', influence: 'medium', engagement: 'neutral', communicationFrequency: 'monthly', notes: '' };

const StakeholderFormModal = ({ initial = EMPTY_STAKEHOLDER, onClose, onSave }) => {
  const [form, setForm] = useState({ ...EMPTY_STAKEHOLDER, ...initial });
  const f = (k, v) => setForm(s => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">{initial.id ? 'Edit Stakeholder' : 'Add Stakeholder'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input value={form.name} onChange={e => f('name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title / Position</label>
              <input value={form.title} onChange={e => f('title', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <input value={form.organization} onChange={e => f('organization', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => f('email', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role / Category</label>
              <input value={form.role} onChange={e => f('role', e.target.value)} placeholder="e.g. Sponsor, End User, Regulator…" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interest Level</label>
              <select value={form.interest} onChange={e => f('interest', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Influence / Power</label>
              <select value={form.influence} onChange={e => f('influence', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Engagement</label>
              <select value={form.engagement} onChange={e => f('engagement', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {Object.entries(ENGAGEMENT_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Communication Frequency</label>
            <select value={form.communicationFrequency} onChange={e => f('communicationFrequency', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Concerns</label>
            <textarea value={form.notes} onChange={e => f('notes', e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={() => { if (!form.name.trim()) return; onSave(form); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">{initial.id ? 'Save Changes' : 'Add Stakeholder'}</button>
        </div>
      </div>
    </div>
  );
};

const StakeholderRegisterTab = ({ projectId, user }) => {
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'grid'

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/plantrack/projects/${projectId}/stakeholders`);
      const data = await r.json();
      setStakeholders(Array.isArray(data) ? data : []);
    } catch { setStakeholders([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const handleSave = async (form) => {
    if (editing) {
      await fetch(`/api/plantrack/projects/${projectId}/stakeholders/${editing.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    } else {
      await fetch(`/api/plantrack/projects/${projectId}/stakeholders`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); load();
  };
  const handleDelete = async (id) => {
    await fetch(`/api/plantrack/projects/${projectId}/stakeholders/${id}`, { method: 'DELETE' });
    load();
  };

  // Power/Interest grid: 3×3 (low/med/high influence × low/med/high interest)
  const gridLevels = ['high', 'medium', 'low'];
  const gridCell = (inf, int) => stakeholders.filter(s => s.influence === inf && s.interest === int);

  const gridQuadrantLabel = (inf, int) => {
    if (inf === 'high' && int === 'high') return { label: 'Manage Closely', bg: '#fef3c7', border: '#f59e0b' };
    if (inf === 'high' && int !== 'high') return { label: 'Keep Satisfied', bg: '#dbeafe', border: '#3b82f6' };
    if (inf !== 'high' && int === 'high') return { label: 'Keep Informed', bg: '#dcfce7', border: '#22c55e' };
    return { label: 'Monitor', bg: '#f3f4f6', border: '#d1d5db' };
  };

  return (
    <div>
      {(showForm || editing) && (
        <StakeholderFormModal initial={editing || EMPTY_STAKEHOLDER} onClose={() => { setShowForm(false); setEditing(null); }} onSave={handleSave} />
      )}

      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Stakeholder Register</h2>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{stakeholders.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setView('list')} className={`px-3 py-1.5 text-xs font-medium ${view === 'list' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>List</button>
            <button onClick={() => setView('grid')} className={`px-3 py-1.5 text-xs font-medium ${view === 'grid' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Power/Interest Grid</button>
          </div>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Stakeholder
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>}
      {!loading && stakeholders.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-sm mb-3">No stakeholders registered yet.</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">+ Add First Stakeholder</button>
        </div>
      )}

      {/* List View */}
      {!loading && stakeholders.length > 0 && view === 'list' && (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Role', 'Interest', 'Influence', 'Engagement', 'Comm. Frequency', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stakeholders.map(s => {
                const eng = ENGAGEMENT_STYLES[s.engagement] || ENGAGEMENT_STYLES.neutral;
                return (
                  <tr key={s.id} className="hover:bg-gray-50 group">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{s.name}</p>
                      {s.title && <p className="text-xs text-gray-400">{s.title}{s.organization ? ` · ${s.organization}` : ''}</p>}
                      {s.email && <p className="text-xs text-teal-600">{s.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{s.role || '—'}</td>
                    <td className="px-4 py-3"><span className="capitalize text-gray-700">{INTEREST_LABELS[s.interest] || s.interest}</span></td>
                    <td className="px-4 py-3"><span className="capitalize text-gray-700">{INFLUENCE_LABELS[s.influence] || s.influence}</span></td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: eng.bg, color: eng.color }}>{eng.label}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{FREQ_LABELS[s.communicationFrequency] || s.communicationFrequency}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditing(s)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded"><Pencil size={13} /></button>
                        <button onClick={() => requestConfirm(`Delete stakeholder "${s.name}"?`, () => handleDelete(s.id))} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Notes panel */}
          {stakeholders.some(s => s.notes) && (
            <div className="border-t border-gray-200 divide-y divide-gray-100">
              {stakeholders.filter(s => s.notes).map(s => (
                <div key={s.id} className="px-4 py-2.5 flex gap-3">
                  <span className="text-xs font-semibold text-gray-500 shrink-0 w-28 pt-0.5">{s.name}</span>
                  <p className="text-xs text-gray-500">{s.notes}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Power / Interest Grid */}
      {!loading && stakeholders.length > 0 && view === 'grid' && (
        <div>
          <div className="flex gap-4">
            {/* Y-axis label */}
            <div className="flex items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Influence / Power →</span>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-3 gap-2" style={{ gridTemplateRows: 'repeat(3, minmax(100px, auto))' }}>
                {gridLevels.map(inf => gridLevels.map(int => {
                  const cells = gridCell(inf, int);
                  const q = gridQuadrantLabel(inf, int);
                  return (
                    <div key={`${inf}-${int}`} className="rounded-lg border-2 p-3 min-h-24" style={{ background: q.bg, borderColor: q.border }}>
                      <p className="text-xs font-bold mb-2" style={{ color: q.border }}>{q.label}</p>
                      <div className="space-y-1">
                        {cells.map(s => {
                          const eng = ENGAGEMENT_STYLES[s.engagement] || ENGAGEMENT_STYLES.neutral;
                          return (
                            <div key={s.id} className="flex items-center gap-1.5 cursor-pointer" onClick={() => setEditing(s)}>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: eng.color }} />
                              <span className="text-xs font-medium text-gray-800 truncate">{s.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }))}
              </div>
              {/* X-axis label */}
              <div className="text-center mt-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Interest →</span>
              </div>
              {/* Axis labels */}
              <div className="grid grid-cols-3 gap-2 mt-1 text-center">
                {['Low Interest', 'Medium Interest', 'High Interest'].map(l => <span key={l} className="text-xs text-gray-400">{l}</span>)}
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500 mr-1">Engagement:</span>
            {Object.entries(ENGAGEMENT_STYLES).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
                {v.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const sanitizeCharterHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/javascript\s*:/gi, '');
};

const CharterWysiwygField = ({ initialValue = '', onChange, placeholder = '' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!initialValue);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (initialValue) { editorRef.current.innerHTML = initialValue; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); syncValue(); };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong>B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable onInput={syncValue} onPaste={handlePaste}
          className="min-h-[64px] max-h-40 overflow-y-auto px-3 py-2 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none"
          suppressContentEditableWarning />
      </div>
    </div>
  );
};

const ProjectCharterTab = ({ projectId, projectName, user }) => {
  const [charter, setCharter] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyCharter());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const canEdit = user?.role !== 'marketing' && user?.role !== 'qa';

  const load = async () => {
    setLoading(true);
    const data = await ModulesModel.getProjectCharter(projectId);
    setCharter(data && Object.keys(data).length > 0 ? data : null);
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId]);

  const parseCharter = (data) => {
    if (!data) return emptyCharter();
    if (data.charter_json) {
      try {
        const p = typeof data.charter_json === 'string' ? JSON.parse(data.charter_json) : data.charter_json;
        const merged = { ...emptyCharter(), ...p };
        // Migrate old flat exclusion fields → array
        if (!p.exclusions && (p.exclusion_1 !== undefined || p.exclusion_2 !== undefined)) {
          merged.exclusions = [p.exclusion_1 || '', p.exclusion_2 || ''].filter(Boolean);
          if (!merged.exclusions.length) merged.exclusions = [''];
        }
        // Migrate old flat dependency fields → arrays
        if (!p.int_deps && (p.int_dep_1 !== undefined || p.int_dep_2 !== undefined)) {
          merged.int_deps = [p.int_dep_1 || '', p.int_dep_2 || ''].filter(Boolean);
          if (!merged.int_deps.length) merged.int_deps = [''];
        }
        if (!p.ext_deps && (p.ext_dep_1 !== undefined || p.ext_dep_2 !== undefined)) {
          merged.ext_deps = [p.ext_dep_1 || '', p.ext_dep_2 || ''].filter(Boolean);
          if (!merged.ext_deps.length) merged.ext_deps = [''];
        }
        return merged;
      } catch {}
    }
    // Legacy fallback
    return { ...emptyCharter(), sponsor: data.sponsor || '', biz_summary: data.executive_summary || '', start_date: data.timeline_start || '', total_budget: data.budget_estimated || emptyCharter().total_budget };
  };

  const startEdit = () => { setForm(parseCharter(charter)); setEditing(true); };

  const handleSave = async () => {
    setSaving(true);
    await ModulesModel.saveProjectCharter(projectId, { charter_json: JSON.stringify(form) });
    await load();
    setEditing(false);
    setSaving(false);
  };

  const updArr = (key, idx, field, val) => setForm(f => ({ ...f, [key]: f[key].map((r, i) => i === idx ? { ...r, [field]: val } : r) }));
  const addRow = (key, tpl) => setForm(f => ({ ...f, [key]: [...f[key], { ...tpl }] }));
  const delRow = (key, idx) => setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const d = charter ? parseCharter(charter) : null;

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading charter…</div>;

  const iCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const lCls = 'block text-xs font-semibold text-gray-500 mb-1';
  const inp = (key, ph = '', type = 'text') => <input type={type} value={form[key] || ''} placeholder={ph} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className={iCls} />;
  const ta = (key, ph = '') => <CharterWysiwygField key={`charter-${projectId}-${key}`} initialValue={form[key] || ''} onChange={v => setForm(f => ({ ...f, [key]: v }))} placeholder={ph} />;
  const lbl = (t) => <label className={lCls}>{t}</label>;
  const fld = (label, children) => <div>{lbl(label)}{children}</div>;
  const richText = (val) => val ? <span className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: sanitizeCharterHtml(val) }} /> : null;

  // ── EDIT MODE ──────────────────────────────────────────────────────────────
  if (editing) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-0 bg-gray-50 z-10 py-2 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Edit Project Charter</h2>
        <div className="flex gap-2">
          <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">{saving ? 'Saving…' : 'Save Charter'}</button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Header</h3>
        {fld('Department', inp('department', 'e.g. IT'))}
      </div>

      {/* Project Details */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Project Details</h3>
        {fld('Project Purpose', ta('project_purpose', 'State the purpose of this project…', 3))}
        <div className="space-y-2">
          {lbl('Business Case')}
          {fld('Executive Summary:', ta('biz_summary', '', 3))}
          {fld('Problem Statement:', ta('biz_problem', '', 2))}
          {fld('Proposed Solution:', ta('biz_solution', '', 2))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {fld('Quantitative Benefits', ta('benefits_qty', '', 2))}
          {fld('Qualitative Benefits', ta('benefits_qlty', '', 2))}
        </div>
        <div className="space-y-3">
          {lbl('Project Scope – Deliverables')}
          {[1,2,3,4].map(n => (
            <div key={n} className="border border-gray-200 rounded-lg p-3 space-y-2">
              {fld(`Deliverable ${n} Title`, inp(`del${n}_title`, `Deliverable ${n}`))}
              {fld('Description:', ta(`del${n}_desc`, '', 2))}
              {fld('Success Criteria:', ta(`del${n}_criteria`, '', 2))}
            </div>
          ))}
        </div>
        {fld('Strategic Alignment', ta('strategic_alignment', '', 2))}
        <div className="space-y-2">
          {lbl('Out of Scope / Potential Enhancement in future')}
          {(form.exclusions || []).map((ex, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={ex} onChange={e => setForm(f => ({ ...f, exclusions: f.exclusions.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="Exclusion or future enhancement…" className={iCls + ' flex-1'} />
              {form.exclusions.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, exclusions: f.exclusions.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, exclusions: [...f.exclusions, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Exclusion</button>
        </div>
        <div className="space-y-3">
          {lbl('Project Objectives')}
          {[1,2,3].map(n => (
            <div key={n} className="border border-gray-200 rounded-lg p-3 space-y-2">
              {fld(`Business Objective ${n}:`, inp(`obj${n}_title`, `Business Objective ${n}`))}
              {fld('Alignment Description:', ta(`obj${n}_alignment`, '', 2))}
              {fld('Success Metrics:', ta(`obj${n}_metrics`, '', 2))}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {fld('Start Date', inp('start_date', '', 'date'))}
          {fld('Frequency of Updates', inp('update_frequency', 'e.g. Weekly'))}
        </div>
        {fld('Total Budget', inp('total_budget'))}
        <div className="space-y-2">
          {lbl('Budget Allocation')}
          <div className="grid grid-cols-2 gap-3">
            {fld('Personnel:', inp('budget_personnel'))}
            {fld('Hardware:', inp('budget_hardware'))}
            {fld('Software:', inp('budget_software'))}
            {fld('Services:', inp('budget_services'))}
          </div>
        </div>
        {fld('Project Sponsor', inp('sponsor'))}
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Project Team Members</h3>
        {[['team_business','Business Users'],['team_it','IT & Vendor']].map(([key, title]) => (
          <div key={key}>
            <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
            <div className="space-y-2">
              {form[key].map((m, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 items-center">
                  <input value={m.name} onChange={e => updArr(key, i, 'name', e.target.value)} placeholder="Name" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <input value={m.role} onChange={e => updArr(key, i, 'role', e.target.value)} placeholder="Role" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <input value={m.responsibilities} onChange={e => updArr(key, i, 'responsibilities', e.target.value)} placeholder="Responsibilities" className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                    <button onClick={() => delRow(key, i)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => addRow(key, { name: '', role: '', responsibilities: '' })} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Member</button>
            </div>
          </div>
        ))}
      </div>

      {/* Steering Committee */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Steering Committee</h3>
        {form.steering.map((m, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 items-center">
            <input value={m.name} onChange={e => updArr('steering', i, 'name', e.target.value)} placeholder="Name" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            <input value={m.title} onChange={e => updArr('steering', i, 'title', e.target.value)} placeholder="Title" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            <div className="flex gap-2">
              <input type="date" value={m.approval_date} onChange={e => updArr('steering', i, 'approval_date', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
              <button onClick={() => delRow('steering', i)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          </div>
        ))}
        <button onClick={() => addRow('steering', { name: '', title: '', approval_date: '' })} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Member</button>
      </div>

      {/* Technical Specifications */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Technical Specifications</h3>
        <div className="space-y-2">
          {lbl('Resources')}
          {fld('Infrastructure:', inp('tech_infrastructure', 'e.g. Existing hosting environment'))}
          {fld('Software:', inp('tech_software'))}
          {fld('Tools:', inp('tech_tools'))}
        </div>
        <div className="space-y-2">
          {lbl('Dependencies')}
          <p className="text-xs font-medium text-gray-600">Internal Dependencies</p>
          {(form.int_deps || []).map((dep, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={dep} onChange={e => setForm(f => ({ ...f, int_deps: f.int_deps.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="Internal dependency…" className={iCls + ' flex-1'} />
              {form.int_deps.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, int_deps: f.int_deps.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, int_deps: [...f.int_deps, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Internal Dependency</button>
          <p className="text-xs font-medium text-gray-600 mt-2">External Dependencies</p>
          {(form.ext_deps || []).map((dep, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={dep} onChange={e => setForm(f => ({ ...f, ext_deps: f.ext_deps.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="External dependency…" className={iCls + ' flex-1'} />
              {form.ext_deps.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, ext_deps: f.ext_deps.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, ext_deps: [...f.ext_deps, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add External Dependency</button>
        </div>
        {fld('Implementation Recommendations', ta('implementation_rec', '', 3))}
      </div>
    </div>
  );

  // ── EMPTY STATE ────────────────────────────────────────────────────────────
  if (!charter) return (
    <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
      <div className="text-4xl mb-3">📋</div>
      <p className="text-gray-500 font-medium mb-1">No charter created yet</p>
      <p className="text-sm text-gray-400 mb-4">Define the project scope, goals, team, and get stakeholder sign-off.</p>
      {canEdit && <button onClick={startEdit} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Charter</button>}
    </div>
  );

  // ── VIEW MODE ──────────────────────────────────────────────────────────────
  const DR = ({ label, children }) => (
    <div className="flex border-b border-gray-300 last:border-b-0 min-h-[2.5rem]">
      <div className="w-52 flex-shrink-0 bg-gray-100 border-r border-gray-300 px-3 py-2.5 font-bold text-sm text-gray-800 leading-tight">{label}</div>
      <div className="flex-1 px-3 py-2.5 text-sm text-gray-800">{children || <span className="text-gray-400 text-xs italic">—</span>}</div>
    </div>
  );
  const SH = ({ title }) => <div className="bg-gray-300 text-center font-bold py-2.5 text-sm text-gray-900 border-b border-gray-400">{title}</div>;
  const Tbl = ({ title, children }) => <div className="border-2 border-gray-400 rounded overflow-hidden">{title && <SH title={title} />}{children}</div>;
  const riskClr = (l) => ({ Critical: 'text-red-700 font-semibold', High: 'text-orange-600 font-semibold', Medium: 'text-yellow-700', Low: 'text-green-600' }[l] || '');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Project Charter</h2>
          {charter.updated_at && <p className="text-xs text-gray-400 mt-0.5">Last updated {charter.updated_at.slice(0,10)}</p>}
        </div>
        {canEdit && <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"><Pencil size={14}/> Edit</button>}
      </div>

      {/* Header Info */}
      <Tbl>
        {[['Project ID', projectId],['Project Name', projectName],['Department', d.department],['Created Date', charter.created_at?.slice(0,10)]].map(([l,v]) => (
          <div key={l} className="flex border-b border-gray-300 last:border-b-0">
            <div className="w-40 flex-shrink-0 bg-gray-100 border-r border-gray-300 px-3 py-2 font-bold text-sm text-gray-800">{l}</div>
            <div className="flex-1 px-3 py-2 text-sm text-gray-800">{v || ''}</div>
          </div>
        ))}
      </Tbl>

      {/* Project Details */}
      <Tbl title="Project Details">
        <DR label="Project Purpose">{richText(d.project_purpose)}</DR>
        <DR label="Business Case">
          {(d.biz_summary || d.biz_problem || d.biz_solution) ? <div className="space-y-2">
            {d.biz_summary && <div><span className="font-semibold">Executive Summary:</span><div className="mt-0.5">{richText(d.biz_summary)}</div></div>}
            {d.biz_problem && <div><span className="font-semibold">Problem Statement:</span><div className="mt-0.5">{richText(d.biz_problem)}</div></div>}
            {d.biz_solution && <div><span className="font-semibold">Proposed Solution:</span><div className="mt-0.5">{richText(d.biz_solution)}</div></div>}
          </div> : null}
        </DR>
        <DR label="Expected Benefits">
          {(d.benefits_qty || d.benefits_qlty) ? <div className="space-y-2">
            {d.benefits_qty && <div><p className="font-semibold">Quantitative Benefits</p><div className="mt-0.5">{richText(d.benefits_qty)}</div></div>}
            {d.benefits_qlty && <div><p className="font-semibold">Qualitative Benefits</p><div className="mt-0.5">{richText(d.benefits_qlty)}</div></div>}
          </div> : null}
        </DR>
        <DR label="Project Scope">
          <div className="space-y-3">
            {[1,2,3,4].map(n => (d[`del${n}_desc`] || d[`del${n}_criteria`]) ? (
              <div key={n}>
                <p className="font-semibold">{d[`del${n}_title`] || `Deliverable ${n}`}:</p>
                {d[`del${n}_desc`] && <div className="mt-0.5">Description: {richText(d[`del${n}_desc`])}</div>}
                {d[`del${n}_criteria`] && <div>Success Criteria: {richText(d[`del${n}_criteria`])}</div>}
              </div>
            ) : null)}
          </div>
        </DR>
        <DR label="Strategic Alignment">{richText(d.strategic_alignment)}</DR>
        <DR label={<>Out of Scope<br/><span className="font-normal text-xs">(Potential Enhancement in future)</span></>}>
          {(d.exclusions || []).filter(Boolean).length ? (
            <ol className="space-y-0.5 list-none">
              {(d.exclusions || []).filter(Boolean).map((ex, i) => <li key={i}>{i + 1}. {ex}</li>)}
            </ol>
          ) : null}
        </DR>
        <DR label="Project Objectives">
          <div className="space-y-3">
            {[1,2,3].map(n => (d[`obj${n}_title`] || d[`obj${n}_alignment`] || d[`obj${n}_metrics`]) ? (
              <div key={n}>
                {d[`obj${n}_title`] && <p className="font-semibold">{d[`obj${n}_title`]}:</p>}
                {d[`obj${n}_alignment`] && <div className="mt-0.5">Alignment Description: {richText(d[`obj${n}_alignment`])}</div>}
                {d[`obj${n}_metrics`] && <div>Success Metrics: {richText(d[`obj${n}_metrics`])}</div>}
              </div>
            ) : null)}
          </div>
        </DR>
        <DR label="Start date">{d.start_date}</DR>
        <DR label="Frequency of updates">{d.update_frequency}</DR>
        <DR label="Total Budget">{d.total_budget}</DR>
        <DR label="Budget allocation">
          {(d.budget_personnel || d.budget_hardware || d.budget_software || d.budget_services) ? <div>
            {d.budget_personnel && <p><span className="font-semibold">Personnel:</span> {d.budget_personnel}</p>}
            {d.budget_hardware && <p><span className="font-semibold">Hardware:</span> {d.budget_hardware}</p>}
            {d.budget_software && <p><span className="font-semibold">Software:</span> {d.budget_software}</p>}
            {d.budget_services && <p><span className="font-semibold">Services:</span> {d.budget_services}</p>}
          </div> : null}
        </DR>
        <DR label="Project Sponsor">{d.sponsor}</DR>
      </Tbl>

      {/* Project Team Members */}
      <Tbl title="Project Team Members">
        {[['team_business','Project Team - Business Users'],['team_it','Project Team - IT & Vendor']].map(([key, rowLabel]) => (
          <React.Fragment key={key}>
            <div className="grid grid-cols-[180px_1fr_1fr_1fr] bg-gray-100 border-b border-gray-300">
              {[rowLabel,'Name','Role','Responsibilities'].map((h, i) => (
                <div key={i} className={`px-3 py-2 font-bold text-sm ${i < 3 ? 'border-r border-gray-300' : ''}`}>{h}</div>
              ))}
            </div>
            {(d[key] || []).map((m, i) => (
              <div key={i} className="grid grid-cols-[180px_1fr_1fr_1fr] border-b border-gray-200 last:border-b-0">
                <div className="px-3 py-2 border-r border-gray-200 bg-gray-50"></div>
                <div className="px-3 py-2 text-sm border-r border-gray-200">{m.name}</div>
                <div className="px-3 py-2 text-sm border-r border-gray-200">{m.role}</div>
                <div className="px-3 py-2 text-sm">{m.responsibilities}</div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </Tbl>

      {/* Steering Committee */}
      <Tbl>
        <div className="grid grid-cols-[180px_1fr_1fr_1fr] bg-gray-100 border-b border-gray-300">
          {['Steering Committee','Name','Title','Approval Date'].map((h, i) => (
            <div key={i} className={`px-3 py-2 font-bold text-sm ${i < 3 ? 'border-r border-gray-300' : ''}`}>{h}</div>
          ))}
        </div>
        {(d.steering || []).map((m, i) => (
          <div key={i} className="grid grid-cols-[180px_1fr_1fr_1fr] border-b border-gray-200 last:border-b-0">
            <div className="px-3 py-2 border-r border-gray-200 bg-gray-50"></div>
            <div className="px-3 py-2 text-sm border-r border-gray-200">{m.name}</div>
            <div className="px-3 py-2 text-sm border-r border-gray-200">{m.title}</div>
            <div className="px-3 py-2 text-sm">{m.approval_date}</div>
          </div>
        ))}
      </Tbl>

      {/* Technical Specifications */}
      <Tbl title="Technical Specifications">
        <DR label="Resources">
          <div>
            <p><span className="font-semibold">Infrastructure:</span> {d.tech_infrastructure}</p>
            {d.tech_software && <p><span className="font-semibold">Software:</span> {d.tech_software}</p>}
            {d.tech_tools && <p><span className="font-semibold">Tools:</span> {d.tech_tools}</p>}
          </div>
        </DR>
        <DR label="Dependencies">
          <div className="space-y-2">
            {(d.int_deps || []).filter(Boolean).length > 0 && (
              <div>
                <p className="font-semibold">Internal Dependencies</p>
                <ol className="space-y-0.5 list-none mt-0.5">
                  {(d.int_deps || []).filter(Boolean).map((dep, i) => <li key={i}>{i + 1}. {dep}</li>)}
                </ol>
              </div>
            )}
            {(d.ext_deps || []).filter(Boolean).length > 0 && (
              <div>
                <p className="font-semibold">External Dependencies</p>
                <ol className="space-y-0.5 list-none mt-0.5">
                  {(d.ext_deps || []).filter(Boolean).map((dep, i) => <li key={i}>{i + 1}. {dep}</li>)}
                </ol>
              </div>
            )}
          </div>
        </DR>
        <DR label="Implementation Recommendations">{richText(d.implementation_rec)}</DR>
      </Tbl>
    </div>
  );
};

const ProjectRisksTab = ({ projectId, user }) => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [showForm, setShowForm] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    const data = await ModulesModel.getRisks(projectId);
    setRisks(data.risks || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId]);

  const filtered = risks.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase()) && !(r.owner || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const summary = {
    total: risks.length,
    high: risks.filter(r => r.risk_score >= 6).length,
    medium: risks.filter(r => r.risk_score >= 3 && r.risk_score < 6).length,
    open: risks.filter(r => r.status === 'open').length,
    mitigated: risks.filter(r => r.status === 'mitigated').length,
  };

  const handleSave = async (form) => {
    if (editingRisk) {
      await ModulesModel.updateRisk(editingRisk.id, form);
    } else {
      await ModulesModel.createRisk({ ...form, project_id: projectId });
    }
    setShowForm(false);
    setEditingRisk(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this risk?')) return;
    await ModulesModel.deleteRisk(id);
    load();
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading risks…</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {[
            { label: 'Total', value: summary.total, color: 'text-gray-700' },
            { label: 'High', value: summary.high, color: 'text-red-600' },
            { label: 'Medium', value: summary.medium, color: 'text-yellow-600' },
            { label: 'Open', value: summary.open, color: 'text-blue-600' },
            { label: 'Mitigated', value: summary.mitigated, color: 'text-green-600' },
          ].map(c => (
            <div key={c.label} className="text-center">
              <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
              <div className="text-xs text-gray-400">{c.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView(v => v === 'table' ? 'heatmap' : 'table')}
            className="px-3 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">
            {view === 'table' ? '🗺 Heatmap' : '☰ Table'}
          </button>
          <button onClick={() => { setEditingRisk(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={14} /> Add Risk
          </button>
        </div>
      </div>

      {view === 'heatmap' ? (
        <RiskHeatmap risks={risks} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                className="pl-8 pr-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Statuses</option>
              {RISK_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <span className="text-xs text-gray-400 ml-auto">{filtered.length} risk{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <AlertCircle size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No risks logged for this project</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-4 py-3">Risk</th>
                      <th className="text-left px-4 py-3">Category</th>
                      <th className="text-left px-4 py-3">Likelihood</th>
                      <th className="text-left px-4 py-3">Impact</th>
                      <th className="text-left px-4 py-3">Score</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Owner</th>
                      <th className="text-left px-4 py-3">Due</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(r => {
                      const sc = riskScoreColor(r.risk_score);
                      const today = new Date().toISOString().slice(0, 10);
                      const overdue = r.due_date && r.due_date < today && r.status === 'open';
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 max-w-xs">
                            <div className="font-medium text-gray-900 truncate">{r.title}</div>
                            {r.description && <div className="text-xs text-gray-400 truncate mt-0.5">{r.description}</div>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">{r.category}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap capitalize text-gray-600">{r.likelihood}</td>
                          <td className="px-4 py-3 whitespace-nowrap capitalize text-gray-600">{r.impact}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                              {r.risk_score} · {sc.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.owner || '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.due_date ? (
                              <span className={overdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                {overdue && '⚠ '}{r.due_date}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingRisk(r); setShowForm(true); }}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded"><Pencil size={13} /></button>
                              <button onClick={() => requestConfirm('Delete this risk?', () => handleDelete(r.id))}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showForm && (
        <RiskFormModal
          risk={editingRisk}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingRisk(null); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// LEADERBOARD MODULE
// ============================================================================

const LeaderBoardModule = ({ data, period = 'weekly', onPeriodChange }) => {
  const teams = [
    { key: 'devops',  label: 'DevOps Team',  gradient: 'from-blue-600 to-blue-800',   bar: 'bg-blue-500',   avatarTop: 'bg-blue-500' },
    { key: 'dataops', label: 'DataOps Team', gradient: 'from-purple-600 to-purple-800', bar: 'bg-purple-500', avatarTop: 'bg-purple-500' },
  ];
  const rankIcon = ['🥇', '🥈', '🥉'];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Trophy size={28} className="text-amber-500" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">LeaderBoard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Rankings based on task ownership and priority points</p>
          </div>
        </div>
        {/* Period tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 self-start sm:self-auto">
          {[{ id: 'weekly', label: 'This Week' }, { id: 'monthly', label: 'This Month' }].map(tab => (
            <button
              key={tab.id}
              onClick={() => onPeriodChange(tab.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Points legend */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 px-5 py-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Points per Priority</p>
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'P1 / Urgent', pts: 10, bg: 'bg-red-50', text: 'text-red-600' },
            { label: 'P2 / High',   pts: 5,  bg: 'bg-orange-50', text: 'text-orange-600' },
            { label: 'P3 / Medium', pts: 3,  bg: 'bg-yellow-50', text: 'text-yellow-600' },
            { label: 'Low',         pts: 1,  bg: 'bg-green-50',  text: 'text-green-600' },
          ].map(({ label, pts, bg, text }) => (
            <span key={label} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${bg} ${text}`}>
              {label} <span className="font-black">= {pts} pts</span>
            </span>
          ))}
        </div>
      </div>

      {/* Team boards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {teams.map(team => {
          const members = (data[team.key] || []).slice().sort((a, b) => b.points - a.points);
          const maxPts = members[0]?.points || 1;
          const totalPts = members.reduce((s, m) => s + m.points, 0);
          const totalTasks = members.reduce((s, m) => s + m.taskCount, 0);

          return (
            <div key={team.key} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* Team header */}
              <div className={`bg-gradient-to-r ${team.gradient} px-6 py-5`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">{team.label}</h2>
                    <p className="text-white/70 text-sm mt-0.5">{members.length} members · {totalTasks} tasks</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-white">{totalPts}</div>
                    <div className="text-white/70 text-xs">total pts</div>
                  </div>
                </div>
              </div>

              {/* Rankings */}
              {members.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <Trophy size={36} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium">No tasks assigned yet</p>
                  <p className="text-xs mt-1">Assign tasks to {team.label} members to see rankings</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {members.map((member, idx) => {
                    const pct = Math.round((member.points / maxPts) * 100);
                    const isTop = idx === 0;
                    return (
                      <div key={member.name} className={`px-5 py-4 ${isTop ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          {/* Rank */}
                          <div className="w-8 text-center shrink-0">
                            {idx < 3
                              ? <span className="text-xl leading-none">{rankIcon[idx]}</span>
                              : <span className="text-sm font-bold text-gray-400">#{idx + 1}</span>}
                          </div>

                          {/* Avatar */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white shrink-0 ${isTop ? 'bg-amber-400' : team.avatarTop}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-sm text-gray-900 truncate">{member.name}</span>
                              <span className={`font-black text-base ml-2 shrink-0 ${isTop ? 'text-amber-600' : 'text-gray-800'}`}>{member.points} pts</span>
                            </div>
                            {/* Progress bar */}
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                              <div
                                className={`h-full rounded-full transition-all ${isTop ? 'bg-amber-400' : team.bar}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            {/* Breakdown */}
                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                              <span>{member.taskCount} task{member.taskCount !== 1 ? 's' : ''}</span>
                              {member.completedCount > 0 && (
                                <span className="text-green-600 font-medium">· ✓ {member.completedCount} done</span>
                              )}
                              {member.p1 > 0 && <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-semibold">P1 ×{member.p1}</span>}
                              {member.p2 > 0 && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded font-semibold">P2 ×{member.p2}</span>}
                              {member.p3 > 0 && <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded font-semibold">P3 ×{member.p3}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Navigation = ({ currentPage, onNavigate, isOpen, onClose, user }) => {
  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'modules', label: 'Modules', icon: Camera },
    { id: 'leaderboard', label: 'LeaderBoard', icon: Trophy },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'audit', label: 'Audit', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];
  const role = user?.role || user?.user_role;
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isDataOpsManager = role === 'dataops_manager';
  const isDevOpsManager = role === 'devops_manager';
  const navItems = allNavItems.filter(item => {
    if (!ADMIN_ONLY_NAV.includes(item.id)) return true;
    if (isAdmin) return true;
    if (item.id === 'tasks' || item.id === 'reports') {
      return isManager || isDataOpsManager || isDevOpsManager;
    }
    if (item.id === 'audit') {
      return isDevOpsManager;
    }
    return false;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      {/* Navigation */}
      <nav className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-auto
        bg-white border-b border-gray-200 shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <span className="font-semibold">Menu</span>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="lg:max-w-7xl lg:mx-auto lg:px-4 lg:flex lg:gap-0">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  onClose();
                }}
                className={`
                  w-full lg:w-auto flex items-center gap-3 px-6 py-4 lg:border-b-3
                  transition-colors
                  ${currentPage === item.id 
                    ? 'text-blue-600 bg-gray-50 lg:border-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600 lg:border-transparent'
                  }
                `}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

const BurnChart = ({ title, subtitle, days, burndown, ideal, total, completedCount }) => {
  if (!days || days.length === 0) return (
    <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No data available</div>
  );

  // Format YYYY-MM-DD → M/D/YYYY
  const fmtDay = d => {
    if (!d || !d.includes('-')) return d;
    const [y, m, day] = d.split('-');
    return `${parseInt(m)}/${parseInt(day)}/${y}`;
  };

  const remaining = burndown ? (burndown[burndown.length - 1] ?? total) : total;
  const completed = completedCount !== undefined ? completedCount : Math.max(0, total - remaining);
  const completedPct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const avgBurndown = days.length > 1 ? parseFloat((completed / (days.length - 1)).toFixed(1)) : 0;

  const W = 560, H = 220, padL = 36, padR = 16, padT = 12, padB = 52;
  const cW = W - padL - padR, cH = H - padT - padB;
  const n = days.length;
  const maxVal = Math.max(total || 0, ...(burndown || []), 1);
  const barW = Math.max(3, (cW / n) * 0.65);
  const x = i => padL + (i / Math.max(n - 1, 1)) * cW;
  const y = v => padT + cH - (Math.min(v, maxVal) / maxVal) * cH;
  const barBot = padT + cH;
  const yTicks = [...new Set([0, Math.round(maxVal / 2), maxVal])];
  const todayIdx = days.length - 1;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="text-right ml-4 shrink-0">
          <div className="text-xs text-gray-400 leading-tight">Task</div>
          <div className="text-xs text-gray-400 leading-tight">Remaining</div>
          <div className="text-3xl font-bold text-gray-900 leading-none mt-0.5">{remaining}</div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex gap-6 mb-3 text-xs border-b border-gray-100 pb-3">
        <div>
          <span className="text-blue-500 font-semibold">Completed</span>
          <span className="ml-1.5 font-bold text-gray-900 text-sm">{completedPct}%</span>
        </div>
        <div className="text-gray-500">
          Average<br />burndown <span className="font-bold text-gray-900 text-sm">{avgBurndown}</span>
        </div>
        <div className="text-gray-500">
          Total Scope<br />Increase <span className="font-bold text-gray-900 text-sm">0</span>
        </div>
      </div>

      {/* Chart */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 220 }}>
        {/* Grid lines */}
        {yTicks.map(v => (
          <line key={v} x1={padL} y1={y(v).toFixed(1)} x2={W - padR} y2={y(v).toFixed(1)} stroke="#f3f4f6" strokeWidth="1" />
        ))}
        {/* Today highlight */}
        {todayIdx > 0 && (
          <rect x={(x(todayIdx) - barW / 2 - 3).toFixed(1)} y={padT} width={(barW + 6).toFixed(1)} height={cH} fill="#f3f4f6" rx="2" />
        )}
        {/* Burndown bars */}
        {(burndown || []).map((v, i) => (
          <rect key={i} x={(x(i) - barW / 2).toFixed(1)} y={y(v).toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, barBot - y(v)).toFixed(1)} fill="#b8860b" opacity="0.88" rx="1" />
        ))}
        {/* Ideal trend line */}
        {ideal && ideal.length > 1 && (
          <polyline points={ideal.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} fill="none" stroke="#9ca3af" strokeWidth="1.5" />
        )}
        {/* Axes */}
        <line x1={padL} y1={padT} x2={padL} y2={barBot} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={padL} y1={barBot} x2={W - padR} y2={barBot} stroke="#e5e7eb" strokeWidth="1" />
        {/* Y labels */}
        {yTicks.map(v => (
          <text key={v} x={padL - 4} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="10" fill="#9ca3af">{v}</text>
        ))}
        {/* X labels — rotated dates */}
        {days.map((d, i) => (
          <text key={i} x={x(i).toFixed(1)} y={(barBot + 8).toFixed(1)} textAnchor="end" fontSize="9" fill="#9ca3af"
            transform={`rotate(-45 ${x(i).toFixed(1)} ${(barBot + 8).toFixed(1)})`}>{fmtDay(d)}</text>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex gap-5 mt-1 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#b8860b' }} />
          Task
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 border-t border-gray-400" />
          Ideal Trend
        </span>
      </div>
    </div>
  );
};

const CycleTimeChart = ({ title, avgDays, total, points }) => {
  const hasData = points && points.length > 0;
  if (!hasData) return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Last 30 days</p>
        </div>
        <div className="flex items-center gap-1.5 ml-4 shrink-0">
          <span className="text-xl">📋</span>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-600 leading-none">0</div>
            <div className="text-xs text-gray-400">Task</div>
          </div>
        </div>
      </div>
      <div className="mb-3 pb-3 border-b border-gray-100">
        <span className="text-3xl font-bold text-gray-900">0</span>
        <span className="ml-2 text-sm text-gray-600">Days on average</span>
        <div className="text-xs text-gray-400">(Started to Completed)</div>
      </div>
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No completed tasks in the last 30 days</div>
    </div>
  );

  // 30-day window ending today
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - 29);

  const parsed = points.map(p => {
    const diff = Math.round((new Date(p.date + 'T00:00:00') - startDate) / 864e5);
    return { dayIdx: diff, days: p.days };
  }).filter(p => p.dayIdx >= 0 && p.dayIdx <= 29);

  const maxY = Math.max(10, ...parsed.map(p => p.days));
  const yMax = Math.ceil(maxY / 10) * 10;

  // Moving average + ±1 std dev band (window ±5 days)
  const trendPoints = Array.from({ length: 30 }, (_, i) => {
    const nearby = parsed.filter(p => Math.abs(p.dayIdx - i) <= 5);
    if (nearby.length === 0) return null;
    const avg = nearby.reduce((s, p) => s + p.days, 0) / nearby.length;
    const std = Math.sqrt(nearby.reduce((s, p) => s + (p.days - avg) ** 2, 0) / nearby.length);
    return { i, avg, lo: Math.max(0, avg - std), hi: avg + std };
  }).filter(Boolean);

  const W = 560, H = 180, padL = 36, padR = 16, padT = 10, padB = 30;
  const cW = W - padL - padR, cH = H - padT - padB;
  const xPos = i => padL + (i / 29) * cW;
  const yPos = v => padT + cH - (Math.min(v, yMax) / yMax) * cH;

  const bandPath = trendPoints.length > 1
    ? `M${trendPoints.map(p => `${xPos(p.i).toFixed(1)},${yPos(p.hi).toFixed(1)}`).join('L')}L${[...trendPoints].reverse().map(p => `${xPos(p.i).toFixed(1)},${yPos(p.lo).toFixed(1)}`).join('L')}Z`
    : '';
  const linePath = trendPoints.length > 1
    ? `M${trendPoints.map(p => `${xPos(p.i).toFixed(1)},${yPos(p.avg).toFixed(1)}`).join('L')}`
    : '';

  const yTicks = [0, Math.round(yMax / 2), yMax];

  // X-axis: day numbers every 5 days
  const xDayLabels = Array.from({ length: 30 }, (_, i) => {
    if (i % 5 !== 0 && i !== 29) return null;
    const d = new Date(startDate); d.setDate(d.getDate() + i);
    return { i, day: d.getDate() };
  }).filter(Boolean);

  const startMonthLabel = startDate.toLocaleString('default', { month: 'short' });

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Last 30 days</p>
        </div>
        <div className="flex items-center gap-1.5 ml-4 shrink-0">
          <span className="text-xl">📋</span>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-600 leading-none">{total}</div>
            <div className="text-xs text-gray-400">Task</div>
          </div>
        </div>
      </div>

      <div className="mb-3 pb-3 border-b border-gray-100">
        <span className="text-3xl font-bold text-gray-900">{avgDays}</span>
        <span className="ml-2 text-sm text-gray-600">Days on average</span>
        <div className="text-xs text-gray-400">(Started to Completed)</div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
        {yTicks.map(v => (
          <line key={v} x1={padL} y1={yPos(v).toFixed(1)} x2={W - padR} y2={yPos(v).toFixed(1)} stroke="#f3f4f6" strokeWidth="1" />
        ))}
        {bandPath && <path d={bandPath} fill="rgba(147,197,253,0.35)" />}
        {linePath && <path d={linePath} fill="none" stroke="#111827" strokeWidth="1.5" />}
        {parsed.map((p, i) => (
          <circle key={i} cx={xPos(p.dayIdx).toFixed(1)} cy={yPos(p.days).toFixed(1)} r="4" fill="#b8860b" opacity="0.85" />
        ))}
        <line x1={padL} y1={padT} x2={padL} y2={padT + cH} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={padL} y1={padT + cH} x2={W - padR} y2={padT + cH} stroke="#e5e7eb" strokeWidth="1" />
        {yTicks.map(v => (
          <text key={v} x={padL - 4} y={(yPos(v) + 4).toFixed(1)} textAnchor="end" fontSize="10" fill="#9ca3af">{v}</text>
        ))}
        {xDayLabels.map(({ i, day }) => (
          <text key={i} x={xPos(i).toFixed(1)} y={(padT + cH + 14).toFixed(1)} textAnchor="middle" fontSize="9" fill="#9ca3af">{day}</text>
        ))}
        <text x={padL} y={H} textAnchor="start" fontSize="9" fill="#9ca3af">{startMonthLabel}</text>
      </svg>
    </div>
  );
};

const DashboardView = ({ data, burnCharts, onNavigate, user }) => {
  const allowedModules = getAllowedModules(user);
  const visibleQuickModules = allowedModules
    ? data.quickModules.filter(m => allowedModules.includes(m.id))
    : data.quickModules;
  const [activityPage, setActivityPage] = useState(0);
  const ACTIVITY_PAGE_SIZE = 5;
  const activities = data.activities || [];
  const totalActivityPages = Math.ceil(activities.length / ACTIVITY_PAGE_SIZE);
  const pagedActivities = activities.slice(activityPage * ACTIVITY_PAGE_SIZE, (activityPage + 1) * ACTIVITY_PAGE_SIZE);
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
      <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {data.stats.map((stat, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-gray-600 text-sm mb-2">{stat.label}</div>
              <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-green-600 text-sm mt-1">{stat.change} from last week</div>
            </div>
            <div className="w-16 h-16 rounded-xl" style={{ background: `${stat.color}20` }} />
          </div>
        </div>
      ))}
    </div>

    {/* Burn Charts */}
    {burnCharts && (burnCharts.devops || burnCharts.dataops) && (
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Burn Charts</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {burnCharts.devops ? (
            <BurnChart
              title={burnCharts.devops.sprintName ? `DevOps Burndown / Burnup — ${burnCharts.devops.sprintName}` : 'DevOps Burndown / Burnup — Last 14 Days'}
              subtitle={burnCharts.devops.sprintName ? `Sprint: ${burnCharts.devops.startDate} → ${burnCharts.devops.endDate} · Sprint tasks + ad hoc tasks` : 'All DevOps tasks (sprint tasks + ad hoc) completed'}
              days={burnCharts.devops.days}
              burndown={burnCharts.devops.burndown}
              burnup={burnCharts.devops.burnup}
              ideal={burnCharts.devops.ideal}
              total={burnCharts.devops.total}
              completedCount={burnCharts.devops.completedCount}
              unit={burnCharts.devops.unit || ' tasks'}
            />
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6 flex items-center justify-center text-gray-400 text-sm">
              No active sprint — DevOps burn chart unavailable
            </div>
          )}
          {burnCharts.dataops ? (
            <BurnChart
              title={burnCharts.dataops.sprintName ? `DataOps Burndown / Burnup — ${burnCharts.dataops.sprintName}` : 'DataOps Burndown / Burnup — Last 14 Days'}
              subtitle={burnCharts.dataops.sprintName ? `Sprint: ${burnCharts.dataops.startDate} → ${burnCharts.dataops.endDate} · Sprint tasks + ad hoc tasks` : 'All DataOps tasks (sprint tasks + ad hoc) completed'}
              days={burnCharts.dataops.days}
              burndown={burnCharts.dataops.burndown}
              burnup={burnCharts.dataops.burnup}
              ideal={burnCharts.dataops.ideal}
              total={burnCharts.dataops.total}
              completedCount={burnCharts.dataops.completedCount}
              unit={burnCharts.dataops.unit || ' tasks'}
            />
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6 flex items-center justify-center text-gray-400 text-sm">
              No pipeline data — DataOps burn chart unavailable
            </div>
          )}
        </div>
      </div>
    )}

    {/* Cycle Time Charts */}
    {burnCharts && (burnCharts.devops_cycle || burnCharts.dataops_cycle) && (
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Cycle Time</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <CycleTimeChart
            title="DevOps Cycle Time"
            avgDays={burnCharts.devops_cycle?.avgDays ?? 0}
            total={burnCharts.devops_cycle?.total ?? 0}
            points={burnCharts.devops_cycle?.points ?? []}
          />
          <CycleTimeChart
            title="DataOps Cycle Time"
            avgDays={burnCharts.dataops_cycle?.avgDays ?? 0}
            total={burnCharts.dataops_cycle?.total ?? 0}
            points={burnCharts.dataops_cycle?.points ?? []}
          />
        </div>
      </div>
    )}

    <div className="grid lg:grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Item</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">User</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pagedActivities.map((activity, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {activity.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{activity.item}</td>
                  <td className="px-6 py-4 text-gray-600">{activity.user}</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{activity.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalActivityPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {activityPage * ACTIVITY_PAGE_SIZE + 1}–{Math.min((activityPage + 1) * ACTIVITY_PAGE_SIZE, activities.length)} of {activities.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setActivityPage(p => p - 1)}
                disabled={activityPage === 0}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >Prev</button>
              <button
                onClick={() => setActivityPage(p => p + 1)}
                disabled={activityPage >= totalActivityPages - 1}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Access */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Quick Access</h2>
        </div>
        <div className="p-4 space-y-2">
          {visibleQuickModules.map((module, i) => (
            <div key={i} onClick={() => onNavigate(module.id)} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer">
              <div className="text-3xl">{module.icon}</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{module.name}</div>
                <div className="text-sm text-gray-600">{module.count}</div>
              </div>
              <ChevronRight className="text-gray-400" size={20} />
            </div>
          ))}
        </div>
      </div>
    </div>

  </div>
  );
};

const ModulesView = ({ modules, onNavigate, user }) => {
  const allowedModules = getAllowedModules(user);
  const visibleModules = allowedModules
    ? modules.filter(m => allowedModules.includes(m.id))
    : modules;
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">ORBIT Modules</h1>
      <p className="text-gray-600 mt-1">Core API modules and shared services for the ORBIT tracking system.</p>
    </div>

    <div className="mb-4">
      <h2 className="text-xl font-semibold text-gray-900 pb-3 border-b-2 border-gray-200">
        Core API Modules
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {visibleModules.map(module => (
        <div key={module.id} onClick={() => onNavigate(module.id)} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-xl hover:border-blue-500 transition-all cursor-pointer">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl text-2xl mb-4" 
               style={{ background: `${module.color}20`, color: module.color }}>
            {module.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{module.name}</h3>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{module.description}</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {module.status}
            </span>
            <span className="text-gray-600 text-sm">{(module.records ?? 0).toLocaleString()} records</span>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

// ---- DevOps Badge Helpers ----
const getTypeBadge = (type) => {
  const normalized = (type || '').toUpperCase();
  const badges = {
    TASK:    { color: '#0366d6', bg: '#d4e4ff',  label: 'Task' },
    PROJECT: { color: '#6f42c1', bg: '#f3e8ff',  label: 'Project' },
    BUG:     { color: '#dc3545', bg: '#ffe5e9',  label: 'Bug' },
    ADHOC:   { color: '#e36209', bg: '#fff5cc',  label: 'Ad-hoc' },
    STORY:   { color: '#17a2b8', bg: '#d4f4ff',  label: 'Story' },
    FEATURE: { color: '#28a745', bg: '#dcffe4',  label: 'Feature' },
  };
  return badges[normalized] || { color: '#586069', bg: '#f6f8fa', label: type || 'Task' };
};

const getDevOpsPriorityBadge = (priority) => {
  const badges = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#ffc107', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return badges[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getOpsStatusStyle = (status) => {
  const styles = {
    running: { color: '#0366d6', bg: '#d4e4ff', label: 'Running' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    failed: { color: '#dc3545', bg: '#ffe5e9', label: 'Failed' },
    idle: { color: '#586069', bg: '#f6f8fa', label: 'Idle' },
    queued: { color: '#6f42c1', bg: '#f3e8ff', label: 'Queued' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const formatRecords = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toString();
};

// ---- DevOps Task Card ----
const DevOpsTaskCard = ({ item, currentStatus, onClick, onStatusChange, onDelete, canDelete }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const typeBadge = getTypeBadge(item.type);
  const priorityBadge = getDevOpsPriorityBadge(item.priority);
  const isQuickTask = !!item.isQuickTask || String(item.id).startsWith('QT-');
  const isBug = !!item.isBug || String(item.id).startsWith('BUG-');

  const allStatuses = isQuickTask
    ? ['backlog', 'in_progress', 'done']
    : isBug
      ? ['in_progress', 'review', 'done']
      : ['backlog', 'ready', 'in_progress', 'review', 'done'];

  const moveOptions = allStatuses.filter(s => s !== currentStatus);
  const statusLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', done: 'Done' };
  const cardBg = isBug ? 'bg-red-50 border-red-200' : isQuickTask ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200';
  const barColor = isBug ? 'bg-red-500' : isQuickTask ? 'bg-amber-500' : 'bg-blue-600';

  const estMins = item.estimatedMinutes || item.estimated_minutes ||
    (item.estimateHours ? Math.round(item.estimateHours * 60) : 0) ||
    (item.estimate ? item.estimate * 60 : 0);
  const actMins = item.actualMinutes || item.actual_minutes ||
    (item.actualHours ? Math.round(item.actualHours * 60) : 0);

  const handleMoveSelect = (e) => {
    e.stopPropagation();
    const val = e.target.value;
    if (!val) return;
    if (val === 'done') { setShowActualTime(true); return; }
    onStatusChange(item.id, val);
    e.target.value = '';
  };

  return (
    <div className={`border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer ${cardBg}`} onClick={() => onClick(item.id)}>
      {showActualTime && (
        <ActualTimeModal
          taskTitle={item.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(item.id, 'done', { actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          <span className="text-xs font-mono text-gray-400">{item.bugCode || item.id}</span>
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.label}</span>
          {isBug && item.severity && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">{item.severity}</span>}
          {isQuickTask && item.category && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">{item.category}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: priorityBadge.bg, color: priorityBadge.color }}>{item.priority}</span>
          {canDelete && isQuickTask && (
            <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete "${item.title}"?`, () => onDelete(item.id)); }}
              className="text-gray-300 hover:text-red-500 text-base leading-none" title="Delete task">×</button>
          )}
        </div>
      </div>
      <div className="text-sm font-medium text-gray-900 mb-1 leading-snug">{item.title}</div>
      {/* Description — always shown */}
      {item.description
        ? <div className="text-xs text-gray-500 mb-2 leading-snug line-clamp-2">{item.description}</div>
        : <div className="text-xs text-gray-300 italic mb-2">No description</div>
      }
      {item.projectName && <div className="text-xs text-blue-600 font-medium mb-1 truncate">{item.projectName}</div>}
      {item.assignee && <div className="text-xs text-gray-500 mb-1">👤 {item.assignee}</div>}
      {/* Time — always shown */}
      <div className="flex gap-3 text-xs mb-1">
        <span className={estMins > 0 ? 'text-gray-500' : 'text-red-400 font-medium'}>
          ⏱ Est: {estMins > 0 ? `${estMins}m` : 'not set'}
        </span>
        {actMins > 0 && (
          <span className={actMins > estMins && estMins > 0 ? 'text-red-500 font-medium' : 'text-gray-500'}>
            ✓ Act: {actMins}m
          </span>
        )}
        {item.storyPoints > 0 && <span className="text-gray-500">◆ {item.storyPoints} pts</span>}
      </div>
      {item.progress > 0 && (
        <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${item.progress}%` }} />
        </div>
      )}
      {onStatusChange && moveOptions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
          <select
            value=""
            onChange={handleMoveSelect}
            className="w-full text-xs border border-gray-200 rounded px-2 py-1 text-gray-500 bg-white"
          >
            <option value="">Move to…</option>
            {moveOptions.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

// ---- DevOps Capacity Cards ----
const DevOpsCapacityCards = ({ capacity }) => (
  <div className="mb-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Total Capacity</div>
        <div className="text-4xl font-bold text-blue-700">{capacity.total}h</div>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Committed</div>
        <div className="text-4xl font-bold text-yellow-700">{capacity.committed}h</div>
        <div className="text-sm text-gray-600 mt-1">{capacity.utilization}% utilized</div>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Completed</div>
        <div className="text-4xl font-bold text-green-700">{capacity.completed}h</div>
      </div>
    </div>
  </div>
);

// ---- DevOps Team Workload ----
const DevOpsTeamWorkload = ({ workload }) => (
  <div className="bg-white rounded-lg shadow p-6 mb-6">
    <h3 className="text-lg font-semibold mb-4">Team Workload</h3>
    <div className="space-y-4">
      {workload.map((member, i) => {
        const tsWidth  = member.capacity > 0 ? Math.min(member.timesheetHours * 100 / member.capacity, 100) : 0;
        const qtWidth  = member.capacity > 0 ? Math.min(member.qtHours * 100 / member.capacity, 100 - tsWidth) : 0;
        const overCapacity = member.percentage > 100;
        return (
          <div key={i}>
            <div className="flex items-center gap-4 mb-1">
              <div className="w-36 text-sm text-gray-700 truncate">{member.name}</div>
              <div className="flex-1 h-7 bg-gray-200 rounded-lg overflow-hidden flex">
                {tsWidth > 0 && (
                  <div
                    className={`h-full flex items-center justify-center text-white text-xs font-semibold ${overCapacity ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${tsWidth}%` }}
                    title={`Timesheet: ${member.timesheetHours}h`}
                  >
                    {tsWidth >= 12 ? `${member.timesheetHours}h` : ''}
                  </div>
                )}
                {qtWidth > 0 && (
                  <div
                    className="h-full flex items-center justify-center text-white text-xs font-semibold bg-indigo-400"
                    style={{ width: `${qtWidth}%` }}
                    title={`QuickTrack: ${member.qtHours}h`}
                  >
                    {qtWidth >= 12 ? `${member.qtHours}h` : ''}
                  </div>
                )}
              </div>
              <div className="w-28 text-sm text-gray-600 text-right whitespace-nowrap">
                {member.assigned}h / {member.capacity}h
                <span className={`ml-1 text-xs font-semibold ${overCapacity ? 'text-red-500' : 'text-gray-400'}`}>
                  ({member.percentage}%)
                </span>
              </div>
            </div>
            {(member.timesheetHours > 0 || member.qtHours > 0) && (
              <div className="flex items-center gap-3 pl-36 text-xs text-gray-400 mb-1">
                {member.timesheetHours > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-blue-500" />Timesheet {member.timesheetHours}h</span>}
                {member.qtHours > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-indigo-400" />QT/Ad hoc {member.qtHours}h ({member.quickTasks} tasks)</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />Timesheet / Sprint hours</span>
      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-indigo-400" />QuickTrack / Ad hoc hours</span>
    </div>
  </div>
);

// ---- DevOps Pipeline Board (shared for Sprint & Kanban) ----
const DevOpsPipelineBoard = ({ pipeline, title, onTaskClick, onStatusChange, onDelete, canDelete }) => {
  const columnLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', done: 'Done' };

  // Normalize all keys to snake_case and merge done/completed
  const normalizedPipeline = {};
  Object.entries(pipeline).forEach(([key, items]) => {
    let normalized = key;
    if (key === 'inProgress') normalized = 'in_progress';
    if (key === 'completed')  normalized = 'done';
    if (!normalizedPipeline[normalized]) normalizedPipeline[normalized] = [];
    normalizedPipeline[normalized] = [...normalizedPipeline[normalized], ...items];
  });

  const columnOrder = ['backlog', 'ready', 'in_progress', 'review', 'done'];
  const uniqueColumns = columnOrder.filter(k => normalizedPipeline[k] !== undefined);

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {uniqueColumns.map(status => {
          const items = normalizedPipeline[status] || [];
          const totalPoints = items.reduce((sum, item) => sum + (item.storyPoints || 0), 0);
          return (
            <div key={status} className="bg-white rounded-lg shadow border border-gray-200 min-h-96">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="font-semibold text-gray-900">{columnLabels[status] || status}</div>
                <div className="text-sm text-gray-600">{items.length} items · {totalPoints} pts</div>
              </div>
              <div className="p-3 space-y-3">
                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-3xl mb-2">🔭</div>
                    <div className="text-sm">No items</div>
                  </div>
                ) : (
                  items.map((item, i) => (
                    <DevOpsTaskCard key={item.id || i} item={item} currentStatus={status} onClick={onTaskClick} onStatusChange={onStatusChange} onDelete={onDelete} canDelete={canDelete} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- DevOps Kanban Board (DataOps-style design) ----
const DevOpsKanbanCard = ({ item, currentStatus, onClick, onStatusChange, onDelete, canDelete }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const priorityColors = {
    critical: { bg: '#fef2f2', color: '#dc2626', label: 'Critical' },
    high: { bg: '#fff7ed', color: '#ea580c', label: 'High' },
    medium: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' },
    low: { bg: '#f0fdf4', color: '#16a34a', label: 'Low' },
    P1: { bg: '#fef2f2', color: '#dc2626', label: 'Critical' },
    P2: { bg: '#fff7ed', color: '#ea580c', label: 'High' },
    P3: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' },
  };
  const p = priorityColors[item.priority] || priorityColors.medium;
  const isQuickTask = !!item.isQuickTask || String(item.id).startsWith('QT-');
  const isBug = !!item.isBug || String(item.id).startsWith('BUG-');
  const typeLabel = isBug ? 'Bug' : isQuickTask ? 'Ad Hoc' : 'Task';
  const isOverdue = currentStatus !== 'done' && item.dueDate && item.dueDate < new Date().toISOString().split('T')[0];

  const nextStatusMap = { open: 'in_progress', in_progress: 'done', done: 'open' };
  const nextLabelMap = { open: '▶ Start', in_progress: '✓ Done', done: '↺ Reopen' };
  const nextStatus = nextStatusMap[currentStatus];
  const nextLabel = nextLabelMap[currentStatus];

  const handleStatusClick = (e) => {
    e.stopPropagation();
    if (nextStatus === 'done') { setShowActualTime(true); return; }
    onStatusChange(item.id, nextStatus);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onClick(item.id)}>
      {showActualTime && (
        <ActualTimeModal
          taskTitle={item.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(item.id, nextStatus, { actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{p.label}</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">{typeLabel}{isBug && item.severity ? ` · ${item.severity}` : ''}</span>
          {canDelete && (isQuickTask || isBug) && (
            <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete "${item.title}"?`, () => onDelete(item.id)); }}
              className="text-gray-300 hover:text-red-500 text-base leading-none ml-1" title="Delete">×</button>
          )}
        </div>
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1 leading-snug">{item.title}</p>
      {item.description
        ? <p className="text-xs text-gray-500 mb-2 truncate">{item.description}</p>
        : <p className="text-xs text-gray-400 font-mono mb-2">{item.bugCode || item.id}</p>
      }
      {item.projectName && <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block mb-2">{item.projectName}</p>}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {item.assignee && <span className="font-medium text-gray-700">{item.assignee}</span>}
          {item.dueDate && (
            <span className={`ml-2 ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : ''}{new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        {nextStatus && (
          <button onClick={handleStatusClick} className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const DevOpsKanbanBoard = ({ pipeline, onTaskClick, onStatusChange, onDelete, canDelete }) => {
  const normalizedPipeline = { open: [], in_progress: [], done: [] };
  Object.entries(pipeline || {}).forEach(([key, items]) => {
    const normalized = (key === 'inProgress' || key === 'in_progress') ? 'in_progress'
      : (key === 'completed' || key === 'done') ? 'done'
      : 'open'; // backlog, ready, and anything else → open
    normalizedPipeline[normalized] = [...normalizedPipeline[normalized], ...(items || [])];
  });
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  normalizedPipeline.done = normalizedPipeline.done.filter(item =>
    !item.completedAt || new Date(item.completedAt).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) === todayStr
  );
  const columns = [
    { key: 'open', label: 'Open', color: 'bg-blue-500' },
    { key: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
    { key: 'done', label: 'Done', color: 'bg-green-500' },
  ];
  const totalTasks = columns.reduce((sum, col) => sum + (normalizedPipeline[col.key]?.length || 0), 0);
  return (
    <div className="mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Team Kanban Board</h2>
        <p className="text-xs text-gray-500 mt-0.5">Sprint tasks, bugs, and ad-hoc tasks for the DevOps team</p>
      </div>
      {totalTasks === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm font-medium">No tasks for the DevOps team yet.</p>
          <p className="text-xs mt-1">Add a QuickTrack task and assign it to a DevOps team member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map(col => {
            const tasks = normalizedPipeline[col.key] || [];
            return (
              <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${col.color}`} />
                  <span className="font-semibold text-sm text-gray-800">{col.label}</span>
                  <span className="ml-auto text-xs bg-white border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">{tasks.length}</span>
                </div>
                <div className="p-3 space-y-2 min-h-32">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-xs">No tasks</div>
                  ) : (
                    tasks.map((item, i) => (
                      <DevOpsKanbanCard key={item.id || i} item={item} currentStatus={col.key} onClick={onTaskClick} onStatusChange={onStatusChange} onDelete={onDelete} canDelete={canDelete} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- DevOps Ops Group (CI/CD Pipelines) ----
const DevOpsOpsGroup = ({ pipelines }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold mb-4">Ops Pipeline</h3>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {pipelines.map(p => {
            const statusStyle = getOpsStatusStyle(p.status);
            return (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">{p.type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${p.status === 'failed' ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{p.progress}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatRecords(p.recordsProcessed)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDuration(p.durationSeconds)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pipelines.length === 0 && (
        <div className="p-8 text-center text-gray-400">No pipelines found.</div>
      )}
    </div>
  </div>
);

// ---- Add DevOps Task Modal ----
const AddDevOpsTaskModal = ({ onClose, onSubmit, teamMembers = [], user, allUsers = [], projects = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  // Use team members if available, otherwise fall back to all users for admin/manager
  const memberPool = teamMembers.length > 0 ? teamMembers : allUsers.map(u => ({ user_id: u.user_id, user_name: u.user_name }));
  const assignableMembers = isAdmin || isManager ? memberPool : [];
  const defaultAssignee = user?.name || '';

  const [form, setForm] = useState({ title: '', type: 'task', priority: 'P2', assignee: defaultAssignee, estimatedMinutes: '', actualMinutes: '', storyPoints: '', status: 'backlog', description: '', projectId: '' });
  const [errors, setErrors] = useState({});
  const isDone = ['done', 'completed'].includes(form.status);
  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0) errs.estimatedMinutes = 'Estimated Time is required';
    if (isDone && (!form.actualMinutes || parseInt(form.actualMinutes) <= 0)) errs.actualMinutes = 'Actual Time is required when status is Done';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(form);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">— No Project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="task">Task</option>
                <option value="BUG">Bug</option>
                <option value="ADHOC">Ad-hoc</option>
                <option value="PROJECT">Project</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              {selfAssign ? (
                <input type="text" value={form.assignee} readOnly
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              ) : (
                <select value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Unassigned</option>
                  {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="backlog">Backlog</option>
                <option value="ready">Ready</option>
                <option value="in_progress">In Progress</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min) <span className="text-red-500">*</span></label>
              <input type="number" min="1" placeholder="e.g. 60" value={form.estimatedMinutes} onChange={e => { setForm({...form, estimatedMinutes: e.target.value}); setErrors(ev => ({...ev, estimatedMinutes: ''})); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.estimatedMinutes ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{errors.estimatedMinutes}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min){isDone && <span className="text-red-500"> *</span>}</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes} onChange={e => { setForm({...form, actualMinutes: e.target.value}); setErrors(ev => ({...ev, actualMinutes: ''})); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.actualMinutes ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.actualMinutes && <p className="text-xs text-red-500 mt-1">{errors.actualMinutes}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story Points</label>
              <input type="number" min="0" value={form.storyPoints} onChange={e => setForm({...form, storyPoints: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Task Detail Panel ----
// ---- Shared task comments component (used by both DevOps and QuickTrack) ----
const TaskComments = ({ taskRef, taskSource, userName }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const apiBase = taskSource === 'devops'
    ? `/api/devops/tasks/${taskRef}`
    : taskSource === 'bug'
      ? `/api/quality/bugs/${taskRef}`
      : `/api/quicktrack/tasks/${taskRef}`;

  const load = async () => {
    try {
      const token = localStorage.getItem('orbit_token');
      const res = await fetch(`${apiBase}/comments`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (Array.isArray(data)) setComments(data);
    } catch { /* leave existing comments on error */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [taskRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const content = text.trim();
    setText('');
    // Add instantly to the list
    const optimistic = { id: `tmp_${Date.now()}`, user_name: userName || 'User', content, created_at: new Date().toISOString() };
    setComments(c => [...c, optimistic]);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('orbit_token');
      const res = await fetch(`${apiBase}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content, userName: userName || 'User' })
      });
      if (res.ok) {
        // Replace optimistic entry with real server data
        const fresh = await fetch(`${apiBase}/comments`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await fresh.json();
        if (Array.isArray(data)) setComments(data);
      }
    } catch { /* keep optimistic comment on error */ }
    setSubmitting(false);
  };

  const handleDelete = async (commentId) => {
    const deleteUrl = taskSource === 'devops'
      ? `/api/devops/tasks/comments/${commentId}`
      : taskSource === 'bug'
        ? `/api/quality/bugs/comments/${commentId}`
        : `/api/quicktrack/tasks/comments/${commentId}`;
    try {
      await fetch(deleteUrl, { method: 'DELETE' });
      setComments(c => c.filter(x => x.id !== commentId));
    } catch { /* ignore */ }
  };

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Comments</h3>
      <form onSubmit={handleSubmit} className="mb-5 flex gap-3">
        <input
          type="text" value={text} onChange={e => setText(e.target.value)}
          placeholder="Write a comment…"
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
        />
        <button type="submit" disabled={submitting || !text.trim()}
          className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium">
          Comment
        </button>
      </form>
      {loading ? (
        <div className="text-sm text-gray-400 py-2">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-400 text-sm">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <div key={c.id} className="bg-white rounded-lg border border-gray-200 p-4 group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {(c.user_name || '?').charAt(0).toUpperCase()}
                </div>
                <span className="font-medium text-sm text-gray-900">{c.user_name}</span>
                <span className="text-xs text-gray-400 ml-auto">{fmtTime(c.created_at)}</span>
                <button onClick={() => requestConfirm('Delete this comment?', () => handleDelete(c.id))}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-opacity ml-1" title="Delete">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{c.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TaskDetailPanel = ({ task, onClose, onUpdate, onDelete, user, teamMembers = [], allUsers = [], projects = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  const memberPool = teamMembers.length > 0 ? teamMembers : allUsers.map(u => ({ user_id: u.user_id, user_name: u.user_name }));
  const assignableMembers = isAdmin || isManager ? memberPool : [];

  const [editStatus, setEditStatus] = useState(task.status);
  const [editAssignee, setEditAssignee] = useState(task.assignee || '');
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editProjectId, setEditProjectId] = useState(task.projectId || '');
  const [editActualHours, setEditActualHours] = useState(task.actualHours || task.estimateHours || task.estimate || 0);
  const [editEstimatedMinutes, setEditEstimatedMinutes] = useState(task.estimatedMinutes || task.estimated_minutes || 0);
  const [editActualMinutes, setEditActualMinutes] = useState(task.actualMinutes || task.actual_minutes || 0);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const isCompleting = (editStatus === 'done' || editStatus === 'completed') && task.status !== 'done' && task.status !== 'completed';

  const isDone = editStatus === 'done' || editStatus === 'completed';
  const handleSave = async () => {
    setSaveError('');
    if (!(parseInt(editEstimatedMinutes) > 0)) {
      setSaveError('Estimated Time is required.');
      return;
    }
    if (isDone && !(parseInt(editActualMinutes) > 0)) {
      setSaveError('Actual Time is required when status is Done.');
      return;
    }
    setSaving(true);
    const changes = {};
    if (editStatus !== task.status) changes.status = editStatus;
    if (editAssignee !== (task.assignee || '')) changes.assignee = editAssignee;
    if (editPriority !== task.priority) changes.priority = editPriority;
    if (editProjectId !== (task.projectId || '')) changes.projectId = editProjectId;
    if (isCompleting && editActualHours > 0) changes.actualHours = parseFloat(editActualHours);
    if (editEstimatedMinutes !== (task.estimatedMinutes || task.estimated_minutes || 0)) changes.estimatedMinutes = parseInt(editEstimatedMinutes) || 0;
    if (editActualMinutes !== (task.actualMinutes || task.actual_minutes || 0)) changes.actualMinutes = parseInt(editActualMinutes) || 0;
    if (editDescription !== (task.description || '')) changes.description = editDescription;
    if (Object.keys(changes).length > 0) {
      await onUpdate(task.id, changes);
    }
    setSaving(false);
    onClose();
  };

  const typeBadge = getTypeBadge(task.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-gray-500">{task.id}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{task.type}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)}
              rows={3} placeholder="Add a description…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="backlog">Backlog</option>
                <option value="ready">Ready</option>
                <option value="in_progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
              <select value={editPriority} onChange={e => setEditPriority(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
            {selfAssign ? (
              <input type="text" value={editAssignee} readOnly
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
            ) : (
              <select value={editAssignee} onChange={e => setEditAssignee(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Unassigned</option>
                {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
              </select>
            )}
          </div>

          {projects.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
              <select value={editProjectId} onChange={e => setEditProjectId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— No Project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {isCompleting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Actual Hours Spent</label>
              <p className="text-[10px] text-blue-500 mb-2">These hours will be automatically logged to your TimeBoard.</p>
              <input type="number" min="0" step="0.5" value={editActualHours}
                onChange={e => setEditActualHours(e.target.value)}
                className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={editEstimatedMinutes}
                onChange={e => setEditEstimatedMinutes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isCompleting ? 'text-red-600' : 'text-gray-500'}`}>
                Actual Time (min){isCompleting && <span className="ml-1 text-red-500">*</span>}
              </label>
              <input type="number" min="0" placeholder="Required" value={editActualMinutes}
                onChange={e => { setEditActualMinutes(e.target.value); setSaveError(''); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent ${isCompleting && !(parseInt(editActualMinutes) > 0) ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Story Points</div>
              <div className="text-lg font-semibold text-gray-900">{task.storyPoints || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Progress</div>
              <div className="text-lg font-semibold text-gray-900">{task.progress || 0}%</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Variance</div>
              <div className={`text-lg font-semibold ${(editActualMinutes - editEstimatedMinutes) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {editEstimatedMinutes > 0 ? `${editActualMinutes - editEstimatedMinutes > 0 ? '+' : ''}${editActualMinutes - editEstimatedMinutes}m` : '-'}
              </div>
            </div>
          </div>

          <TaskComments taskRef={task.id} taskSource="devops" userName={user?.user_name || user?.name} />

          {saveError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{saveError}</div>
          )}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            {onDelete && (isAdmin || isManager)
              ? <button onClick={() => requestConfirm(`Delete task "${task.title}"?`, () => onDelete(task.id))} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Task</button>
              : <div />}
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- File attachment helpers (shared by bug & project attachment managers) ----
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ fileName: file.name, fileType: file.type, fileSize: file.size, fileData: reader.result });
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_ATTACHMENTS = 5;
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', 'application/csv', 'text/plain',
  'application/vnd.ms-excel', 'application/msword', 'application/octet-stream',
];
const ALLOWED_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.docx', '.xlsx', '.csv']);
const ACCEPT_ATTR = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.xlsx,.csv';
const isAllowedFile = (f) => {
  const ext = '.' + f.name.split('.').pop().toLowerCase();
  return ALLOWED_TYPES.includes(f.type) || ALLOWED_EXTENSIONS.has(ext);
};
const getFileDisplay = (fileName, fileType) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (ext === 'pdf' || fileType === 'application/pdf')
    return { label: 'PDF', emoji: '📄', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', hover: 'hover:bg-red-100' };
  if (ext === 'docx' || (fileType || '').includes('wordprocessingml'))
    return { label: 'DOCX', emoji: '📝', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', hover: 'hover:bg-blue-100' };
  if (ext === 'xlsx' || (fileType || '').includes('spreadsheetml') || fileType === 'application/vnd.ms-excel')
    return { label: 'XLSX', emoji: '📊', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', hover: 'hover:bg-green-100' };
  if (ext === 'csv' || fileType === 'text/csv' || fileType === 'application/csv')
    return { label: 'CSV', emoji: '📋', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', hover: 'hover:bg-teal-100' };
  return { label: 'FILE', emoji: '📎', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', hover: 'hover:bg-gray-100' };
};

// API-backed attachment manager (used in BugDetailPanel and EditBugModal)
const ApiAttachmentManager = ({ bugId, uploadedBy }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  const load = async () => { setLoading(true); setAttachments(await ModulesModel.getBugAttachments(bugId)); setLoading(false); };
  useEffect(() => { load(); }, [bugId]);

  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    setUploading(true);
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (attachments.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      const att = await readFileAsBase64(f);
      await ModulesModel.addBugAttachment(bugId, att);
    }
    if (errors.length) alert(errors.join('\n'));
    setUploading(false);
    await load();
  };

  const handleDelete = async (id) => {
    requestConfirm('Remove this attachment?', async () => {
      await ModulesModel.deleteBugAttachment(id);
      await load();
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Attachments</label>
        {attachments.length < MAX_ATTACHMENTS && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-600 disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Add'}
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {loading ? <p className="text-xs text-gray-400">Loading…</p> : attachments.length === 0 ? (
        <p className="text-xs text-gray-400">No attachments yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map(a => (
            <div key={a.id} className="relative group">
              {a.fileType.startsWith('image/') ? (
                <a href={a.fileData} target="_blank" rel="noreferrer">
                  <img src={a.fileData} alt={a.fileName} className="w-16 h-16 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80" />
                </a>
              ) : (() => { const d = getFileDisplay(a.fileName, a.fileType); return (
                <a href={a.fileData} download={a.fileName} className={`w-16 h-16 flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded ${d.text} text-xs font-medium ${d.hover}`}>
                  {d.label}<span className="text-gray-400 text-[10px] truncate max-w-[56px] mt-0.5">{a.fileName}</span>
                </a>
              ); })()}
              {uploadedBy === a.uploadedBy && (
                <button type="button" onClick={() => handleDelete(a.id)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center leading-none">×</button>
              )}
              <div className="text-[10px] text-gray-400 text-center mt-0.5 w-16 truncate">{a.uploadedBy || ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Bug Detail Panel ----
const BugDetailPanel = ({ bug, onClose, onUpdate, onDelete, readOnly = false, user, teamMembers = [] }) => {
  const [form, setForm] = useState({
    title: bug.title || '',
    status: bug.status || 'open',
    severity: bug.severity || 'medium',
    environment: bug.environment || 'DEV',
    module: bug.module || '',
    assignedTo: bug.assignedTo || '',
    stepsToReproduce: bug.stepsToReproduce || '',
    expectedResult: bug.expectedResult || '',
    actualResult: bug.actualResult || '',
  });
  const [saving, setSaving] = useState(false);
  const isManager = user?.role?.endsWith('_manager') || user?.role === 'manager';
  const managerModule = (isManager && user.role !== 'manager') ? user.role.replace('_manager', '') : null;
  const myTeamMembers = isManager ? (managerModule ? teamMembers.filter(m => m.team_module === managerModule) : teamMembers) : [];

  const severityColors = { critical: { bg: '#fee2e2', color: '#dc2626' }, high: { bg: '#fed7aa', color: '#ea580c' }, medium: { bg: '#fef9c3', color: '#ca8a04' }, low: { bg: '#dcfce7', color: '#16a34a' } };
  const bugStatusColors = { open: { bg: '#fee2e2', color: '#dc2626' }, in_progress: { bg: '#dbeafe', color: '#2563eb' }, resolved: { bg: '#dcfce7', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
  const sc = severityColors[form.severity] || severityColors.medium;
  const bc = bugStatusColors[form.status] || bugStatusColors.open;

  const handleSave = async () => {
    setSaving(true);
    const changes = {};
    if (form.title !== (bug.title || '')) changes.title = form.title;
    if (form.status !== (bug.status || 'open')) changes.status = form.status;
    if (form.severity !== (bug.severity || 'medium')) changes.severity = form.severity;
    if (form.environment !== (bug.environment || '')) changes.environment = form.environment;
    if (form.module !== (bug.module || '')) changes.module = form.module;
    if (form.assignedTo !== (bug.assignedTo || '')) changes.assignedTo = form.assignedTo;
    if (form.stepsToReproduce !== (bug.stepsToReproduce || '')) changes.stepsToReproduce = form.stepsToReproduce;
    if (form.expectedResult !== (bug.expectedResult || '')) changes.expectedResult = form.expectedResult;
    if (form.actualResult !== (bug.actualResult || '')) changes.actualResult = form.actualResult;
    if (Object.keys(changes).length > 0 && onUpdate) await onUpdate(bug.id, changes);
    setSaving(false);
    onClose();
  };

  const iCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400';
  const rCls = 'text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 min-h-[36px]';

  const Field = ({ label, children }) => (
    <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>{children}</div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono text-gray-500">{bug.bugCode}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: sc.bg, color: sc.color }}>{form.severity}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bc.bg, color: bc.color }}>{form.status}</span>
            {readOnly && <span className="text-xs text-gray-400 italic">· view only</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <Field label="Title">
            {readOnly
              ? <p className={rCls}>{form.title}</p>
              : <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={iCls} />}
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Status">
              {readOnly
                ? <p className={rCls + ' capitalize'}>{form.status.replace('_', ' ')}</p>
                : <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={iCls}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>}
            </Field>
            <Field label="Severity">
              {readOnly
                ? <p className={rCls + ' capitalize'}>{form.severity}</p>
                : <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} className={iCls}>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>}
            </Field>
            <Field label="Environment">
              {readOnly
                ? <p className={rCls}>{form.environment || '—'}</p>
                : <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className={iCls}>
                    <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
                  </select>}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Module">
              {readOnly
                ? <p className={rCls}>{form.module || '—'}</p>
                : <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} className={iCls}>
                    <option value="">None</option>
                    <option>Authentication</option><option>Settlement Calculator</option>
                    <option>Payment Processing</option><option>Reporting</option>
                    <option>User Management</option><option>UI</option>
                  </select>}
            </Field>
            <Field label="Assigned To">
              {readOnly && isManager
                ? <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} className={iCls}>
                    <option value="">— Unassigned —</option>
                    {myTeamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                  </select>
                : readOnly
                  ? <p className={rCls}>{form.assignedTo || '—'}</p>
                  : <input value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} placeholder="Developer name" className={iCls} />}
            </Field>
          </div>

          <Field label="Steps to Reproduce">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap min-h-[48px]">{form.stepsToReproduce || '—'}</p>
              : <textarea value={form.stepsToReproduce} onChange={e => setForm({ ...form, stepsToReproduce: e.target.value })} rows={3} className={iCls + ' resize-none'} />}
          </Field>

          <Field label="Expected Result">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-green-50 rounded-lg p-3 min-h-[40px]">{form.expectedResult || '—'}</p>
              : <textarea value={form.expectedResult} onChange={e => setForm({ ...form, expectedResult: e.target.value })} rows={2} className={iCls + ' resize-none'} />}
          </Field>

          <Field label="Actual Result">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-red-50 rounded-lg p-3 min-h-[40px]">{form.actualResult || '—'}</p>
              : <textarea value={form.actualResult} onChange={e => setForm({ ...form, actualResult: e.target.value })} rows={2} className={iCls + ' resize-none'} />}
          </Field>

          <div className="border-t border-gray-100 pt-4">
            <ApiAttachmentManager bugId={bug.id} uploadedBy={user?.user_name || user?.name} />
          </div>

          <TaskComments taskRef={bug.id} taskSource="bug" userName={user?.user_name || user?.name} />

          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            {!readOnly && onDelete
              ? <button onClick={() => requestConfirm('Delete this bug?', () => onDelete(bug.id))} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
              : <div />}
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
              {readOnly && isManager && onUpdate && (
                <button onClick={async () => { setSaving(true); await onUpdate(bug.id, { assignedTo: form.assignedTo }); setSaving(false); onClose(); }}
                  disabled={saving || form.assignedTo === (bug.assignedTo || '')}
                  className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Reassign'}
                </button>
              )}
              {!readOnly && (
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- DevOps Module (Main Container) ----
const EodSettingsModal = ({ onClose }) => {
  const [settings, setSettings] = React.useState({
    eod_enabled: 'false', eod_slack_channel: '#daily-summary',
    pipedream_outbound_url: '', pipedream_project_url: '', pipedream_api_key: ''
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    ModulesModel.getAppSettings().then(s => { setSettings(s); setLoading(false); });
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const res = await ModulesModel.saveAppSettings(settings);
    setSaving(false);
    if (res?.success) setSaved(true);
    else setError(res?.error || 'Failed to save settings');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-900">⚙️ EOD Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading settings…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-800">EOD Summary Enabled</div>
                <div className="text-xs text-gray-500">Allow sending EOD reports to Slack</div>
              </div>
              <button
                onClick={() => setSettings(s => ({ ...s, eod_enabled: s.eod_enabled === 'true' ? 'false' : 'true' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.eod_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.eod_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slack Channel</label>
              <input type="text" value={settings.eod_slack_channel || ''} onChange={e => setSettings(s => ({ ...s, eod_slack_channel: e.target.value }))}
                placeholder="#daily-summary"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">EOD Pipedream URL</label>
              <input type="text" value={settings.pipedream_outbound_url || ''} onChange={e => setSettings(s => ({ ...s, pipedream_outbound_url: e.target.value }))}
                placeholder="https://eod.m.pipedream.net"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Update Pipedream URL</label>
              <input type="text" value={settings.pipedream_project_url || ''} onChange={e => setSettings(s => ({ ...s, pipedream_project_url: e.target.value }))}
                placeholder="https://project-update.m.pipedream.net"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipedream API Key <span className="text-gray-400 font-normal">(leave blank to keep existing)</span></label>
              <input type="password" value={settings.pipedream_api_key || ''} onChange={e => setSettings(s => ({ ...s, pipedream_api_key: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {saved && <p className="text-green-600 text-xs font-medium">✓ Settings saved</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SendEodModal = ({ module, onClose }) => {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    setSending(true); setError('');
    const res = await ModulesModel.sendEodSummary(module);
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send EOD summary');
  };

  const moduleLabel = module === 'devops' ? 'DevOps' : 'DataOps';
  const moduleColor = module === 'devops' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 {moduleLabel} EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Members included</span><span className="font-medium">{result.usersIncluded}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Completed today</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">In progress</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-5">
              This will send an End of Day summary for all <strong>{moduleLabel}</strong> team members to Slack via Pipedream,
              showing tasks completed today, in progress, and pending.
            </p>
            {error && <p className="text-red-500 text-sm mb-3 bg-red-50 p-3 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className={`px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${moduleColor}`}>
                {sending ? 'Sending…' : 'Send EOD Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DevOpsModule = ({ data, viewMode, group, onViewModeChange, onGroupChange, onAddTask, onTaskClick, onStatusChange, onSendEod, onEodSettings, userRole, onDelete }) => {
  const canManageEod = userRole === 'admin' || userRole === 'manager';
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DevOps Pipeline</h1>
          <p className="text-gray-600 mt-1">Developer pipeline & operations tracking</p>
        </div>
        <div className="flex gap-2">
          {canManageEod && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
          {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>}
          <button onClick={onAddTask} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Task</button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-4">
        {['sprint', 'kanban'].map(mode => (
          <button key={mode} onClick={() => onViewModeChange(mode)}
            className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {mode === 'sprint' ? 'Sprint Board' : 'Kanban Board'}
          </button>
        ))}
      </div>

      {/* Group Toggle */}
      <div className="flex gap-2 mb-6">
        {[{ key: 'all', label: 'All' }, { key: 'dev', label: 'Dev Pipeline' }, { key: 'ops', label: 'Ops Pipeline' }].map(g => (
          <button key={g.key} onClick={() => onGroupChange(g.key)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium ${group === g.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Sprint Header (only in sprint view) */}
      {viewMode === 'sprint' && data.sprint && (group === 'all' || group === 'dev') && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
              <p className="text-sm text-gray-600">{data.sprint.goal}</p>
              <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
            </div>
          </div>
        </div>
      )}

      {/* Dev Pipeline content */}
      {(group === 'all' || group === 'dev') && (
        <>
          {viewMode === 'sprint' && (
            <>
              <DevOpsCapacityCards capacity={data.capacity} />
              <DevOpsTeamWorkload workload={data.workload} />
              <DevOpsPipelineBoard
                pipeline={data.pipeline}
                title="Sprint Pipeline"
                onTaskClick={onTaskClick}
                onStatusChange={onStatusChange}
              />
            </>
          )}
          {viewMode === 'kanban' && (
            <DevOpsKanbanBoard
              pipeline={data.kanbanPipeline || data.pipeline}
              onTaskClick={onTaskClick}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              canDelete={userRole === 'admin' || userRole === 'devops_manager' || userRole === 'manager'}
            />
          )}
        </>
      )}

      {/* Ops Pipeline content */}
      {(group === 'all' || group === 'ops') && data.ops && (
        <DevOpsOpsGroup pipelines={data.ops.pipelines} />
      )}
    </div>
  );
};

// Shared style helpers for PlanTrack
const getStatusStyle = (status) => {
  const styles = {
    active: { color: '#0366d6', bg: '#d4e4ff', label: 'Active' },
    planning: { color: '#6f42c1', bg: '#f3e8ff', label: 'Planning' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    on_hold: { color: '#e36209', bg: '#fff5cc', label: 'On Hold' },
    backlog: { color: '#586069', bg: '#f6f8fa', label: 'Backlog' },
    in_progress: { color: '#0366d6', bg: '#d4e4ff', label: 'In Progress' },
    ready: { color: '#17a2b8', bg: '#d4f4ff', label: 'Ready' },
    review: { color: '#6f42c1', bg: '#f3e8ff', label: 'Review' },
    blocked: { color: '#dc3545', bg: '#ffe5e9', label: 'Blocked' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const getPriorityStyle = (priority) => {
  const styles = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#e36209', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return styles[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getMilestoneIcon = (status) => {
  if (status === 'completed') return { symbol: '✓', color: '#28a745', bg: '#dcffe4' };
  if (status === 'in_progress') return { symbol: '●', color: '#0366d6', bg: '#d4e4ff' };
  if (status === 'overdue') return { symbol: '!', color: '#dc3545', bg: '#ffe5e9' };
  return { symbol: '○', color: '#959da5', bg: '#f6f8fa' };
};

// ---- Milestone Engine Utilities ----

const getHealthBadgeStyle = (hs) => {
  const s = {
    'completed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Completed' },
    'on-track': { bg: 'bg-green-100', text: 'text-green-700', label: 'On Track' },
    'at-risk': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'At Risk' },
    'critical': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Critical' },
    'overdue': { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' }
  };
  return s[hs] || s['on-track'];
};

const formatRiskScore = (score) => {
  if (score >= 80) return { level: 'Critical', color: 'text-red-600' };
  if (score >= 60) return { level: 'High', color: 'text-orange-600' };
  if (score >= 40) return { level: 'Medium', color: 'text-yellow-600' };
  if (score >= 20) return { level: 'Low', color: 'text-lime-600' };
  return { level: 'Minimal', color: 'text-green-600' };
};

// ---- Milestone Engine Modals ----

const AddMilestoneModal = ({ onClose, onSubmit, projectId, projects }) => {
  const [form, setForm] = useState({ projectId: projectId || '', title: '', description: '', dueDate: '', ownerId: '', isCriticalPath: false });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title || !form.projectId || !form.dueDate) return; onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Create Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
            <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required disabled={!!projectId}>
              <option value="">Select project...</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g., Requirements Complete" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date *</label>
            <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Mark as Critical Path
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Create Milestone</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditMilestoneModal = ({ milestone, allMilestones, onClose, onSubmit, onDelete }) => {
  const [form, setForm] = useState({
    title: milestone.title || '', description: milestone.description || '', dueDate: milestone.due_date || '',
    status: milestone.status || 'pending', completionPercentage: milestone.completion_percentage || 0,
    ownerId: milestone.owner_id || '', isCriticalPath: !!milestone.is_critical_path
  });
  const [deps, setDeps] = useState(milestone.dependencies || []);
  const [showAddDep, setShowAddDep] = useState(false);
  const [newDep, setNewDep] = useState({ predecessorId: '', type: 'finish-to-start' });

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const handleAddDep = async () => {
    if (!newDep.predecessorId) return;
    await ModulesModel.createMilestoneDependency({ predecessorId: newDep.predecessorId, successorId: milestone.milestone_id, dependencyType: newDep.type });
    const updated = await ModulesModel.getMilestoneDetail(milestone.milestone_id);
    if (updated) setDeps(updated.dependencies || []);
    setShowAddDep(false); setNewDep({ predecessorId: '', type: 'finish-to-start' });
  };
  const handleRemoveDep = async (predId) => {
    await ModulesModel.deleteMilestoneDependency(predId, milestone.milestone_id);
    setDeps(deps.filter(d => d.predecessor_milestone_id !== predId));
  };

  // Filter milestones: same project, not self
  const depOptions = (allMilestones || []).filter(m => m.project_id === milestone.project_id && m.milestone_id !== milestone.milestone_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">Edit Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Completion: {form.completionPercentage}%</label>
              <input type="range" min="0" max="100" value={form.completionPercentage}
                onChange={e => setForm({...form, completionPercentage: parseInt(e.target.value)})} className="w-full mt-2" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Critical Path Milestone
          </label>

          {/* Dependencies section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-900">Dependencies (predecessors)</h3>
              <button type="button" onClick={() => setShowAddDep(!showAddDep)}
                className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">+ Add</button>
            </div>
            {showAddDep && (
              <div className="bg-gray-50 p-3 rounded-lg mb-3 flex gap-2 items-end">
                <div className="flex-1">
                  <select value={newDep.predecessorId} onChange={e => setNewDep({...newDep, predecessorId: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                    <option value="">Select milestone...</option>
                    {depOptions.map(m => <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>)}
                  </select>
                </div>
                <select value={newDep.type} onChange={e => setNewDep({...newDep, type: e.target.value})}
                  className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="finish-to-start">Finish-to-Start</option>
                  <option value="start-to-start">Start-to-Start</option>
                  <option value="finish-to-finish">Finish-to-Finish</option>
                </select>
                <button type="button" onClick={handleAddDep} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
              </div>
            )}
            {deps.length === 0 ? <p className="text-sm text-gray-400 text-center py-2">No dependencies</p> : (
              <div className="space-y-1">
                {deps.filter(d => d.successor_milestone_id === milestone.milestone_id).map(d => (
                  <div key={d.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span><span className="font-medium">{d.predecessor_title}</span> <span className="text-gray-400 text-xs mx-1">{d.dependency_type}</span></span>
                    <button type="button" onClick={() => handleRemoveDep(d.predecessor_milestone_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked tasks */}
          {milestone.linkedTasks && milestone.linkedTasks.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Linked Tasks ({milestone.linkedTasks.length})</h3>
              <div className="space-y-1">
                {milestone.linkedTasks.map(t => (
                  <div key={t.task_id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span>{t.title}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.status === 'completed' || t.status === 'done' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <button type="button" onClick={() => requestConfirm('Delete this milestone?', () => onDelete(milestone.milestone_id))}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 font-medium">Delete</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Save Changes</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const ManageTemplatesModal = ({ onClose, onChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', projectType: 'general', description: '' });
  const [newItem, setNewItem] = useState({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
  const [showNewItem, setShowNewItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    const data = await ModulesModel.getMilestoneTemplates();
    setTemplates(data || []);
    setLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (selectedId) {
      ModulesModel.getTemplateItems(selectedId).then(data => setItems(data || []));
    } else {
      setItems([]);
    }
  }, [selectedId]);

  const selected = templates.find(t => t.id === selectedId);

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) return;
    setSaving(true);
    await ModulesModel.createMilestoneTemplate(newTemplate);
    setNewTemplate({ name: '', projectType: 'general', description: '' });
    setShowNewTemplate(false);
    setSaving(false);
    await loadTemplates();
    onChanged();
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template and all its milestones?')) return;
    await ModulesModel.deleteMilestoneTemplate(id);
    if (selectedId === id) setSelectedId(null);
    await loadTemplates();
    onChanged();
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.title.trim() || !selectedId) return;
    setSaving(true);
    await ModulesModel.createTemplateItem(selectedId, { ...newItem, sortOrder: items.length });
    setNewItem({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
    setShowNewItem(false);
    setSaving(false);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const handleDeleteItem = async (itemId) => {
    await ModulesModel.deleteTemplateItem(selectedId, itemId);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const projectTypeOptions = ['general', 'software', 'marketing', 'operations', 'research', 'infrastructure'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Manage Milestone Templates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Template list */}
          <div className="w-64 border-r flex flex-col bg-gray-50">
            <div className="p-3 border-b">
              <button onClick={() => setShowNewTemplate(true)} className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Template</button>
            </div>
            {showNewTemplate && (
              <form onSubmit={handleCreateTemplate} className="p-3 border-b bg-white space-y-2">
                <input required placeholder="Template name" value={newTemplate.name} onChange={e => setNewTemplate({...newTemplate, name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <select value={newTemplate.projectType} onChange={e => setNewTemplate({...newTemplate, projectType: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                  {projectTypeOptions.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input placeholder="Description (optional)" value={newTemplate.description} onChange={e => setNewTemplate({...newTemplate, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <div className="flex gap-2">
                  <button type="submit" disabled={saving} className="flex-1 px-2 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">Save</button>
                  <button type="button" onClick={() => setShowNewTemplate(false)} className="flex-1 px-2 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              </form>
            )}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="text-xs text-gray-400 p-3">Loading...</p>
              ) : templates.length === 0 ? (
                <p className="text-xs text-gray-400 p-3">No templates yet.</p>
              ) : templates.map(t => (
                <div key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`px-3 py-2.5 cursor-pointer border-b border-gray-100 hover:bg-purple-50 flex justify-between items-start ${selectedId === t.id ? 'bg-purple-100 border-l-4 border-l-purple-500' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.template_name}</p>
                    <p className="text-xs text-gray-500">{t.project_type} · {t.item_count} milestones</p>
                  </div>
                  <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete template "${t.template_name}"?`, () => handleDeleteTemplate(t.id)); }} className="text-red-400 hover:text-red-600 text-xs ml-1 mt-0.5">✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Template items */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a template to view and edit its milestones</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selected.template_name}</h3>
                    {selected.description && <p className="text-xs text-gray-500 mt-0.5">{selected.description}</p>}
                  </div>
                  <button onClick={() => setShowNewItem(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Milestone</button>
                </div>

                {showNewItem && (
                  <form onSubmit={handleAddItem} className="px-5 py-3 bg-blue-50 border-b grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input required placeholder="Milestone title" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start offset (days from project start)</label>
                      <input type="number" min="0" value={newItem.offsetDays} onChange={e => setNewItem({...newItem, offsetDays: parseInt(e.target.value) || 0})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Duration (days)</label>
                      <input type="number" min="1" value={newItem.durationDays} onChange={e => setNewItem({...newItem, durationDays: parseInt(e.target.value) || 1})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={newItem.isCritical} onChange={e => setNewItem({...newItem, isCritical: e.target.checked})} />
                        Mark as critical path
                      </label>
                      <div className="flex gap-2">
                        <button type="submit" disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
                        <button type="button" onClick={() => setShowNewItem(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="flex-1 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-sm">No milestones in this template yet. Add one above.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Milestone</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Offset</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Duration</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Critical</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map((item, i) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{item.title}</td>
                            <td className="px-4 py-2.5 text-gray-600">Day {item.offset_from_start}</td>
                            <td className="px-4 py-2.5 text-gray-600">{item.default_duration_days}d</td>
                            <td className="px-4 py-2.5">
                              {item.is_critical ? <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">Critical</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => requestConfirm('Remove this checklist item?', () => handleDeleteItem(item.id))} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GenerateFromTemplateModal = ({ projects, templates, onClose, onGenerate }) => {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [templateItems, setTemplateItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (selectedTemplate) {
      ModulesModel.getTemplateItems(parseInt(selectedTemplate)).then(items => setTemplateItems(items || []));
    } else { setTemplateItems([]); }
  }, [selectedTemplate]);

  const handleGenerate = async () => {
    if (!selectedProject || !selectedTemplate || !startDate) return;
    setGenerating(true);
    await onGenerate({ projectId: selectedProject, templateId: parseInt(selectedTemplate), startDate });
    setGenerating(false);
  };

  const tmpl = templates.find(t => t.id === parseInt(selectedTemplate));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Generate from Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Template *</label>
              <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.template_name} ({t.item_count} milestones)</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project Start Date *</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Milestone due dates are calculated from this date</p>
          </div>
          {tmpl && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-1">{tmpl.template_name}</h3>
              <p className="text-sm text-blue-800">{tmpl.description}</p>
              <p className="text-xs text-blue-600 mt-1">Type: {tmpl.project_type}</p>
            </div>
          )}
          {templateItems.length > 0 && (
            <div className="border rounded-lg">
              <div className="bg-gray-50 px-4 py-2 border-b"><h3 className="font-semibold text-gray-900 text-sm">Preview: {templateItems.length} Milestones</h3></div>
              <div className="p-4 max-h-52 overflow-y-auto space-y-2">
                {templateItems.map((item, i) => (
                  <div key={item.id} className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">{i+1}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.title}{item.is_critical ? <span className="ml-2 text-red-600 font-semibold text-xs">CRITICAL</span> : ''}</div>
                      <div className="text-xs text-gray-500">Day {item.offset_from_start} + {item.default_duration_days}d</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleGenerate} disabled={generating || !selectedProject || !selectedTemplate || !startDate}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50">
              {generating ? 'Generating...' : `Generate ${templateItems.length} Milestones`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Milestone Engine View ----

const MilestoneEngineView = ({ onBack, projects }) => {
  const [milestones, setMilestones] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [execSummary, setExecSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [editingMs, setEditingMs] = useState(null);
  const [filters, setFilters] = useState({ project: '', status: '', health: '' });

  const loadData = async () => {
    setLoading(true);
    const [ms, tmpl, summary] = await Promise.all([
      ModulesModel.getMilestones(), ModulesModel.getMilestoneTemplates(), ModulesModel.getExecutiveSummary()
    ]);
    setMilestones(ms || []); setTemplates(tmpl || []); setExecSummary(summary);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);

  const handleCreate = async (data) => { await ModulesModel.createMilestone(data); setShowCreate(false); loadData(); };
  const handleUpdate = async (msId, data) => { await ModulesModel.updateMilestone(msId, data); setEditingMs(null); loadData(); };
  const handleDelete = async (msId) => { await ModulesModel.deleteMilestone(msId); setEditingMs(null); loadData(); };
  const handleComplete = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed'); else loadData();
  };
  const handleGenerate = async (data) => {
    const r = await ModulesModel.generateMilestonesFromTemplate(data);
    if (r.success) { alert(`Generated ${r.createdCount} milestones!`); setShowGenerate(false); loadData(); }
    else alert(r.error || 'Failed');
  };

  const filtered = milestones.filter(m => {
    if (filters.project && m.project_id !== filters.project) return false;
    if (filters.status && m.status !== filters.status) return false;
    if (filters.health && m.health_status !== filters.health) return false;
    return true;
  });

  const s = execSummary?.summary || {};

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading Milestone Engine...</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Milestone Engine</h1>
          <p className="text-gray-600 mt-1">Template-driven milestones, risk scoring, and critical path analysis</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowManageTemplates(true)} className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 font-medium">Manage Templates</button>
          <button onClick={() => setShowGenerate(true)} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">Generate from Template</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Milestone</button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total', value: s.total_milestones || 0, sub: `${s.completed || 0} completed`, color: 'text-gray-900' },
          { label: 'Overdue', value: s.overdue || 0, sub: 'Requires attention', color: 'text-red-600' },
          { label: 'Critical', value: s.critical || 0, sub: 'High priority', color: 'text-orange-600' },
          { label: 'At Risk', value: s.at_risk || 0, sub: 'Monitor closely', color: 'text-yellow-600' },
          { label: 'Critical Path', value: s.on_critical_path || 0, sub: 'Essential', color: 'text-purple-600' }
        ].map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">{c.label}</div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Delayed + Upcoming */}
      {(execSummary?.delayedMilestones?.length > 0 || execSummary?.upcomingMilestones?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {execSummary?.delayedMilestones?.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-red-200 p-4">
              <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Delayed Milestones</h3>
              <div className="space-y-2">
                {execSummary.delayedMilestones.map(m => {
                  const hs = getHealthBadgeStyle(m.health_status);
                  return (
                    <div key={m.milestone_id} className="flex justify-between items-center text-sm">
                      <div><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-500 ml-2 text-xs">{m.project_name}</span></div>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {execSummary?.upcomingMilestones?.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-blue-200 p-4">
              <h3 className="text-sm font-bold text-blue-700 mb-3">Upcoming Milestones</h3>
              <div className="space-y-2">
                {execSummary.upcomingMilestones.map(m => (
                  <div key={m.milestone_id} className="flex justify-between items-center text-sm">
                    <div><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-500 ml-2 text-xs">{m.project_name}</span></div>
                    <span className="text-xs text-gray-500">{m.due_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Project</label>
            <select value={filters.project} onChange={e => setFilters({...filters, project: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Projects</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Health</label>
            <select value={filters.health} onChange={e => setFilters({...filters, health: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All</option><option value="on-track">On Track</option><option value="at-risk">At Risk</option><option value="critical">Critical</option><option value="overdue">Overdue</option><option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Milestone list */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <p className="text-gray-500 mb-4">No milestones match your filters.</p>
            <button onClick={() => setFilters({ project: '', status: '', health: '' })} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Filters</button>
          </div>
        ) : filtered.map(ms => {
          const hs = getHealthBadgeStyle(ms.health_status);
          const rf = formatRiskScore(ms.risk_score || 0);
          return (
            <div key={ms.id || ms.milestone_id} className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-base font-semibold text-gray-900">{ms.title}</h3>
                    {ms.is_critical_path === 1 && <span className="px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700">CRITICAL PATH</span>}
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">{ms.project_name}</span>
                    {ms.owner_name && <span className="ml-3 text-gray-400">Owner: {ms.owner_name}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    Due: {ms.due_date}{ms.status === 'completed' && ms.completed_at && <span className="ml-3 text-green-600">Completed: {ms.completed_at}</span>}
                  </div>
                  {ms.status !== 'completed' && (
                    <div className="flex items-center gap-6">
                      <div className="flex-1 max-w-xs">
                        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Completion</span><span>{ms.completion_percentage || 0}%</span></div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${ms.completion_percentage || 0}%` }} /></div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Risk</div>
                        <div className={`text-lg font-bold ${rf.color}`}>{ms.risk_score || 0}</div>
                        <div className="text-xs text-gray-500">{rf.level}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  {ms.status !== 'completed' && (
                    <button onClick={() => handleComplete(ms.milestone_id)} className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium">Complete</button>
                  )}
                  <button onClick={async () => { const d = await ModulesModel.getMilestoneDetail(ms.milestone_id); if (d) setEditingMs(d); }}
                    className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium">Edit</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && <AddMilestoneModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} projects={projects || []} />}
      {showGenerate && <GenerateFromTemplateModal projects={projects || []} templates={templates} onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} />}
      {showManageTemplates && <ManageTemplatesModal onClose={() => setShowManageTemplates(false)} onChanged={() => { loadData(); }} />}
      {editingMs && <EditMilestoneModal milestone={editingMs} allMilestones={milestones} onClose={() => setEditingMs(null)} onSubmit={(data) => handleUpdate(editingMs.milestone_id, data)} onDelete={handleDelete} />}
    </div>
  );
};

// ---- Modal Components ----

const AddProjectModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', description: '', priority: 'P2', owner: '', startDate: '', targetEndDate: '', budgetHours: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
            <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Project</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditProjectModal = ({ project, sprints, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: project.name || '',
    description: project.description || '',
    status: project.status || 'planning',
    priority: project.priority || 'P2',
    owner: project.owner || '',
    startDate: project.startDate || '',
    targetEndDate: project.targetEndDate || '',
    budgetHours: project.budgetHours || '',
    progress: project.progress || 0,
    sprintId: project.sprintId || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Edit Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Progress %</label>
              <input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({...form, progress: parseInt(e.target.value) || 0})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
              <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint</label>
              <select value={form.sprintId} onChange={e => setForm({...form, sprintId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">No Sprint</option>
                {(sprints || []).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.status === 'active' ? ' (Active)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddSprintModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', number: '', startDate: '', endDate: '', status: 'active', goal: '', capacityHours: '160' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Sprint</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Number</label>
              <input type="number" value={form.number} onChange={e => setForm({...form, number: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="active">Active</option>
              <option value="planned">Planned</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Goal</label>
            <input value={form.goal} onChange={e => setForm({...form, goal: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity Hours</label>
            <input type="number" value={form.capacityHours} onChange={e => setForm({...form, capacityHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Sprint</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddTaskInline = ({ onSubmit, onCancel, user, teamMembers = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  const assignableMembers = isAdmin || isManager ? teamMembers : [];
  const defaultAssignee = user?.name || '';

  const [form, setForm] = useState({ title: '', priority: 'P2', assignee: defaultAssignee, type: 'task', estimateHours: '', storyPoints: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <input required placeholder="Task title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
        </select>
        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="task">Project Task</option><option value="ADHOC">Ad hoc</option>
        </select>
        {selfAssign ? (
          <input type="text" value={form.assignee} readOnly
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
        ) : (
          <select value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Unassigned</option>
            {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
          </select>
        )}
        <input type="number" placeholder="Hours" value={form.estimateHours} onChange={e => setForm({...form, estimateHours: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Task</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
      </div>
    </form>
  );
};

// ---- Project Detail View ----

const ProjectDetailView = ({ detail, onBack, onRefresh, onEdit, onCreateMilestone, onEditMilestone, onCompleteMilestone, user, teamMembers = [] }) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [commentText, setCommentText] = useState('');
  const [updateText, setUpdateText] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});
  const [selectedProjectTask, setSelectedProjectTask] = useState(null);
  const [selectedProjectBug, setSelectedProjectBug] = useState(null);

  const { project, tasks, comments, updates, milestones, bugs = [], attachments = [] } = detail;

  useEffect(() => {
    if (project?.id) {
      ModulesModel.getProjectOverview(project.id).then(data => { if (data) setOverview(data); });
    }
  }, [project?.id]);
  const statusStyle = getStatusStyle(project.status);
  const priorityStyle = getPriorityStyle(project.priority);
  const budgetPercent = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    await ModulesModel.addComment(project.id, commentText.trim());
    setCommentText('');
    setSubmitting(false);
    onRefresh();
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!updateText.trim()) return;
    setSubmitting(true);
    await ModulesModel.addUpdate(project.id, updateText.trim());
    setUpdateText('');
    setSubmitting(false);
    onRefresh();
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task? This cannot be undone.')) return;
    await ModulesModel.deleteProjectTask(project.id, taskId);
    onRefresh();
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditTaskForm({ title: task.title, status: task.status, priority: task.priority, assignee: task.assignee || '', progress: task.progress || 0 });
  };

  const handleSaveTask = async () => {
    await ModulesModel.updateDevOpsTask(editingTaskId, editTaskForm);
    setEditingTaskId(null);
    onRefresh();
  };

  const handleProjectTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    onRefresh();
  };

  const handleAddTask = async (taskData) => {
    setSubmitting(true);
    if (taskData.type === 'ADHOC') {
      const priorityMap = { P1: 'urgent', P2: 'high', P3: 'medium' };
      await ModulesModel.createQuickTask({
        title: taskData.title,
        priority: priorityMap[taskData.priority] || 'medium',
        assignedTo: taskData.assignee || null,
        projectId: project.id,
        category: 'general',
        status: 'open'
      });
    } else {
      await ModulesModel.createProjectTask(project.id, taskData);
    }
    setShowAddTask(false);
    setSubmitting(false);
    onRefresh();
  };

  const tabs = [
    { id: 'charter', label: 'Charter', count: null },
    { id: 'stakeholders', label: 'Stakeholders', count: null },
    { id: 'tasks', label: 'Tasks', count: tasks.length },
    { id: 'bugs', label: 'Bugs', count: bugs.length },
    { id: 'risks', label: 'Risks', count: null },
    { id: 'updates', label: 'Updates', count: updates.length },
    { id: 'comments', label: 'Comments', count: comments.length },
    { id: 'milestones', label: 'Milestones', count: milestones.length },
    { id: 'attachments', label: 'Artifacts', count: attachments.length }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {selectedProjectTask && (
        <TaskDetailPanel
          task={selectedProjectTask}
          onClose={() => setSelectedProjectTask(null)}
          onUpdate={handleProjectTaskUpdate}
          user={user}
        />
      )}
      {selectedProjectBug && (
        <BugDetailPanel
          bug={selectedProjectBug}
          onClose={() => setSelectedProjectBug(null)}
          user={user}
          readOnly={true}
        />
      )}
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-500">{project.id}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: priorityStyle.bg, color: priorityStyle.color }}>{project.priority}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
            {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
          </div>
          <button onClick={onEdit} className="px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 font-medium">Edit Project</button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{project.name}</h1>
        <p className="text-gray-600 mb-4">{project.description}</p>
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-4">
          <span>👤 {project.owner}</span>
          <span>📅 {project.startDate} → {project.targetEndDate}</span>
          <span>💰 {project.spentHours}h / {project.budgetHours}h budget ({budgetPercent}%)</span>
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{project.progress}%</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
          </div>
        </div>
      </div>

      {/* Cross-Module Summary */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'DevOps Tasks', value: overview.tasks, icon: '🔧', color: '#0366d6' },
            { label: 'Quick Tasks', value: overview.quickTasks, icon: '⚡', color: '#667eea' },
            { label: 'Pipelines', value: overview.pipelines, icon: '📊', color: '#6f42c1' },
            { label: 'Time Logged', value: `${overview.timeLogged}h`, icon: '⏱️', color: '#17a2b8' },
            ...(overview.qa ? [
              { label: 'QA Test Cases', value: overview.qa.testCases, icon: '✓', color: '#20c997' },
              { label: 'Open Bugs', value: overview.qa.openBugs, icon: '🐛', color: overview.qa.openBugs > 0 ? '#dc3545' : '#28a745' }
            ] : [
              { label: 'QA Status', value: 'Not Enabled', icon: '✓', color: '#6b7280' },
              { label: '', value: '', icon: '', color: '' }
            ])
          ].filter(s => s.label).map(s => (
            <div key={s.label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span>{s.icon}</span>
                <span className="text-xs font-medium text-gray-500">{s.label}</span>
              </div>
              <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
            {tab.label} {tab.count !== null && <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Linked Tasks</h2>
            {!showAddTask && (
              <button onClick={() => setShowAddTask(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Task</button>
            )}
          </div>
          {showAddTask && <div className="mb-4"><AddTaskInline onSubmit={handleAddTask} onCancel={() => setShowAddTask(false)} user={user} teamMembers={teamMembers} /></div>}
          {tasks.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No tasks linked to this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map(task => {
                    const isEditing = editingTaskId === task.id;
                    const ts = getStatusStyle(task.status);
                    const tp = getPriorityStyle(task.priority);
                    if (isEditing) {
                      return (
                        <tr key={task.id} className="bg-blue-50">
                          <td className="px-4 py-2 text-xs font-mono text-gray-500">{task.id}</td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.title} onChange={e => setEditTaskForm({...editTaskForm, title: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500" />
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.status} onChange={e => setEditTaskForm({...editTaskForm, status: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['backlog','ready','in_progress','review','done'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.priority} onChange={e => setEditTaskForm({...editTaskForm, priority: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['P1','P2','P3'].map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.assignee} onChange={e => setEditTaskForm({...editTaskForm, assignee: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm" placeholder="Assignee" />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <input type="number" min="0" max="100" value={editTaskForm.progress} onChange={e => setEditTaskForm({...editTaskForm, progress: parseInt(e.target.value) || 0})}
                                className="w-16 border border-blue-300 rounded px-2 py-1 text-xs" />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <button onClick={handleSaveTask} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-2">Save</button>
                            <button onClick={() => setEditingTaskId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{task.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: tp.bg, color: tp.color }}>{task.priority}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{task.assignee}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${task.progress || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{task.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => setSelectedProjectTask(task)} className="text-blue-500 hover:text-blue-700 text-xs font-medium mr-2">View</button>
                          <button onClick={() => startEditTask(task)} className="text-indigo-500 hover:text-indigo-700 text-xs font-medium mr-2">Edit</button>
                          <button onClick={() => requestConfirm(`Delete task "${task.title}"?`, () => handleDeleteTask(task.id))} className="text-red-400 hover:text-red-600 text-xs font-medium">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bugs Tab */}
      {activeTab === 'bugs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">QA Bugs</h2>
            <span className="text-xs text-gray-500">Bugs are created in the Quality module</span>
          </div>
          {bugs.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No bugs reported for this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Bug ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Module</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Reported</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bugs.map(bug => {
                    const severityColors = { critical: { bg: '#fee2e2', color: '#dc2626' }, high: { bg: '#fed7aa', color: '#ea580c' }, medium: { bg: '#fef9c3', color: '#ca8a04' }, low: { bg: '#dcfce7', color: '#16a34a' } };
                    const sc = severityColors[bug.severity] || severityColors.medium;
                    const bugStatusColors = { open: { bg: '#fee2e2', color: '#dc2626' }, 'in-progress': { bg: '#dbeafe', color: '#2563eb' }, resolved: { bg: '#dcfce7', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
                    const bc = bugStatusColors[bug.status] || bugStatusColors.open;
                    return (
                      <tr key={bug.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{bug.bugCode}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bug.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: sc.bg, color: sc.color }}>{bug.severity}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bc.bg, color: bc.color }}>{bug.status || 'open'}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.module || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.assignedTo || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">{bug.createdAt ? new Date(bug.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setSelectedProjectBug(bug)} className="text-blue-500 hover:text-blue-700 text-xs font-medium">View</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Updates Tab */}
      {activeTab === 'updates' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Updates</h2>
          <form onSubmit={handleAddUpdate} className="mb-6 flex gap-3">
            <input value={updateText} onChange={e => setUpdateText(e.target.value)} placeholder="Post a project update..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={submitting || !updateText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Post</button>
          </form>
          {updates.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No updates yet.</div>
          ) : (
            <div className="space-y-3">
              {updates.map(upd => (
                <div key={upd.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{upd.userName.charAt(0)}</div>
                    <span className="font-medium text-sm text-gray-900">{upd.userName}</span>
                    {upd.updateType === 'status_change' && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">Status Change</span>}
                    {upd.updateType === 'progress' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">Progress</span>}
                    <span className="text-xs text-gray-400 ml-auto">{formatDateTime(upd.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{upd.content}</p>
                  {upd.oldValue && upd.newValue && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 rounded">{upd.oldValue}</span> → <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">{upd.newValue}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
          <form onSubmit={handleAddComment} className="mb-6 flex gap-3">
            <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Write a comment..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={submitting || !commentText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Comment</button>
          </form>
          {comments.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No comments yet. Be the first to comment!</div>
          ) : (
            <div className="space-y-3">
              {comments.map(comment => (
                <div key={comment.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold">{comment.userName.charAt(0)}</div>
                    <span className="font-medium text-sm text-gray-900">{comment.userName}</span>
                    <span className="text-xs text-gray-400 ml-auto">{formatDateTime(comment.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Milestones Tab — Enhanced with Milestone Engine */}
      {activeTab === 'milestones' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Milestones</h2>
            <div className="flex gap-2">
              <button onClick={async () => { const r = await ModulesModel.calculateCriticalPath(project.id); if (r.success) onRefresh(); }}
                className="px-3 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 font-medium">Calculate Critical Path</button>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">+ Add Milestone</button>}
            </div>
          </div>
          {milestones.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <p className="text-gray-400 mb-3">No milestones defined for this project.</p>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Create first milestone</button>}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <div className="space-y-3">
                {milestones.map((ms, i) => {
                  const icon = getMilestoneIcon(ms.status);
                  const hs = getHealthBadgeStyle(ms.health_status || (ms.status === 'completed' ? 'completed' : 'on-track'));
                  const rf = formatRiskScore(ms.risk_score || 0);
                  return (
                    <div key={ms.id} className="flex gap-4 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${ms.is_critical_path ? 'ring-2 ring-purple-400' : ''}`} style={{ background: icon.bg, color: icon.color }}>{icon.symbol}</div>
                        {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="font-semibold text-gray-900">{ms.title}</span>
                              {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">CRITICAL</span>}
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                            </div>
                            {ms.description && <p className="text-sm text-gray-600 mb-1">{ms.description}</p>}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{ms.status === 'completed' ? `Completed ${formatDate(ms.completedAt)}` : `Due ${formatDate(ms.dueDate)}`}</span>
                              {ms.status !== 'completed' && ms.risk_score > 0 && <span className={`font-semibold ${rf.color}`}>Risk: {ms.risk_score}</span>}
                              {ms.completion_percentage > 0 && ms.status !== 'completed' && <span>{ms.completion_percentage}% done</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            {ms.status !== 'completed' && onCompleteMilestone && (
                              <button onClick={() => onCompleteMilestone(ms.milestone_id || ms.id)} className="text-green-600 hover:text-green-800 text-xs font-medium px-2 py-1">Complete</button>
                            )}
                            {onEditMilestone && <button onClick={() => onEditMilestone(ms.milestone_id || ms.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1">Edit</button>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Charter Tab */}
      {activeTab === 'charter' && (
        <ProjectCharterTab projectId={project.id} projectName={project.name} user={user} />
      )}

      {/* Stakeholders Tab */}
      {activeTab === 'stakeholders' && (
        <StakeholderRegisterTab projectId={project.id} user={user} />
      )}

      {/* Risks Tab */}
      {activeTab === 'risks' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={18} className="text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">Risk Register</h2>
          </div>
          <ProjectRisksTab projectId={project.id} user={user} />
        </div>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Artifacts</h2>
          </div>
          <ProjectAttachmentManager projectId={project.id} user={user} />
        </div>
      )}
    </div>
  );
};

// ---- PlanTrack List View ----

const PostUpdateReportModal = ({ onClose }) => {
  const [preparedBy, setPreparedBy] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    if (!preparedBy.trim()) { setError('Enter your name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.postProjectUpdateReport(preparedBy.trim(), role.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send report');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📋 Post Project Update Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-medium text-gray-800">Report sent to Pipedream!</p>
            <p className="text-sm text-gray-500 mt-1">{result.projectCount} active project(s) included</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">Sends a consolidated status report covering all active projects with their latest updates.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prepared By</label>
            <input type="text" value={preparedBy} onChange={e => setPreparedBy(e.target.value)}
              placeholder="e.g. Gian Halili"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" value={role} onChange={e => setRole(e.target.value)}
              placeholder="e.g. IT Project Manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PlanTrackView = ({ data, onSelectProject, onShowAddProject, onShowSprints, onShowMilestones, onShowPostUpdateReport }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 PlanTrack</h1>
          <p className="text-gray-600 mt-1">Project planning, sprint management, and milestone tracking.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onShowPostUpdateReport} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📋 Post Update</button>
          <button onClick={onShowAddProject} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Project</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Projects', value: data.stats.total, color: '#667eea' },
          { label: 'Active', value: data.stats.active, color: '#0366d6' },
          { label: 'Completed', value: data.stats.completed, color: '#28a745' },
          { label: 'On Hold', value: data.stats.onHold, color: '#e36209' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-5">
            <div className="text-sm text-gray-600 mb-1">{stat.label}</div>
            <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Projects */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Projects</h2>
          <div className="space-y-4">
            {data.projects.map(project => {
              const ss = getStatusStyle(project.status);
              const ps = getPriorityStyle(project.priority);
              const bp = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;
              return (
                <div key={project.id} onClick={() => onSelectProject(project.id)} className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold text-gray-500">{project.id}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ps.bg, color: ps.color }}>{project.priority}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ss.bg, color: ss.color }}>{ss.label}</span>
                    {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 mb-3">
                    <span>👤 {project.owner}</span>
                    <span>📅 {project.startDate} → {project.targetEndDate}</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Progress</span><span>{project.progress}%</span></div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Budget: {project.spentHours}h / {project.budgetHours}h ({bp}%)</span>
                    <span className={bp > 90 ? 'text-red-600 font-semibold' : ''}>{project.budgetHours - project.spentHours}h remaining</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Active Sprint */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Sprint</h2>
              <button onClick={onShowSprints} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Sprints</button>
            </div>
            {data.sprint ? (() => {
              const progress = data.sprint.capacityHours > 0 ? Math.round((data.sprint.completedHours / data.sprint.capacityHours) * 100) : 0;
              return (
                <div className="bg-white rounded-lg shadow border border-blue-400 ring-1 ring-blue-100 p-5">
                  <div className="font-semibold text-gray-900 text-lg mb-1">{data.sprint.name}</div>
                  {data.sprint.goal && <p className="text-sm text-gray-600 mb-3">{data.sprint.goal}</p>}
                  <div className="text-xs text-gray-500 mb-3">{data.sprint.startDate} → {data.sprint.endDate}</div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Completed</span><span>{data.sprint.completedHours}h / {data.sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${progress}%` }} /></div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Committed</span><span>{data.sprint.committedHours}h / {data.sprint.capacityHours}h</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 rounded-full" style={{ width: `${Math.min(Math.round((data.sprint.committedHours / data.sprint.capacityHours) * 100), 100)}%` }} /></div>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6 text-center">
                <p className="text-gray-500 text-sm mb-3">No active sprint</p>
                <button onClick={onShowSprints} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Manage Sprints</button>
              </div>
            )}
          </div>

          {/* Milestones Timeline */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Milestones</h2>
              <button onClick={onShowMilestones} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Milestones</button>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <div className="space-y-0">
                {data.milestones.map((ms, i) => {
                  const icon = getMilestoneIcon(ms.status);
                  const hs = getHealthBadgeStyle(ms.health_status || (ms.status === 'completed' ? 'completed' : 'on-track'));
                  return (
                    <div key={ms.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${ms.is_critical_path ? 'ring-2 ring-purple-400' : ''}`} style={{ background: icon.bg, color: icon.color }}>{icon.symbol}</div>
                        {i < data.milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
                      </div>
                      <div className="pb-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{ms.title}</span>
                          {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">CP</span>}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                        </div>
                        <div className="text-xs text-gray-500">{ms.projectName}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{ms.status === 'completed' ? `Completed ${ms.completedAt}` : `Due ${ms.dueDate}`}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Sprints Management View ----

const SprintsManagementView = ({ sprints, onBack, onCreateSprint, onUpdateSprint }) => {
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async (formData) => {
    await onCreateSprint(formData);
    setShowCreate(false);
  };

  const handleStatusChange = async (sprintId, newStatus) => {
    await onUpdateSprint(sprintId, { status: newStatus });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sprint Management</h1>
          <p className="text-gray-600 mt-1">Create, manage, and track all sprints.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Sprint</button>
      </div>

      {(sprints || []).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No sprints created yet.</p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Create First Sprint</button>
        </div>
      ) : (
        <div className="space-y-4">
          {(sprints || []).map(sprint => {
            const progress = sprint.capacityHours > 0 ? Math.round((sprint.completedHours / sprint.capacityHours) * 100) : 0;
            const isActive = sprint.status === 'active';
            return (
              <div key={sprint.id} className={`bg-white rounded-lg shadow border p-5 ${isActive ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{sprint.name}</h3>
                      <span className="text-xs font-semibold text-gray-500">#{sprint.number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isActive ? 'bg-green-100 text-green-700' : sprint.status === 'completed' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sprint.status}
                      </span>
                    </div>
                    {sprint.goal && <p className="text-sm text-gray-600 mb-2">{sprint.goal}</p>}
                    <div className="text-sm text-gray-500 mb-3">{sprint.startDate} → {sprint.endDate}</div>
                    <div className="max-w-md">
                      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{sprint.completedHours}h / {sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: isActive ? '#22c55e' : sprint.status === 'completed' ? '#9ca3af' : '#eab308' }} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <select
                      value={sprint.status}
                      onChange={(e) => handleStatusChange(sprint.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="planned">Planned</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <AddSprintModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} />}
    </div>
  );
};

// ============================================================================
// TASK MANAGEMENT VIEW (Manager/Admin cross-module view)
// ============================================================================

const SOURCE_META = {
  sprint:     { label: 'Sprint Board', bg: '#dbeafe', color: '#1e40af' },
  quicktrack: { label: 'QuickTrack',   bg: '#ede9fe', color: '#5b21b6' },
};

const SPRINT_STATUSES  = ['backlog', 'ready', 'in_progress', 'review', 'completed', 'blocked', 'cancelled'];
const QUICK_STATUSES   = ['open', 'in_progress', 'done', 'blocked'];
const ALL_STATUSES     = [...new Set([...SPRINT_STATUSES, ...QUICK_STATUSES])];
const PRIORITIES       = ['P1', 'P2', 'P3', 'P4', 'high', 'medium', 'low'];

const MultiSelect = ({ options, values = [], onChange, placeholder, labelKey = 'label', valueKey = 'value' }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val];
    onChange(next);
  };

  const label = values.length === 0
    ? placeholder
    : values.length === 1
      ? (options.find(o => (o[valueKey] ?? o) === values[0])?.[labelKey] ?? values[0])
      : `${values.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none min-w-[130px] justify-between ${values.length > 0 ? 'border-blue-400 text-blue-700 font-medium' : 'border-gray-300 text-gray-600'}`}>
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-full max-h-56 overflow-y-auto">
          {options.map(opt => {
            const val = opt[valueKey] ?? opt;
            const lbl = opt[labelKey] ?? opt;
            const checked = values.includes(val);
            return (
              <label key={val} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                <input type="checkbox" checked={checked} onChange={() => toggle(val)} className="rounded accent-blue-600" />
                <span className={`capitalize ${checked ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{String(lbl).replace(/_/g, ' ')}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaskManagementView = ({ data, filters, onFilterChange, onBulkUpdate, onRefresh }) => {
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkStatus, setBulkStatus]   = useState('');
  const [applying, setApplying]       = useState(false);

  const tasks     = data?.tasks     || [];
  const summary   = data?.summary   || {};
  const assignees = data?.assignees || [];
  const projects  = data?.projects  || [];

  const taskKey = (t) => `${t.source}:${t.id}`;

  const toggleOne = (t) => setSelected(prev => {
    const s = new Set(prev);
    s.has(taskKey(t)) ? s.delete(taskKey(t)) : s.add(taskKey(t));
    return s;
  });
  const toggleAll = () => setSelected(
    selected.size === tasks.length ? new Set() : new Set(tasks.map(taskKey))
  );

  const handleApplyBulk = async () => {
    if (!bulkAssignee && !bulkStatus) return;
    const selectedTasks = [...selected].map(k => {
      const idx = k.indexOf(':');
      return { source: k.slice(0, idx), id: k.slice(idx + 1) };
    });
    const updates = {};
    if (bulkAssignee) updates.assignee = bulkAssignee === '__unassigned__' ? '' : bulkAssignee;
    if (bulkStatus)   updates.status   = bulkStatus;
    setApplying(true);
    await onBulkUpdate(selectedTasks, updates);
    setSelected(new Set()); setBulkAssignee(''); setBulkStatus('');
    setApplying(false);
  };

  const isOverdue = (t) => t.due_date && t.due_date < new Date().toISOString().slice(0, 10)
    && !['completed', 'done', 'cancelled'].includes(t.status);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
        <p className="text-gray-600 mt-1">Unified view across Sprint Board and QuickTrack — for managers and admins.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total Tasks',   value: summary.total       || 0, color: 'blue'   },
          { label: 'Active',        value: summary.active      || 0, color: 'green',  sub: 'In progress' },
          { label: 'Overdue',       value: summary.overdue     || 0, color: 'red',    sub: 'Past due date' },
          { label: 'Unassigned',    value: summary.unassigned  || 0, color: 'orange', sub: 'No assignee' },
          { label: 'Sprint Board',  value: summary.sprintTasks || 0, color: 'blue',   sub: 'DevOps / PlanTrack' },
          { label: 'QuickTrack',    value: summary.quickTasks  || 0, color: 'purple', sub: 'Ad-hoc tasks' },
        ].map(c => <ReportCard key={c.label} {...c} />)}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Source toggle */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Source</div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-wrap">
              {[['', 'All'], ['sprint', 'Sprint'], ['quicktrack', 'QuickTrack']].map(([v, l]) => (
                <button key={v} onClick={() => onFilterChange({ source: v })}
                  className={`px-3 py-1.5 font-medium ${filters.source === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Search */}
          <div className="flex-1 min-w-[160px]">
            <div className="text-xs font-medium text-gray-500 mb-1">Search</div>
            <input value={filters.search || ''} onChange={e => onFilterChange({ search: e.target.value })}
              placeholder="Search tasks…"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          {/* Status */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Status</div>
            <MultiSelect
              options={ALL_STATUSES}
              values={filters.status || []}
              onChange={v => onFilterChange({ status: v })}
              placeholder="All Statuses"
            />
          </div>
          {/* Priority */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Priority</div>
            <MultiSelect
              options={PRIORITIES}
              values={filters.priority || []}
              onChange={v => onFilterChange({ priority: v })}
              placeholder="All Priorities"
            />
          </div>
          {/* Assignee */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Assignee</div>
            <MultiSelect
              options={assignees}
              values={filters.assignee || []}
              onChange={v => onFilterChange({ assignee: v })}
              placeholder="All Assignees"
            />
          </div>
          {/* Project */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Project</div>
            <MultiSelect
              options={projects.map(p => ({ label: p.name, value: p.project_id }))}
              values={filters.project || []}
              onChange={v => onFilterChange({ project: v })}
              placeholder="All Projects"
              labelKey="label"
              valueKey="value"
            />
          </div>
          {/* Clear */}
          <button onClick={() => onFilterChange({ source: '', status: [], priority: [], assignee: [], project: [], search: '' })}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Clear
          </button>
          <button onClick={onRefresh} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm font-semibold text-blue-800">{selected.size} selected</span>
          <div className="flex-1" />
          <div className="flex items-center gap-2 flex-wrap">
            <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Reassign to…</option>
              <option value="__unassigned__">— Unassigned —</option>
              {assignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Change status…</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <button onClick={handleApplyBulk} disabled={applying || (!bulkAssignee && !bulkStatus)}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
              {applying ? 'Applying…' : 'Apply'}
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg bg-white">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Task Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={tasks.length > 0 && selected.size === tasks.length}
                  onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[240px]">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Due Date</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Est. Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map(t => {
              const key  = taskKey(t);
              const src  = SOURCE_META[t.source] || SOURCE_META.sprint;
              const over = isOverdue(t);
              return (
                <tr key={key} className={`hover:bg-gray-50 ${selected.has(key) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggleOne(t)} className="rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
                      style={{ background: src.bg, color: src.color }}>{src.label}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{t.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 max-w-xs truncate">{t.title}</div>
                    {t.sprint_name && <div className="text-xs text-gray-400">{t.sprint_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">{t.type?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.status} map={statusColors} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.priority} map={statusColors} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {t.assignee_name
                      ? <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{t.assignee_name.charAt(0)}</div>
                          <span className="truncate max-w-[100px]">{t.assignee_name}</span>
                        </div>
                      : <span className="text-orange-500 text-xs font-medium">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[120px]">
                    {t.project_name || t.project_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {t.due_date
                      ? <span className={over ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                          {over ? '⚠ ' : ''}{t.due_date}
                        </span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {t.estimate_hours > 0 ? `${t.estimate_hours}h` : '—'}
                  </td>
                </tr>
              );
            })}
            {tasks.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                No tasks match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
        {tasks.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            {selected.size > 0 && ` · ${selected.size} selected`}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// REPORTS VIEW
// ============================================================================

const downloadCSV = (filename, headers, rows) => {
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const ReportCard = ({ label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    pink: 'bg-pink-50 border-pink-200 text-pink-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  const cls = colors[color] || colors.blue;
  return (
    <div className={`rounded-lg border p-5 ${cls.split(' ').slice(0,2).join(' ')}`}>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${cls.split(' ')[2]}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
};

const StatusBadge = ({ value, map }) => {
  const cfg = map[value] || { bg: '#e5e7eb', color: '#374151' };
  return <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: cfg.bg, color: cfg.color }}>{value}</span>;
};

const statusColors = {
  active: { bg: '#dcffe4', color: '#166534' }, completed: { bg: '#dbeafe', color: '#1e40af' },
  'on-hold': { bg: '#fef9c3', color: '#854d0e' }, cancelled: { bg: '#fee2e2', color: '#991b1b' },
  planning: { bg: '#ede9fe', color: '#5b21b6' }, backlog: { bg: '#f3f4f6', color: '#374151' },
  in_progress: { bg: '#dbeafe', color: '#1e40af' }, review: { bg: '#fef9c3', color: '#854d0e' },
  blocked: { bg: '#fee2e2', color: '#991b1b' }, open: { bg: '#fee2e2', color: '#991b1b' },
  approved: { bg: '#dcffe4', color: '#166534' }, pending: { bg: '#fef9c3', color: '#854d0e' },
  rejected: { bg: '#fee2e2', color: '#991b1b' }, draft: { bg: '#f3f4f6', color: '#374151' },
  submitted: { bg: '#ede9fe', color: '#5b21b6' }, 'in-progress': { bg: '#dbeafe', color: '#1e40af' },
  pass: { bg: '#dcffe4', color: '#166534' }, fail: { bg: '#fee2e2', color: '#991b1b' },
  resolved: { bg: '#dcffe4', color: '#166534' }, closed: { bg: '#f3f4f6', color: '#374151' },
  critical: { bg: '#fee2e2', color: '#991b1b' }, high: { bg: '#fed7aa', color: '#9a3412' },
  medium: { bg: '#fef9c3', color: '#854d0e' }, low: { bg: '#dcffe4', color: '#166534' },
  P1: { bg: '#fee2e2', color: '#991b1b' }, P2: { bg: '#fed7aa', color: '#9a3412' },
  P3: { bg: '#fef9c3', color: '#854d0e' }, P4: { bg: '#dcffe4', color: '#166534' },
};

const ExportBtn = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
    Export CSV
  </button>
);

const ReportsView = ({ data }) => {
  const [tab, setTab] = useState('overview');
  const d = data || {};
  const s = d.summary || {};

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'projects',   label: 'Projects' },
    { id: 'sprints',    label: 'Sprints & Velocity' },
    { id: 'tasks',      label: 'Tasks' },
    { id: 'time',       label: 'Time & Leave' },
    { id: 'quality',    label: 'Quality' },
  ];

  // ---- CSV exports ----
  const exportProjects = () => {
    downloadCSV('projects_report.csv',
      ['Project ID','Name','Status','Priority','Health','Progress %','Budget Hours','Spent Hours','Tasks','Completed Tasks','Estimated Hours','Milestones','Start Date','End Date'],
      (d.projects || []).map(p => [p.project_id, p.name, p.status, p.priority, p.health_status, p.progress, p.budget_hours, p.spent_hours, p.task_count, p.completed_tasks, p.estimated_hours, p.milestone_count, p.start_date, p.end_date])
    );
  };
  const exportSprints = () => {
    downloadCSV('sprints_report.csv',
      ['Sprint','Name','Status','Start Date','End Date','Points Planned','Points Completed','Velocity','Capacity Hours','Committed Hours','Completed Hours'],
      (d.sprints || []).map(sp => [sp.sprint_number, sp.name, sp.status, sp.start_date, sp.end_date, sp.story_points_planned, sp.story_points_completed, sp.velocity, sp.capacity_hours, sp.committed_hours, sp.completed_hours])
    );
  };
  const exportTasks = () => {
    downloadCSV('tasks_by_assignee.csv',
      ['Assignee','Total Tasks','Completed','Estimated Hours','Actual Hours'],
      (d.tasks?.byAssignee || []).map(t => [t.assignee, t.total, t.completed, t.estimated_hours, t.actual_hours])
    );
  };
  const exportTime = () => {
    downloadCSV('timesheet_report.csv',
      ['User','Total Hours','Billable Hours','Leave Hours','Weeks Logged'],
      (d.timesheets?.byUser || []).map(t => [t.user_name, t.total_hours, t.billable_hours, t.leave_hours, t.weeks_logged])
    );
  };
  const exportLeave = () => {
    downloadCSV('leave_report.csv',
      ['User','Approved Days','Pending Days','Rejected Days'],
      (d.leaves?.byUser || []).map(l => [l.user_name, l.approved_days, l.pending_days, l.rejected_days])
    );
  };
  const exportQuality = () => {
    downloadCSV('bugs_report.csv',
      ['Bug ID','Title','Severity','Status','Environment','Assignee','Created'],
      (d.quality?.recentBugs || []).map(b => [b.bug_id, b.title, b.severity, b.status, b.environment, b.assignee, b.created_at])
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600 mt-1">Generate, view, and export comprehensive system reports.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <ReportCard label="Total Projects"    value={s.totalProjects}    color="blue"   sub="All projects" />
            <ReportCard label="Active Sprints"    value={s.activeSprints}    color="green"  sub="Currently running" />
            <ReportCard label="Open Bugs"         value={s.openBugs}         color="red"    sub="Requiring attention" />
            <ReportCard label="Pending Leave"     value={s.pendingLeave}     color="orange" sub="Awaiting approval" />
            <ReportCard label="Hours This Month"  value={`${s.hoursThisMonth}h`} color="purple" sub="Last 30 days" />
            <ReportCard label="Active Users"      value={s.activeUsers}      color="gray"   sub="In the system" />
            <ReportCard label="Open Quick Tasks"  value={s.openQuickTasks}   color="pink"   sub="Unresolved" />
          </div>

          {/* Task status + Quick tasks side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <div className="space-y-2">
                {(d.tasks?.byStatus || []).map(row => {
                  const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.status} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-right capitalize text-gray-600">{row.status?.replace('_', ' ')}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.status]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <div className="space-y-2">
                {(d.quality?.bugsBySeverity || []).map(row => {
                  const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.severity} className="flex items-center gap-3">
                      <div className="w-16 text-xs text-right capitalize text-gray-600">{row.severity}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.severity]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Weekly hours trend */}
          {(d.timesheets?.weekly || []).length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Weekly Hours (Last 4 Weeks)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="pb-2 text-left text-xs font-semibold text-gray-600">Week Starting</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Total Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable %</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.timesheets.weekly.map(w => {
                      const pct = w.total_hours > 0 ? Math.round((w.billable_hours / w.total_hours) * 100) : 0;
                      return (
                        <tr key={w.week_start_date} className="hover:bg-gray-50">
                          <td className="py-2 text-gray-800">{w.week_start_date}</td>
                          <td className="py-2 text-right font-semibold text-blue-700">{w.total_hours}h</td>
                          <td className="py-2 text-right text-green-700">{w.billable_hours}h</td>
                          <td className="py-2 text-right text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PROJECTS ── */}
      {tab === 'projects' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Status Report</h2>
            <ExportBtn onClick={exportProjects} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Project','Status','Priority','Health','Progress','Budget','Spent','Tasks','Milestones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.projects || []).map(p => (
                  <tr key={p.project_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-400">{p.project_id}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge value={p.status} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.priority} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.health_status || 'on-track'} map={statusColors} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${p.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{p.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.budget_hours || 0}h</td>
                    <td className="px-4 py-3">
                      <span className={p.spent_hours > p.budget_hours && p.budget_hours > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}>{p.spent_hours || 0}h</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.completed_tasks || 0}/{p.task_count || 0}</td>
                    <td className="px-4 py-3 text-gray-700">{p.milestone_count || 0}</td>
                  </tr>
                ))}
                {(!d.projects || d.projects.length === 0) && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No project data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SPRINTS ── */}
      {tab === 'sprints' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sprint Velocity Report</h2>
            <ExportBtn onClick={exportSprints} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Sprint','Status','Dates','Story Points','Velocity','Capacity','Committed','Completed'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.sprints || []).map(sp => {
                  const completion = sp.story_points_planned > 0 ? Math.round((sp.story_points_completed / sp.story_points_planned) * 100) : 0;
                  return (
                    <tr key={sp.sprint_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">Sprint {sp.sprint_number}</div>
                        <div className="text-xs text-gray-400">{sp.name}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge value={sp.status} map={statusColors} /></td>
                      <td className="px-4 py-3 text-xs text-gray-600">{sp.start_date} → {sp.end_date}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-gray-900">{sp.story_points_completed}/{sp.story_points_planned}</div>
                        <div className="text-xs text-gray-500">{completion}% done</div>
                      </td>
                      <td className="px-4 py-3 text-blue-700 font-bold">{sp.velocity || 0}</td>
                      <td className="px-4 py-3 text-gray-700">{sp.capacity_hours}h</td>
                      <td className="px-4 py-3 text-gray-700">{sp.committed_hours}h</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{sp.completed_hours}h</td>
                    </tr>
                  );
                })}
                {(!d.sprints || d.sprints.length === 0) && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No sprint data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TASKS ── */}
      {tab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Task Analysis Report</h2>
            <ExportBtn onClick={exportTasks} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* By Status */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byStatus || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* By Priority */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Priority</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Priority</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byPriority || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byPriority || []).map(row => (
                      <tr key={row.priority} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.priority} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          {/* By Assignee */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Tasks by Assignee</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Assignee','Total','Completed','Completion %','Estimated Hours','Actual Hours'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.tasks?.byAssignee || []).map(row => {
                  const pct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
                  return (
                    <tr key={row.assignee} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{(row.assignee || '?').charAt(0)}</div>
                          <span className="font-medium text-gray-900">{row.assignee}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{row.total}</td>
                      <td className="px-4 py-3 text-green-700">{row.completed}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }} />
                          </div>
                          <span className="text-xs text-gray-600">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-purple-700">{row.estimated_hours || 0}h</td>
                      <td className="px-4 py-3 text-blue-700">{row.actual_hours || 0}h</td>
                    </tr>
                  );
                })}
                {(!d.tasks?.byAssignee || d.tasks.byAssignee.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No task data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TIME & LEAVE ── */}
      {tab === 'time' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Timesheet by User */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Hours by Team Member (Last 4 Weeks)</h2>
                <ExportBtn onClick={exportTime} />
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Member','Total','Billable','Leave','Weeks'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byUser || []).map(row => (
                      <tr key={row.user_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                        <td className="px-4 py-3 font-semibold text-blue-700">{row.total_hours}h</td>
                        <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                        <td className="px-4 py-3 text-pink-600">{row.leave_hours > 0 ? `${row.leave_hours}h` : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.weeks_logged}w</td>
                      </tr>
                    ))}
                    {(!d.timesheets?.byUser || d.timesheets.byUser.length === 0) && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No timesheet data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Hours by Project */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Hours by Project</h2>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Project','Hours','Billable','Billable %'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byProject || []).map(row => {
                      const pct = row.hours > 0 ? Math.round((row.billable_hours / row.hours) * 100) : 0;
                      return (
                        <tr key={row.project_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.project_id}</td>
                          <td className="px-4 py-3 font-semibold text-blue-700">{row.hours}h</td>
                          <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                          <td className="px-4 py-3 text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                    {(!d.timesheets?.byProject || d.timesheets.byProject.length === 0) && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No project hours data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Leave Report */}
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Leave Report</h2>
            <ExportBtn onClick={exportLeave} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leave by User */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">By Team Member</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Approved Days','Pending','Rejected'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.byUser || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{row.approved_days}d</td>
                      <td className="px-4 py-3 text-yellow-700">{row.pending_days > 0 ? `${row.pending_days}d` : '—'}</td>
                      <td className="px-4 py-3 text-red-600">{row.rejected_days > 0 ? `${row.rejected_days}d` : '—'}</td>
                    </tr>
                  ))}
                  {(!d.leaves?.byUser || d.leaves.byUser.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No leave data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Leave Balances */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">Leave Balances</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Vacation','Sick','Personal'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.balances || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-blue-700">{row.vacation_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.vacation_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-red-600">{row.sick_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.sick_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-purple-700">{row.personal_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.personal_total}</span>
                      </td>
                    </tr>
                  ))}
                  {(!d.leaves?.balances || d.leaves.balances.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No balance data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── QUALITY ── */}
      {tab === 'quality' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quality & Bug Report</h2>
            <ExportBtn onClick={exportQuality} />
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {(() => {
              const bugs = d.quality?.bugsBySeverity || [];
              const totalBugs = bugs.reduce((s, r) => s + r.count, 0);
              const critical = bugs.find(r => r.severity === 'critical')?.count || 0;
              const high = bugs.find(r => r.severity === 'high')?.count || 0;
              const tc = d.quality?.testCases || [];
              const totalTC = tc.reduce((s, r) => s + r.count, 0);
              const passed = tc.find(r => r.status === 'pass' || r.status === 'passed')?.count || 0;
              const coverage = totalTC > 0 ? Math.round((passed / totalTC) * 100) : 0;
              return [
                <ReportCard key="total" label="Total Bugs"    value={totalBugs} color="red"    sub="All bugs" />,
                <ReportCard key="crit"  label="Critical Bugs" value={critical}  color="red"    sub="Needs immediate action" />,
                <ReportCard key="high"  label="High Severity" value={high}      color="orange" sub="High priority" />,
                <ReportCard key="cov"   label="Test Pass Rate" value={`${coverage}%`} color="green" sub={`${passed}/${totalTC} test cases`} />,
              ];
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Bugs by severity */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Severity</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.bugsBySeverity || []).map(row => (
                      <tr key={row.severity} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.severity} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* Test Cases */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Test Case Results</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.testCases || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.testCases || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Bugs table */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Bug List (by Severity)</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Bug ID','Title','Severity','Status','Environment','Assignee','Reported'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.quality?.recentBugs || []).map(b => (
                  <tr key={b.bug_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{b.bug_id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{b.title}</td>
                    <td className="px-4 py-3"><StatusBadge value={b.severity} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={b.status} map={statusColors} /></td>
                    <td className="px-4 py-3 text-gray-600">{b.environment || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.assignee || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{b.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {(!d.quality?.recentBugs || d.quality.recentBugs.length === 0) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No bug data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- TimeBoard Components ----

const getWeekDates = (weekStart) => {
  const dates = [];
  const start = new Date(weekStart + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

// ============================================================
// KNOWLEDGEBOARD COMPONENTS
// ============================================================

// Simple markdown renderer (converts common markdown to HTML)
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // fenced code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) =>
      `<pre class="bg-gray-900 text-green-300 rounded-lg p-4 overflow-x-auto text-sm my-3"><code>${code.replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>')}</code></pre>`)
    // inline code
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
    // headings
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-bold text-gray-900 mt-5 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 mt-6 mb-3">$1</h1>')
    // bold/italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    // links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-indigo-600 hover:underline">$1</a>')
    // horizontal rule
    .replace(/^---$/gm, '<hr class="border-gray-200 my-4" />')
    // blockquote
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-indigo-300 pl-4 text-gray-600 italic my-2">$1</blockquote>')
    // unordered list items
    .replace(/^[\*\-] (.+)$/gm, '<li class="ml-4 list-disc text-gray-700">$1</li>')
    // ordered list items
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-gray-700">$1</li>')
    // wrap consecutive li elements in ul/ol (simple approach)
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, m => `<ul class="my-2 space-y-1">${m}</ul>`)
    // paragraphs (blank-line separated)
    .replace(/\n\n+/g, '</p><p class="mb-3 text-gray-700 leading-relaxed">')
    .replace(/\n/g, '<br />');
  return `<p class="mb-3 text-gray-700 leading-relaxed">${html}</p>`;
}

// ============================================================
// INCIDENTBOARD COMPONENTS
// ============================================================

const INCIDENT_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const INCIDENT_STATUSES = ['Open', 'Investigating', 'Mitigating', 'Monitoring', 'Resolved', 'Closed'];
const INCIDENT_TEAMS = ['DevOps', 'DataOps', 'Engineering', 'QA'];

const incSeverityColor = (s) => ({ Critical: 'bg-red-100 text-red-700', High: 'bg-orange-100 text-orange-700', Medium: 'bg-yellow-100 text-yellow-700', Low: 'bg-gray-100 text-gray-600' }[s] || 'bg-gray-100 text-gray-600');
const incStatusColor = (s) => ({ Open: 'bg-red-100 text-red-700', Investigating: 'bg-orange-100 text-orange-700', Mitigating: 'bg-yellow-100 text-yellow-700', Monitoring: 'bg-blue-100 text-blue-700', Resolved: 'bg-green-100 text-green-700', Closed: 'bg-gray-100 text-gray-500' }[s] || 'bg-gray-100 text-gray-600');
const incStatusDot = (s) => ({ Open: 'bg-red-500', Investigating: 'bg-orange-500', Mitigating: 'bg-yellow-500', Monitoring: 'bg-blue-500', Resolved: 'bg-green-500', Closed: 'bg-gray-400' }[s] || 'bg-gray-400');

const IncidentCreateModal = ({ onSave, onClose, allUsers = [], user }) => {
  const [form, setForm] = React.useState({ title: '', description: '', severity: 'Medium', status: 'Open', service_affected: '', team: 'Engineering', assigned_to: '', assigned_to_id: '', encountered_by: '', encountered_by_id: '', location: '', ticket_owner: '', ticket_owner_id: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSave(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><span>🚨</span> Report Incident</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} required placeholder="Brief description of the incident"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="What happened? What is the impact?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={form.severity} onChange={e => set('severity', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
              <select value={form.team} onChange={e => set('team', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {INCIDENT_TEAMS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service / System Affected</label>
            <input value={form.service_affected} onChange={e => set('service_affected', e.target.value)} placeholder="e.g. API Gateway, Payment Service, Data Pipeline"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
          </div>
          {/* Who encountered the issue */}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">People</p>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reported By</label>
              <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                {user?.user_name || user?.name || '—'}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Auto-filled from your account</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {allUsers.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Encountered By *</label>
                  <select value={form.encountered_by_id} onChange={e => {
                    const u = allUsers.find(x => String(x.id) === e.target.value);
                    set('encountered_by_id', e.target.value); set('encountered_by', u?.user_name || u?.name || '');
                  }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select user —</option>
                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name || u.name}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Encountered By *</label>
                  <input value={form.encountered_by} onChange={e => set('encountered_by', e.target.value)} placeholder="Person who experienced this"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Manila Office, WFH, Cebu Branch"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {allUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Owner</label>
                  <select value={form.ticket_owner_id} onChange={e => {
                    const u = allUsers.find(x => String(x.id) === e.target.value);
                    set('ticket_owner_id', e.target.value); set('ticket_owner', u?.user_name || u?.name || '');
                  }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Unassigned —</option>
                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name || u.name}</option>)}
                  </select>
                </div>
              )}
              {allUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To (Resolver)</label>
                  <select value={form.assigned_to_id} onChange={e => {
                    const u = allUsers.find(x => String(x.id) === e.target.value);
                    set('assigned_to_id', e.target.value); set('assigned_to', u?.user_name || u?.name || '');
                  }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Unassigned —</option>
                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name || u.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium">Report Incident</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const IncidentDetailView = ({ incident, user, onBack, onUpdateIncident, onAddUpdate, onDeleteIncident }) => {
  const [editMode, setEditMode] = React.useState(false);
  const [form, setForm] = React.useState({ ...incident });
  const [updateMsg, setUpdateMsg] = React.useState('');
  const [submittingUpdate, setSubmittingUpdate] = React.useState(false);
  const isAdmin = (user?.role || user?.user_role) === 'admin';

  const handleSave = async () => {
    await onUpdateIncident(incident.id, form);
    setEditMode(false);
  };

  const handleAddUpdate = async () => {
    if (!updateMsg.trim()) return;
    setSubmittingUpdate(true);
    await onAddUpdate(incident.id, updateMsg.trim());
    setUpdateMsg('');
    setSubmittingUpdate(false);
  };

  const updates = incident.updates || [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"><ArrowLeft size={16} /> Back</button>
        <span className="text-gray-300">|</span>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${incSeverityColor(incident.severity)}`}>{incident.severity}</span>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${incStatusColor(incident.status)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${incStatusDot(incident.status)}`}></span>{incident.status}
        </span>
        <span className="ml-auto text-xs text-gray-400">INC-{incident.id}</span>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main content */}
        <div className="col-span-2 space-y-5">
          {/* Title + description */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            {editMode ? (
              <div className="space-y-3">
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full text-xl font-bold border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500" />
                <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-red-500" placeholder="Description" />
                <div className="flex gap-2">
                  <button onClick={handleSave} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Save</button>
                  <button onClick={() => { setEditMode(false); setForm({ ...incident }); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-4">
                  <h1 className="text-xl font-bold text-gray-900">{incident.title}</h1>
                  <button onClick={() => setEditMode(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-600"><Pencil size={16} /></button>
                </div>
                {incident.description && <p className="mt-3 text-sm text-gray-600 leading-relaxed">{incident.description}</p>}
              </div>
            )}
          </div>

          {/* Status + RCA edit */}
          {editMode && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">Status & Assignment</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    {INCIDENT_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
                  <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Encountered By</label>
                  <input value={form.encountered_by || ''} onChange={e => setForm(f => ({ ...f, encountered_by: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Who experienced this?" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Location</label>
                  <input value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="e.g. Manila Office, WFH" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Ticket Owner</label>
                  <input value={form.ticket_owner || ''} onChange={e => setForm(f => ({ ...f, ticket_owner: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Owner of this ticket" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Assigned To (Resolver)</label>
                  <input value={form.assigned_to || ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Who is resolving this?" />
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 text-sm pt-2">Root Cause Analysis</h3>
              <textarea value={form.root_cause || ''} onChange={e => setForm(f => ({ ...f, root_cause: e.target.value }))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" placeholder="Root cause..." />
              <textarea value={form.resolution_summary || ''} onChange={e => setForm(f => ({ ...f, resolution_summary: e.target.value }))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" placeholder="Resolution summary..." />
              <textarea value={form.preventive_actions || ''} onChange={e => setForm(f => ({ ...f, preventive_actions: e.target.value }))} rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" placeholder="Preventive actions..." />
            </div>
          )}

          {/* RCA section (read-only) */}
          {!editMode && (incident.root_cause || incident.resolution_summary || incident.preventive_actions) && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <h3 className="font-semibold text-gray-800">Root Cause Analysis</h3>
              {incident.root_cause && <div><p className="text-xs font-medium text-gray-500 mb-1">Root Cause</p><p className="text-sm text-gray-700">{incident.root_cause}</p></div>}
              {incident.resolution_summary && <div><p className="text-xs font-medium text-gray-500 mb-1">Resolution Summary</p><p className="text-sm text-gray-700">{incident.resolution_summary}</p></div>}
              {incident.preventive_actions && <div><p className="text-xs font-medium text-gray-500 mb-1">Preventive Actions</p><p className="text-sm text-gray-700">{incident.preventive_actions}</p></div>}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Timeline</h3>
            <div className="space-y-3">
              {updates.length === 0 && <p className="text-sm text-gray-400">No updates yet.</p>}
              {updates.map((u, i) => (
                <div key={u.id || i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0"></div>
                    {i < updates.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1"></div>}
                  </div>
                  <div className="pb-3 flex-1">
                    <p className="text-sm text-gray-800">{u.update_message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{u.created_by && `${u.created_by} · `}{u.created_at ? new Date(u.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Add update input */}
            <div className="mt-4 flex gap-2">
              <input value={updateMsg} onChange={e => setUpdateMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddUpdate()}
                placeholder="Add a timeline update..." className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
              <button onClick={handleAddUpdate} disabled={!updateMsg.trim() || submittingUpdate}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-40">Add</button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-700 text-sm">Details</h3>
            <div><p className="text-xs text-gray-400">Status</p>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5 ${incStatusColor(incident.status)}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${incStatusDot(incident.status)}`}></span>{incident.status}
              </span>
            </div>
            <div><p className="text-xs text-gray-400">Severity</p>
              <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5 ${incSeverityColor(incident.severity)}`}>{incident.severity}</span>
            </div>
            {incident.encountered_by && <div><p className="text-xs text-gray-400">Encountered By</p><p className="text-sm text-gray-700 font-medium">👤 {incident.encountered_by}</p></div>}
            {incident.location && <div><p className="text-xs text-gray-400">Location</p><p className="text-sm text-gray-700">📍 {incident.location}</p></div>}
            {incident.ticket_owner && <div><p className="text-xs text-gray-400">Ticket Owner</p><p className="text-sm text-gray-700">🎫 {incident.ticket_owner}</p></div>}
            {incident.service_affected && <div><p className="text-xs text-gray-400">Affected Service</p><p className="text-sm text-gray-700 font-medium">{incident.service_affected}</p></div>}
            {incident.team && <div><p className="text-xs text-gray-400">Team</p><p className="text-sm text-gray-700">{incident.team}</p></div>}
            {incident.assigned_to && <div><p className="text-xs text-gray-400">Assigned To (Resolver)</p><p className="text-sm text-gray-700">{incident.assigned_to}</p></div>}
            {incident.reported_by && <div><p className="text-xs text-gray-400">Reported By</p><p className="text-sm text-gray-700">{incident.reported_by}</p></div>}
            <div><p className="text-xs text-gray-400">Created</p><p className="text-sm text-gray-700">{incident.created_at ? new Date(incident.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</p></div>
            {incident.resolved_at && <div><p className="text-xs text-gray-400">Resolved</p><p className="text-sm text-green-700 font-medium">{new Date(incident.resolved_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p></div>}
          </div>

          {/* Quick status change */}
          {!editMode && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-2">Update Status</h3>
              <div className="space-y-1">
                {INCIDENT_STATUSES.map(s => (
                  <button key={s} onClick={() => onUpdateIncident(incident.id, { ...incident, status: s })}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 ${incident.status === s ? incStatusColor(s) + ' font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                    <span className={`w-2 h-2 rounded-full ${incStatusDot(s)}`}></span>{s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// REQUESTBOARD MODULE
// ============================================================================

const REQ_TYPES = ['Feature Request','Change Request','IT Support','Access Request','Process Improvement','Data Request','General Request'];
const REQ_STATUSES = ['Submitted','Under Review','Approved','Rejected','In Progress','Completed'];
const REQ_PRIORITIES = ['Low','Medium','High','Critical'];
const CONVERT_TARGETS = [
  { id: 'quicktrack',    label: 'QuickTrack Task' },
  { id: 'quality',       label: 'Quality Bug' },
  { id: 'incidentboard', label: 'Incident' },
];

const reqStatusStyle = {
  'Submitted':    'bg-gray-100 text-gray-600',
  'Under Review': 'bg-blue-100 text-blue-700',
  'Approved':     'bg-green-100 text-green-700',
  'Rejected':     'bg-red-100 text-red-700',
  'In Progress':  'bg-purple-100 text-purple-700',
  'Completed':    'bg-teal-100 text-teal-700',
};
const reqPriorityStyle = {
  'Low':      'bg-gray-100 text-gray-500',
  'Medium':   'bg-blue-50 text-blue-600',
  'High':     'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

function RequestFormModal({ req, categories, user, onSave, onClose }) {
  const isEdit = !!req?.id;
  const [form, setForm] = React.useState({
    title: req?.title || '',
    description: req?.description || '',
    request_type: req?.request_type || 'General Request',
    category_id: req?.category_id || '',
    priority: req?.priority || 'Medium',
    requested_by: req?.requested_by || user?.user_name || user?.name || '',
    department: req?.department || '',
    assigned_to: req?.assigned_to || '',
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400';

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave({ ...form, category_id: form.category_id || null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Request' : 'Submit New Request'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className={inp} placeholder="Brief summary of your request" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={4} className={inp} placeholder="Detailed description, business justification, expected outcome..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Request Type</label>
              <select value={form.request_type} onChange={e => set('request_type', e.target.value)} className={inp}>
                {REQ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)} className={inp}>
                {REQ_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} className={inp}>
                <option value="">Select...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              <input value={form.department} onChange={e => set('department', e.target.value)} className={inp} placeholder="e.g. Engineering" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Requested By</label>
              <input value={form.requested_by} onChange={e => set('requested_by', e.target.value)} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assign To</label>
              <input value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)} className={inp} placeholder="Username (optional)" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title.trim()} className="px-5 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Submitting…' : isEdit ? 'Save Changes' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetailView({ requestId, categories, user, onBack, onRefresh }) {
  const [req, setReq] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [comment, setComment] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [showApprove, setShowApprove] = React.useState(false);
  const [showReject, setShowReject]  = React.useState(false);
  const [showConvert, setShowConvert] = React.useState(false);
  const [actionNotes, setActionNotes] = React.useState('');
  const [convertTarget, setConvertTarget] = React.useState('quicktrack');
  const [acting, setActing] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);

  const role = user?.role || user?.user_role;
  const canManage = ['admin', 'manager', 'devops_manager'].includes(role);
  const canReview = canManage;

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, c] = await Promise.all([ModulesModel.getRequest(requestId), ModulesModel.getRequestComments(requestId)]);
    setReq(r); setComments(c);
    setLoading(false);
  }, [requestId]);

  React.useEffect(() => { load(); }, [load]);

  const handleComment = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    await ModulesModel.addRequestComment(requestId, { comment_text: comment, created_by: user?.user_name || user?.name || 'User' });
    setComment(''); load();
    setPosting(false);
  };

  const handleApprove = async () => {
    setActing(true);
    await ModulesModel.approveRequest(requestId, actionNotes);
    setShowApprove(false); setActionNotes(''); load(); onRefresh();
    setActing(false);
  };

  const handleReject = async () => {
    setActing(true);
    await ModulesModel.rejectRequest(requestId, actionNotes);
    setShowReject(false); setActionNotes(''); load(); onRefresh();
    setActing(false);
  };

  const handleConvert = async () => {
    setActing(true);
    const result = await ModulesModel.convertRequest(requestId, { target_module: convertTarget });
    if (result.success) { setShowConvert(false); load(); onRefresh(); }
    setActing(false);
  };

  const handleEditSave = async (data) => {
    await ModulesModel.updateRequest(requestId, data);
    setShowEdit(false); load(); onRefresh();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!req) return <div className="p-6 text-gray-500">Request not found.</div>;

  const catName = categories.find(c => String(c.id) === String(req.category_id))?.name || req.category_name || '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1">← Back to Requests</button>

      <div className="bg-white rounded-2xl border border-gray-200 shadow p-6 mb-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs text-gray-400 font-mono">#{req.id}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[req.status] || 'bg-gray-100 text-gray-600'}`}>{req.status}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[req.priority] || 'bg-gray-100 text-gray-600'}`}>{req.priority}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{req.request_type}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">{req.title}</h1>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap justify-end">
            {canManage && <button onClick={() => setShowEdit(true)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Edit</button>}
            {canReview && req.status === 'Submitted' && <button onClick={() => setShowApprove(true)} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Approve</button>}
            {canReview && ['Submitted','Under Review'].includes(req.status) && <button onClick={() => setShowReject(true)} className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">Reject</button>}
            {canManage && req.status === 'Approved' && !req.converted_module && <button onClick={() => setShowConvert(true)} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Convert →</button>}
          </div>
        </div>

        {req.description && <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">{req.description}</p>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {[
            ['Requested By', req.requested_by || '—'],
            ['Created By', req.created_by || req.requested_by || '—'],
            ['Department', req.department || '—'],
            ['Category', catName],
            ['Assigned To', req.assigned_to || '—'],
            ['Submitted', req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'],
            ['Last Updated', req.updated_at ? new Date(req.updated_at).toLocaleDateString() : '—'],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-1">{label}</div>
              <div className="font-medium text-gray-800">{val}</div>
            </div>
          ))}
        </div>

        {(req.reviewed_by || req.approval_notes) && (
          <div className={`mt-4 p-3 rounded-lg border text-sm ${req.status === 'Approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="font-medium mb-1">{req.status === 'Approved' ? '✓ Approved' : '✗ Rejected'} by {req.reviewed_by} {req.reviewed_at ? `on ${new Date(req.reviewed_at).toLocaleDateString()}` : ''}</div>
            {req.approval_notes && <div className="text-gray-600">{req.approval_notes}</div>}
          </div>
        )}

        {req.converted_module && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
            <span className="font-medium text-purple-700">↗ Converted to {req.converted_module}</span>
            {req.converted_item_id && <span className="text-purple-500 ml-2">ID: {req.converted_item_id}</span>}
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Comments ({comments.length})</h2>
        <div className="space-y-3 mb-4">
          {comments.length === 0 && <p className="text-sm text-gray-400">No comments yet.</p>}
          {comments.map(c => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">{(c.created_by || '?')[0].toUpperCase()}</div>
              <div className="flex-1 bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">{c.created_by}</span>
                  <span className="text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.comment_text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{(user?.user_name || user?.name || 'U')[0].toUpperCase()}</div>
          <div className="flex-1 flex gap-2">
            <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment…" rows={2} className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleComment(); }} />
            <button onClick={handleComment} disabled={posting || !comment.trim()} className="px-4 self-end py-2 bg-emerald-600 text-white text-sm rounded-xl hover:bg-emerald-700 disabled:opacity-50">Post</button>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApprove && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApprove(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Approve Request</h3>
            <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Approval notes (optional)" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowApprove(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleApprove} disabled={acting} className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">{acting ? 'Approving…' : 'Approve'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowReject(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Reject Request</h3>
            <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Reason for rejection *" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReject(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleReject} disabled={acting} className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50">{acting ? 'Rejecting…' : 'Reject'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowConvert(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-1">Convert to Work Item</h3>
            <p className="text-sm text-gray-500 mb-4">Creates a new item in the target module.</p>
            <div className="space-y-2 mb-4">
              {CONVERT_TARGETS.map(t => (
                <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${convertTarget === t.id ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="target" value={t.id} checked={convertTarget === t.id} onChange={() => setConvertTarget(t.id)} className="accent-purple-600" />
                  <span className="text-sm font-medium">{t.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConvert(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleConvert} disabled={acting} className="px-4 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50">{acting ? 'Converting…' : 'Convert'}</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && <RequestFormModal req={req} categories={categories} user={user} onSave={handleEditSave} onClose={() => setShowEdit(false)} />}
    </div>
  );
}

const RequestBoardModule = ({ user }) => {
  const [requests, setRequests] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, pending: 0, approved: 0, rejected: 0, inprogress: 0, completed: 0, todayCount: 0, byType: [], byDept: [] });
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard');
  const [viewingRequest, setViewingRequest] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterStatus, setFilterStatus]   = React.useState('');
  const [filterType, setFilterType]       = React.useState('');
  const [filterPriority, setFilterPriority] = React.useState('');

  const role = user?.role || user?.user_role;
  const canManage = ['admin', 'manager', 'devops_manager'].includes(role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, s, c] = await Promise.all([
      ModulesModel.getRequests({ search, status: filterStatus, type: filterType, priority: filterPriority }),
      ModulesModel.getRequestStats(),
      ModulesModel.getRequestCategories(),
    ]);
    setRequests(r || []); setStats(s); setCategories(c || []);
    setLoading(false);
  }, [search, filterStatus, filterType, filterPriority]);

  React.useEffect(() => { load(); }, [load]);

  const handleSubmit = async (data) => {
    await ModulesModel.createRequest(data);
    setShowForm(false); load();
  };

  if (viewingRequest) {
    return <RequestDetailView requestId={viewingRequest} categories={categories} user={user} onBack={() => setViewingRequest(null)} onRefresh={load} />;
  }

  const statCards = [
    { label: 'Total',        value: stats.total,      color: 'text-gray-800',   bg: 'bg-gray-50',    border: 'border-gray-200' },
    { label: 'Pending',      value: stats.pending,    color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
    { label: 'Approved',     value: stats.approved,   color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
    { label: 'In Progress',  value: stats.inprogress, color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200' },
    { label: 'Rejected',     value: stats.rejected,   color: 'text-red-600',    bg: 'bg-red-50',     border: 'border-red-200' },
    { label: 'Today',        value: stats.todayCount, color: 'text-emerald-700',bg: 'bg-emerald-50', border: 'border-emerald-200' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">📥 RequestBoard</h1>
          <p className="text-sm text-gray-500 mt-1">Central intake for all internal requests</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{id:'dashboard',label:'Dashboard'},{id:'requests',label:'All Requests'}].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">+ New Request</button>
        </div>
      </div>

      {view === 'dashboard' ? (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
            {statCards.map(s => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* By Type */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Requests by Type</h3>
              <div className="space-y-2">
                {stats.byType.length === 0 ? <p className="text-sm text-gray-400">No data yet.</p> : stats.byType.map(t => (
                  <div key={t.request_type} className="flex items-center gap-2">
                    <div className="flex-1 text-xs text-gray-600 truncate">{t.request_type}</div>
                    <div className="text-xs font-semibold text-gray-800 w-6 text-right">{t.n}</div>
                    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (t.n / (stats.total || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* By Department */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Requests by Department</h3>
              <div className="space-y-2">
                {stats.byDept.length === 0 ? <p className="text-sm text-gray-400">No data yet.</p> : stats.byDept.map(d => (
                  <div key={d.department} className="flex items-center gap-2">
                    <div className="flex-1 text-xs text-gray-600 truncate">{d.department}</div>
                    <div className="text-xs font-semibold text-gray-800 w-6 text-right">{d.n}</div>
                    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(100, (d.n / (stats.total || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Approval */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Awaiting Review</h3>
              <div className="space-y-2">
                {requests.filter(r => r.status === 'Submitted').length === 0
                  ? <p className="text-sm text-gray-400">No pending requests.</p>
                  : requests.filter(r => r.status === 'Submitted').slice(0, 5).map(r => (
                    <button key={r.id} onClick={() => setViewingRequest(r.id)} className="w-full text-left p-2 rounded-lg hover:bg-gray-50 border border-gray-100">
                      <div className="text-xs font-medium text-gray-800 truncate">{r.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{r.requested_by} · <span className={`font-medium ${reqPriorityStyle[r.priority]?.split(' ')[1]}`}>{r.priority}</span></div>
                    </button>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Recent requests */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">Recent Requests</h2>
              <button onClick={() => setView('requests')} className="text-sm text-emerald-600 hover:underline">View all →</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{['#','Title','Type','Priority','Requester','Dept','Status','Date'].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                : requests.slice(0, 10).map(r => (
                  <tr key={r.id} onClick={() => setViewingRequest(r.id)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">#{r.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{r.title}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.request_type}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[r.priority] || 'bg-gray-100 text-gray-600'}`}>{r.priority}</span></td>
                    <td className="px-4 py-3 text-gray-600">{r.requested_by}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.department || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {!loading && requests.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No requests yet. Click "+ New Request" to submit one.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        /* All Requests View */
        <div>
          <div className="flex flex-wrap gap-3 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or description…" className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Statuses</option>
              {REQ_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Types</option>
              {REQ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Priorities</option>
              {REQ_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {(search || filterStatus || filterType || filterPriority) && (
              <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterType(''); setFilterPriority(''); }} className="px-3 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50">Clear</button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{['#','Title','Type','Priority','Requester','Dept','Assigned','Status','Date'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                : requests.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No requests found.</td></tr>
                : requests.map(r => (
                  <tr key={r.id} onClick={() => setViewingRequest(r.id)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">#{r.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate">{r.title}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{r.request_type}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[r.priority] || 'bg-gray-100 text-gray-600'}`}>{r.priority}</span></td>
                    <td className="px-4 py-3 text-gray-600">{r.requested_by}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.department || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.assigned_to || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-400">{requests.length} request{requests.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      )}

      {showForm && <RequestFormModal categories={categories} user={user} onSave={handleSubmit} onClose={() => setShowForm(false)} />}
    </div>
  );
};

// ============================================================================
// ASSETBOARD MODULE
// ============================================================================

const ASSET_STATUSES = ['Available', 'Assigned', 'In Maintenance', 'Retired'];
const assetStatusStyle = {
  'Available':      'bg-green-100 text-green-700',
  'Assigned':       'bg-blue-100 text-blue-700',
  'In Maintenance': 'bg-yellow-100 text-yellow-700',
  'Retired':        'bg-gray-100 text-gray-500',
};

function AssetFormModal({ asset, categories, onSave, onClose }) {
  const isEdit = !!asset?.id;
  const [form, setForm] = React.useState({
    asset_name: asset?.asset_name || '',
    category_id: asset?.category_id || '',
    asset_tag: asset?.asset_tag || '',
    serial_number: asset?.serial_number || '',
    purchase_date: asset?.purchase_date || '',
    warranty_expiration: asset?.warranty_expiration || '',
    status: asset?.status || 'Available',
    current_owner: asset?.current_owner || '',
    location: asset?.location || '',
    notes: asset?.notes || '',
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.asset_name.trim()) return;
    setSaving(true);
    await onSave({ ...form, category_id: form.category_id || null });
    setSaving(false);
  };

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Asset' : 'New Asset'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Asset Name *</label>
            <input value={form.asset_name} onChange={e => set('asset_name', e.target.value)} className={inp} placeholder="e.g. MacBook Pro 16" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select value={form.category_id} onChange={e => set('category_id', e.target.value)} className={inp}>
              <option value="">Select category...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inp}>
              {ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Asset Tag</label>
            <input value={form.asset_tag} onChange={e => set('asset_tag', e.target.value)} className={inp} placeholder="Auto-generated if blank" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Serial Number</label>
            <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Purchase Date</label>
            <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Warranty Expiration</label>
            <input type="date" value={form.warranty_expiration} onChange={e => set('warranty_expiration', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Current Owner</label>
            <input value={form.current_owner} onChange={e => set('current_owner', e.target.value)} className={inp} placeholder="Username" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} className={inp} placeholder="e.g. Office A, Remote" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inp} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.asset_name.trim()} className="px-5 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Create Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ asset, onSave, onClose }) {
  const [assignTo, setAssignTo] = React.useState(asset?.current_owner || '');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const handleSave = async () => {
    if (!assignTo.trim()) return;
    setSaving(true);
    await onSave({ asset_id: asset.id, assigned_to: assignTo, notes });
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Assign Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">Assigning: <strong>{asset?.asset_name}</strong></p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assign To (username) *</label>
            <input value={assignTo} onChange={e => setAssignTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" placeholder="Enter username" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving || !assignTo.trim()} className="px-4 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetDetailView({ assetId, categories, user, onBack, onEdit, onRefresh }) {
  const [asset, setAsset] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAssign, setShowAssign] = React.useState(false);
  const canManage = ['admin', 'manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [a, h] = await Promise.all([ModulesModel.getAsset(assetId), ModulesModel.getAssetHistory(assetId)]);
    setAsset(a); setHistory(h);
    setLoading(false);
  }, [assetId]);

  React.useEffect(() => { load(); }, [load]);

  const handleAssign = async (data) => {
    await ModulesModel.assignAsset(data);
    setShowAssign(false); load(); onRefresh();
  };
  const handleReturn = async () => {
    if (!confirm('Mark this asset as returned?')) return;
    await ModulesModel.returnAsset({ asset_id: assetId });
    load(); onRefresh();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!asset) return <div className="p-6 text-gray-500">Asset not found.</div>;

  const today = new Date().toISOString().split('T')[0];
  const warrantyDays = asset.warranty_expiration ? Math.ceil((new Date(asset.warranty_expiration) - new Date(today)) / 86400000) : null;
  const catName = categories.find(c => c.id === asset.category_id)?.name || asset.category_name || '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">← Back to Assets</button>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{asset.asset_name}</h1>
            <p className="text-sm text-gray-500 mt-1">{asset.asset_tag} · {catName}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${assetStatusStyle[asset.status] || 'bg-gray-100 text-gray-600'}`}>{asset.status}</span>
            {canManage && (
              <>
                <button onClick={() => onEdit(asset)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-sm rounded-lg">Edit</button>
                {asset.status !== 'Assigned' && <button onClick={() => setShowAssign(true)} className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700">Assign</button>}
                {asset.status === 'Assigned' && <button onClick={handleReturn} className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600">Return</button>}
              </>
            )}
          </div>
        </div>

        {warrantyDays !== null && warrantyDays <= 30 && (
          <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${warrantyDays < 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
            ⚠️ {warrantyDays < 0 ? `Warranty expired ${Math.abs(warrantyDays)} days ago` : `Warranty expiring in ${warrantyDays} day${warrantyDays !== 1 ? 's' : ''}`}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          {[
            ['Serial Number', asset.serial_number || '—'],
            ['Current Owner', asset.current_owner || '—'],
            ['Location', asset.location || '—'],
            ['Purchase Date', asset.purchase_date || '—'],
            ['Warranty Expires', asset.warranty_expiration || '—'],
            ['Added', asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">{label}</div>
              <div className="font-medium text-gray-800">{val}</div>
            </div>
          ))}
        </div>
        {asset.notes && <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600"><span className="font-medium">Notes:</span> {asset.notes}</div>}
      </div>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Assignment History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400">No assignment history.</p>
        ) : (
          <div className="space-y-3">
            {history.map(h => (
              <div key={h.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${h.returned_at ? 'bg-gray-300' : 'bg-green-500'}`} />
                <div className="flex-1 text-sm">
                  <span className="font-medium text-gray-900">{h.assigned_to}</span>
                  <span className="text-gray-500"> · Assigned by {h.assigned_by || '—'} on {new Date(h.assigned_at).toLocaleDateString()}</span>
                  {h.returned_at && <span className="text-gray-400"> · Returned {new Date(h.returned_at).toLocaleDateString()}</span>}
                  {h.notes && <div className="text-gray-400 text-xs mt-0.5">{h.notes}</div>}
                </div>
                {!h.returned_at && <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Active</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAssign && <AssignModal asset={asset} onSave={handleAssign} onClose={() => setShowAssign(false)} />}
    </div>
  );
}

const AssetBoardModule = ({ user }) => {
  const [assets, setAssets] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, assigned: 0, available: 0, maintenance: 0, retired: 0, expiringCount: 0, expiringAssets: [] });
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard'); // dashboard | assets
  const [viewingAsset, setViewingAsset] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState(null);
  const [showAssign, setShowAssign] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [filterCategory, setFilterCategory] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');

  const canManage = ['admin', 'manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [a, s, c] = await Promise.all([
      ModulesModel.getAssets({ search, category_id: filterCategory, status: filterStatus }),
      ModulesModel.getAssetStats(),
      ModulesModel.getAssetCategories(),
    ]);
    setAssets(a || []); setStats(s); setCategories(c || []);
    setLoading(false);
  }, [search, filterCategory, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const handleSaveAsset = async (data) => {
    if (editingAsset?.id) await ModulesModel.updateAsset(editingAsset.id, data);
    else await ModulesModel.createAsset(data);
    setShowForm(false); setEditingAsset(null); load();
  };
  const handleDelete = async (id) => {
    if (!confirm('Delete this asset?')) return;
    await ModulesModel.deleteAsset(id); load();
  };
  const handleAssign = async (data) => {
    await ModulesModel.assignAsset(data);
    setShowAssign(null); load();
  };
  const handleReturn = async (assetId) => {
    if (!confirm('Mark as returned?')) return;
    await ModulesModel.returnAsset({ asset_id: assetId }); load();
  };
  const openEdit = (asset) => { setEditingAsset(asset); setShowForm(true); };

  if (viewingAsset) {
    return <AssetDetailView assetId={viewingAsset} categories={categories} user={user} onBack={() => setViewingAsset(null)} onEdit={openEdit} onRefresh={load} />;
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">🖥️ AssetBoard</h1>
          <p className="text-sm text-gray-500 mt-1">Track company assets, hardware, and software licenses</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{ id: 'dashboard', label: 'Dashboard' }, { id: 'assets', label: 'All Assets' }].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          {canManage && (
            <button onClick={() => { setEditingAsset(null); setShowForm(true); }} className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700">+ New Asset</button>
          )}
        </div>
      </div>

      {/* Warranty Alerts Banner */}
      {stats.expiringAssets.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-600 font-semibold text-sm">⚠️ Warranty Alerts ({stats.expiringAssets.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.expiringAssets.map(a => {
              const days = Math.ceil((new Date(a.warranty_expiration) - new Date(today)) / 86400000);
              return (
                <button key={a.id} onClick={() => setViewingAsset(a.id)} className="text-xs bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-amber-800 hover:bg-amber-50">
                  {a.asset_name} · {days <= 0 ? 'EXPIRED' : `${days}d left`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'dashboard' ? (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Total Assets',    value: stats.total,       color: 'text-gray-800',   bg: 'bg-gray-50',    border: 'border-gray-200' },
              { label: 'Available',       value: stats.available,   color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
              { label: 'Assigned',        value: stats.assigned,    color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
              { label: 'In Maintenance',  value: stats.maintenance, color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-yellow-200' },
              { label: 'Expiring Soon',   value: stats.expiringCount, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4`}>
                <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-sm text-gray-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Recent Assets Table */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">Recent Assets</h2>
              <button onClick={() => setView('assets')} className="text-sm text-cyan-600 hover:underline">View all →</button>
            </div>
            {loading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Asset', 'Category', 'Tag', 'Owner', 'Status', 'Warranty'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assets.slice(0, 8).map(a => (
                    <tr key={a.id} onClick={() => setViewingAsset(a.id)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3 font-medium text-gray-900">{a.asset_name}</td>
                      <td className="px-4 py-3 text-gray-500">{a.category_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{a.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-600">{a.current_owner || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${assetStatusStyle[a.status] || 'bg-gray-100 text-gray-600'}`}>{a.status}</span></td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{a.warranty_expiration || '—'}</td>
                    </tr>
                  ))}
                  {assets.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No assets yet. {canManage ? 'Click "+ New Asset" to add one.' : ''}</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        /* All Assets View */
        <div>
          {/* Search & Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, tag, serial, owner…" className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" />
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Statuses</option>
              {ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(search || filterCategory || filterStatus) && (
              <button onClick={() => { setSearch(''); setFilterCategory(''); setFilterStatus(''); }} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded-lg">Clear</button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Asset Name', 'Category', 'Tag', 'Serial', 'Owner', 'Location', 'Status', 'Warranty', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                ) : assets.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No assets found.</td></tr>
                ) : assets.map(a => {
                  const days = a.warranty_expiration ? Math.ceil((new Date(a.warranty_expiration) - new Date(today)) / 86400000) : null;
                  const warnColor = days !== null && days <= 30 ? (days <= 0 ? 'text-red-600 font-semibold' : 'text-amber-600 font-medium') : 'text-gray-400';
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 group">
                      <td className="px-4 py-3">
                        <button onClick={() => setViewingAsset(a.id)} className="font-medium text-gray-900 hover:text-cyan-700 text-left">{a.asset_name}</button>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{a.category_name || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{a.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{a.serial_number || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.current_owner || '—'}</td>
                      <td className="px-4 py-3 text-gray-400">{a.location || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${assetStatusStyle[a.status] || 'bg-gray-100 text-gray-600'}`}>{a.status}</span></td>
                      <td className={`px-4 py-3 text-xs ${warnColor}`}>
                        {a.warranty_expiration ? (days <= 0 ? `Expired` : days <= 30 ? `${days}d left` : a.warranty_expiration) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {canManage && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {a.status !== 'Assigned' && (
                              <button onClick={() => setShowAssign(a)} className="px-2 py-1 text-xs bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100">Assign</button>
                            )}
                            {a.status === 'Assigned' && (
                              <button onClick={() => handleReturn(a.id)} className="px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100">Return</button>
                            )}
                            <button onClick={() => openEdit(a)} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Edit</button>
                            <button onClick={() => handleDelete(a.id)} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">Del</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">{assets.length} asset{assets.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      )}

      {showForm && (
        <AssetFormModal asset={editingAsset} categories={categories} onSave={handleSaveAsset} onClose={() => { setShowForm(false); setEditingAsset(null); }} />
      )}
      {showAssign && (
        <AssignModal asset={showAssign} onSave={handleAssign} onClose={() => setShowAssign(null)} />
      )}
    </div>
  );
};

// ============================================================================
// AUTOMATIONBOARD MODULE
// ============================================================================

const TRIGGER_OPTIONS = [
  { module: 'quicktrack',   event: 'task_completed',  label: 'QuickTrack — Task Completed' },
  { module: 'quicktrack',   event: 'task_created',    label: 'QuickTrack — Task Created' },
  { module: 'quality',      event: 'bug_created',     label: 'Quality — Bug Created' },
  { module: 'devops',       event: 'task_completed',  label: 'DevOps — Sprint Task Completed' },
  { module: 'leaveboard',   event: 'leave_approved',  label: 'LeaveBoard — Leave Approved' },
  { module: 'incidentboard',event: 'incident_created',label: 'IncidentBoard — Incident Created' },
];

const ACTION_OPTIONS = [
  { module: 'quicktrack',   type: 'create_quick_task',  label: 'Create QuickTrack Task' },
  { module: 'timeboard',    type: 'log_hours',           label: 'Log Hours to TimeBoard' },
  { module: 'incidentboard',type: 'create_incident',     label: 'Create Incident' },
  { module: 'system',       type: 'send_notification',   label: 'Send Notification' },
  { module: 'devops',       type: 'update_task_status',  label: 'Update Task Status' },
];

const CONDITION_FIELDS = {
  quicktrack:    ['priority', 'assignee', 'title'],
  quality:       ['severity', 'assignee', 'reporter'],
  devops:        ['priority', 'assignee'],
  leaveboard:    ['leaveType', 'totalDays', 'userName'],
  incidentboard: ['severity', 'team', 'assignee'],
};

const CONDITION_OPERATORS = [
  { value: 'equals',       label: 'equals' },
  { value: 'not_equals',   label: 'not equals' },
  { value: 'contains',     label: 'contains' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than',    label: 'less than' },
];

const autoStatusColors = { active: 'bg-green-100 text-green-700', paused: 'bg-yellow-100 text-yellow-700' };
const autoExecColors   = { success: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700' };
const autoModuleColors = {
  quicktrack: 'bg-purple-100 text-purple-700', quality: 'bg-teal-100 text-teal-700',
  devops: 'bg-blue-100 text-blue-700', timeboard: 'bg-orange-100 text-orange-700',
  leaveboard: 'bg-pink-100 text-pink-700', incidentboard: 'bg-red-100 text-red-700',
  system: 'bg-gray-100 text-gray-700', plantrack: 'bg-green-100 text-green-700',
};

function RuleBuilderModal({ rule, onSave, onClose }) {
  const isEdit = !!rule?.id;
  const [form, setForm] = React.useState({
    name: rule?.name || '',
    description: rule?.description || '',
    triggerModule: rule?.trigger_module || '',
    triggerEvent: rule?.trigger_event || '',
    hasCondition: !!(rule?.condition),
    conditionField: rule?.condition?.field || '',
    conditionOperator: rule?.condition?.operator || 'equals',
    conditionValue: rule?.condition?.value || '',
    actionModule: rule?.action_module || '',
    actionType: rule?.action_type || '',
    actionConfig: rule?.actionConfig || {},
    status: rule?.status || 'active',
  });
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedTrigger = TRIGGER_OPTIONS.find(t => t.module === form.triggerModule && t.event === form.triggerEvent);
  const selectedAction  = ACTION_OPTIONS.find(a => a.type === form.actionType);
  const condFields = CONDITION_FIELDS[form.triggerModule] || [];

  const handleTriggerChange = (val) => {
    const [mod, ...evtParts] = val.split('|');
    set('triggerModule', mod); set('triggerEvent', evtParts.join('|'));
    set('conditionField', '');
  };
  const handleActionChange = (val) => {
    const opt = ACTION_OPTIONS.find(a => a.type === val);
    if (opt) { set('actionType', val); set('actionModule', opt.module); set('actionConfig', {}); }
  };

  const updateConfig = (k, v) => setForm(f => ({ ...f, actionConfig: { ...f.actionConfig, [k]: v } }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.triggerModule || !form.actionType) return;
    setSaving(true);
    const payload = {
      name: form.name, description: form.description,
      triggerModule: form.triggerModule, triggerEvent: form.triggerEvent,
      condition: form.hasCondition && form.conditionField ? { field: form.conditionField, operator: form.conditionOperator, value: form.conditionValue } : null,
      actionModule: form.actionModule, actionType: form.actionType,
      actionConfig: form.actionConfig, status: form.status,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Automation Rule' : 'New Automation Rule'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Auto-log hours on task complete" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>

          {/* Trigger */}
          <div className="bg-indigo-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">1</span>
              <span className="font-semibold text-indigo-800">Trigger — When this happens</span>
            </div>
            <select value={form.triggerModule && form.triggerEvent ? `${form.triggerModule}|${form.triggerEvent}` : ''} onChange={e => handleTriggerChange(e.target.value)} className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="">Select trigger event...</option>
              {TRIGGER_OPTIONS.map(t => <option key={t.module + t.event} value={`${t.module}|${t.event}`}>{t.label}</option>)}
            </select>
          </div>

          {/* Condition */}
          {form.triggerModule && (
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold">2</span>
                <span className="font-semibold text-amber-800">Condition — Optional filter</span>
                <label className="ml-auto flex items-center gap-2 text-sm text-amber-700 cursor-pointer">
                  <input type="checkbox" checked={form.hasCondition} onChange={e => set('hasCondition', e.target.checked)} className="rounded" />
                  Add condition
                </label>
              </div>
              {form.hasCondition && (
                <div className="grid grid-cols-3 gap-2">
                  <select value={form.conditionField} onChange={e => set('conditionField', e.target.value)} className="border border-amber-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    <option value="">Field...</option>
                    {condFields.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select value={form.conditionOperator} onChange={e => set('conditionOperator', e.target.value)} className="border border-amber-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input value={form.conditionValue} onChange={e => set('conditionValue', e.target.value)} placeholder="Value..." className="border border-amber-200 rounded-lg px-2 py-2 text-sm focus:outline-none" />
                </div>
              )}
              {!form.hasCondition && <p className="text-sm text-amber-600">No condition — rule runs for every trigger.</p>}
            </div>
          )}

          {/* Action */}
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">3</span>
              <span className="font-semibold text-green-800">Action — Then do this</span>
            </div>
            <select value={form.actionType} onChange={e => handleActionChange(e.target.value)} className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400 mb-3">
              <option value="">Select action...</option>
              {ACTION_OPTIONS.map(a => <option key={a.type} value={a.type}>{a.label}</option>)}
            </select>

            {form.actionType === 'create_quick_task' && (
              <div className="space-y-2">
                <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)} placeholder="Task title" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <input value={form.actionConfig.assigned_to || ''} onChange={e => updateConfig('assigned_to', e.target.value)} placeholder="Assign to (username or leave blank to use trigger's assignee)" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <select value={form.actionConfig.priority || 'medium'} onChange={e => updateConfig('priority', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                  {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            {form.actionType === 'log_hours' && (
              <div className="space-y-2">
                <input type="number" min="0.5" step="0.5" value={form.actionConfig.hours || ''} onChange={e => updateConfig('hours', e.target.value)} placeholder="Hours to log" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <input value={form.actionConfig.user_name || ''} onChange={e => updateConfig('user_name', e.target.value)} placeholder="Username (leave blank to use trigger's assignee)" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              </div>
            )}
            {form.actionType === 'send_notification' && (
              <div className="space-y-2">
                <input value={form.actionConfig.notify_user || ''} onChange={e => updateConfig('notify_user', e.target.value)} placeholder="Notify username" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)} placeholder="Notification title" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <textarea value={form.actionConfig.message || ''} onChange={e => updateConfig('message', e.target.value)} placeholder="Notification message" rows={2} className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              </div>
            )}
            {form.actionType === 'create_incident' && (
              <div className="space-y-2">
                <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)} placeholder="Incident title" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <select value={form.actionConfig.severity || 'Medium'} onChange={e => updateConfig('severity', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                  {['Low','Medium','High','Critical'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {form.actionType === 'update_task_status' && (
              <select value={form.actionConfig.status || 'in_progress'} onChange={e => updateConfig('status', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                {['backlog','ready','in_progress','review','done'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            {['active','paused'].map(s => (
              <button key={s} onClick={() => set('status', s)} className={`px-3 py-1 rounded-full text-sm font-medium border ${form.status === s ? (s === 'active' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-yellow-100 border-yellow-400 text-yellow-700') : 'bg-white border-gray-300 text-gray-500'}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.triggerModule || !form.actionType} className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

const AutomationBoardModule = ({ user }) => {
  const [rules, setRules] = React.useState([]);
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard'); // dashboard | logs
  const [showBuilder, setShowBuilder] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);

  const canManage = ['admin', 'manager', 'devops_manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, l] = await Promise.all([ModulesModel.getAutomationRules(), ModulesModel.getAutomationLogs()]);
    setRules(r || []);
    setLogs(l || []);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    if (editingRule?.id) {
      await ModulesModel.updateAutomationRule(editingRule.id, data);
    } else {
      await ModulesModel.createAutomationRule(data);
    }
    setShowBuilder(false);
    setEditingRule(null);
    load();
  };

  const handleToggle = async (rule) => {
    await ModulesModel.updateAutomationRule(rule.id, { status: rule.status === 'active' ? 'paused' : 'active' });
    load();
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    await ModulesModel.deleteAutomationRule(id);
    setDeleting(null);
    load();
  };

  const handleEdit = (rule) => { setEditingRule(rule); setShowBuilder(true); };

  const activeRules  = rules.filter(r => r.status === 'active');
  const pausedRules  = rules.filter(r => r.status === 'paused');
  const recentLogs   = logs.slice(0, 20);
  const failedCount  = logs.filter(l => l.execution_status === 'failed').length;
  const successCount = logs.filter(l => l.execution_status === 'success').length;

  const getTriggerLabel = (r) => TRIGGER_OPTIONS.find(t => t.module === r.trigger_module && t.event === r.trigger_event)?.label || `${r.trigger_module} / ${r.trigger_event}`;
  const getActionLabel  = (r) => ACTION_OPTIONS.find(a => a.type === r.action_type)?.label || r.action_type;

  const RuleCard = ({ rule }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900 truncate">{rule.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoStatusColors[rule.status] || 'bg-gray-100 text-gray-600'}`}>{rule.status}</span>
          </div>
          {rule.description && <p className="text-xs text-gray-500 mb-2 line-clamp-1">{rule.description}</p>}
        </div>
        {canManage && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => handleToggle(rule)} title={rule.status === 'active' ? 'Pause' : 'Activate'} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 text-sm">
              {rule.status === 'active' ? '⏸' : '▶'}
            </button>
            <button onClick={() => handleEdit(rule)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-500 text-sm">✏️</button>
            <button onClick={() => handleDelete(rule.id)} disabled={deleting === rule.id} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 text-sm">
              {deleting === rule.id ? '…' : '🗑'}
            </button>
          </div>
        )}
      </div>

      {/* Flow: Trigger → Condition → Action */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <div className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1.5">
          <span className="text-indigo-400">⚡</span>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${autoModuleColors[rule.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{rule.trigger_module}</span>
          <span className="text-indigo-700 font-medium">{rule.trigger_event?.replace(/_/g, ' ')}</span>
        </div>
        {rule.condition && (
          <>
            <span className="text-gray-400">→</span>
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
              <span className="text-amber-500">⚙</span>
              <span className="text-amber-700">{rule.condition.field} {rule.condition.operator?.replace(/_/g, ' ')} <strong>{rule.condition.value}</strong></span>
            </div>
          </>
        )}
        <span className="text-gray-400">→</span>
        <div className="flex items-center gap-1.5 bg-green-50 border border-green-100 rounded-lg px-2.5 py-1.5">
          <span className="text-green-400">✓</span>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${autoModuleColors[rule.action_module] || 'bg-gray-100 text-gray-600'}`}>{rule.action_module}</span>
          <span className="text-green-700 font-medium">{rule.action_type?.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-400">Created by {rule.created_by || 'System'}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">🤖</span> AutomationBoard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Build workflows that connect Orbit modules automatically</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{ id: 'dashboard', label: 'Rules' }, { id: 'logs', label: 'Execution Logs' }].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          {canManage && (
            <button onClick={() => { setEditingRule(null); setShowBuilder(true); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              + New Rule
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
      ) : view === 'dashboard' ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Active Rules',    value: activeRules.length,  color: 'text-green-600',  bg: 'bg-green-50' },
              { label: 'Paused Rules',    value: pausedRules.length,  color: 'text-yellow-600', bg: 'bg-yellow-50' },
              { label: 'Total Executions',value: logs.length,          color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'Failed',          value: failedCount,          color: 'text-red-600',    bg: 'bg-red-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-sm text-gray-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Active Rules */}
          <div className="mb-6">
            <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> Active Rules ({activeRules.length})
            </h2>
            {activeRules.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-400">
                No active rules yet.{canManage ? ' Click "+ New Rule" to create one.' : ''}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activeRules.map(r => <RuleCard key={r.id} rule={r} />)}
              </div>
            )}
          </div>

          {/* Paused Rules */}
          {pausedRules.length > 0 && (
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span> Paused Rules ({pausedRules.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pausedRules.map(r => <RuleCard key={r.id} rule={r} />)}
              </div>
            </div>
          )}

          {/* Recent Executions */}
          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-3">Recent Executions</h2>
            {recentLogs.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-400 text-sm">No executions yet. Rules will fire automatically when trigger events occur.</div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>{['Rule', 'Trigger', 'Result', 'Status', 'Time'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentLogs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate">{log.rule_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${autoModuleColors[log.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{log.trigger_module}</span>
                          <span className="ml-1 text-xs">{log.trigger_event?.replace(/_/g,' ')}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{log.execution_result || '—'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoExecColors[log.execution_status] || 'bg-gray-100 text-gray-600'}`}>{log.execution_status}</span></td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{log.executed_at ? new Date(log.executed_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Logs View */
        <div>
          <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
            <span>Total: <strong>{logs.length}</strong></span>
            <span className="text-green-600">Success: <strong>{successCount}</strong></span>
            <span className="text-red-600">Failed: <strong>{failedCount}</strong></span>
          </div>
          {logs.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-400">No execution logs yet.</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['#', 'Rule', 'Trigger Module', 'Trigger Event', 'Result', 'Status', 'Executed At'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs">{log.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{log.rule_name || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${autoModuleColors[log.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{log.trigger_module}</span></td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{log.trigger_event?.replace(/_/g,' ')}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{log.execution_result || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoExecColors[log.execution_status] || 'bg-gray-100 text-gray-600'}`}>{log.execution_status}</span></td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{log.executed_at ? new Date(log.executed_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Rule Builder Modal */}
      {showBuilder && (
        <RuleBuilderModal
          rule={editingRule}
          onSave={handleSave}
          onClose={() => { setShowBuilder(false); setEditingRule(null); }}
        />
      )}
    </div>
  );
};

const IncidentBoardModule = ({
  data, user, filters, viewingIncident, showCreate,
  onFilterChange, onViewIncident, onCreateIncident, onUpdateIncident,
  onAddUpdate, onDeleteIncident, onBack, onShowCreate, onCloseCreate
}) => {
  const incidents = data?.incidents || [];
  const metrics = data?.metrics || {};
  const allUsers = data?.allUsers || [];

  if (viewingIncident) {
    return <IncidentDetailView incident={viewingIncident} user={user} onBack={onBack} onUpdateIncident={onUpdateIncident} onAddUpdate={onAddUpdate} onDeleteIncident={onDeleteIncident} />;
  }

  const activeCount = metrics.open || 0;
  const criticalCount = metrics.critical || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">🚨 IncidentBoard</h1>
          <p className="text-gray-500 mt-1 text-sm">Track production incidents, outages, and operational disruptions</p>
        </div>
        <button onClick={onShowCreate} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium whitespace-nowrap">+ Report Incident</button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active Incidents', value: activeCount, icon: '🔥', color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Critical', value: criticalCount, icon: '🚨', color: 'text-red-700', bg: 'bg-red-100' },
          { label: 'Resolved Today', value: metrics.resolvedToday || 0, icon: '✅', color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Total', value: metrics.total || 0, icon: '📊', color: 'text-gray-700', bg: 'bg-gray-50' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 border border-gray-100`}>
            <div className="flex items-center gap-2 mb-1"><span className="text-xl">{c.icon}</span><span className="text-xs text-gray-500">{c.label}</span></div>
            <div className={`text-3xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-40">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={filters.search} onChange={e => onFilterChange({ search: e.target.value })} placeholder="Search incidents..."
            className="flex-1 text-sm border-0 outline-none bg-transparent text-gray-700 placeholder-gray-400" />
        </div>
        <select value={filters.severity} onChange={e => onFilterChange({ severity: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
          <option value="">All Severities</option>
          {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.status} onChange={e => onFilterChange({ status: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
          <option value="">All Statuses</option>
          {INCIDENT_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.team} onChange={e => onFilterChange({ team: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
          <option value="">All Teams</option>
          {INCIDENT_TEAMS.map(t => <option key={t}>{t}</option>)}
        </select>
        {(filters.severity || filters.status || filters.team || filters.search) && (
          <button onClick={() => onFilterChange({ severity: '', status: '', team: '', search: '' })} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
        )}
      </div>

      {/* Incident list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {incidents.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-5xl mb-3">🟢</div>
            <p className="text-gray-500 font-medium">No incidents found</p>
            <p className="text-gray-400 text-sm mt-1">All systems operational</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {incidents.map(inc => (
              <div key={inc.id} onClick={() => onViewIncident(inc.id)}
                className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors">
                <div className="flex-shrink-0 mt-0.5">
                  <span className={`w-2.5 h-2.5 rounded-full block mt-1 ${incStatusDot(inc.status)}`}></span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-400 font-mono">INC-{inc.id}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${incSeverityColor(inc.severity)}`}>{inc.severity}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${incStatusColor(inc.status)}`}>{inc.status}</span>
                    {inc.team && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{inc.team}</span>}
                  </div>
                  <p className="text-sm font-semibold text-gray-900 truncate">{inc.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {inc.service_affected && <span>⚡ {inc.service_affected}</span>}
                    {inc.assigned_to && <span>👤 {inc.assigned_to}</span>}
                    <span>{inc.created_at ? new Date(inc.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-2" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Severity breakdown */}
      {metrics.total > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Severity</h3>
            <div className="space-y-2">
              {INCIDENT_SEVERITIES.map(s => (
                <div key={s} className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full w-20 text-center ${incSeverityColor(s)}`}>{s}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-400 rounded-full" style={{ width: `${metrics.total ? ((metrics.bySeverity?.[s] || 0) / metrics.total) * 100 : 0}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-500 w-5 text-right">{metrics.bySeverity?.[s] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Status</h3>
            <div className="space-y-2">
              {INCIDENT_STATUSES.map(s => (
                <div key={s} className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full w-24 text-center ${incStatusColor(s)}`}>{s}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${metrics.total ? ((metrics.byStatus?.[s] || 0) / metrics.total) * 100 : 0}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-500 w-5 text-right">{metrics.byStatus?.[s] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showCreate && <IncidentCreateModal onSave={onCreateIncident} onClose={onCloseCreate} allUsers={allUsers} user={user} />}
    </div>
  );
};

// ============================================================
// KNOWLEDGEBOARD COMPONENTS
// ============================================================

const KB_CATEGORIES_PRESET = [
  { name: 'Engineering', icon: '⚙️', color: '#0366d6' },
  { name: 'DevOps',      icon: '🔧', color: '#6f42c1' },
  { name: 'DataOps',     icon: '📊', color: '#17a2b8' },
  { name: 'QA',          icon: '✓',  color: '#20c997' },
  { name: 'Operations',  icon: '🏢', color: '#fd7e14' },
  { name: 'HR',          icon: '👥', color: '#e83e8c' },
  { name: 'Vendor',      icon: '📡', color: '#e74c3c' },
  { name: 'General',     icon: '📄', color: '#6c757d' },
];

// Article editor component
const KnowledgeArticleEditor = ({ article, categories, user, onSave, onCancel }) => {
  const [form, setForm] = React.useState({
    title: article?.title || '',
    categoryId: article?.category_id || '',
    content: article?.content || '',
    status: article?.status || 'draft',
  });
  const [tab, setTab] = React.useState('write');
  const isEdit = !!article;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert('Title is required');
    onSave({ title: form.title, categoryId: form.categoryId || null, content: form.content, status: form.status, updatedBy: user?.user_name, updatedById: user?.user_id || user?.id });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"><ChevronRight size={18} className="rotate-180" /></button>
        <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Article' : 'New Article'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
              placeholder="Article title..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="">— No category —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
        </div>

        {/* Editor with write/preview tabs */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-1 px-4 pt-3 border-b border-gray-200">
            {['write', 'preview'].map(t => (
              <button key={t} type="button" onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === t ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>
                {t === 'write' ? '✏️ Write' : '👁 Preview'}
              </button>
            ))}
            <div className="ml-auto text-xs text-gray-400 pb-2">Markdown supported</div>
          </div>
          {tab === 'write' ? (
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder={`Write your article content here...\n\n# Heading 1\n## Heading 2\n\n**Bold text**, *italic text*\n\n\`\`\`javascript\n// Code block\n\`\`\`\n\n- Bullet list item\n- Another item`}
              className="w-full min-h-[420px] px-5 py-4 text-sm font-mono text-gray-800 leading-relaxed resize-y focus:outline-none rounded-b-lg"
            />
          ) : (
            <div className="min-h-[420px] px-5 py-4 prose max-w-none overflow-auto"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) || '<p class="text-gray-400 italic">Nothing to preview yet.</p>' }}
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-400">{form.content.length} characters</div>
          <div className="flex gap-3">
            <button type="button" onClick={() => { setForm(f => ({ ...f, status: 'draft' })); setTimeout(() => handleSubmit({ preventDefault: () => {} }), 0); }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
              Save Draft
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button type="submit" className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium">
              {form.status === 'published' ? '🚀 Publish' : '💾 Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Article view with version history panel
const KnowledgeArticleView = ({ article, versions, showVersions, user, isAdmin, onEdit, onDelete, onBack, onViewVersions, onRestoreVersion, onCloseVersions }) => {
  if (!article) return null;
  const canEdit = isAdmin || article.created_by === user?.user_name;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Back + actions */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium">
          <ChevronRight size={16} className="rotate-180" /> Back to articles
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => onViewVersions(article.id)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1.5">
            🕐 History <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded-full">v{article.version_number}</span>
          </button>
          {canEdit && (
            <>
              <button onClick={() => onEdit(article)} className="px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 font-medium">Edit</button>
              {isAdmin && <button onClick={() => onDelete(article.id)} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 font-medium">Delete</button>}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* Article content */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              {article.category_name && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">{article.category_name}</span>
              )}
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${article.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {article.status === 'published' ? 'Published' : 'Draft'}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{article.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>By <span className="font-medium text-gray-700">{article.created_by}</span></span>
              <span>·</span>
              <span>Updated {article.updated_at ? new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
              {article.updated_by && article.updated_by !== article.created_by && (
                <><span>·</span><span>Edited by <span className="font-medium text-gray-700">{article.updated_by}</span></span></>
              )}
              {article.views > 0 && <><span>·</span><span>{article.views} views</span></>}
            </div>
          </div>
          <div className="px-8 py-6">
            {article.content ? (
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }} />
            ) : (
              <p className="text-gray-400 italic">This article has no content yet.</p>
            )}
          </div>
        </div>

        {/* Version history panel */}
        {showVersions && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">Version History</h3>
              <button onClick={onCloseVersions} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            {versions.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-400 text-center">No previous versions</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                {versions.map(v => (
                  <div key={v.id} className="px-4 py-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-700">Version {v.version_number}</span>
                      {canEdit && (
                        <button onClick={() => onRestoreVersion(article.id, v.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Restore</button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{v.edited_by}</div>
                    <div className="text-xs text-gray-400">{v.edited_at ? new Date(v.edited_at).toLocaleString() : '—'}</div>
                    {v.title && v.title !== article.title && <div className="text-xs text-gray-500 mt-1 italic">Title: {v.title}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Category management modal
const KnowledgeCategoryModal = ({ onSave, onClose }) => {
  const [form, setForm] = React.useState({ name: '', description: '', icon: '📄', color: '#6366f1' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.name.trim()) return; onSave(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Category</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-5 gap-2">
            {KB_CATEGORIES_PRESET.map(p => (
              <button key={p.name} type="button" onClick={() => setForm(f => ({ ...f, name: p.name, icon: p.icon, color: p.color }))}
                className="flex flex-col items-center p-2 rounded-lg border border-gray-200 hover:border-indigo-400 text-xs gap-1">
                <span className="text-lg">{p.icon}</span><span className="text-gray-600 truncate w-full text-center">{p.name}</span>
              </button>
            ))}
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
              <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-lg px-2 cursor-pointer" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium">Create Category</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main KnowledgeBoard module
const KnowledgeBoardModule = ({
  data, user, search, selectedCategory, viewingArticle, showEditor, editingArticle,
  showVersions, articleVersions, showCategoryModal,
  onSearch, onSelectCategory, onViewArticle, onCreateArticle, onEditArticle,
  onSaveArticle, onDeleteArticle, onCloseEditor, onViewVersions, onRestoreVersion,
  onCloseVersions, onShowCategoryModal, onCreateCategory, onCloseCategoryModal, onBackToList
}) => {
  const isAdmin = (user?.role || user?.user_role) === 'admin';
  const categories = data?.categories || [];
  const articles = data?.articles || [];
  const metrics = data?.metrics || {};

  // Show editor
  if (showEditor) {
    return <KnowledgeArticleEditor article={editingArticle} categories={categories} user={user} onSave={onSaveArticle} onCancel={onCloseEditor} />;
  }

  // Show article view
  if (viewingArticle) {
    return (
      <>
        <KnowledgeArticleView
          article={viewingArticle} versions={articleVersions} showVersions={showVersions}
          user={user} isAdmin={isAdmin}
          onEdit={onEditArticle} onDelete={onDeleteArticle} onBack={onBackToList}
          onViewVersions={onViewVersions} onRestoreVersion={onRestoreVersion} onCloseVersions={onCloseVersions}
        />
        {showCategoryModal && <KnowledgeCategoryModal onSave={onCreateCategory} onClose={onCloseCategoryModal} />}
      </>
    );
  }

  // Main list view
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{minHeight: '80vh'}}>
      {/* Left sidebar */}
      <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-4 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">📚</span>
            <h2 className="font-bold text-gray-900 text-base">KnowledgeBoard</h2>
          </div>
          <p className="text-xs text-gray-500">Internal knowledge base</p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-2 px-3 py-3 border-b border-gray-100">
          <div className="bg-indigo-50 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-indigo-700">{metrics.published || 0}</div>
            <div className="text-xs text-gray-500">Published</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-yellow-700">{metrics.drafts || 0}</div>
            <div className="text-xs text-gray-500">Drafts</div>
          </div>
        </div>

        {/* Category list */}
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Categories</span>
            {isAdmin && (
              <button onClick={onShowCategoryModal} className="text-gray-400 hover:text-indigo-600 p-1 rounded" title="New category">
                <span className="text-base leading-none">+</span>
              </button>
            )}
          </div>
          <button
            onClick={() => onSelectCategory(null)}
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-lg mx-1 ${!selectedCategory ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            <span>📋</span> All Articles
            <span className="ml-auto text-xs text-gray-400">{metrics.total || 0}</span>
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => onSelectCategory(cat.id)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-lg mx-1 ${selectedCategory === cat.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span>{cat.icon || '📄'}</span>
              <span className="truncate flex-1">{cat.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{cat.article_count || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search} onChange={e => onSearch(e.target.value)}
              placeholder="Search articles by title or content..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
            />
          </div>
          <button onClick={onCreateArticle}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2 flex-shrink-0">
            <span className="text-base leading-none">+</span> New Article
          </button>
        </div>

        {/* Article list */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Section heading */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedCategory ? (categories.find(c => c.id === selectedCategory)?.name || 'Category') : 'All Articles'}
              {search && <span className="ml-2 text-sm font-normal text-gray-500">— results for "{search}"</span>}
            </h2>
            <span className="text-sm text-gray-400">{articles.length} article{articles.length !== 1 ? 's' : ''}</span>
          </div>

          {articles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="text-5xl mb-4">📄</span>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles yet</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-xs">
                {search ? `No results for "${search}". Try a different search.` : 'Create your first article to start building your knowledge base.'}
              </p>
              {!search && <button onClick={onCreateArticle} className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium">+ New Article</button>}
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map(article => {
                const cat = categories.find(c => c.id === article.category_id);
                return (
                  <div key={article.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer p-5"
                    onClick={() => onViewArticle(article.id)}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {cat && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: (cat.color || '#6366f1') + '20', color: cat.color || '#6366f1' }}>
                              {cat.icon} {cat.name}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${article.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {article.status === 'published' ? 'Published' : 'Draft'}
                          </span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">v{article.version_number}</span>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">{article.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>By {article.created_by}</span>
                          <span>·</span>
                          <span>{article.updated_at ? new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                          {article.views > 0 && <><span>·</span><span>{article.views} views</span></>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCategoryModal && <KnowledgeCategoryModal onSave={onCreateCategory} onClose={onCloseCategoryModal} />}
    </div>
    </div>
  );
};

// ============================================================
// TIMEBOARD COMPONENTS
// ============================================================

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const categoryLabels = { project: 'Project Work', adhoc: 'Ad Hoc', meeting: 'Meetings', admin: 'Admin', training: 'Training', leave: 'Leave' };
const categoryColors = { project: '#0366d6', adhoc: '#6f42c1', meeting: '#e36209', admin: '#586069', training: '#fd7e14', leave: '#e83e8c' };

const MyTimesheetView = ({ taskDays, weekDates, leaveDays }) => {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));

  // Build a set of leave dates for display
  const leaveDates = new Set();
  if (leaveDays) {
    leaveDays.forEach(leave => {
      const s = new Date(leave.startDate + 'T00:00:00'), e = new Date(leave.endDate + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) leaveDates.add(d.toISOString().split('T')[0]);
    });
  }

  // Map taskDays by date for quick lookup
  const dayMap = {};
  (taskDays || []).forEach(d => { dayMap[d.date] = d; });

  return (
    <div className="mb-6 bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {/* Header row */}
      <div className="grid grid-cols-[1fr_auto_auto] items-center px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <span>Day</span>
        <span className="pr-6">Tasks</span>
        <span className="w-20 text-right">Hours</span>
      </div>

      {weekDates.map((date, i) => {
        const day = dayMap[date] || { date, tasks: [], totalHours: 0 };
        const isWeekend = i >= 5;
        const isLeave = leaveDates.has(date);
        const isOpen = !!expanded[date];
        const hasTasks = day.tasks.length > 0;
        const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return (
          <div key={date} className={`border-b border-gray-100 last:border-0 ${isWeekend ? 'bg-gray-50' : ''}`}>
            {/* Day summary row */}
            <div
              className={`grid grid-cols-[1fr_auto_auto] items-center px-5 py-3 ${hasTasks && !isWeekend ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
              onClick={() => hasTasks && !isWeekend && toggle(date)}
            >
              <div className="flex items-center gap-3">
                {hasTasks && !isWeekend ? (
                  <ChevronRight size={15} className={`text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`} />
                ) : (
                  <span className="w-[15px]" />
                )}
                <div>
                  <span className={`text-sm font-semibold ${isWeekend ? 'text-gray-400' : 'text-gray-800'}`}>
                    {dayLabels[i]}
                  </span>
                  <span className={`ml-2 text-sm ${isWeekend ? 'text-gray-300' : 'text-gray-500'}`}>{dateLabel}</span>
                  {isLeave && <span className="ml-2 text-xs bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full font-medium">Leave</span>}
                </div>
              </div>
              <span className={`pr-6 text-xs ${hasTasks ? 'text-gray-400' : 'text-transparent'}`}>
                {hasTasks ? `${day.tasks.length} task${day.tasks.length !== 1 ? 's' : ''}` : '—'}
              </span>
              <span className={`w-20 text-right text-sm font-bold ${
                day.totalHours === 0 ? (isWeekend ? 'text-gray-300' : 'text-gray-300') :
                day.totalHours >= 8 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {isLeave ? '8h' : day.totalHours > 0 ? `${day.totalHours}h` : '0h'}
              </span>
            </div>

            {/* Expanded task list */}
            {isOpen && hasTasks && (
              <div className="border-t border-gray-100 bg-gray-50">
                {day.tasks.map((task, ti) => (
                  <div key={ti} className={`flex items-center gap-3 px-5 py-2.5 pl-12 ${ti < day.tasks.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                      task.source === 'QuickTrack' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {task.source}
                    </span>
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${
                      task.source === 'QuickTrack' ? 'bg-purple-50 text-purple-500' : 'bg-blue-50 text-blue-500'
                    }`}>
                      {task.id}
                    </span>
                    <span className="flex-1 text-sm text-gray-700 truncate">{task.title}</span>
                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{task.hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TimeEntryModal = ({ date, category, onClose, onSubmit }) => {
  const [form, setForm] = useState({ hours: '', category: category || 'project', projectId: '', description: '', isBillable: true });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit({ ...form, entryDate: date, hours: parseFloat(form.hours) || 0 }); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Add Time Entry — {date}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
              <input required type="number" step="0.5" min="0.5" max="24" value={form.hours} onChange={e => setForm({...form, hours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="project">Project Work</option>
                <option value="adhoc">Ad-hoc</option>
                <option value="meeting">Meeting</option>
                <option value="admin">Admin</option>
                <option value="training">Training</option>
              </select>
            </div>
          </div>
          {form.category === 'project' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select project...</option>
                <option value="PROJ-001">Settlement Vault API Modernization</option>
                <option value="PROJ-002">Negotiator Worklist Dashboard</option>
                <option value="PROJ-003">Customer Portal Redesign</option>
                <option value="PROJ-004">Data Warehouse Migration</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="billable" checked={form.isBillable} onChange={e => setForm({...form, isBillable: e.target.checked})} className="rounded" />
            <label htmlFor="billable" className="text-sm text-gray-700">Billable</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Entry</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimeBoardModule = ({ data, weekStart, user, onWeekChange, onSubmitTimesheet, onApproveTimesheet }) => {
  const weekDates = getWeekDates(weekStart);
  const weekEnd = weekDates[6];

  const formatWeekLabel = (start, end) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Task-derived totals (source of truth)
  const totalHours = data?.weekTaskTotal || 0;
  const leaveHours = data?.myTimesheet?.leaveHours || 0;
  const accountedHours = Math.round((totalHours + leaveHours) * 10) / 10;
  const availableHours = data?.myTimesheet?.availableHours ?? (40 - leaveHours);
  const estimatedHours = data?.myTimesheet?.estimatedHours || 0;
  const timesheetStatus = data?.myTimesheet?.status || 'draft';
  const projectComparison = data?.projectComparison || [];

  // Leave days for this user this week
  const leaveDaysThisWeek = (data?.leaveDays || []).filter(l => l.userId === (user?.id || user?.user_id));

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TimeBoard</h1>
          <p className="text-gray-600 mt-1">Time tracking & timesheet management</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Timesheet status */}
          {timesheetStatus === 'draft' && data?.myTimesheet && (
            <button onClick={() => onSubmitTimesheet(data.myTimesheet.id)} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">Submit for Approval</button>
          )}
          {timesheetStatus === 'submitted' && (
            <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-semibold">Awaiting Approval</span>
          )}
          {timesheetStatus === 'approved' && (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">Approved</span>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => onWeekChange(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} className="rotate-180" />
        </button>
        <div className="text-lg font-semibold text-gray-900">{formatWeekLabel(weekStart, weekEnd)}</div>
        <button onClick={() => onWeekChange(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Estimated</div>
          <div className="text-3xl font-bold text-purple-700">{estimatedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Active task estimates</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Completed This Week</div>
          <div className="text-3xl font-bold text-blue-700">{totalHours}h</div>
          <div className="text-xs text-gray-500 mt-1">From finished tasks</div>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Leave Hours</div>
          <div className="text-3xl font-bold text-pink-700">{leaveHours}h</div>
          <div className="text-xs text-gray-500 mt-1">From approved leave</div>
        </div>
        <div className={`rounded-lg p-5 border ${accountedHours >= 40 ? 'bg-green-50 border-green-200' : accountedHours >= 32 ? 'bg-yellow-50 border-yellow-200' : 'bg-orange-50 border-orange-200'}`}>
          <div className="text-sm text-gray-600 mb-1">Accounted</div>
          <div className={`text-3xl font-bold ${accountedHours >= 40 ? 'text-green-700' : accountedHours >= 32 ? 'text-yellow-700' : 'text-orange-700'}`}>{accountedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Tasks + leave / {availableHours}h avail</div>
        </div>
      </div>

      {/* My Timesheet — auto-generated from completed tasks */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">My Timesheet</h2>
        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Auto-generated from completed tasks</span>
      </div>
      <MyTimesheetView
        taskDays={data?.taskDays || []}
        weekDates={weekDates}
        leaveDays={leaveDaysThisWeek}
      />

      {/* Estimate vs Actual by Project */}
      {projectComparison.length > 0 && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estimate vs Actual — by Project</h2>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual (This Week)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Variance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projectComparison.map(p => {
                  const est = p.estimated_hours || 0;
                  const act = p.actual_hours || 0;
                  const variance = act - est;
                  const pct = est > 0 ? Math.min(100, Math.round((act / est) * 100)) : null;
                  return (
                    <tr key={p.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                        <div className="text-xs text-gray-400">{p.project_id}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{est}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-blue-700">{act}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold">
                        {est > 0 ? (
                          <span className={variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-500'}>
                            {variance > 0 ? '+' : ''}{variance.toFixed(1)}h
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct !== null ? (
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 100 ? '#dc2626' : pct > 75 ? '#f59e0b' : '#10b981' }} />
                            </div>
                            <span className="text-xs text-gray-600">{pct}%</span>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Team Timesheets */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Overview</h2>
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual Logged</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Leave</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Accounted</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Billable</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(data?.teamTimesheets || []).map(ts => {
              const ss = getStatusStyle(ts.status === 'draft' ? 'backlog' : ts.status === 'submitted' ? 'review' : ts.status === 'approved' ? 'completed' : 'blocked');
              const tsEst = ts.estimatedHours || 0;
              const tsAct = ts.totalHours || 0;
              const tsLeave = ts.leaveHours || 0;
              const tsAccounted = ts.accountedHours ?? (tsAct + tsLeave);
              const tsVariance = tsAct - tsEst;
              return (
                <tr key={ts.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{ts.userName.charAt(0)}</div>
                      <span className="text-sm font-medium text-gray-900">{ts.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{tsEst}h</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-sm font-semibold text-blue-700">{tsAct}h</div>
                    {tsEst > 0 && (
                      <div className={`text-xs font-medium ${tsVariance > 0 ? 'text-red-500' : tsVariance < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                        {tsVariance > 0 ? '+' : ''}{tsVariance.toFixed(1)}h
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-pink-600">{tsLeave > 0 ? `${tsLeave}h` : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className={`text-sm font-semibold ${tsAccounted >= 40 ? 'text-green-700' : tsAccounted >= 32 ? 'text-yellow-700' : 'text-orange-700'}`}>{tsAccounted}h</div>
                    <div className="text-xs text-gray-400">{tsLeave > 0 ? `${tsAct}w + ${tsLeave}l` : 'of 40h'}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">{ts.billableHours}h</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{ts.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {ts.status === 'submitted' && (
                      <button onClick={() => onApproveTimesheet(ts.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!data?.teamTimesheets || data.teamTimesheets.length === 0) && (
          <div className="p-8 text-center text-gray-400">No timesheets for this week.</div>
        )}
      </div>
    </div>
  );
};

// ---- LeaveBoard Components ----

const getLeaveTypeBadge = (type) => {
  const badges = {
    vacation: { color: '#0366d6', bg: '#d4e4ff', label: 'Vacation', icon: '🏖️' },
    sick: { color: '#dc3545', bg: '#ffe5e9', label: 'Sick', icon: '🤒' },
    personal: { color: '#6f42c1', bg: '#f3e8ff', label: 'Personal', icon: '👤' },
    wfh: { color: '#28a745', bg: '#dcffe4', label: 'WFH', icon: '🏠' }
  };
  return badges[type] || { color: '#586069', bg: '#f6f8fa', label: type, icon: '📅' };
};

const getLeaveStatusBadge = (status) => {
  const badges = {
    pending: { color: '#e36209', bg: '#fff5cc', label: 'Pending' },
    approved: { color: '#28a745', bg: '#dcffe4', label: 'Approved' },
    rejected: { color: '#dc3545', bg: '#ffe5e9', label: 'Rejected' },
    cancelled: { color: '#586069', bg: '#f6f8fa', label: 'Cancelled' }
  };
  return badges[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const LeaveBalanceCards = ({ balance }) => {
  const cards = [
    { label: 'Vacation', total: balance.vacation_total, used: balance.vacation_used, remaining: balance.vacation_remaining, color: '#0366d6', icon: '🏖️' },
    { label: 'Sick Leave', total: balance.sick_total, used: balance.sick_used, remaining: balance.sick_remaining, color: '#dc3545', icon: '🤒' },
    { label: 'Personal', total: balance.personal_total, used: balance.personal_used, remaining: balance.personal_remaining, color: '#6f42c1', icon: '👤' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {cards.map(card => {
        const usedPercent = card.total > 0 ? Math.round((card.used / card.total) * 100) : 0;
        return (
          <div key={card.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-gray-600">{card.icon} {card.label}</div>
              <div className="text-2xl font-bold" style={{ color: card.color }}>{card.remaining}</div>
            </div>
            <div className="text-xs text-gray-500 mb-2">{card.used} used of {card.total} days</div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${usedPercent}%`, background: card.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RequestLeaveModal = ({ onClose, onSubmit, balance }) => {
  const [form, setForm] = useState({ leaveType: 'vacation', startDate: '', endDate: '', reason: '' });

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    let days = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return days;
  };

  const totalDays = calculateDays(form.startDate, form.endDate);
  const typeKey = form.leaveType === 'sick' ? 'sick' : form.leaveType === 'personal' ? 'personal' : 'vacation';
  const remaining = balance?.[`${typeKey}_remaining`] || 0;
  const insufficient = totalDays > remaining;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (insufficient) return;
    onSubmit({ ...form, totalDays });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Request Leave</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
            <select value={form.leaveType} onChange={e => setForm({...form, leaveType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="vacation">Vacation ({balance?.vacation_remaining || 0} days remaining)</option>
              <option value="sick">Sick Leave ({balance?.sick_remaining || 0} days remaining)</option>
              <option value="personal">Personal ({balance?.personal_remaining || 0} days remaining)</option>
              <option value="wfh">Work from Home</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          {totalDays > 0 && (
            <div className={`px-3 py-2 rounded-lg text-sm ${insufficient ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
              {totalDays} business day{totalDays > 1 ? 's' : ''} {insufficient ? '— Insufficient balance!' : 'requested'}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" disabled={insufficient || totalDays === 0} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Submit Request</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamAbsenceCalendar = ({ upcomingLeave }) => {
  // Build a simple month view
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthName = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const calDayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Map leave days
  const leaveMap = {};
  (upcomingLeave || []).forEach(leave => {
    const s = new Date(leave.startDate + 'T00:00:00');
    const e = new Date(leave.endDate + 'T00:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      if (!leaveMap[dateStr]) leaveMap[dateStr] = [];
      leaveMap[dateStr].push(leave);
    }
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1">
        {calDayLabels.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const leaveForDay = leaveMap[dateStr] || [];
          const isToday = day === now.getDate();
          const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;

          return (
            <div key={day} className={`text-center py-1.5 rounded text-xs relative ${isToday ? 'bg-blue-600 text-white font-bold' : isWeekend ? 'text-gray-300' : 'text-gray-700'} ${leaveForDay.length > 0 && !isToday ? 'bg-pink-100' : ''}`}
              title={leaveForDay.map(l => `${l.userName} (${l.leaveType})`).join(', ')}>
              {day}
              {leaveForDay.length > 0 && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {leaveForDay.slice(0, 3).map((l, j) => (
                    <div key={j} className="w-1 h-1 rounded-full" style={{ background: getLeaveTypeBadge(l.leaveType).color }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
        {upcomingLeave && [...new Set(upcomingLeave.map(l => l.userName))].map(name => {
          const leaves = upcomingLeave.filter(l => l.userName === name);
          return (
            <div key={name} className="text-xs text-gray-600">
              <span className="font-medium">{name}</span>: {leaves.map(l => {
                const badge = getLeaveTypeBadge(l.leaveType);
                return <span key={l.startDate} className="ml-1 px-1.5 py-0.5 rounded" style={{ background: badge.bg, color: badge.color }}>{badge.label} {l.startDate.slice(5)}</span>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LeaveBoardModule = ({ data, onRequestLeave, onApproveRequest, onRejectRequest }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">LeaveBoard</h1>
          <p className="text-gray-600 mt-1">Leave management & absence calendar</p>
        </div>
        <button onClick={onRequestLeave} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ Request Leave</button>
      </div>

      {/* Leave Balances */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">My Leave Balance</h2>
      <LeaveBalanceCards balance={data?.myBalance || {}} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Leave Requests */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Leave Requests</h2>
          <div className="space-y-3">
            {(data?.requests || []).map(req => {
              const typeBadge = getLeaveTypeBadge(req.leaveType);
              const statusBadge = getLeaveStatusBadge(req.status);
              return (
                <div key={req.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{req.userName.charAt(0)}</div>
                      <span className="font-medium text-sm text-gray-900">{req.userName}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.icon} {typeBadge.label}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    📅 {formatDate(req.startDate)} — {formatDate(req.endDate)} ({req.totalDays} day{req.totalDays > 1 ? 's' : ''})
                  </div>
                  {req.reason && <p className="text-sm text-gray-500 mb-2">{req.reason}</p>}
                  {req.status === 'pending' && (
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button onClick={() => onApproveRequest(req.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
                      <button onClick={() => onRejectRequest(req.id)} className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200">Reject</button>
                    </div>
                  )}
                  {req.status === 'approved' && req.approvedBy && (
                    <div className="text-xs text-gray-400">Approved by {req.approvedBy}</div>
                  )}
                </div>
              );
            })}
            {(!data?.requests || data.requests.length === 0) && (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No leave requests found.</div>
            )}
          </div>

          {/* Team Balances */}
          <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-4">Team Balances</h2>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Vacation</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Sick</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Personal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data?.teamBalances || []).map(tb => (
                  <tr key={tb.userName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{tb.userName}</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700 font-semibold">{tb.vacationRemaining}</td>
                    <td className="px-4 py-3 text-center text-sm text-red-600 font-semibold">{tb.sickRemaining}</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700 font-semibold">{tb.personalRemaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calendar */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Calendar</h2>
          <TeamAbsenceCalendar upcomingLeave={data?.upcomingLeave} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MEETINGBOARD MODULE VIEWS
// ============================================================================

const getMeetingStatusBadge = (status) => {
  const badges = {
    scheduled: { color: '#0366d6', bg: '#d4e4ff', label: 'Scheduled' },
    ongoing:   { color: '#28a745', bg: '#dcffe4', label: 'Live' },
    ended:     { color: '#586069', bg: '#f6f8fa', label: 'Ended' },
    cancelled: { color: '#dc3545', bg: '#ffe5e9', label: 'Cancelled' },
  };
  return badges[status] || badges.scheduled;
};

const ScheduleMeetingModal = ({ onClose, onSubmit, users = [] }) => {
  const now = new Date();
  const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    title: '', description: '', scheduledAt: localIso,
    durationMinutes: 60, meetingType: 'scheduled', participants: [], agenda: ''
  });
  const [errors, setErrors] = useState({});

  const toggleParticipant = (u) => {
    setForm(f => {
      const exists = f.participants.find(p => p.userId === u.user_id);
      return { ...f, participants: exists
        ? f.participants.filter(p => p.userId !== u.user_id)
        : [...f.participants, { userId: u.user_id, name: u.user_name }]
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">Schedule Meeting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
            <div className="flex gap-3">
              {[['scheduled', '📅 Scheduled'], ['instant', '⚡ Start Now']].map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => setForm(f => ({ ...f, meetingType: val }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.meetingType === val ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.title} onChange={e => { setForm(f => ({ ...f, title: e.target.value })); setErrors(ev => ({ ...ev, title: '' })); }}
              placeholder="e.g. Weekly Standup"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.title ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {form.meetingType === 'scheduled' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <select value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m/60}h`}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agenda</label>
            <textarea value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))} rows={2}
              placeholder="Meeting agenda or topics..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {users.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite Participants</label>
              <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                {users.map(u => {
                  const checked = !!form.participants.find(p => p.userId === u.user_id);
                  return (
                    <label key={u.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input type="checkbox" checked={checked} onChange={() => toggleParticipant(u)} className="rounded" />
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.user_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-700">{u.user_name}</span>
                      {u.is_active ? <span className="w-2 h-2 rounded-full bg-green-500 ml-auto shrink-0" /> : <span className="w-2 h-2 rounded-full bg-gray-300 ml-auto shrink-0" />}
                    </label>
                  );
                })}
              </div>
              {form.participants.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{form.participants.length} participant{form.participants.length > 1 ? 's' : ''} selected</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              {form.meetingType === 'instant' ? '⚡ Start Meeting' : '📅 Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MeetingNotesModal = ({ meeting, user, onClose, onSaved }) => {
  const [summary, setSummary] = React.useState(meeting.notesSummary || '');
  const [actionItems, setActionItems] = React.useState(meeting.notesActionItems || '');
  const [keyDecisions, setKeyDecisions] = React.useState(meeting.notesKeyDecisions || '');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const editorKey = meeting.id;

  const handleSave = async () => {
    setSaving(true);
    await ModulesModel.saveMeetingNotes(meeting.id, { summary, actionItems, keyDecisions });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved?.({ ...meeting, notesSummary: summary, notesActionItems: actionItems, notesKeyDecisions: keyDecisions, notesUpdatedBy: user?.user_name });
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const sanitize = (html) => {
    if (!html) return '';
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '').replace(/javascript\s*:/gi, '');
  };

  const NoteEditor = ({ label, value, onChange, edKey, placeholder }) => {
    const ref = React.useRef(null);
    const mountedRef = React.useRef(false);
    const [empty, setEmpty] = React.useState(!value);
    React.useEffect(() => {
      if (ref.current && !mountedRef.current) {
        mountedRef.current = true;
        if (value) { ref.current.innerHTML = value; setEmpty(false); }
      }
    }, []);
    const sync = () => {
      const html = ref.current?.innerHTML || '';
      const isEmpty = !html || html === '<br>' || html === '<div><br></div>';
      setEmpty(isEmpty);
      onChange(isEmpty ? '' : html);
    };
    const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); sync(); };
    const tbtn = (cmd, content, title) => (
      <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
        className="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-600 select-none">{content}</button>
    );
    return (
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
        <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 bg-white">
          <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-wrap">
            {tbtn('bold', <strong>B</strong>, 'Bold')}
            {tbtn('italic', <em>I</em>, 'Italic')}
            {tbtn('underline', <u>U</u>, 'Underline')}
            <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
            {tbtn('insertUnorderedList', '• List', 'Bullet list')}
            {tbtn('insertOrderedList', '1. List', 'Numbered list')}
            <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
            {tbtn('removeFormat', 'Aa✕', 'Clear')}
          </div>
          <div className="relative">
            {empty && <div className="absolute top-2 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
            <div ref={ref} contentEditable onInput={sync}
              onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
              className="min-h-[80px] max-h-52 overflow-y-auto px-3 py-2 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none"
              suppressContentEditableWarning />
          </div>
        </div>
      </div>
    );
  };

  const hasNotes = meeting.notesSummary || meeting.notesActionItems || meeting.notesKeyDecisions;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg">📝</span>
              <h2 className="text-lg font-semibold text-gray-900 truncate">Meeting Notes</h2>
            </div>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{meeting.title}</p>
            <p className="text-xs text-gray-400 mt-0.5">📅 {fmtDate(meeting.scheduledAt)} · 👤 {meeting.hostName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {meeting.notesUpdatedBy && (
            <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
              Last updated by <span className="font-medium text-gray-600">{meeting.notesUpdatedBy}</span>
              {meeting.notesUpdatedAt && <> · {fmtDate(meeting.notesUpdatedAt)}</>}
            </div>
          )}
          <NoteEditor key={`sum-${editorKey}`} label="📋 Summary" value={summary} onChange={setSummary} placeholder="Summarize what was discussed…" />
          <NoteEditor key={`act-${editorKey}`} label="✅ Action Items" value={actionItems} onChange={setActionItems} placeholder="List action items, owners, deadlines…" />
          <NoteEditor key={`dec-${editorKey}`} label="🔑 Key Decisions" value={keyDecisions} onChange={setKeyDecisions} placeholder="Record important decisions made…" />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">Notes are visible to all participants</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
            <button onClick={handleSave} disabled={saving}
              className={`px-5 py-2 text-sm rounded-lg font-medium text-white transition-colors ${saved ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'} disabled:opacity-50`}>
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MeetingCard = ({ meeting: initialMeeting, user, onEnd, onStart, onCancel, onDelete }) => {
  const [meeting, setMeeting] = React.useState(initialMeeting);
  const [showNotes, setShowNotes] = React.useState(false);
  const badge = getMeetingStatusBadge(meeting.status);
  const isHost = Number(meeting.hostId) === Number(user?.id);
  const isOngoing = meeting.status === 'ongoing';
  const isScheduled = meeting.status === 'scheduled';
  const isPast = meeting.status === 'ended' || meeting.status === 'cancelled';
  const joinUrl = `https://meet.jit.si/${meeting.roomCode}`;
  const hasNotes = meeting.notesSummary || meeting.notesActionItems || meeting.notesKeyDecisions;

  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const sanitize = (html) => html ? html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '').replace(/javascript\s*:/gi, '') : '';

  return (
    <>
    {showNotes && (
      <MeetingNotesModal meeting={meeting} user={user}
        onClose={() => setShowNotes(false)}
        onSaved={(updated) => setMeeting(updated)} />
    )}
    <div className={`bg-white rounded-lg shadow border p-4 ${isOngoing ? 'border-green-400 ring-1 ring-green-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{meeting.title}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            {isOngoing && <span className="flex items-center gap-1 text-xs text-green-600 font-medium animate-pulse">● Live</span>}
            {hasNotes && <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600 font-medium">📝 Notes</span>}
          </div>
          {meeting.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{meeting.description}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
        <span>👤 {meeting.hostName}</span>
        <span>📅 {fmtDate(meeting.scheduledAt)}</span>
        <span>⏱ {meeting.durationMinutes}m</span>
        {meeting.participants?.length > 0 && <span>👥 {meeting.participants.length} invited</span>}
      </div>
      {meeting.agenda && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-3 leading-relaxed">
          <span className="font-medium text-gray-700">Agenda: </span>{meeting.agenda}
        </div>
      )}
      {!isPast && (
        <div className="flex flex-wrap gap-2">
          <a href={joinUrl} target="_blank" rel="noreferrer"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 ${isOngoing ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            📹 {isOngoing ? 'Join Now' : 'Join'}
          </a>
          <button onClick={() => navigator.clipboard?.writeText(joinUrl)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50">
            🔗 Copy Link
          </button>
          {isHost && isScheduled && (
            <button onClick={() => onStart(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200">
              ▶ Start
            </button>
          )}
          {isHost && isOngoing && (
            <button onClick={() => onEnd(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">
              ■ End
            </button>
          )}
          {isHost && (
            <button onClick={() => onCancel(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50">
              Cancel
            </button>
          )}
          {isHost && (
            <button onClick={() => { if (window.confirm('Delete this meeting permanently?')) onDelete(meeting.id); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:bg-red-50 hover:text-red-600">
              🗑 Delete
            </button>
          )}
          <button onClick={() => setShowNotes(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${hasNotes ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            📝 {hasNotes ? 'View Notes' : 'Take Notes'}
          </button>
        </div>
      )}
      {isPast && (
        <div className="flex items-center gap-2 mt-1">
          {isHost && <button onClick={() => { if (window.confirm('Delete this meeting permanently?')) onDelete(meeting.id); }} className="text-xs text-gray-400 hover:text-red-500">🗑 Delete</button>}
          <button onClick={() => setShowNotes(true)}
            className={`text-xs font-medium flex items-center gap-1 ${hasNotes ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            📝 {hasNotes ? 'View Notes' : 'Take Notes'}
          </button>
        </div>
      )}
      {hasNotes && isPast && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          {meeting.notesSummary && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">📋 Summary</p>
              <div className="text-xs text-gray-700 prose prose-sm max-w-none line-clamp-3" dangerouslySetInnerHTML={{ __html: sanitize(meeting.notesSummary) }} />
            </div>
          )}
          {meeting.notesActionItems && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">✅ Action Items</p>
              <div className="text-xs text-gray-700 prose prose-sm max-w-none line-clamp-2" dangerouslySetInnerHTML={{ __html: sanitize(meeting.notesActionItems) }} />
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

const MeetingBoardModule = ({ data, user, onSchedule, onStart, onEnd, onCancel, onDelete }) => {
  const [tab, setTab] = useState('upcoming');
  const stats = data?.stats || {};
  const statCards = [
    { label: 'Total', value: stats.total || 0, icon: '📹', color: '#0ea5e9' },
    { label: 'Upcoming', value: stats.upcoming || 0, icon: '📅', color: '#6f42c1' },
    { label: 'Completed', value: stats.completed || 0, icon: '✅', color: '#28a745' },
    { label: 'Cancelled', value: stats.cancelled || 0, icon: '❌', color: '#dc3545' },
  ];

  const meetings = tab === 'upcoming' ? (data?.upcoming || []) : (data?.past || []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">MeetingBoard</h1>
          <p className="text-gray-600 mt-1">Video conferencing & team huddles powered by Jitsi Meet</p>
        </div>
        <button onClick={onSchedule} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap">
          + New Meeting
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {statCards.map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
            <div className="text-2xl mb-1">{c.icon}</div>
            <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Info banner */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mb-6 flex items-start gap-3 text-sm text-sky-800">
        <span className="text-xl shrink-0">📹</span>
        <div>
          <span className="font-semibold">Powered by Jitsi Meet — </span>
          free, browser-based video conferencing. No account or install needed. Clicking <strong>Join</strong> opens the meeting in a new tab.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {[['upcoming', 'Upcoming & Live'], ['past', 'Past Meetings']].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label} {val === 'upcoming' && data?.upcoming?.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{data.upcoming.length}</span>}
          </button>
        ))}
      </div>

      {/* Meeting list */}
      {meetings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📹</div>
          <p className="text-lg font-medium">{tab === 'upcoming' ? 'No upcoming meetings' : 'No past meetings'}</p>
          {tab === 'upcoming' && <p className="text-sm mt-1">Schedule a meeting or start an instant huddle</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map(m => (
            <MeetingCard key={m.id} meeting={m} user={user}
              onStart={onStart} onEnd={onEnd} onCancel={onCancel} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATAOPS MODULE VIEWS
// ============================================================================

const getDataOpsPipelineStatusBadge = (status) => {
  const badges = {
    running: { bg: '#d4e4ff', color: '#0366d6', label: 'Running' },
    idle: { bg: '#f6f8fa', color: '#586069', label: 'Idle' },
    queued: { bg: '#f3e8ff', color: '#6f42c1', label: 'Queued' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    failed: { bg: '#ffe5e9', color: '#dc3545', label: 'Failed' }
  };
  return badges[status] || badges.idle;
};

const formatPipelineDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatDataOpsDateTime = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const DataOpsMetricsCards = ({ metrics }) => {
  const cards = [
    { label: 'Active Pipelines', value: metrics.activePipelines, icon: '🔧', color: '#6f42c1' },
    { label: 'Data Processed', value: metrics.dataProcessed, icon: '💾', color: '#17a2b8' },
    { label: 'Reports Generated', value: metrics.reportsGenerated, icon: '📊', color: '#28a745' },
    { label: 'Success Rate', value: metrics.successRate, icon: '✓', color: '#20c997' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(card => (
        <div key={card.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{card.label}</span>
            <span className="text-2xl">{card.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</div>
        </div>
      ))}
    </div>
  );
};

const DataOpsActivePipelines = ({ pipelines, onTrigger }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Active Pipelines</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!pipelines || pipelines.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No active pipelines</div>
      ) : pipelines.map((p, i) => {
        const badge = getDataOpsPipelineStatusBadge(p.status);
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{p.name}</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{p.type}</span>
                {onTrigger && p.status !== 'running' && (
                  <button onClick={() => onTrigger(p.pipelineId)} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200">▶</button>
                )}
              </div>
            </div>
            {p.progress !== undefined && (
              <>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{p.progress}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: badge.color }} />
                </div>
              </>
            )}
            <div className="text-xs text-gray-500">{p.records?.toLocaleString()} records</div>
          </div>
        );
      })}
    </div>
  </div>
);

const DataOpsPowerBIReports = ({ reports }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!reports || reports.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No reports available</div>
      ) : reports.map((r, i) => (
        <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{r.icon}</span>
            <div>
              <div className="font-medium text-sm text-gray-900">{r.title}</div>
              <div className="text-xs text-gray-500">Updated {r.lastUpdated}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">👁 {r.views}</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DataOpsPipelinesView = ({ pipelines, onAdd, onPipelineClick, onTrigger }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">All Pipelines</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Pipeline</button>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Run</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(pipelines || []).map(p => {
            const badge = getDataOpsPipelineStatusBadge(p.status);
            return (
              <tr key={p.pipelineId} className="hover:bg-gray-50 cursor-pointer" onClick={() => onPipelineClick(p.pipelineId)}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.pipelineName}</div>
                  <div className="text-xs font-mono text-gray-500">{p.pipelineId}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.pipelineType}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatDataOpsDateTime(p.lastRun)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatPipelineDuration(p.durationSeconds)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{p.recordsProcessed?.toLocaleString() || 0}</td>
                <td className="px-4 py-3">
                  {p.progress != null ? (
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: badge.color }} />
                      </div>
                      <span className="text-xs text-gray-500">{p.progress}%</span>
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3">
                  <button onClick={(e) => { e.stopPropagation(); onTrigger(p.pipelineId); }}
                    disabled={p.status === 'running'}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    ▶ Run
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(!pipelines || pipelines.length === 0) && (
        <div className="p-8 text-center text-gray-400">No pipelines found. Create your first pipeline to get started.</div>
      )}
    </div>
  </div>
);

const DataOpsReportsView = ({ reports, onAdd, onReportClick, onToggleFavorite }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Report</button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(reports || []).map(r => {
        const published = r.status === 'Published';
        return (
          <div key={r.reportId} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onReportClick(r.reportId)}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{r.reportIcon}</span>
              <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(r.reportId, !r.isFavorite); }} className="text-xl">{r.isFavorite ? '⭐' : '☆'}</button>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{r.reportName}</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
              {r.category && <span className="text-xs text-gray-500">{r.category}</span>}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>👁 {r.viewCount} views</span>
              <span>{formatDataOpsDateTime(r.lastUpdated)}</span>
            </div>
          </div>
        );
      })}
    </div>
    {(!reports || reports.length === 0) && (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No reports found.</div>
    )}
  </div>
);

// ---- DataOps Kanban Board (QuickTrack tasks for DataOps team) ----
const DataOpsKanbanCard = ({ task, onStatusChange, onDelete, canDelete }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const priorityColors = { urgent: { bg: '#fef2f2', color: '#dc2626', label: 'Urgent' }, high: { bg: '#fff7ed', color: '#ea580c', label: 'High' }, medium: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' }, low: { bg: '#f0fdf4', color: '#16a34a', label: 'Low' } };
  const p = priorityColors[task.priority] || priorityColors.medium;
  const isOverdue = task.status !== 'done' && task.dueDate && task.dueDate < new Date().toISOString().split('T')[0];
  const nextStatus = { open: 'in_progress', in_progress: 'done', done: 'open' };
  const nextLabel = { open: '▶ Start', in_progress: '✓ Done', done: '↺ Reopen' };
  const handleStatusClick = () => {
    if (nextStatus[task.status] === 'done') { setShowActualTime(true); return; }
    onStatusChange(task.id, { status: nextStatus[task.status] });
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow">
      {showActualTime && (
        <ActualTimeModal
          taskTitle={task.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(task.id, { status: 'done', actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{p.label}</span>
        <div className="flex items-center gap-1">
          {task.category && <span className="text-xs text-gray-400 capitalize">{task.category}</span>}
          {canDelete && (
            <button onClick={() => requestConfirm(`Delete "${task.title}"?`, () => onDelete(task.id))}
              className="text-gray-300 hover:text-red-500 text-base leading-none ml-1" title="Delete task">×</button>
          )}
        </div>
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1 leading-snug">{task.title}</p>
      {task.description && <p className="text-xs text-gray-500 mb-2 truncate">{task.description}</p>}
      {task.projectName && <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block mb-2">{task.projectName}</p>}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {task.assignedTo && <span className="font-medium text-gray-700">{task.assignedTo}</span>}
          {task.dueDate && (
            <span className={`ml-2 ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : ''}{new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        <button onClick={handleStatusClick} className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
          {nextLabel[task.status]}
        </button>
      </div>
    </div>
  );
};

const DataOpsKanbanBoard = ({ kanban, onStatusChange, onDelete, userRole }) => {
  const canDelete = userRole === 'admin' || userRole === 'dataops_manager' || userRole === 'manager';
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const doneTodayTasks = (kanban?.done || []).filter(t =>
    !t.completedAt || new Date(t.completedAt).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) === todayStr
  );
  const columns = [
    { key: 'open', label: 'Open', color: 'bg-blue-500', tasks: kanban?.open || [] },
    { key: 'in_progress', label: 'In Progress', color: 'bg-amber-500', tasks: kanban?.in_progress || [] },
    { key: 'done', label: 'Done', color: 'bg-green-500', tasks: doneTodayTasks },
  ];
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Team Kanban Board</h2>
        <p className="text-xs text-gray-500 mt-0.5">Ad-hoc tasks assigned to DataOps team members</p>
      </div>
      {(!kanban || kanban.total === 0) ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm font-medium">No tasks for the DataOps team yet.</p>
          <p className="text-xs mt-1">Add a QuickTrack task and assign it to a DataOps team member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map(col => (
            <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${col.color}`} />
                <span className="font-semibold text-sm text-gray-800">{col.label}</span>
                <span className="ml-auto text-xs bg-white border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">{col.tasks.length}</span>
              </div>
              <div className="p-3 space-y-2 min-h-32">
                {col.tasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-xs">No tasks</div>
                ) : (
                  col.tasks.map(t => <DataOpsKanbanCard key={t.id} task={t} onStatusChange={onStatusChange} onDelete={onDelete} canDelete={canDelete} />)
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DataOpsModule = ({ data, viewMode, onViewModeChange, onAddPipeline, onAddReport, onPipelineClick, onReportClick, onTriggerPipeline, onToggleFavorite, onTaskClick, onStatusChange, onAddTask, onSendEod, onEodSettings, userRole, kanban, onKanbanStatusChange, onKanbanDelete }) => {
  const canManageEod = userRole === 'admin' || userRole === 'manager';
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">DataOps</h1>
        <p className="text-gray-600 mt-1">Data pipeline orchestration & Power BI integration</p>
      </div>
      <div className="flex gap-2">
        {canManageEod && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
        {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>}
        {viewMode === 'sprint' && onAddTask && (
          <button onClick={onAddTask} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Task</button>
        )}
      </div>
    </div>
    <div className="flex gap-2 mb-6 flex-wrap">
      {['dashboard', 'sprint', 'kanban', 'pipelines', 'reports'].map(mode => (
        <button key={mode} onClick={() => onViewModeChange(mode)}
          className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {mode === 'dashboard' ? '📊 Dashboard' : mode === 'sprint' ? '🗂️ Sprint Board' : mode === 'kanban' ? '🗃️ Kanban' : mode === 'pipelines' ? '🔧 Pipelines' : '📈 Reports'}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && data && (
      <>
        <DataOpsMetricsCards metrics={data.metrics} />
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <DataOpsActivePipelines pipelines={data.activePipelines} onTrigger={onTriggerPipeline} />
          <DataOpsPowerBIReports reports={data.powerbiReports} />
        </div>
      </>
    )}
    {viewMode === 'sprint' && (
      <>
        {data?.sprint ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
                <p className="text-sm text-gray-600">{data.sprint.goal}</p>
                <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold uppercase">{data.sprint.status}</span>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">No active sprint found.</div>
        )}
        {data?.sprintBoard && (
          <DevOpsPipelineBoard pipeline={data.sprintBoard} title="DataOps Sprint Board" onTaskClick={onTaskClick} onStatusChange={onStatusChange} />
        )}
      </>
    )}
    {viewMode === 'kanban' && (
      <DataOpsKanbanBoard kanban={kanban} onStatusChange={onKanbanStatusChange} onDelete={onKanbanDelete} userRole={userRole} />
    )}
    {viewMode === 'pipelines' && (
      <DataOpsPipelinesView pipelines={data?.allPipelines} onAdd={onAddPipeline} onPipelineClick={onPipelineClick} onTrigger={onTriggerPipeline} />
    )}
    {viewMode === 'reports' && (
      <DataOpsReportsView reports={data?.allReports} onAdd={onAddReport} onReportClick={onReportClick} onToggleFavorite={onToggleFavorite} />
    )}
  </div>
  );
};

// ============================================================================
// DATAOPS MODALS
// ============================================================================

const AddPipelineModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ pipelineName: '', pipelineType: 'ETL', description: '', scheduleCron: '', maxRetries: 3 });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Data Pipeline</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name *</label>
            <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily Sales ETL" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Type</label>
            <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this pipeline does..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron Expression)</label>
            <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., 0 2 * * * (2 AM daily)" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
            <input type="number" value={form.maxRetries} onChange={e => setForm({...form, maxRetries: parseInt(e.target.value)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" min="0" max="10" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Pipeline</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PipelineDetailModal = ({ pipeline, onClose, onUpdate, onDelete, onTrigger }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    pipelineName: pipeline.pipelineName || '', pipelineType: pipeline.pipelineType || 'ETL',
    description: pipeline.description || '', scheduleCron: pipeline.scheduleCron || '', maxRetries: pipeline.maxRetries || 3
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(pipeline.pipelineId, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete pipeline "${pipeline.pipelineName}"?`, () => onDelete(pipeline.pipelineId));
  const badge = getDataOpsPipelineStatusBadge(pipeline.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">{pipeline.pipelineName}</h2>
            <p className="text-xs font-mono text-gray-500 mt-1">{pipeline.pipelineId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded text-sm font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              <span className="text-sm text-gray-600">{pipeline.pipelineType}</span>
            </div>
            {pipeline.description && <p className="text-sm text-gray-600 mb-4">{pipeline.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Run</h3><p className="text-sm text-gray-900">{pipeline.lastRun ? new Date(pipeline.lastRun).toLocaleString() : 'Never'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{formatPipelineDuration(pipeline.durationSeconds)}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Records Processed</h3><p className="text-sm text-gray-900">{pipeline.recordsProcessed?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Schedule</h3><p className="text-sm text-gray-900 font-mono">{pipeline.scheduleCron || 'Manual'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Data Processed</h3><p className="text-sm text-gray-900">{pipeline.dataProcessedGb ? `${pipeline.dataProcessedGb} GB` : '-'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Retries</h3><p className="text-sm text-gray-900">{pipeline.retryCount || 0} / {pipeline.maxRetries || 3}</p></div>
            </div>
            {pipeline.progress != null && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Progress</span><span>{pipeline.progress}%</span></div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pipeline.progress}%`, background: badge.color }} />
                </div>
              </div>
            )}
            {pipeline.errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-xs font-semibold text-red-700 mb-1">Error</h3>
                <p className="text-sm text-red-600">{pipeline.errorMessage}</p>
              </div>
            )}
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => onTrigger(pipeline.pipelineId)} disabled={pipeline.status === 'running'}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">▶ Trigger Run</button>
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name</label>
              <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron)</label>
              <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const AddReportModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ reportName: '', reportIcon: '📊', workspaceId: '', datasetId: '', description: '', category: 'General', refreshSchedule: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Power BI Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
            <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Sales Performance Dashboard" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex gap-2 flex-wrap">
              {iconOptions.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
              <input value={form.workspaceId} onChange={e => setForm({...form, workspaceId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dataset ID</label>
              <input value={form.datasetId} onChange={e => setForm({...form, datasetId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
              <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this report shows..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Schedule</label>
            <input value={form.refreshSchedule} onChange={e => setForm({...form, refreshSchedule: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily at 6 AM" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Report</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReportDetailModal = ({ report, onClose, onUpdate, onDelete, onToggleFavorite }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    reportName: report.reportName || '', reportIcon: report.reportIcon || '📊',
    description: report.description || '', category: report.category || 'General',
    status: report.status || 'draft', refreshSchedule: report.refreshSchedule || ''
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(report.reportId, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete report "${report.reportName}"?`, () => onDelete(report.reportId));
  const published = report.status === 'Published';
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{report.reportIcon}</span>
            <div>
              <h2 className="text-xl font-semibold">{report.reportName}</h2>
              <p className="text-xs font-mono text-gray-500 mt-1">{report.reportId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded text-sm font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{report.status}</span>
              {report.category && <span className="text-sm text-gray-600">{report.category}</span>}
              <button onClick={() => onToggleFavorite(report.reportId, !report.isFavorite)} className="text-2xl ml-auto">{report.isFavorite ? '⭐' : '☆'}</button>
            </div>
            {report.description && <p className="text-sm text-gray-600 mb-4">{report.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">View Count</h3><p className="text-sm text-gray-900">{report.viewCount?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Updated</h3><p className="text-sm text-gray-900">{report.lastUpdated ? new Date(report.lastUpdated).toLocaleString() : 'Never'}</p></div>
              {report.workspaceId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Workspace ID</h3><p className="text-sm font-mono text-gray-900">{report.workspaceId}</p></div>}
              {report.datasetId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Dataset ID</h3><p className="text-sm font-mono text-gray-900">{report.datasetId}</p></div>}
              {report.refreshSchedule && <div className="col-span-2"><h3 className="text-xs font-semibold text-gray-500 mb-1">Refresh Schedule</h3><p className="text-sm text-gray-900">{report.refreshSchedule}</p></div>}
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
              <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
              <div className="flex gap-2 flex-wrap">
                {iconOptions.map(icon => (
                  <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                    className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
                <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="draft">Draft</option><option value="Published">Published</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// LEARNTRACK MODULE VIEWS
// ============================================================================

const getDifficultyBadge = (d) => {
  const badges = { beginner: { bg: '#dcffe4', color: '#28a745', label: 'Beginner' }, intermediate: { bg: '#fff3cd', color: '#856404', label: 'Intermediate' }, advanced: { bg: '#ffe5e9', color: '#dc3545', label: 'Advanced' } };
  return badges[d] || badges.beginner;
};

const getGroupLabel = (g) => {
  const labels = { all: 'All Users', admin: 'Admins', developer: 'Developers' };
  return labels[g] || g;
};

const getEnrollmentStatusBadge = (s) => {
  const badges = {
    assigned: { bg: '#f6f8fa', color: '#586069', label: 'Assigned' },
    in_progress: { bg: '#d4e4ff', color: '#0366d6', label: 'In Progress' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    dropped: { bg: '#ffe5e9', color: '#dc3545', label: 'Dropped' }
  };
  return badges[s] || badges.assigned;
};

const LearnTrackMetrics = ({ data }) => {
  const totalCourses = data?.courses?.length || 0;
  const myEnrolled = data?.myEnrollments?.length || 0;
  const myCompleted = (data?.myEnrollments || []).filter(e => e.status === 'completed').length;
  const myBadges = data?.myBadges?.length || 0;
  const cards = [
    { label: 'Total Courses', value: totalCourses, icon: '📚', color: '#fd7e14' },
    { label: 'My Enrollments', value: myEnrolled, icon: '📝', color: '#0366d6' },
    { label: 'Completed', value: myCompleted, icon: '✅', color: '#28a745' },
    { label: 'Badges Earned', value: myBadges, icon: '🏅', color: '#6f42c1' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const SkillsOverview = ({ skills, courses }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Skills</h2>
    <div className="flex flex-wrap gap-3">
      {(skills || []).map(s => {
        const count = (courses || []).filter(c => c.skillName === s.skillName).length;
        const categoryColors = { technical: '#0366d6', soft: '#fd7e14', leadership: '#6f42c1', compliance: '#dc3545' };
        return (
          <div key={s.id} className="bg-white rounded-lg shadow border border-gray-200 px-4 py-3 flex items-center gap-3">
            <div className="w-2 h-8 rounded-full" style={{ background: categoryColors[s.category] || '#586069' }} />
            <div>
              <div className="text-sm font-semibold text-gray-900">{s.skillName}</div>
              <div className="text-xs text-gray-500">{count} course{count !== 1 ? 's' : ''} · {s.category}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const TeamProgressTable = ({ teamProgress }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Team Progress</h2>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Enrolled</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">In Progress</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Completed</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Badges</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(teamProgress || []).map(tp => (
            <tr key={tp.userId} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{tp.userName}</td>
              <td className="px-4 py-3 text-center text-sm text-gray-600">{tp.totalEnrollments}</td>
              <td className="px-4 py-3 text-center text-sm text-blue-600 font-semibold">{tp.inProgressCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-green-600 font-semibold">{tp.completedCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-purple-600 font-semibold">{tp.badgeCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CourseCatalog = ({ courses, skills, onAdd, onCourseClick, onEnroll, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => (
  <div>
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Course Catalog</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterSkill} onChange={e => onFilterSkill(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All Skills</option>
          {(skills || []).map(s => <option key={s.id} value={s.skillName}>{s.skillName}</option>)}
        </select>
        <select value={filterGroup} onChange={e => onFilterGroup(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All Groups</option>
          <option value="all">All Users</option>
          <option value="developer">Developers</option>
          <option value="admin">Admins</option>
        </select>
        <button onClick={onAdd} className="px-4 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 font-medium">+ New Course</button>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(courses || []).filter(c => (!filterSkill || c.skillName === filterSkill) && (!filterGroup || c.targetGroup === filterGroup)).map(c => {
        const diff = getDifficultyBadge(c.difficulty);
        return (
          <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onCourseClick(c.id)}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{c.badgeIcon || '📚'}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{c.courseName}</h3>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {c.skillName && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{c.skillName}</span>}
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(c.targetGroup)}</span>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              {c.durationHours}h · {c.instructor || 'TBD'}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{c.enrollmentCount} enrolled · {c.completedCount} completed</span>
              <button onClick={(e) => { e.stopPropagation(); onEnroll({ courseId: c.id, userId: 2, userName: 'John Developer' }); }}
                className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200 font-medium">Enroll</button>
            </div>
          </div>
        );
      })}
    </div>
    {(!courses || courses.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No courses found.</div>}
  </div>
);

const MyEnrollments = ({ enrollments, onUpdateProgress }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Courses</h2>
    <div className="space-y-3">
      {(enrollments || []).map(e => {
        const badge = getEnrollmentStatusBadge(e.status);
        return (
          <div key={e.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{e.badgeIcon || '📚'}</span>
                <div>
                  <div className="font-medium text-sm text-gray-900">{e.courseName}</div>
                  <div className="text-xs text-gray-500">{e.skillName} · {e.difficulty} · {e.durationHours}h</div>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{e.progress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${e.progress}%`, background: e.status === 'completed' ? '#28a745' : '#0366d6' }} />
                </div>
              </div>
              {e.score != null && <span className="text-sm font-bold text-purple-600">Score: {e.score}%</span>}
              {e.status === 'assigned' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'in_progress' })} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Start</button>
              )}
              {e.status === 'in_progress' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'completed' })} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Complete</button>
              )}
            </div>
          </div>
        );
      })}
      {(!enrollments || enrollments.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No enrollments yet. Browse the course catalog to get started.</div>}
    </div>
  </div>
);

const MyBadges = ({ badges }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Badges</h2>
    {(!badges || badges.length === 0) ? (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No badges earned yet. Complete courses to earn badges!</div>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {badges.map(b => (
          <div key={b.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
            <div className="text-4xl mb-2">{b.badgeIcon}</div>
            <div className="text-sm font-semibold text-gray-900">{b.badgeName}</div>
            <div className="text-xs text-gray-500 mt-1">{b.description}</div>
            <div className="text-xs text-gray-400 mt-2">{b.awardedAt ? new Date(b.awardedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const LearnTrackModule = ({ data, viewMode, onViewModeChange, onAddCourse, onCourseClick, onEnroll, onUpdateProgress, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">LearnTrack</h1>
        <p className="text-gray-600 mt-1">Learning management, courses & skill development</p>
      </div>
    </div>
    <div className="flex gap-2 mb-6">
      {['dashboard', 'courses', 'mylearning'].map(mode => (
        <button key={mode} onClick={() => onViewModeChange(mode)}
          className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {mode === 'dashboard' ? '📊 Dashboard' : mode === 'courses' ? '📚 Courses' : '🎓 My Learning'}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && (
      <>
        <LearnTrackMetrics data={data} />
        <SkillsOverview skills={data?.skills} courses={data?.courses} />
        <TeamProgressTable teamProgress={data?.teamProgress} />
      </>
    )}
    {viewMode === 'courses' && (
      <CourseCatalog courses={data?.courses} skills={data?.skills} onAdd={onAddCourse} onCourseClick={onCourseClick} onEnroll={onEnroll}
        filterSkill={filterSkill} filterGroup={filterGroup} onFilterSkill={onFilterSkill} onFilterGroup={onFilterGroup} />
    )}
    {viewMode === 'mylearning' && (
      <>
        <MyEnrollments enrollments={data?.myEnrollments} onUpdateProgress={onUpdateProgress} />
        <MyBadges badges={data?.myBadges} />
      </>
    )}
  </div>
);

// ============================================================================
// LEARNTRACK MODALS
// ============================================================================

const AddCourseModal = ({ onClose, onSubmit, skills }) => {
  const [form, setForm] = useState({
    courseName: '', description: '', skillId: '', difficulty: 'beginner',
    durationHours: 1, instructor: '', targetGroup: 'all', badgeName: '', badgeIcon: '🏅'
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, skillId: form.skillId ? parseInt(form.skillId) : null });
  };
  const badgeIcons = ['🏅', '⚛️', '🟨', '🐍', '🚀', '👑', '🛡️', '⭐', '🎯', '💎', '🔥', '🌟'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Course</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
            <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Advanced Patterns" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
              <select value={form.skillId} onChange={e => setForm({...form, skillId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="">Select skill...</option>
                {(skills || []).map(s => <option key={s.id} value={s.id}>{s.skillName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
              <input type="number" min="0.5" step="0.5" value={form.durationHours} onChange={e => setForm({...form, durationHours: parseFloat(e.target.value)})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
              <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructor</label>
            <input value={form.instructor} onChange={e => setForm({...form, instructor: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Instructor name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Course description..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Name (awarded on completion)</label>
            <input value={form.badgeName} onChange={e => setForm({...form, badgeName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Pro" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Badge Icon</label>
            <div className="flex gap-2 flex-wrap">
              {badgeIcons.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, badgeIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-orange-500 ${form.badgeIcon === icon ? 'border-orange-600 bg-orange-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">Create Course</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CourseDetailModal = ({ course, onClose, onUpdate, onDelete, onEnroll }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    courseName: course.courseName || '', description: course.description || '',
    difficulty: course.difficulty || 'beginner', durationHours: course.durationHours || 1,
    instructor: course.instructor || '', targetGroup: course.targetGroup || 'all',
    status: course.status || 'published'
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(course.id, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete course "${course.courseName}"?`, () => onDelete(course.id));
  const diff = getDifficultyBadge(course.difficulty);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{course.badgeIcon || '📚'}</span>
            <div>
              <h2 className="text-xl font-semibold">{course.courseName}</h2>
              {course.skillName && <span className="text-xs text-blue-600">{course.skillName}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(course.targetGroup)}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{course.status}</span>
            </div>
            {course.description && <p className="text-sm text-gray-600 mb-4">{course.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{course.durationHours} hours</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Instructor</h3><p className="text-sm text-gray-900">{course.instructor || 'TBD'}</p></div>
              {course.badgeName && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Badge</h3><p className="text-sm text-gray-900">{course.badgeIcon} {course.badgeName}</p></div>}
            </div>

            {/* Enrolled Users */}
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Enrolled Users ({(course.enrolledUsers || []).length})</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
              {(course.enrolledUsers || []).length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">No enrollments yet</div>
              ) : (
                <table className="w-full">
                  <thead><tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">User</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Score</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200">
                    {(course.enrolledUsers || []).map(eu => {
                      const sb = getEnrollmentStatusBadge(eu.status);
                      return (
                        <tr key={eu.id}>
                          <td className="px-3 py-2 text-sm text-gray-900">{eu.userName}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: sb.bg, color: sb.color }}>{sb.label}</span></td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${eu.progress}%` }} /></div>
                              <span className="text-xs text-gray-500">{eu.progress}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">{eu.score != null ? `${eu.score}%` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex gap-2 mb-4">
              <button onClick={() => onEnroll({ courseId: course.id, userId: 1, userName: 'Jim Manager' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Jim</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 2, userName: 'John Developer' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll John</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 3, userName: 'Sarah Frontend' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Sarah</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 4, userName: 'Mike Fullstack' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Mike</button>
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name</label>
              <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
                <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// QMS (QUALITY MANAGEMENT SYSTEM) VIEWS
// ============================================================================

const qmsSeverityStyle = (s) => {
  const m = { critical: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#eff6ff', color: '#2563eb' } };
  return m[s] || m.medium;
};
const qmsStatusStyle = (s) => {
  const m = { pending: { bg: '#fefce8', color: '#ca8a04' }, approved: { bg: '#f0fdf4', color: '#16a34a' }, rejected: { bg: '#fef2f2', color: '#dc2626' }, pass: { bg: '#f0fdf4', color: '#16a34a' }, fail: { bg: '#fef2f2', color: '#dc2626' }, open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, resolved: { bg: '#f0fdf4', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
  return m[s] || m.pending;
};
const qmsStageLabels = { requirements: 'Review Requirements', testplan: 'Create Test Plan', testcases: 'Prepare Test Cases', testing: 'Execute Testing', uat: 'UAT Sign-off', verification: 'Production Verification' };

const QmsWorkflowSteps = ({ currentStage, onStageChange }) => {
  const stages = ['requirements', 'testplan', 'testcases', 'testing', 'uat', 'verification'];
  const icons = ['📋', '📝', '✅', '🧪', '✍️', '🚀'];
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-4">QA Workflow Stages</h2>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {stages.map((s, i) => (
          <button key={s} onClick={() => onStageChange(s)}
            className={`flex-1 min-w-[130px] px-3 py-3 rounded-lg text-center text-xs font-semibold transition-all ${currentStage === s ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <span className="text-lg block mb-1">{icons[i]}</span>
            {qmsStageLabels[s]}
            {i < stages.length - 1 && <span className="hidden sm:inline text-gray-300 ml-1">→</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

const QmsMetrics = ({ metrics, stage }) => {
  let cards = [];
  if (stage === 'requirements') {
    cards = [
      { label: 'Total Requirements', value: metrics?.requirements?.total || 0, sub: metrics?.requirements?.topLevel != null ? `${metrics.requirements.topLevel} top-level` : null, icon: '📋', color: '#20c997' },
      { label: 'Approved', value: metrics?.requirements?.approved || 0, icon: '✅', color: '#16a34a' },
      { label: 'Pending Review', value: metrics?.requirements?.pending || 0, icon: '⏳', color: '#d97706' }
    ];
  } else if (stage === 'testing' || stage === 'testcases') {
    cards = [
      { label: 'Total Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#20c997' },
      { label: 'Passed', value: metrics?.testCases?.passed || 0, icon: '✅', color: '#16a34a' },
      { label: 'Failed', value: metrics?.testCases?.failed || 0, icon: '❌', color: '#dc2626' }
    ];
  } else if (stage === 'uat' || stage === 'verification') {
    cards = [
      { label: 'Pass Rate', value: `${metrics?.passRate || 0}%`, icon: '📊', color: '#16a34a' },
      { label: 'Open Defects', value: metrics?.bugs?.open || 0, icon: '🐛', color: '#ea580c' },
      { label: 'Resolved', value: metrics?.bugs?.resolved || 0, icon: '✅', color: '#20c997' }
    ];
  } else {
    cards = [
      { label: 'Requirements', value: metrics?.requirements?.total || 0, icon: '📋', color: '#20c997' },
      { label: 'Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#2563eb' },
      { label: 'Bugs', value: metrics?.bugs?.total || 0, icon: '🐛', color: '#ea580c' }
    ];
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
          {c.sub && <div className="text-xs text-gray-400 mt-1">{c.sub}</div>}
        </div>
      ))}
    </div>
  );
};

const REQ_TYPE_COLOR_PALETTE = {
  teal:   { header: '#0f766e', light: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
  red:    { header: '#b91c1c', light: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
  blue:   { header: '#1d4ed8', light: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  purple: { header: '#7e22ce', light: '#faf5ff', border: '#e9d5ff', text: '#7e22ce' },
  orange: { header: '#c2410c', light: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
  green:  { header: '#15803d', light: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  amber:  { header: '#b45309', light: '#fffbeb', border: '#fde68a', text: '#b45309' },
  pink:   { header: '#be185d', light: '#fdf2f8', border: '#fbcfe8', text: '#be185d' },
  slate:  { header: '#334155', light: '#f8fafc', border: '#cbd5e1', text: '#334155' },
};

const DEFAULT_REQ_TYPES = [
  { id: 1, name: 'functional',  label: 'Functional Requirements',  color: 'teal',   sort_order: 1 },
  { id: 2, name: 'security',    label: 'Security Requirements',    color: 'red',    sort_order: 2 },
  { id: 3, name: 'performance', label: 'Performance Requirements', color: 'blue',   sort_order: 3 },
  { id: 4, name: 'usability',   label: 'Usability Requirements',   color: 'purple', sort_order: 4 },
];

const ManageReqTypesModal = ({ onClose, onRefresh }) => {
  const [types, setTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', color: 'teal' });
  const [newForm, setNewForm] = useState({ label: '', color: 'teal' });
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');

  const load = () => ModulesModel.getReqTypes().then(ts => setTypes(Array.isArray(ts) ? ts : []));
  useEffect(() => { load(); }, []);

  const startEdit = t => { setEditingId(t.id); setEditForm({ label: t.label, color: t.color || 'teal' }); setError(''); };
  const cancelEdit = () => { setEditingId(null); setError(''); };

  const saveEdit = async () => {
    if (!editForm.label.trim()) { setError('Label is required'); return; }
    const r = await ModulesModel.updateReqType(editingId, editForm);
    if (r?.success === false) { setError(r.error || 'Failed to save'); return; }
    setEditingId(null); setError(''); load(); onRefresh();
  };

  const handleDelete = async (id) => {
    const r = await ModulesModel.deleteReqType(id);
    if (r?.success === false) { setError(r.error || 'Cannot delete'); return; }
    setError(''); load(); onRefresh();
  };

  const handleAdd = async () => {
    if (!newForm.label.trim()) { setError('Label is required'); return; }
    const r = await ModulesModel.createReqType(newForm);
    if (r?.success === false) { setError(r.error || 'Failed to add'); return; }
    setNewForm({ label: '', color: 'teal' }); setShowAdd(false); setError(''); load(); onRefresh();
  };

  const ColorSwatch = ({ selected, onChange }) => (
    <div className="flex gap-1.5 flex-wrap">
      {Object.keys(REQ_TYPE_COLOR_PALETTE).map(c => (
        <button key={c} type="button" onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${selected === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
          style={{ background: REQ_TYPE_COLOR_PALETTE[c].header }} title={c} />
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Manage Requirement Types</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <div className="p-6 space-y-2 max-h-[70vh] overflow-y-auto">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</p>}
          {types.map(t => {
            const st = REQ_TYPE_COLOR_PALETTE[t.color] || REQ_TYPE_COLOR_PALETTE.teal;
            if (editingId === t.id) {
              return (
                <div key={t.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                    <input value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Color</label>
                    <ColorSwatch selected={editForm.color} onChange={c => setEditForm(f => ({ ...f, color: c }))} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
                    <button onClick={cancelEdit} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg group hover:bg-gray-50">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: st.header }} />
                <span className="flex-1 text-sm font-medium text-gray-800">{t.label}</span>
                <span className="text-xs text-gray-400 font-mono">{t.name}</span>
                <button onClick={() => startEdit(t)} className="p-1.5 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={14} /></button>
                <button onClick={() => requestConfirm(`Delete type "${t.label}"?`, () => handleDelete(t.id))} className="p-1.5 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </div>
            );
          })}

          {/* Add new type */}
          {showAdd ? (
            <div className="border border-teal-200 bg-teal-50 rounded-lg p-4 space-y-3 mt-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
                <input value={newForm.label} onChange={e => setNewForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Business Requirements, Compliance Requirements…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Color</label>
                <ColorSwatch selected={newForm.color} onChange={c => setNewForm(f => ({ ...f, color: c }))} />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAdd} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Add Type</button>
                <button onClick={() => { setShowAdd(false); setNewForm({ label: '', color: 'teal' }); setError(''); }}
                  className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setShowAdd(true); setEditingId(null); }}
              className="w-full mt-2 px-4 py-2.5 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg text-sm font-medium hover:border-teal-400 hover:text-teal-600 transition-colors">
              + Add New Requirement Type
            </button>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>
  );
};

const QmsRequirementsStage = ({ requirements, projectId, onApprove, onReject, onAdd, onNext, onEdit, onDelete, onRefresh }) => {
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reqTypes, setReqTypes] = useState(DEFAULT_REQ_TYPES);
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // { type, cat }
  const [editCatVal, setEditCatVal] = useState('');

  const loadTypes = () => ModulesModel.getReqTypes().then(ts => { if (Array.isArray(ts) && ts.length > 0) setReqTypes(ts); });
  useEffect(() => { loadTypes(); }, []);

  const TYPE_LABELS = Object.fromEntries(reqTypes.map(t => [t.name, t.label]));
  const TYPE_STYLES = Object.fromEntries(reqTypes.map(t => [t.name, REQ_TYPE_COLOR_PALETTE[t.color] || REQ_TYPE_COLOR_PALETTE.teal]));
  const TYPE_ORDER = reqTypes.map(t => t.name);

  const allReqs = requirements || [];
  const allReqIds = new Set(allReqs.map(r => r.id));
  // Top-level: no parent, OR parent was deleted (orphaned sub-req)
  const topLevel = allReqs.filter(r => !r.parentRequirementId || !allReqIds.has(r.parentRequirementId));
  const subReqsMap = {};
  allReqs.filter(r => r.parentRequirementId && allReqIds.has(r.parentRequirementId)).forEach(r => {
    if (!subReqsMap[r.parentRequirementId]) subReqsMap[r.parentRequirementId] = [];
    subReqsMap[r.parentRequirementId].push(r);
  });

  // Group top-level: reqType → parentCategory → requirements
  const byType = {};
  topLevel.forEach(r => {
    const type = r.reqType || 'functional';
    if (!byType[type]) byType[type] = {};
    const cat = r.parentCategory || '';
    if (!byType[type][cat]) byType[type][cat] = [];
    byType[type][cat].push(r);
  });
  const types = Object.keys(byType).sort((a, b) => (TYPE_ORDER.indexOf(a) + 1 || 99) - (TYPE_ORDER.indexOf(b) + 1 || 99));

  const toggleKey = k => setCollapsed(c => ({ ...c, [k]: !c[k] }));

  const startEdit = r => {
    setEditingId(r.id);
    setEditForm({ title: r.title, description: r.description || '', reqType: r.reqType, priority: r.priority, parentCategory: r.parentCategory || '' });
  };
  const saveEdit = async () => { await onEdit(editingId, editForm); setEditingId(null); };

  const EditForm = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-2">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select value={editForm.reqType} onChange={e => setEditForm(f => ({ ...f, reqType: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
              {reqTypes.map(t => <option key={t.name} value={t.name}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
            <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <input value={editForm.parentCategory} onChange={e => setEditForm(f => ({ ...f, parentCategory: e.target.value }))}
              placeholder="Category…" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        <div className="flex gap-2">
          <button onClick={saveEdit} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
          <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );

  // Level 4: sub-requirement
  const renderSubReq = r => {
    const ss = qmsStatusStyle(r.status);
    const ps = qmsSeverityStyle(r.priority);
    if (editingId === r.id) return <div key={r.id}><EditForm /></div>;
    return (
      <div key={r.id} className="flex items-start gap-2 py-2 px-3 bg-white border border-gray-100 rounded-lg mb-1.5 group hover:border-gray-200">
        <span className="text-gray-300 font-mono text-xs mt-0.5 select-none shrink-0">&gt;&gt;&gt;</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{r.reqCode}{r.title ? `: ${r.title}` : ''}</span>
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{r.status}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{r.priority}</span>
              <button onClick={() => startEdit(r)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={11} /></button>
              <button onClick={() => requestConfirm(`Delete "${r.reqCode}"?`, () => onDelete(r.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={11} /></button>
            </div>
          </div>
          {r.description && <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>}
          {r.status === 'pending' && (
            <div className="flex gap-1.5 mt-1.5">
              <button onClick={() => onApprove(r.id)} className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">✓ Approve</button>
              <button onClick={() => onReject(r.id)} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">✗ Reject</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Level 3: requirement card
  const renderReq = r => {
    const ss = qmsStatusStyle(r.status);
    const ps = qmsSeverityStyle(r.priority);
    const children = subReqsMap[r.id] || [];
    if (editingId === r.id) return <div key={r.id} className="mb-3"><EditForm /></div>;
    return (
      <div key={r.id} className="mb-3">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 group" style={{ borderLeftWidth: '3px', borderLeftColor: ps.color }}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-gray-300 font-mono text-xs shrink-0 select-none">&gt;&gt;</span>
              <h4 className="font-semibold text-gray-900 text-sm">{r.reqCode}: {r.title}</h4>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{r.status}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{r.priority}</span>
              <button onClick={() => startEdit(r)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={13} /></button>
              <button onClick={() => requestConfirm(`Delete "${r.reqCode}${r.title ? ': ' + r.title : ''}"?`, () => onDelete(r.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
            </div>
          </div>
          {r.description && <p className="text-xs text-gray-500 ml-5 mb-2">{r.description}</p>}
          <div className="flex items-center gap-2 ml-5 flex-wrap">
            {r.status === 'pending' && (
              <>
                <button onClick={() => onApprove(r.id)} className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">✓ Approve</button>
                <button onClick={() => onReject(r.id)} className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">✗ Reject</button>
              </>
            )}
            <button onClick={() => onAdd({ parentRequirementId: r.id, reqType: r.reqType, parentCategory: r.parentCategory })}
              className="px-2.5 py-1 border border-dashed border-gray-300 text-gray-400 rounded text-xs hover:border-teal-400 hover:text-teal-600 ml-auto">
              + Sub-requirement
            </button>
          </div>
        </div>
        {children.length > 0 && (
          <div className="ml-6 mt-1 border-l-2 border-gray-100 pl-3 space-y-0">
            {children.map(sub => renderSubReq(sub))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {showManageTypes && <ManageReqTypesModal onClose={() => setShowManageTypes(false)} onRefresh={loadTypes} />}
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-lg font-bold text-gray-900">Requirements</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowManageTypes(true)}
            className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5">
            <Settings size={14} /> Manage Types
          </button>
          <button onClick={() => onAdd({})} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Requirement
          </button>
        </div>
      </div>

      {types.length === 0 && <p className="text-sm text-gray-400 py-4">No requirements yet.</p>}

      {types.map(type => {
        const st = TYPE_STYLES[type] || TYPE_STYLES.functional;
        const cats = byType[type];
        const totalCount = Object.values(cats).reduce((s, a) => s + a.length, 0);
        const isTypeCollapsed = collapsed[`type:${type}`];
        return (
          <div key={type} className="mb-6">
            {/* Level 1: req_type section */}
            <button onClick={() => toggleKey(`type:${type}`)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wide mb-3"
              style={{ background: st.header }}>
              <span>{TYPE_LABELS[type] || type}</span>
              <span className="flex items-center gap-2">
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-medium">{totalCount}</span>
                <ChevronDown size={16} className={`transition-transform ${isTypeCollapsed ? '-rotate-90' : ''}`} />
              </span>
            </button>

            {!isTypeCollapsed && (
              <div className="ml-2">
                {Object.keys(cats).sort((a, b) => { if (a === '') return 1; if (b === '') return -1; return a.localeCompare(b); }).map(cat => {
                  const items = cats[cat];
                  const hasCat = cat !== '';
                  const isCatCollapsed = collapsed[`cat:${type}:${cat}`];
                  return (
                    <div key={cat || '__none__'} className="mb-4">
                      {/* Level 2: category / feature */}
                      {hasCat ? (
                        editingCat?.type === type && editingCat?.cat === cat ? (
                          <div className="flex items-center gap-2 mb-2 px-1">
                            <span className="font-mono text-xs text-gray-400 shrink-0">&gt;</span>
                            <input autoFocus value={editCatVal} onChange={e => setEditCatVal(e.target.value)}
                              onKeyDown={async e => {
                                if (e.key === 'Enter') { await ModulesModel.renameReqCategory(projectId, cat, editCatVal); setEditingCat(null); onRefresh && onRefresh(); }
                                if (e.key === 'Escape') setEditingCat(null);
                              }}
                              className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-teal-400" />
                            <button onClick={async () => { await ModulesModel.renameReqCategory(projectId, cat, editCatVal); setEditingCat(null); onRefresh && onRefresh(); }}
                              className="px-2.5 py-1 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700">Save</button>
                            <button onClick={() => setEditingCat(null)}
                              className="px-2.5 py-1 border border-gray-200 text-gray-500 rounded text-xs hover:bg-gray-50">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 mb-2 group/cat">
                            <button onClick={() => toggleKey(`cat:${type}:${cat}`)}
                              className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold hover:opacity-80"
                              style={{ background: st.light, borderColor: st.border, color: st.text }}>
                              <ChevronRight size={14} className={`transition-transform ${!isCatCollapsed ? 'rotate-90' : ''}`} />
                              <span className="font-mono text-xs opacity-50">&gt;</span>
                              {cat}
                              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ background: st.border, color: st.text }}>{items.length}</span>
                            </button>
                            <button onClick={() => { setEditingCat({ type, cat }); setEditCatVal(cat); }}
                              className="p-1.5 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover/cat:opacity-100 transition-opacity shrink-0">
                              <Pencil size={13} />
                            </button>
                          </div>
                        )
                      ) : (
                        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
                          <span>Uncategorized</span>
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">{items.length}</span>
                        </div>
                      )}
                      {/* Level 3+4: requirements and their sub-requirements */}
                      {!isCatCollapsed && (
                        <div className="ml-4 border-l-2 pl-3" style={{ borderColor: st.border }}>
                          {items.map(r => renderReq(r))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <button onClick={onNext} className="mt-4 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to Test Plan →</button>
    </div>
  );
};

const QmsTestPlanStage = ({ project, onUpdate, onNext, onBack }) => {
  const [testPlanTitle, setTestPlanTitle] = useState(project?.testPlanTitle || '');
  const [env, setEnv] = useState(project?.environment || 'DEV');
  const [targetDate, setTargetDate] = useState(project?.targetDate || '');
  const [qaLeadId, setQaLeadId] = useState(project?.qaLeadId ? String(project.qaLeadId) : '');
  const [qaLeads, setQaLeads] = useState([]);

  useEffect(() => {
    ModulesModel.getQaLeads().then(leads => setQaLeads(leads));
  }, []);

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Test Plan</h3>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Test Plan Title *</label>
          <input type="text" value={testPlanTitle} onChange={e => setTestPlanTitle(e.target.value)}
            placeholder="e.g. Login Feature Test Plan, Payment Module QA Plan…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
          <input type="text" value={project?.projectName || ''} readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">QA Lead</label>
          <select value={qaLeadId} onChange={e => setQaLeadId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
            <option value="">— Select QA Lead —</option>
            {qaLeads.map(u => (
              <option key={u.id} value={String(u.id)}>{u.user_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Test Environment</label>
          <select value={env} onChange={e => setEnv(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
            <option value="DEV">DEV Environment</option>
            <option value="UAT">UAT Environment</option>
            <option value="Staging">Staging Environment</option>
            <option value="Production">Production</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Completion</label>
          <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={() => { onUpdate(project?.projectId, { testPlanTitle: testPlanTitle.trim() || null, environment: env, targetDate, qaLeadId: qaLeadId || null }); onNext(); }} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Save & Proceed to Test Cases →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsTestCasesStage = ({ testCases, requirements = [], onAdd, onEdit, onDelete, onNext, onBack }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [tcModuleList, setTcModuleList] = useState([]);
  const [showManageMods, setShowManageMods] = useState(false);

  const loadTcModules = async () => { setTcModuleList(await ModulesModel.getTcModules()); };
  useEffect(() => { loadTcModules(); }, []);

  const toggleKey = k => setCollapsed(c => ({ ...c, [k]: !c[k] }));

  const startEdit = (tc) => {
    setEditingId(tc.id);
    setEditForm({ title: tc.title, module: tc.module || '', priority: tc.priority, prerequisites: tc.prerequisites || '', requirementId: tc.requirementId || null });
  };
  const saveEdit = async () => { await onEdit(editingId, editForm); setEditingId(null); };

  const allTcs = testCases || [];

  // Group by module (like req_type grouping)
  const byModule = {};
  allTcs.forEach(tc => {
    const mod = tc.module || 'Unassigned';
    if (!byModule[mod]) byModule[mod] = [];
    byModule[mod].push(tc);
  });
  const modules = Object.keys(byModule).sort((a, b) => a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b));

  // Module header color (cycles through a small palette)
  const MODULE_COLORS = [
    { header: '#0f766e', light: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
    { header: '#1d4ed8', light: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
    { header: '#7c3aed', light: '#f5f3ff', border: '#ddd6fe', text: '#7c3aed' },
    { header: '#b45309', light: '#fffbeb', border: '#fde68a', text: '#b45309' },
    { header: '#be123c', light: '#fff1f2', border: '#fecdd3', text: '#be123c' },
  ];

  const renderStep = (step, idx) => (
    <div key={idx} className="flex items-start gap-2 py-2 px-3 bg-white border border-gray-100 rounded-lg mb-1.5 group">
      <span className="text-gray-300 font-mono text-xs mt-0.5 select-none shrink-0">&gt;&gt;&gt;</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 shrink-0">Step {idx + 1}</span>
          <span className="text-sm text-gray-800">{step.action || <span className="italic text-gray-400">No action</span>}</span>
        </div>
        {step.expected && (
          <p className="text-xs text-teal-700 mt-0.5">
            <span className="font-medium">Expected:</span> {step.expected}
          </p>
        )}
      </div>
    </div>
  );

  const renderTc = (tc, modColor) => {
    const ss = qmsStatusStyle(tc.status);
    const ps = qmsSeverityStyle(tc.priority);
    const steps = Array.isArray(tc.steps) ? tc.steps : (tc.steps ? (() => { try { return JSON.parse(tc.steps); } catch { return []; } })() : []);
    const stepsCollapsed = collapsed[`steps:${tc.id}`];
    const linkedReq = tc.requirementId ? requirements.find(r => r.id === tc.requirementId) : null;

    if (editingId === tc.id) {
      return (
        <div key={tc.id} className="mb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-600">Module</label>
                    <button type="button" onClick={() => setShowManageMods(true)} className="text-xs text-teal-600 hover:text-teal-700 font-medium">⚙ Manage</button>
                  </div>
                  <select value={editForm.module} onChange={e => setEditForm(f => ({ ...f, module: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                    <option value="">— Select module —</option>
                    {tcModuleList.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                    <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prerequisites</label>
                <textarea value={editForm.prerequisites} onChange={e => setEditForm(f => ({ ...f, prerequisites: e.target.value }))} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
              {requirements.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Linked Requirement</label>
                  <select value={editForm.requirementId || ''} onChange={e => setEditForm(f => ({ ...f, requirementId: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                    <option value="">— None —</option>
                    {requirements.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={saveEdit} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
                <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={tc.id} className="mb-3">
        {/* Test case card — mirrors requirement card (Level 3) */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 group" style={{ borderLeftWidth: '3px', borderLeftColor: ps.color }}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-gray-300 font-mono text-xs shrink-0 select-none">&gt;&gt;</span>
              <h4 className="font-semibold text-gray-900 text-sm">{tc.tcCode}: {tc.title}</h4>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{tc.status}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{tc.priority}</span>
              <button onClick={() => startEdit(tc)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={13} /></button>
              <button onClick={() => requestConfirm(`Delete test case "${tc.tcCode}"?`, () => onDelete(tc.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
            </div>
          </div>
          {tc.prerequisites && <p className="text-xs text-gray-500 ml-5 mb-1"><span className="font-medium">Prerequisites:</span> {tc.prerequisites}</p>}
          {linkedReq && (
            <p className="text-xs text-teal-700 ml-5 mb-1">
              <span className="font-medium">Req:</span> {linkedReq.reqCode}: {linkedReq.title}
            </p>
          )}
          {steps.length > 0 && (
            <button onClick={() => toggleKey(`steps:${tc.id}`)}
              className="ml-5 mt-1 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 font-medium">
              <ChevronRight size={12} className={`transition-transform ${!stepsCollapsed ? 'rotate-90' : ''}`} />
              {steps.length} step{steps.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        {/* Steps — mirrors sub-requirements (Level 4) */}
        {steps.length > 0 && !stepsCollapsed && (
          <div className="ml-6 mt-1 border-l-2 border-gray-100 pl-3">
            {steps.map((step, i) => renderStep(step, i))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {showManageMods && (
        <ManageTcModulesModal onClose={() => setShowManageMods(false)} onSaved={loadTcModules} />
      )}
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-lg font-bold text-gray-900">Test Cases</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowManageMods(true)} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5">
            ⚙ Manage Modules
          </button>
          <button onClick={onAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Test Case
          </button>
        </div>
      </div>

      {modules.length === 0 && <p className="text-sm text-gray-400 py-4">No test cases yet.</p>}

      {modules.map((mod, modIdx) => {
        const items = byModule[mod];
        const modColor = MODULE_COLORS[modIdx % MODULE_COLORS.length];
        const isModCollapsed = collapsed[`mod:${mod}`];
        return (
          <div key={mod} className="mb-6">
            {/* Level 1: module section header */}
            <button onClick={() => toggleKey(`mod:${mod}`)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wide mb-3"
              style={{ background: modColor.header }}>
              <span>{mod}</span>
              <span className="flex items-center gap-2">
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-medium">{items.length}</span>
                <ChevronDown size={16} className={`transition-transform ${isModCollapsed ? '-rotate-90' : ''}`} />
              </span>
            </button>
            {!isModCollapsed && (
              <div className="ml-2 border-l-2 pl-3" style={{ borderColor: modColor.border }}>
                {items.map(tc => renderTc(tc, modColor))}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex gap-3 mt-4">
        <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Start Testing →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsTestingStage = ({ testCases, bugs, metrics, onPass, onFail, onReportBug, onEditBug, onNext, onBack }) => {
  const total = metrics?.testCases?.total || 0;
  const executed = (metrics?.testCases?.passed || 0) + (metrics?.testCases?.failed || 0);
  const progress = total > 0 ? Math.round((executed / total) * 100) : 0;
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Test Execution</h3>
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Test Progress</span>
          <span>{progress}% Complete</span>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(testCases || []).map(tc => {
              const ss = qmsStatusStyle(tc.status);
              return (
                <tr key={tc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{tc.tcCode}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{tc.title}</td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{tc.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {tc.status === 'pending' && (
                        <>
                          <button onClick={() => onPass(tc.id)} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">Pass</button>
                          <button onClick={() => onFail(tc.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200">Fail</button>
                        </>
                      )}
                      {tc.status === 'fail' && (
                        <button onClick={() => onReportBug(tc)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200">Report Bug</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {bugs && bugs.filter(b => b.status === 'open' || b.status === 'in_progress').length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Open Bugs</h4>
          {bugs.filter(b => b.status === 'open' || b.status === 'in_progress').map(b => {
            const bs = qmsSeverityStyle(b.severity);
            return (
              <div key={b.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-2" style={{ borderLeftWidth: '4px', borderLeftColor: bs.color }}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-900">{b.bugCode}: {b.title}</span>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
                    <button onClick={() => onEditBug(b)} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">Edit</button>
                  </div>
                </div>
                {b.assignedTo && <p className="text-xs text-gray-500 mt-1">Assigned: {b.assignedTo}</p>}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to UAT →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
        <button onClick={() => onReportBug(null)} className="ml-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200">🐛 Report Bug</button>
      </div>
    </div>
  );
};

const QmsUatStage = ({ metrics, bugs, project, onUpdateProject, onNext, onBack }) => {
  const noBlockers = !(bugs || []).some(b => b.severity === 'critical' && b.status === 'open');
  const passRate = metrics?.passRate || 0;
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">UAT Sign-off</h3>
      <div className={`p-5 rounded-lg mb-6 border-l-4 ${noBlockers && passRate >= 80 ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
        <h4 className={`font-semibold mb-1 ${noBlockers && passRate >= 80 ? 'text-green-800' : 'text-yellow-800'}`}>
          {noBlockers && passRate >= 80 ? '✓ Ready for UAT Sign-off' : '⚠️ Not Ready — Issues Remain'}
        </h4>
        <p className={`text-sm ${noBlockers && passRate >= 80 ? 'text-green-700' : 'text-yellow-700'}`}>
          {noBlockers && passRate >= 80
            ? 'All critical test cases passed. No blocker issues identified.'
            : `Pass rate: ${passRate}%. Open critical bugs: ${(bugs || []).filter(b => b.severity === 'critical' && b.status === 'open').length}`}
        </p>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3 mb-6">
        <div className="flex justify-between text-sm"><span className="text-gray-600">QA Lead</span><span className="font-medium text-gray-900">{project?.qaLead || '-'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Total Test Cases</span><span className="font-medium text-gray-900">{metrics?.testCases?.total || 0}</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Pass Rate</span><span className="font-medium text-green-600">{passRate}%</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Open Defects</span><span className="font-medium text-orange-600">{metrics?.bugs?.open || 0}</span></div>
      </div>
      <div className="flex gap-3">
        <button onClick={() => { onUpdateProject(project?.projectId, { status: 'production', currentStage: 'verification' }); onNext(); }}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700" disabled={!noBlockers}>
          Submit Sign-off & Deploy →
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsVerificationStage = ({ project, onUpdateProject, onBack }) => {
  const [checks, setChecks] = useState({});
  const items = [
    { key: 'accessible', label: 'Verify application is accessible', desc: 'URL: https://app.company.com' },
    { key: 'login', label: 'Test user login functionality', desc: 'Verify authentication works with production credentials' },
    { key: 'database', label: 'Verify database connections', desc: 'Ensure all database operations are functioning correctly' },
    { key: 'workflows', label: 'Test critical business workflows', desc: 'Settlement calculations, payment processing, reporting' },
    { key: 'health', label: 'Monitor system health', desc: 'Check server logs, performance metrics, error rates' },
    { key: 'integrations', label: 'Verify third-party integrations', desc: 'CRM, API gateways, email services, etc.' }
  ];
  const allChecked = items.every(i => checks[i.key]);
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Production Verification</h3>
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg mb-6">
        <h4 className="font-semibold text-yellow-800 mb-1">⚠️ Production Deployment In Progress</h4>
        <p className="text-sm text-yellow-700">Please verify all critical functionality below.</p>
      </div>
      <div className="space-y-3 mb-6">
        {items.map(item => (
          <div key={item.key} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3 cursor-pointer hover:bg-gray-100"
            onClick={() => setChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))}>
            <input type="checkbox" checked={!!checks[item.key]} onChange={() => {}} className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
            <div>
              <h4 className="font-semibold text-sm text-gray-900">{item.label}</h4>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={() => onUpdateProject(project?.projectId, { status: 'completed' })}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium ${allChecked ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={!allChecked}>
          ✓ Confirm Verification Complete
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const AddRequirementModal = ({ onClose, onSubmit, projectId, existingCategories = [], requirements = [], defaultValues = {} }) => {
  const isSubReq = !!defaultValues.parentRequirementId;
  const [form, setForm] = useState({
    title: '', description: '',
    reqType: defaultValues.reqType || 'functional',
    priority: 'medium',
    parentCategory: defaultValues.parentCategory || '',
    parentRequirementId: defaultValues.parentRequirementId || null,
  });
  const [newCategory, setNewCategory] = useState('');
  const [categoryMode, setCategoryMode] = useState('existing');
  const [reqTypes, setReqTypes] = useState(DEFAULT_REQ_TYPES);
  useEffect(() => { ModulesModel.getReqTypes().then(ts => { if (Array.isArray(ts) && ts.length > 0) setReqTypes(ts); }); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cat = categoryMode === 'new' ? newCategory.trim() : form.parentCategory;
    onSubmit({ ...form, parentCategory: cat || null, projectId });
  };

  const parentReq = isSubReq ? requirements.find(r => r.id === defaultValues.parentRequirementId) : null;
  const topLevelReqs = requirements.filter(r => !r.parentRequirementId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">{isSubReq ? 'Add Sub-requirement' : 'Add Requirement'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Sub-requirement context banner */}
          {parentReq && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg">
              <span className="text-teal-500 font-mono text-xs mt-0.5 shrink-0">&gt;&gt;&gt;</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-teal-700 mb-0.5">Sub-requirement of:</p>
                <p className="text-sm text-teal-900 font-medium">{parentReq.reqCode}: {parentReq.title}</p>
              </div>
            </div>
          )}

          {/* Optional parent picker (only when not pre-filled) */}
          {!isSubReq && topLevelReqs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Requirement <span className="text-gray-400 font-normal">(optional — makes this a sub-requirement)</span></label>
              <select value={form.parentRequirementId || ''}
                onChange={e => setForm(f => ({ ...f, parentRequirementId: e.target.value ? parseInt(e.target.value) : null }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— Top-level requirement —</option>
                {topLevelReqs.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
              </select>
            </div>
          )}

          {/* Category (hide for sub-reqs — inherits from parent) */}
          {!isSubReq && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Feature Group / Category</label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => setCategoryMode('existing')}
                  className={`px-3 py-1 text-xs rounded-full border ${categoryMode === 'existing' ? 'bg-teal-600 text-white border-teal-600' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  Existing
                </button>
                <button type="button" onClick={() => setCategoryMode('new')}
                  className={`px-3 py-1 text-xs rounded-full border ${categoryMode === 'new' ? 'bg-teal-600 text-white border-teal-600' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  + New Group
                </button>
              </div>
              {categoryMode === 'existing' ? (
                <select value={form.parentCategory} onChange={e => setForm({ ...form, parentCategory: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                  <option value="">— No Group —</option>
                  {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)}
                  placeholder="e.g. Login Feature, Payment Module…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.reqType} onChange={e => setForm({ ...form, reqType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {reqTypes.map(t => <option key={t.name} value={t.name}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">{isSubReq ? 'Add Sub-requirement' : 'Add Requirement'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ManageTcModulesModal = ({ onClose, onSaved }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [error, setError] = useState('');

  const load = async () => { setLoading(true); setModules(await ModulesModel.getTcModules()); setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true); setError('');
    const r = await ModulesModel.createTcModule({ name: newName.trim() });
    if (r?.success === false) { setError(r.error || 'Failed to add'); }
    else { setNewName(''); await load(); onSaved?.(); }
    setAdding(false);
  };

  const handleRename = async (id) => {
    if (!editVal.trim()) return;
    setError('');
    const r = await ModulesModel.updateTcModule(id, { name: editVal.trim() });
    if (r?.success === false) { setError(r.error || 'Failed to rename'); }
    else { setEditingId(null); await load(); onSaved?.(); }
  };

  const handleDelete = (m) => {
    requestConfirm(`Delete module "${m.name}"?`, async () => {
      const r = await ModulesModel.deleteTcModule(m.id);
      if (r?.success === false) { setError(r.error || 'Failed to delete'); }
      else { await load(); onSaved?.(); }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Manage Modules</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>}
          {loading ? <p className="text-sm text-gray-400 text-center py-4">Loading…</p> : (
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {modules.map(m => (
                <li key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 group">
                  {editingId === m.id ? (
                    <>
                      <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(m.id); if (e.key === 'Escape') setEditingId(null); }}
                        className="flex-1 border border-teal-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-teal-400 outline-none" />
                      <button onClick={() => handleRename(m.id)} className="text-xs text-teal-600 font-medium hover:text-teal-700">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-800">{m.name}</span>
                      <button onClick={() => { setEditingId(m.id); setEditVal(m.name); setError(''); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-teal-600 rounded transition-opacity"><Pencil size={13} /></button>
                      <button onClick={() => handleDelete(m)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 rounded transition-opacity"><Trash2 size={13} /></button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 pt-1 border-t border-gray-100">
            <input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="New module name…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 outline-none" />
            <button onClick={handleAdd} disabled={adding || !newName.trim()}
              className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {adding ? '…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddTestCaseModal = ({ onClose, onSubmit, projectId, requirements = [] }) => {
  const [form, setForm] = useState({ title: '', module: '', priority: 'high', prerequisites: '', steps: [{ action: '', expected: '' }], requirementId: null });
  const [modules, setModules] = useState([]);
  const [showManageModules, setShowManageModules] = useState(false);

  const loadModules = async () => {
    const list = await ModulesModel.getTcModules();
    setModules(list);
    if (list.length > 0 && !form.module) setForm(f => ({ ...f, module: list[0].name }));
  };
  useEffect(() => { loadModules(); }, []);

  const addStep = () => setForm({ ...form, steps: [...form.steps, { action: '', expected: '' }] });
  const updateStep = (i, field, val) => { const s = [...form.steps]; s[i][field] = val; setForm({ ...form, steps: s }); };
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId }); };

  return (
    <>
    {showManageModules && (
      <ManageTcModulesModal
        onClose={() => setShowManageModules(false)}
        onSaved={async () => { await loadModules(); }}
      />
    )}
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Add Test Case</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Module</label>
                <button type="button" onClick={() => setShowManageModules(true)}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium">⚙ Manage</button>
              </div>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {modules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
            <textarea value={form.prerequisites} onChange={e => setForm({ ...form, prerequisites: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          {requirements.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Linked Requirement <span className="text-gray-400 font-normal">(optional)</span></label>
              <select value={form.requirementId || ''} onChange={e => setForm({ ...form, requirementId: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— None —</option>
                {requirements.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Test Steps</label>
            {form.steps.map((step, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 mb-2">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">Step {i + 1}</h5>
                <input type="text" placeholder="Action" value={step.action} onChange={e => updateStep(i, 'action', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-2 focus:ring-2 focus:ring-teal-400" />
                <input type="text" placeholder="Expected Result" value={step.expected} onChange={e => updateStep(i, 'expected', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
            ))}
            <button type="button" onClick={addStep} className="text-xs text-teal-600 font-medium hover:text-teal-700">+ Add Step</button>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Add Test Case</button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

// Local-only attachment manager (used inside ReportBugModal before bug exists)
const LocalAttachmentPicker = ({ files, onChange }) => {
  const inputRef = React.useRef();
  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    const valid = [];
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (files.length + valid.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      valid.push(await readFileAsBase64(f));
    }
    if (errors.length) alert(errors.join('\n'));
    if (valid.length) onChange([...files, ...valid]);
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => inputRef.current?.click()}
          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium">
          + Attach File
        </button>
        <span className="text-xs text-gray-400">Images, PDF, DOCX, XLSX, CSV · max 2 MB · up to 5 files</span>
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative group">
              {f.fileType.startsWith('image/') ? (
                <img src={f.fileData} alt={f.fileName} className="w-16 h-16 object-cover rounded border border-gray-200" />
              ) : (() => { const d = getFileDisplay(f.fileName, f.fileType); return (
                <div className={`w-16 h-16 flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded ${d.text} text-xs font-medium`}>
                  {d.label}<span className="text-gray-400 text-[10px] truncate max-w-[56px] mt-0.5">{f.fileName}</span>
                </div>
              ); })()}
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center leading-none">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// API-backed attachment manager for PlanTrack projects
const ProjectAttachmentManager = ({ projectId, user }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  const load = async () => { setLoading(true); setAttachments(await ModulesModel.getProjectAttachments(projectId)); setLoading(false); };
  useEffect(() => { load(); }, [projectId]);

  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    setUploading(true);
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (attachments.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      const att = await readFileAsBase64(f);
      await ModulesModel.addProjectAttachment(projectId, att);
    }
    if (errors.length) alert(errors.join('\n'));
    setUploading(false);
    await load();
  };

  const handleDelete = async (id) => {
    requestConfirm('Remove this attachment?', async () => {
      await ModulesModel.deleteProjectAttachment(id);
      await load();
    });
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          {attachments.length} / {MAX_ATTACHMENTS} files
        </span>
        {attachments.length < MAX_ATTACHMENTS && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Upload File'}
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      <p className="text-xs text-gray-400 mb-3">Accepted: JPG, PNG, GIF, WebP, PDF, DOCX, XLSX, CSV · Max 2 MB per file</p>
      {loading ? <p className="text-sm text-gray-400">Loading…</p> : attachments.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <div className="text-3xl mb-2">📎</div>
          <p className="text-sm">No artifacts yet.</p>
          <p className="text-xs mt-1">Upload images, PDFs, or documents related to this project.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {attachments.map(a => (
            <div key={a.id} className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              {a.fileType.startsWith('image/') ? (
                <a href={a.fileData} target="_blank" rel="noreferrer">
                  <img src={a.fileData} alt={a.fileName} className="w-full h-24 object-cover hover:opacity-80 transition-opacity" />
                </a>
              ) : (() => { const d = getFileDisplay(a.fileName, a.fileType); return (
                <a href={a.fileData} download={a.fileName}
                  className={`w-full h-24 flex flex-col items-center justify-center ${d.bg} ${d.hover} transition-colors ${d.text}`}>
                  <span className="text-2xl">{d.emoji}</span>
                  <span className="text-[10px] text-gray-500 truncate px-1 max-w-full mt-1">{a.fileName}</span>
                </a>
              ); })()}
              <div className="px-2 py-1 bg-white border-t border-gray-100">
                <p className="text-[10px] text-gray-600 truncate">{a.fileName}</p>
                <p className="text-[10px] text-gray-400">{a.uploadedBy || 'Unknown'} · {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}</p>
              </div>
              {(user?.user_name || user?.name) === a.uploadedBy && (
                <button type="button" onClick={() => requestConfirm('Delete this attachment?', () => handleDelete(a.id))}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center">×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportBugModal = ({ onClose, onSubmit, projectId, testCases, prefillTc }) => {
  const [form, setForm] = useState({ title: '', testCaseId: prefillTc?.id || '', severity: 'medium', module: prefillTc?.module || 'Authentication', stepsToReproduce: '', expectedResult: '', actualResult: '', environment: 'UAT' });
  const [attachments, setAttachments] = useState([]);
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId, testCaseId: form.testCaseId || null, attachments }); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">🐛 Report Bug</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bug Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Test Case</label>
              <select value={form.testCaseId} onChange={e => setForm({ ...form, testCaseId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">None</option>
                {(testCases || []).map(tc => <option key={tc.id} value={tc.id}>{tc.tcCode} - {tc.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option><option>Reporting</option><option>User Management</option><option>UI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
            <BugFieldEditor placeholder="1. Navigate to…&#10;2. Click on…&#10;3. Observe…" onChange={v => setForm(f => ({ ...f, stepsToReproduce: v }))} minHeight="80px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Result</label>
            <BugFieldEditor placeholder="What should happen?" onChange={v => setForm(f => ({ ...f, expectedResult: v }))} minHeight="60px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Result</label>
            <BugFieldEditor placeholder="What actually happened?" onChange={v => setForm(f => ({ ...f, actualResult: v }))} minHeight="60px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
            <LocalAttachmentPicker files={attachments} onChange={setAttachments} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Submit Bug Report</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBugModal = ({ bug, onClose, onSave, onDelete, user, allUsers = [], teamMembers = [], allProjects = [] }) => {
  const [form, setForm] = useState({
    title: bug.title || '', severity: bug.severity || 'medium', status: bug.status || 'open',
    module: bug.module || '', assignedTo: bug.assignedTo || '', environment: bug.environment || 'DEV',
    stepsToReproduce: bug.stepsToReproduce || '', expectedResult: bug.expectedResult || '', actualResult: bug.actualResult || '',
    projectId: bug.projectId || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); onSave(bug.id, form); };

  // Role-based permissions
  const role = user?.role || user?.user_role;
  const canReassignProject = role === 'admin' || role === 'qa_lead' || role === 'qa';
  const isManager = role?.endsWith('_manager') || role === 'manager';
  const managerModule = (isManager && role !== 'manager') ? role.replace('_manager', '') : null;
  const assigneeOptions = (() => {
    if (isManager && managerModule) return teamMembers.filter(m => m.team_module === managerModule);
    if (isManager || role === 'admin') return allUsers;
    return allUsers;
  })();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Edit Bug — {bug.bugCode}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          {canReassignProject && allProjects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {allProjects.map(p => <option key={p.projectId || p.id} value={p.projectId || p.id}>{p.projectName || p.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">None</option><option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option><option>Reporting</option><option>User Management</option><option>UI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— Unassigned —</option>
                {assigneeOptions.map(u => (
                  <option key={u.user_id} value={u.user_name}>{u.user_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
            <BugFieldEditor key={`str-${bug.id}`} initialValue={form.stepsToReproduce} onChange={v => setForm(f => ({ ...f, stepsToReproduce: v }))} minHeight="80px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Result</label>
            <BugFieldEditor key={`exp-${bug.id}`} initialValue={form.expectedResult} onChange={v => setForm(f => ({ ...f, expectedResult: v }))} minHeight="60px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Result</label>
            <BugFieldEditor key={`act-${bug.id}`} initialValue={form.actualResult} onChange={v => setForm(f => ({ ...f, actualResult: v }))} minHeight="60px" />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <ApiAttachmentManager bugId={bug.id} uploadedBy={user?.name} />
          </div>
          <div className="flex justify-between items-center pt-2">
            <button type="button" onClick={() => requestConfirm('Delete this bug?', () => onDelete(bug.id))}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Save Changes</button>
            </div>
          </div>
        </form>
        <div className="px-6 pb-6">
          <TaskComments taskRef={bug.id} taskSource="bug" userName={user?.user_name || user?.name} />
        </div>
      </div>
    </div>
  );
};

const QmsSidebar = ({ data, projects, currentProjectId, onProjectChange, onReportBug, onEditBug, onViewBug }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Project</h3>
      <select value={currentProjectId || ''} onChange={e => onProjectChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-teal-400">
        {(projects || []).map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
      </select>
      {data?.project && (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">QA Lead</span><span className="font-medium">{data.project.qaLead || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Environment</span><span className="font-medium">{data.project.environment || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Stage</span><span className="font-medium capitalize">{data.project.currentStage || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium capitalize">{data.project.status || '-'}</span></div>
        </div>
      )}
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Bugs</h3>
      {(data?.bugs || []).slice(0, 3).map(b => {
        const bs = qmsSeverityStyle(b.severity);
        return (
          <div key={b.id} className="bg-gray-50 rounded-lg p-3 mb-2 cursor-pointer hover:bg-gray-100" onClick={() => onViewBug ? onViewBug(b.id) : onEditBug(b)} style={{ borderLeftWidth: '3px', borderLeftColor: bs.color }}>
            <div className="flex justify-between items-center">
              <div className="font-semibold text-xs text-gray-900">{b.bugCode}</div>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{b.title}</div>
            <div className="text-[10px] text-gray-400 mt-1 capitalize">{b.status}</div>
          </div>
        );
      })}
      <div className="flex gap-2 mt-2">
        <button onClick={() => onReportBug(null)} className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">+ Report Bug</button>
        {onViewBug && <button onClick={() => onViewBug('list')} className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">View All →</button>}
      </div>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
      <div className="space-y-2">
        {[
          { label: 'Test Cases', value: data?.metrics?.testCases?.total || 0 },
          { label: 'Bugs Reported', value: data?.metrics?.bugs?.total || 0 },
          { label: 'Bugs Resolved', value: data?.metrics?.bugs?.resolved || 0 },
          { label: 'Pass Rate', value: `${data?.metrics?.passRate || 0}%`, color: '#20c997' }
        ].map(s => (
          <div key={s.label} className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className="text-lg font-bold" style={{ color: s.color || '#1f2937' }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SendQaEodModal = ({ onClose }) => {
  const [userName, setUserName] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    ModulesModel.getQaLeads().then(leads => {
      if (leads.length > 0 && !userName) setUserName(leads[0].user_name);
    });
  }, []);

  const handleSend = async () => {
    if (!userName.trim()) { setError('Enter the QA Lead name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.sendQualityEodSummary(userName.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send QA EOD summary');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 QA EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">QA EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Bugs reported + TC executed</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Open bugs assigned</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending (TC + requirements)</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Sends today's QA activity — bugs reported, test cases executed, requirements created,
              open bugs assigned, and pending test cases.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">QA Lead Name</label>
            <input
              type="text" value={userName} onChange={e => setUserName(e.target.value)}
              placeholder="Auto-filled from QA Lead role"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">Auto-filled from the assigned QA Lead. Edit if needed.</p>
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send QA EOD'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ALL BUGS PAGE + BUG DETAIL PAGE
// ============================================================================

const BUG_SEVERITIES = ['critical', 'high', 'medium', 'low'];
const BUG_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

const bugSevStyle = (s) => ({ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' }[s] || 'bg-gray-100 text-gray-600');
const bugStatStyle = (s) => ({ open: 'bg-red-50 text-red-600', in_progress: 'bg-blue-50 text-blue-700', resolved: 'bg-green-50 text-green-700', closed: 'bg-gray-100 text-gray-500' }[s] || 'bg-gray-100 text-gray-600');
const bugStatDot = (s) => ({ open: 'bg-red-500', in_progress: 'bg-blue-500', resolved: 'bg-green-500', closed: 'bg-gray-400' }[s] || 'bg-gray-400');

const BugRow = ({ bug, onViewBug, onEditBug, canEdit, showProject = false }) => (
  <div className="grid gap-x-4 px-4 py-3 hover:bg-teal-50 items-center transition-colors border-b border-gray-100 last:border-0"
    style={{ gridTemplateColumns: canEdit ? '5rem 1fr auto auto auto auto' : '5rem 1fr auto auto auto' }}>
    <span className="text-xs font-mono text-gray-400 cursor-pointer" onClick={() => onViewBug(bug.id)}>{bug.bugCode}</span>
    <div className="min-w-0 cursor-pointer" onClick={() => onViewBug(bug.id)}>
      <p className="text-sm font-medium text-gray-900 truncate">{bug.title}</p>
      <p className="text-xs text-gray-400 truncate">{[bug.module, showProject && bug.projectName].filter(Boolean).join(' · ')}</p>
    </div>
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${bugSevStyle(bug.severity)}`}>{bug.severity}</span>
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${bugStatStyle(bug.status)}`}>{bug.status.replace('_', ' ')}</span>
    <span className="text-xs text-gray-500 truncate max-w-28">{bug.assignedTo || '—'}</span>
    {canEdit && (
      <button onClick={e => { e.stopPropagation(); onEditBug(bug); }}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 hover:bg-teal-50 px-2 py-1 rounded-lg border border-transparent hover:border-teal-200 transition-colors whitespace-nowrap">
        <Pencil size={11} /> Edit
      </button>
    )}
  </div>
);

const ProjectBugGroup = ({ projectName, projectId, bugs, onViewBug, onEditBug, onFilterToProject, canEdit, defaultOpen = true }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const openCount = bugs.filter(b => b.status === 'open').length;
  const criticalCount = bugs.filter(b => b.severity === 'critical').length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-3">
      {/* Project header row */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}>
        <ChevronDown size={15} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? '' : '-rotate-90'}`} />
        <span className="font-semibold text-gray-800 text-sm flex-1">{projectName || 'Unknown Project'}</span>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{criticalCount} critical</span>
          )}
          {openCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">{openCount} open</span>
          )}
          <span className="text-xs text-gray-400">{bugs.length} bug{bugs.length !== 1 ? 's' : ''}</span>
          <button onClick={e => { e.stopPropagation(); onFilterToProject(projectId); }}
            className="text-xs text-teal-600 hover:text-teal-800 hover:underline ml-1">Filter →</button>
        </div>
      </div>
      {/* Bugs */}
      {open && (
        bugs.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">No bugs match current filters</div>
        ) : (
          <div>
            <div className="gap-x-4 px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 bg-white"
              style={{ display: 'grid', gridTemplateColumns: canEdit ? '5rem 1fr auto auto auto auto' : '5rem 1fr auto auto auto' }}>
              <span>ID</span><span>Title</span><span>Severity</span><span>Status</span><span>Assigned</span>
              {canEdit && <span></span>}
            </div>
            {bugs.map(bug => <BugRow key={bug.id} bug={bug} onViewBug={onViewBug} onEditBug={onEditBug} canEdit={canEdit} />)}
          </div>
        )
      )}
    </div>
  );
};

const AllBugsView = ({ bugsData, filters, onFilterChange, onViewBug, onEditBug, canEdit, onReportBug }) => {
  const bugs = bugsData?.bugs || [];
  const metrics = bugsData?.metrics || {};
  const allProjects = bugsData?.projects || [];

  // Group bugs by project
  const grouped = React.useMemo(() => {
    const map = {};
    bugs.forEach(b => {
      const key = b.projectId || '__none__';
      if (!map[key]) map[key] = { projectId: b.projectId, projectName: b.projectName || 'No Project', bugs: [] };
      map[key].bugs.push(b);
    });
    // Sort groups: most bugs first
    return Object.values(map).sort((a, b) => b.bugs.length - a.bugs.length);
  }, [bugs]);

  const isFiltered = !!(filters.severity || filters.status || filters.projectId || filters.search);

  return (
    <div>
      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Bugs', value: metrics.total || 0, icon: '🐛', color: 'text-gray-800', bg: 'bg-gray-50' },
          { label: 'Open', value: metrics.open || 0, icon: '🔴', color: 'text-red-700', bg: 'bg-red-50' },
          { label: 'In Progress', value: metrics.inProgress || 0, icon: '🔵', color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Resolved', value: (metrics.resolved || 0) + (metrics.closed || 0), icon: '✅', color: 'text-green-700', bg: 'bg-green-50' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 border border-gray-100`}>
            <div className="flex items-center gap-2 mb-1"><span className="text-lg">{c.icon}</span><span className="text-xs text-gray-500">{c.label}</span></div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-36">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={filters.search} onChange={e => onFilterChange({ search: e.target.value })}
            placeholder="Search bugs..." className="flex-1 text-sm border-0 outline-none bg-transparent placeholder-gray-400" />
        </div>
        <select value={filters.severity} onChange={e => onFilterChange({ severity: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="">All Severities</option>
          {BUG_SEVERITIES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select value={filters.status} onChange={e => onFilterChange({ status: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="">All Statuses</option>
          {BUG_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        {allProjects.length > 1 && (
          <select value={filters.projectId} onChange={e => onFilterChange({ projectId: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
            <option value="">All Projects</option>
            {allProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select value={`${filters.sort}_${filters.order}`} onChange={e => { const [sort, order] = e.target.value.split('_'); onFilterChange({ sort, order }); }} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="created_at_desc">Newest first</option>
          <option value="created_at_asc">Oldest first</option>
          <option value="updated_at_desc">Recently updated</option>
          <option value="severity_asc">Severity (high→low)</option>
        </select>
        {isFiltered && (
          <button onClick={() => onFilterChange({ severity: '', status: '', projectId: '', search: '' })} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
        )}
        <button onClick={onReportBug} className="ml-auto px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 font-medium whitespace-nowrap">+ Report Bug</button>
      </div>

      {/* Active project filter banner */}
      {filters.projectId && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
          <span>Showing bugs for: <strong>{allProjects.find(p => p.id === filters.projectId)?.name || filters.projectId}</strong></span>
          <button onClick={() => onFilterChange({ projectId: '' })} className="ml-auto text-teal-500 hover:text-teal-800 text-xs underline">Show all projects</button>
        </div>
      )}

      {/* Grouped bug list */}
      {bugs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-14 text-center">
          <div className="text-4xl mb-2">🎉</div>
          <p className="text-gray-500 font-medium">No bugs found</p>
          <p className="text-gray-400 text-sm mt-1">{isFiltered ? 'Try adjusting your filters' : 'All systems clean!'}</p>
        </div>
      ) : (
        <div>
          {grouped.map((group, i) => (
            <ProjectBugGroup
              key={group.projectId || i}
              projectName={group.projectName}
              projectId={group.projectId}
              bugs={group.bugs}
              onViewBug={onViewBug}
              onEditBug={onEditBug}
              canEdit={canEdit}
              onFilterToProject={(pid) => onFilterChange({ projectId: pid })}
              defaultOpen={i === 0 || grouped.length <= 3}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const sanitizeBugHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/javascript\s*:/gi, '');
};

const BugFieldEditor = ({ initialValue = '', onChange, placeholder = '', minHeight = '80px' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!initialValue);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (initialValue) { editorRef.current.innerHTML = initialValue; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); syncValue(); };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (!blob) return;
      if (blob.size > 2 * 1024 * 1024) { alert('Image too large. Max 2MB.'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => { document.execCommand('insertHTML', false, `<img src="${ev.target.result}" style="max-width:100%;border-radius:4px;margin:4px 0;" alt="pasted image"/>`); syncValue(); };
      reader.readAsDataURL(blob);
    } else {
      e.preventDefault();
      document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
    }
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable onInput={syncValue} onPaste={handlePaste}
          style={{ minHeight }}
          className="max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none"
          suppressContentEditableWarning />
      </div>
    </div>
  );
};

const BugCommentEditor = ({ onChange, placeholder = 'Add a comment…' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(true);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    syncValue();
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (!blob) return;
      if (blob.size > 2 * 1024 * 1024) { alert('Image too large. Max 2MB.'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        document.execCommand('insertHTML', false, `<img src="${ev.target.result}" style="max-width:100%;border-radius:4px;margin:4px 0;" alt="pasted image"/>`);
        syncValue();
      };
      reader.readAsDataURL(blob);
    } else {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    }
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd}
      onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">
      {content}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400 bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      {/* Editable area */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={syncValue}
          onPaste={handlePaste}
          className="min-h-[80px] max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none"
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};

const BugPageView = ({ bug, user, onBack, onUpdate, onAddComment, onDeleteComment }) => {
  const [editField, setEditField] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  const [commentHtml, setCommentHtml] = React.useState('');
  const [commentKey, setCommentKey] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);

  if (!bug) return null;

  const userRole = user?.role || user?.user_role;
  const isAdmin = userRole === 'admin';
  const canEdit = ['admin', 'qa_lead', 'qa'].includes(userRole) || user?.user_name === bug.reportedBy || user?.user_name === bug.assignedTo;

  const startEdit = (field, val) => { setEditField(field); setEditValue(val || ''); };
  const saveField = async (field) => {
    const keyMap = { status: 'status', severity: 'severity', assignedTo: 'assignedTo', module: 'module', environment: 'environment', title: 'title', stepsToReproduce: 'stepsToReproduce', expectedResult: 'expectedResult', actualResult: 'actualResult' };
    await onUpdate(bug.id, { [keyMap[field]]: editValue });
    setEditField(null);
  };

  const handleComment = async () => {
    const stripped = commentHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const hasImage = commentHtml.includes('<img');
    if (!stripped && !hasImage) return;
    setSubmitting(true);
    await onAddComment(bug.id, commentHtml);
    setCommentHtml('');
    setCommentKey(k => k + 1);
    setSubmitting(false);
  };

  // Build unified feed: comments + history interleaved by time
  const feed = [
    ...(bug.comments || []).map(c => ({ ...c, _type: 'comment' })),
    ...(bug.history || []).map(h => ({ ...h, _type: 'history', createdAt: h.createdAt })),
  ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const inlineSelect = (field, options, label) => (
    editField === field ? (
      <div className="flex items-center gap-2">
        <select value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="text-sm border border-teal-400 rounded-lg px-2 py-1 focus:ring-2 focus:ring-teal-400">
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={() => saveField(field)} className="text-xs px-2 py-1 bg-teal-600 text-white rounded">Save</button>
        <button onClick={() => setEditField(null)} className="text-xs text-gray-500 hover:text-gray-800">✕</button>
      </div>
    ) : (
      <div className="flex items-center gap-2 group cursor-pointer" onClick={() => canEdit && startEdit(field, bug[field])}>
        <span className={field === 'severity' ? `text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${bugSevStyle(bug[field])}` : `text-xs font-medium px-2 py-0.5 rounded-full capitalize flex items-center gap-1 ${bugStatStyle(bug[field])}`}>
          {field === 'status' && <span className={`w-1.5 h-1.5 rounded-full ${bugStatDot(bug[field])}`}></span>}
          {(bug[field] || '—').replace('_', ' ')}
        </span>
        {canEdit && <Pencil size={12} className="text-gray-300 group-hover:text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    )
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-sm">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-teal-700 font-medium"><ArrowLeft size={15} /> All Bugs</button>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="font-mono text-gray-400">{bug.bugCode}</span>
        <span className="ml-auto text-xs text-gray-400">{bug.projectName || ''}</span>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main */}
        <div className="col-span-2 space-y-4">
          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-3 mb-3">
              {inlineSelect('severity', BUG_SEVERITIES.map(s => ({ value: s, label: s })), 'Severity')}
              {inlineSelect('status', BUG_STATUSES.map(s => ({ value: s, label: s.replace('_', ' ') })), 'Status')}
            </div>
            {editField === 'title' ? (
              <div className="space-y-2">
                <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="w-full text-xl font-bold border border-teal-400 rounded-lg px-3 py-2" />
                <div className="flex gap-2">
                  <button onClick={() => saveField('title')} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg">Save</button>
                  <button onClick={() => setEditField(null)} className="text-sm text-gray-500">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 group cursor-pointer" onClick={() => canEdit && startEdit('title', bug.title)}>
                <h1 className="text-xl font-bold text-gray-900 flex-1">{bug.title}</h1>
                {canEdit && <Pencil size={14} className="text-gray-300 group-hover:text-teal-500 opacity-0 group-hover:opacity-100 mt-1 flex-shrink-0" />}
              </div>
            )}
          </div>

          {/* Steps, Expected, Actual */}
          {['stepsToReproduce', 'expectedResult', 'actualResult'].map(field => {
            const labels = { stepsToReproduce: 'Steps to Reproduce', expectedResult: 'Expected Result', actualResult: 'Actual Result' };
            return (
              <div key={field} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">{labels[field]}</h3>
                  {canEdit && editField !== field && <button onClick={() => startEdit(field, bug[field])} className="text-gray-300 hover:text-teal-500"><Pencil size={13} /></button>}
                </div>
                {editField === field ? (
                  <div className="space-y-2">
                    <BugFieldEditor key={`${field}-${bug.id}`} initialValue={editValue} onChange={setEditValue} minHeight="80px" />
                    <div className="flex gap-2">
                      <button onClick={() => saveField(field)} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg">Save</button>
                      <button onClick={() => setEditField(null)} className="text-sm text-gray-500">Cancel</button>
                    </div>
                  </div>
                ) : (
                  bug[field]
                    ? <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(bug[field]) }} />
                    : <span className="text-gray-400 italic text-sm">Not provided</span>
                )}
              </div>
            );
          })}

          {/* Comments + History Feed */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">💬 Activity</h3>
            <div className="space-y-3 mb-4">
              {feed.length === 0 && <p className="text-sm text-gray-400">No activity yet.</p>}
              {feed.map((item, i) => (
                item._type === 'comment' ? (
                  <div key={`c-${item.id}`} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700 flex-shrink-0">
                      {(item.userName || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-700">{item.userName}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{fmtDate(item.createdAt)}</span>
                          {(isAdmin || user?.user_name === item.userName) && (
                            <button onClick={() => onDeleteComment(bug.id, item.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={11} /></button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(item.content) }} />
                    </div>
                  </div>
                ) : (
                  <div key={`h-${item.id}`} className="flex gap-3 items-start">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">⚡</div>
                    <div className="flex-1 py-1">
                      <p className="text-xs text-gray-500">
                        <span className="font-medium text-gray-700">{item.changedBy || 'System'}</span>
                        {' changed '}<span className="font-medium">{item.field.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                        {item.oldValue && <> from <span className="font-mono bg-gray-100 px-1 rounded">{item.oldValue}</span></>}
                        {' → '}<span className="font-mono bg-teal-50 text-teal-700 px-1 rounded">{item.newValue}</span>
                        <span className="ml-2 text-gray-400">{fmtDate(item.createdAt)}</span>
                      </p>
                    </div>
                  </div>
                )
              ))}
            </div>
            {/* Add comment — WYSIWYG */}
            <div className="space-y-2">
              <BugCommentEditor key={commentKey} onChange={setCommentHtml} placeholder="Add a comment… (supports bold, italic, lists)" />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline</p>
                <button onClick={handleComment} disabled={!commentHtml || submitting}
                  className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-40 font-medium">
                  {submitting ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Bug Details</h3>
            {[
              { label: 'Reported By', value: bug.reportedBy },
              { label: 'Assigned To', value: bug.assignedTo, field: 'assignedTo', editable: true, type: 'text' },
              { label: 'Environment', value: bug.environment, field: 'environment', editable: true, type: 'select', options: ['DEV', 'QA', 'UAT', 'PROD'] },
              { label: 'Module', value: bug.module, field: 'module', editable: true, type: 'text' },
              { label: 'Project', value: bug.projectName },
              { label: 'Linked TC', value: bug.testCaseCode ? `${bug.testCaseCode} — ${bug.testCaseTitle || ''}` : null },
              { label: 'Created', value: fmtDate(bug.createdAt) },
              { label: 'Last Updated', value: fmtDate(bug.updatedAt) },
            ].filter(r => r.value !== undefined).map(row => (
              <div key={row.label}>
                <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                {row.editable && canEdit && editField === row.field ? (
                  <div className="flex gap-1">
                    {row.type === 'select' ? (
                      <select value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 text-sm border border-teal-400 rounded px-2 py-1">
                        {row.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="flex-1 text-sm border border-teal-400 rounded px-2 py-1" />
                    )}
                    <button onClick={() => saveField(row.field)} className="px-2 py-1 bg-teal-600 text-white text-xs rounded">✓</button>
                    <button onClick={() => setEditField(null)} className="text-gray-400 text-xs px-1">✕</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 group">
                    <p className="text-sm text-gray-800 font-medium flex-1">{row.value || <span className="text-gray-400 italic">—</span>}</p>
                    {row.editable && canEdit && <button onClick={() => startEdit(row.field, bug[row.field])} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-teal-500"><Pencil size={11} /></button>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick status panel */}
          {canEdit && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Status</h3>
              <div className="space-y-1">
                {BUG_STATUSES.map(s => (
                  <button key={s} onClick={() => onUpdate(bug.id, { status: s })}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 capitalize ${bug.status === s ? bugStatStyle(s) + ' font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                    <span className={`w-2 h-2 rounded-full ${bugStatDot(s)}`}></span>{s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {bug.attachments?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-2">📎 Attachments ({bug.attachments.length})</h3>
              <div className="space-y-1">
                {bug.attachments.map(a => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-gray-600">
                    <span>📄</span><span className="truncate flex-1">{a.fileName}</span>
                    <span className="text-gray-400">{a.fileSize ? `${Math.round(a.fileSize / 1024)}KB` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const QmsModule = ({ data, stage, onStageChange, onApproveReq, onRejectReq, onAddReq, onEditReq, onDeleteReq, onRefreshReq, onUpdateProject, onAddTestCase, onEditTestCase, onDeleteTestCase, onPassTC, onFailTC, onReportBug, onEditBug, onProjectChange, onSendEod, onEodSettings, userRole, user,
  qmsView, onQmsViewChange, allBugsData, allBugsFilters, viewingBugDetail, onAllBugsFilterChange, onViewBugDetail, onBugDetailUpdate, onBugDetailComment, onDeleteBugComment, onBackFromBugDetail, onReportBugFromList
}) => {
  const effectiveRole = userRole || user?.role || user?.user_role;
  const canManageEod = effectiveRole === 'admin' || effectiveRole === 'manager';
  const canEditBugs = ['admin', 'qa_lead', 'qa'].includes(effectiveRole);
  const renderStage = () => {
    switch (stage) {
      case 'requirements': return <QmsRequirementsStage requirements={data?.requirements} projectId={data?.project?.projectId} onApprove={onApproveReq} onReject={onRejectReq} onAdd={onAddReq} onNext={() => onStageChange('testplan')} onEdit={onEditReq} onDelete={onDeleteReq} onRefresh={onRefreshReq} />;
      case 'testplan': return <QmsTestPlanStage project={data?.project} onUpdate={onUpdateProject} onNext={() => onStageChange('testcases')} onBack={() => onStageChange('requirements')} />;
      case 'testcases': return <QmsTestCasesStage testCases={data?.testCases} requirements={data?.requirements || []} onAdd={onAddTestCase} onEdit={onEditTestCase} onDelete={onDeleteTestCase} onNext={() => onStageChange('testing')} onBack={() => onStageChange('testplan')} />;
      case 'testing': return <QmsTestingStage testCases={data?.testCases} bugs={data?.bugs} metrics={data?.metrics} onPass={onPassTC} onFail={onFailTC} onReportBug={onReportBug} onEditBug={onEditBug} onNext={() => onStageChange('uat')} onBack={() => onStageChange('testcases')} />;
      case 'uat': return <QmsUatStage metrics={data?.metrics} bugs={data?.bugs} project={data?.project} onUpdateProject={onUpdateProject} onEditBug={onEditBug} onNext={() => onStageChange('verification')} onBack={() => onStageChange('testing')} />;
      case 'verification': return <QmsVerificationStage project={data?.project} onUpdateProject={onUpdateProject} onBack={() => onStageChange('uat')} />;
      default: return null;
    }
  };
  // If viewing a specific bug detail page
  if (viewingBugDetail) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <BugPageView
          bug={viewingBugDetail}
          user={user}
          onBack={onBackFromBugDetail}
          onUpdate={onBugDetailUpdate}
          onAddComment={onBugDetailComment}
          onDeleteComment={onDeleteBugComment}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">QA Management System</h1>
          <p className="text-gray-600 mt-1">Complete QA workflow management — requirements to production</p>
        </div>
        <div className="flex gap-2">
          {canManageEod && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
          {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">📤 QA EOD</button>}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { id: 'pipeline', label: '⚙️ Pipeline', count: null },
          { id: 'bugs', label: '🐛 All Bugs', count: allBugsData?.metrics?.total },
        ].map(tab => (
          <button key={tab.id} onClick={() => onQmsViewChange(tab.id)}
            className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 transition-colors ${qmsView === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
            {tab.count !== null && tab.count !== undefined && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${qmsView === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-500'}`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {qmsView === 'bugs' ? (
        <AllBugsView
          bugsData={allBugsData}
          filters={allBugsFilters}
          onFilterChange={onAllBugsFilterChange}
          onViewBug={onViewBugDetail}
          onEditBug={onEditBug}
          canEdit={canEditBugs}
          onReportBug={onReportBugFromList}
        />
      ) : (
        <>
          <QmsWorkflowSteps currentStage={stage} onStageChange={onStageChange} />
          <QmsMetrics metrics={data?.metrics} stage={stage} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">{renderStage()}</div>
            <div><QmsSidebar data={data} projects={data?.projects} currentProjectId={data?.project?.projectId} onProjectChange={onProjectChange} onReportBug={onReportBug} onEditBug={onEditBug} onViewBug={onViewBugDetail} /></div>
          </div>
          {data?.project?.projectId && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle size={18} className="text-orange-500" />
                <h2 className="text-lg font-semibold text-gray-900">Risk Register</h2>
                <span className="text-sm text-gray-400">— {data.project.projectName || data.project.projectId}</span>
              </div>
              <ProjectRisksTab projectId={data.project.projectId} user={{ role: userRole }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// QUICKTRACK VIEWS
// ============================================================================

const getQtPriorityStyle = (p) => {
  const styles = { urgent: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[p] || styles.medium;
};
const getQtStatusStyle = (s) => {
  const styles = { open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, done: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[s] || styles.open;
};
const getQtCategoryStyle = (c) => {
  const styles = { bug: { bg: '#fef2f2', color: '#dc2626' }, feature: { bg: '#f0f9ff', color: '#0284c7' }, chore: { bg: '#f5f3ff', color: '#7c3aed' }, general: { bg: '#f9fafb', color: '#6b7280' } };
  return styles[c] || styles.general;
};
const QuickTrackMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Tasks', value: metrics?.total || 0, icon: '📋', color: '#667eea' },
    { label: 'Open', value: metrics?.open || 0, icon: '🔵', color: '#2563eb' },
    { label: 'In Progress', value: metrics?.inProgress || 0, icon: '🟡', color: '#ca8a04' },
    { label: 'Done', value: metrics?.done || 0, icon: '🟢', color: '#16a34a' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const QuickTrackFilters = ({ filterStatus, filterPriority, filterCategory, onFilterStatus, onFilterPriority, onFilterCategory, onReset, onAddTask }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
        <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Priority</label>
        <select value={filterPriority} onChange={e => onFilterPriority(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Category</label>
        <select value={filterCategory} onChange={e => onFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Categories</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="chore">Chore</option>
          <option value="general">General</option>
        </select>
      </div>
      <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
      <div className="flex-1" />
      <button onClick={onAddTask} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
        <span className="text-lg leading-none">+</span> New Task
      </button>
    </div>
  </div>
);

const QuickTrackTable = ({ tasks, onStatusChange, onDelete, onEdit, user, teamMembers = [] }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = (isManager && role !== 'manager') ? role.replace('_manager', '') : null;
  const myTeamNames = isManager
    ? new Set((managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers).map(m => m.user_name))
    : null;
  const canDelete = (t) => isAdmin || (isManager && myTeamNames?.has(t.assignedTo)) || (user?.user_name || user?.name) === t.assignedTo;
  const [pendingDone, setPendingDone] = useState(null); // { id, title }

  const handleStatusChange = (task, newStatus) => {
    if (newStatus === 'done') { setPendingDone({ id: task.id, title: task.title }); return; }
    onStatusChange(task.id, { status: newStatus });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {pendingDone && (
        <ActualTimeModal
          taskTitle={pendingDone.title}
          onConfirm={mins => { onStatusChange(pendingDone.id, { status: 'done', actualMinutes: mins }); setPendingDone(null); }}
          onCancel={() => setPendingDone(null)}
        />
      )}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Tasks</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(tasks || []).length} items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assigned To</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time (min)</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tasks || []).map(t => {
              const ps = getQtPriorityStyle(t.priority);
              const cs = getQtCategoryStyle(t.category);
              return (
                <tr key={t.id} className={`hover:bg-gray-50 ${t.status === 'done' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className={`text-sm font-semibold text-gray-900 ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{t.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {t.projectName ? <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{t.projectName}</span> : <span className="text-xs text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-medium capitalize" style={{ background: cs.bg, color: cs.color }}>{t.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.assignedTo || '-'}</td>
                  <td className="px-4 py-3">
                    {(t.estimatedMinutes > 0 || t.actualMinutes > 0) ? (
                      <div className="text-xs space-y-0.5">
                        <div className="text-gray-500">Est: <span className="font-medium text-gray-700">{t.estimatedMinutes || 0}m</span></div>
                        <div className="text-gray-500">Act: <span className={`font-medium ${t.actualMinutes > t.estimatedMinutes && t.estimatedMinutes > 0 ? 'text-red-600' : 'text-gray-700'}`}>{t.actualMinutes || 0}m</span></div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={e => handleStatusChange(t, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-indigo-400"
                      style={{ background: getQtStatusStyle(t.status).bg, color: getQtStatusStyle(t.status).color }}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => onEdit(t)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium" title="Edit task">Edit</button>
                      {canDelete(t) && (
                        <button onClick={() => requestConfirm(`Delete "${t.title}"?`, () => onDelete(t.id))} className="text-gray-400 hover:text-red-500 text-sm" title="Delete task">&times;</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!tasks || tasks.length === 0) && (
          <div className="p-12 text-center text-gray-400">No tasks match the selected filters.</div>
        )}
      </div>
    </div>
  );
};

const ActualTimeModal = ({ taskTitle, onConfirm, onCancel }) => {
  const [minutes, setMinutes] = useState('');
  const [error, setError] = useState('');
  const handleConfirm = () => {
    const val = parseInt(minutes);
    if (!minutes || isNaN(val) || val <= 0) { setError('Actual time is required and must be greater than 0.'); return; }
    onConfirm(val);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900">Log Actual Time</h2>
          <p className="text-sm text-gray-500 mt-1 truncate">"{taskTitle}"</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min) <span className="text-red-500">*</span></label>
            <input
              type="number" min="1" value={minutes} onChange={e => { setMinutes(e.target.value); setError(''); }}
              placeholder="e.g. 60" autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <p className="text-xs text-gray-400">Required before marking this task as Done.</p>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleConfirm} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">Mark as Done</button>
        </div>
      </div>
    </div>
  );
};

const AddQuickTaskModal = ({ onClose, onSubmit, projects, teamMembers = [], user }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = isManager
    ? (role !== 'manager'
      ? role.replace('_manager', '')
      : teamMembers.find(m => m.user_id === user?.user_id || m.user_name === (user?.user_name || user?.name))?.team_module || null)
    : null;
  const assignableMembers = isAdmin
    ? teamMembers
    : isManager
    ? (managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers)
    : [];
  const selfAssign = !isAdmin && !isManager;
  const defaultAssignee = selfAssign ? (user?.user_name || user?.name || '') : '';

  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general', assignedTo: defaultAssignee, projectId: '', estimatedMinutes: '', actualMinutes: '', status: 'open' });
  const [errors, setErrors] = useState({});
  const isDone = ['done', 'completed'].includes(form.status);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const errs = {};
    if (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0) errs.estimatedMinutes = 'Estimated Time is required';
    if (isDone && (!form.actualMinutes || parseInt(form.actualMinutes) <= 0)) errs.actualMinutes = 'Actual Time is required when status is Done';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(form);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">New Quick Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature</option>
                <option value="chore">Chore</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              {selfAssign ? (
                <input type="text" value={form.assignedTo} readOnly
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              ) : (
                <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                  <option value="">Unassigned</option>
                  {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                </select>
              )}
            </div>
          </div>
          {projects && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">No Project</option>
                {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min) <span className="text-red-500">*</span></label>
              <input type="number" min="1" placeholder="e.g. 60" value={form.estimatedMinutes}
                onChange={e => { setForm({ ...form, estimatedMinutes: e.target.value }); setErrors(ev => ({...ev, estimatedMinutes: ''})); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ${errors.estimatedMinutes ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{errors.estimatedMinutes}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min){isDone && <span className="text-red-500"> *</span>}</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                onChange={e => { setForm({ ...form, actualMinutes: e.target.value }); setErrors(ev => ({...ev, actualMinutes: ''})); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ${errors.actualMinutes ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.actualMinutes && <p className="text-xs text-red-500 mt-1">{errors.actualMinutes}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditQuickTaskModal = ({ task, onClose, onSubmit, projects, user, teamMembers = [] }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = isManager
    ? (role !== 'manager'
      ? role.replace('_manager', '')
      : teamMembers.find(m => m.user_id === user?.user_id || m.user_name === (user?.user_name || user?.name))?.team_module || null)
    : null;
  const selfAssign = !isAdmin && !isManager;
  const assignableMembers = isAdmin
    ? teamMembers
    : isManager
    ? (managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers)
    : [];
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || 'medium',
    category: task.category || 'general',
    assignedTo: task.assignedTo || '',
    projectId: task.projectId || '',
    estimatedMinutes: task.estimatedMinutes || task.estimated_minutes || '',
    actualMinutes: task.actualMinutes || task.actual_minutes || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit(task.id, form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Edit Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature</option>
                <option value="chore">Chore</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              {selfAssign ? (
                <input type="text" value={form.assignedTo} readOnly
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              ) : (
                <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                  <option value="">Unassigned</option>
                  {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                </select>
              )}
            </div>
          </div>
          {projects && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">No Project</option>
                {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.estimatedMinutes}
                onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                onChange={e => setForm({ ...form, actualMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Save Changes</button>
          </div>
        </form>
        <div className="px-6 pb-6">
          <TaskComments taskRef={task.id} taskSource="quicktrack" userName={user?.user_name || user?.name} />
        </div>
      </div>
    </div>
  );
};

const QuickTrackModule = ({ data, filterStatus, filterPriority, filterCategory, onFilterStatus, onFilterPriority, onFilterCategory, onResetFilters, onAddTask, onStatusChange, onDelete, onEdit, user, teamMembers = [] }) => {
  // Client-side filtering
  let tasks = data?.tasks || [];
  if (filterStatus) tasks = tasks.filter(t => t.status === filterStatus);
  if (filterPriority) tasks = tasks.filter(t => t.priority === filterPriority);
  if (filterCategory) tasks = tasks.filter(t => t.category === filterCategory);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">QuickTrack</h1>
        <p className="text-gray-600 mt-1">Ad-hoc task tracker for quick turnaround items</p>
      </div>
      <QuickTrackMetrics metrics={data?.metrics} />
      <QuickTrackFilters
        filterStatus={filterStatus} filterPriority={filterPriority} filterCategory={filterCategory}
        onFilterStatus={onFilterStatus} onFilterPriority={onFilterPriority} onFilterCategory={onFilterCategory}
        onReset={onResetFilters} onAddTask={onAddTask}
      />
      <QuickTrackTable tasks={tasks} onStatusChange={onStatusChange} onDelete={onDelete} onEdit={onEdit} user={user} teamMembers={teamMembers} />
    </div>
  );
};

// ============================================================================
// VENDOR EVENT OBSERVABILITY VIEWS
// ============================================================================

const getVendorEventStatusStyle = (status) => {
  const styles = {
    Accepted: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fff7ed', color: '#9a3412' },
    Failed: { bg: '#fee2e2', color: '#991b1b' },
    Pending: { bg: '#f1f5f9', color: '#475569' }
  };
  return styles[status] || styles.Pending;
};

const getWorkflowStyle = (wf) => {
  const styles = {
    Creation: { bg: '#dbeafe', color: '#1e40af' },
    Reattribution: { bg: '#f3e8ff', color: '#6b21a8' },
    Rejection: { bg: '#fef3c7', color: '#92400e' },
    Reengagement: { bg: '#d1fae5', color: '#065f46' }
  };
  return styles[wf] || { bg: '#f1f5f9', color: '#475569' };
};

const VendorEventsMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Leads', value: metrics?.totalLeads ?? 0, sub: 'Matching current filters', color: '#3b82f6' },
    { label: "Today's Leads", value: metrics?.todayLeads ?? 0, sub: 'Pacific Time', color: '#22c55e' },
    { label: 'Active Partners', value: metrics?.uniquePartners ?? 0, sub: 'Distinct partner names', color: '#f59e0b' },
    { label: 'Top Partner', value: metrics?.topPartner || '—', sub: 'Highest lead volume', color: '#8b5cf6' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{c.label}</div>
          <div className="text-3xl font-bold mb-1" style={{ color: c.color }}>{c.value}</div>
          <div className="text-xs text-gray-400">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

const VendorEventsFilters = ({ events, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onReset }) => {
  const allEvents = events || [];
  const uniqueWorkflows = [...new Set(allEvents.map(e => e.workflow).filter(Boolean))].sort();
  const uniqueStatuses  = [...new Set(allEvents.map(e => e.status).filter(Boolean))].sort();
  const uniqueVendors   = [...new Set(allEvents.map(e => e.partner || e.vendor).filter(Boolean))].sort();
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-3">Filters</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Partner</label>
          <select value={filterVendor} onChange={e => onFilterVendor(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Partners</option>
            {uniqueVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Workflow</label>
          <select value={filterWorkflow} onChange={e => onFilterWorkflow(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Workflows</option>
            {uniqueWorkflows.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
          <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Statuses</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
      </div>
    </div>
  );
};

const VendorEventsTable = ({ events }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const allEvents = events || [];
  const totalPages = Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE));
  const pageEvents = allEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); setExpandedId(null); }, [events]);
  const utmSourceColor = (src) => {
    if (!src) return { bg: '#f1f5f9', color: '#64748b' };
    const s = src.toLowerCase();
    if (s.includes('google')) return { bg: '#dbeafe', color: '#1e40af' };
    if (s.includes('facebook') || s.includes('meta')) return { bg: '#ede9fe', color: '#5b21b6' };
    if (s.includes('email') || s.includes('mail')) return { bg: '#dcfce7', color: '#166534' };
    if (s.includes('organic') || s.includes('seo')) return { bg: '#fef9c3', color: '#854d0e' };
    return { bg: '#f1f5f9', color: '#374151' };
  };
  const dash = <span className="text-gray-300">—</span>;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Lead Events</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(events || []).length} records</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Contact ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Partner</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Workflow</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Campaign</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Lead Src ID</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageEvents.map(ev => {
              const ws = getWorkflowStyle(ev.workflow);
              const utmSrc = utmSourceColor(ev.utmSource);
              const rowKey = ev.id || ev.leadId;
              const isExpanded = expandedId === rowKey;
              const hasExtra = ev.utmMedium || ev.utmContent || ev.utmNetwork || ev.clickId;
              return (
                <React.Fragment key={rowKey}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{ev.leadId || dash}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{ev.partner || ev.vendor || dash}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap" style={{ background: ws.bg, color: ws.color }}>{ev.workflow}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.date || dash}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.time || dash}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.utmSource
                        ? <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: utmSrc.bg, color: utmSrc.color }}>{ev.utmSource}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[160px]">
                      {ev.utmCampaign ? <span title={ev.utmCampaign} className="block truncate">{ev.utmCampaign}</span> : dash}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.leadSourceId
                        ? <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-mono">{ev.leadSourceId}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3">
                      {hasExtra && (
                        <button onClick={() => setExpandedId(isExpanded ? null : rowKey)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                          {isExpanded ? 'Less ▲' : 'More ▼'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50">
                      <td colSpan={10} className="px-6 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          {ev.utmMedium && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Medium</span><span className="text-gray-800">{ev.utmMedium}</span></div>
                          )}
                          {ev.utmContent && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Content</span><span className="text-gray-800">{ev.utmContent}</span></div>
                          )}
                          {ev.utmNetwork && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Network</span><span className="text-gray-800">{ev.utmNetwork}</span></div>
                          )}
                          {ev.clickId && (
                            <div><span className="text-gray-500 uppercase font-semibold block">Click ID</span><span className="text-gray-800 font-mono">{ev.clickId}</span></div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {allEvents.length === 0 && (
          <div className="p-12 text-center text-gray-400">No records match the selected filters.</div>
        )}
        {allEvents.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, allEvents.length)}–{Math.min(page * PAGE_SIZE, allEvents.length)} of {allEvents.length} records
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`ellipsis-${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={`px-2.5 py-1 text-xs rounded border font-medium ${page === p ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                )}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const VENDOR_WORKFLOWS = [
  { key: 'New Contact',    color: 'bg-blue-100 text-blue-700' },
  { key: 'Reattribution',  color: 'bg-purple-100 text-purple-700' },
  { key: 'Reengagement',   color: 'bg-amber-100 text-amber-700' },
  { key: 'Reject',         color: 'bg-red-100 text-red-700' },
];

const VendorList = ({ vendors, events }) => {
  // Count events per vendor per workflow
  const counts = {};
  (events || []).forEach(ev => {
    const name = ev.partner || ev.vendor;
    if (!name) return;
    if (!counts[name]) counts[name] = {};
    const wf = ev.workflow || '';
    counts[name][wf] = (counts[name][wf] || 0) + 1;
  });
  // Match workflow key loosely (e.g. "Reject" matches "Rejection")
  const getCount = (vendorName, wfKey) => {
    const vc = counts[vendorName] || {};
    return Object.entries(vc).reduce((sum, [k, n]) => sum + (k.toLowerCase().startsWith(wfKey.toLowerCase()) ? n : 0), 0);
  };
  return (
  <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
    <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
      <h2 className="text-sm font-semibold text-gray-700">Registered Vendors</h2>
      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(vendors || []).length} vendors</span>
    </div>
    {(!vendors || vendors.length === 0) ? (
      <div className="p-8 text-center text-gray-400 text-sm">No vendors registered yet. Click "+ New Vendor" to add one.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Partner Name</th>
              {VENDOR_WORKFLOWS.map(w => (
                <th key={w.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">{w.key}</th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(vendors || []).map(v => (
              <tr key={v.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold text-gray-900">{v.vendorName}</td>
                {VENDOR_WORKFLOWS.map(w => {
                  const n = getCount(v.vendorName, w.key);
                  return (
                    <td key={w.key} className="px-4 py-3">
                      {n > 0
                        ? <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${w.color}`}>{n}</span>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                  );
                })}
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${v.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.status || 'active'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

const downloadCsv = (filename, headers, rows) => {
  const escape = (val) => {
    const s = String(val ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};


const toPacificDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const VendorEventsModule = ({ data, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onResetFilters }) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const [exportOpen, setExportOpen] = React.useState(false);

  const handleDownloadEvents = () => {
    const events = data?.events || [];
    const headers = ['Contact ID', 'Partner', 'Workflow', 'Date', 'Time', 'UTM Source', 'UTM Campaign', 'Lead Src ID', 'Status', 'UTM Medium', 'UTM Content', 'UTM Network', 'Click ID'];
    const rows = events.map(ev => [
      ev.leadId, ev.partner || ev.vendor || '', ev.workflow, ev.date || '', ev.time || '',
      ev.utmSource || '', ev.utmCampaign || '', ev.leadSourceId || '', ev.status || '',
      ev.utmMedium || '', ev.utmContent || '', ev.utmNetwork || '', ev.clickId || '',
    ]);
    downloadCsv(`vendor-events-${today}.csv`, headers, rows);
    setExportOpen(false);
  };

  const handleDownloadVendors = () => {
    const vendors = data?.vendors || [];
    const headers = ['Vendor Name', 'URL', 'Contact Email', 'Status', 'Last Check Status', 'Last Checked At (PT)'];
    const rows = vendors.map(v => [v.vendorName, v.vendorUrl || '', v.contactEmail || '', v.status, v.lastCheckStatus ?? '', v.lastCheckAt ? toPacificDateTime(v.lastCheckAt) : '']);
    downloadCsv(`vendors-${today}.csv`, headers, rows);
    setExportOpen(false);
  };

  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Vendor Event Observability</h1>
        <p className="text-gray-600 mt-1">Real-time monitoring of vendor lead events across all workflow lines</p>
      </div>
      <div className="flex gap-2">
        <div className="relative">
          <button onClick={() => setExportOpen(o => !o)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 whitespace-nowrap border border-gray-300">
            Export CSV
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {exportOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[220px]">
                <button onClick={handleDownloadEvents} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
                  Lead Events ({(data?.events || []).length})
                </button>
                <button onClick={handleDownloadVendors} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100">
                  Vendor List ({(data?.vendors || []).length})
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    <VendorEventsMetrics metrics={data?.metrics} />
    <VendorList vendors={data?.vendors} events={data?.events} />
    <VendorEventsFilters
      events={data?.events}
      vendors={data?.vendors}
      filterVendor={filterVendor} filterWorkflow={filterWorkflow} filterStatus={filterStatus}
      onFilterVendor={onFilterVendor} onFilterWorkflow={onFilterWorkflow} onFilterStatus={onFilterStatus}
      onReset={onResetFilters}
    />
    <VendorEventsTable events={data?.events} />
  </div>
  );
};

// ============================================================================
// LOGIN VIEW
// ============================================================================

const AuthInput = ({ label, type = 'text', value, onChange, placeholder, required, autoComplete }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      required={required} autoComplete={autoComplete}
      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
    />
  </div>
);

const AuthAlert = ({ type, message }) => type === 'error' ? (
  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{message}
  </div>
) : (
  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{message}</div>
);

const LoginBranding = () => (
  <div className="text-center mb-8">
    <div className="text-5xl mb-3">🛰️</div>
    <h1 className="text-3xl font-bold text-white tracking-wide">ORBIT</h1>
    <p className="text-blue-200 mt-1 text-sm">Enterprise Operations Hub</p>
  </div>
);

// ---- Forgot Password panel ----
const ForgotPasswordPanel = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setResult({ ok: true, data });
    } catch { setResult({ ok: false, msg: 'Network error. Please try again.' }); }
    setLoading(false);
  };

  if (result?.ok) {
    return (
      <div className="p-8 space-y-4">
        <div className="text-center">
          <div className="text-4xl mb-3">✉️</div>
          <h3 className="font-semibold text-gray-900">Reset Link Generated</h3>
          <p className="text-sm text-gray-500 mt-1">{result.data.message}</p>
        </div>
        {result.data.resetUrl && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-700 mb-1">Reset URL (share securely):</p>
            <p className="text-xs text-blue-600 break-all font-mono">{result.data.resetUrl}</p>
            <p className="text-xs text-blue-500 mt-1">Expires in: {result.data.expiresIn}</p>
          </div>
        )}
        <button onClick={onBack} className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium">← Back to Sign In</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-8 space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-semibold text-gray-900">Reset your password</h3>
        <p className="text-xs text-gray-500 mt-1">Enter your email and we'll generate a secure reset link</p>
      </div>
      {result && !result.ok && <AuthAlert type="error" message={result.msg} />}
      <AuthInput label="Work Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@company.com" required autoComplete="email" />
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
        {loading ? 'Generating…' : 'Send Reset Link'}
      </button>
      <button type="button" onClick={onBack} className="w-full text-sm text-gray-500 hover:text-gray-700">← Back to Sign In</button>
    </form>
  );
};

// ---- Reset Password page (standalone, shown when ?reset_token= in URL) ----
const ResetPasswordView = ({ token, onDone }) => {
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: form.newPassword, confirmPassword: form.confirmPassword })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Reset failed'); }
      else { setSuccess(true); }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {success ? (
            <div className="p-8 text-center space-y-4">
              <div className="text-4xl">✅</div>
              <h3 className="font-semibold text-gray-900">Password Reset Successfully</h3>
              <p className="text-sm text-gray-500">You can now sign in with your new password.</p>
              <button onClick={onDone} className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700">
                Go to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              <div className="text-center mb-2">
                <h3 className="font-semibold text-gray-900">Set new password</h3>
                <p className="text-xs text-gray-500 mt-1">Choose a strong password of at least 8 characters</p>
              </div>
              {error && <AuthAlert type="error" message={error} />}
              <AuthInput label="New Password" type="password" value={form.newPassword}
                onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
                required autoComplete="new-password" />
              <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                required autoComplete="new-password" />
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {loading ? 'Saving…' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Main Login View ----
const LoginView = ({ onLogin }) => {
  const [mode, setMode] = useState('login');        // 'login' | 'register' | 'forgot'
  const [authMethod, setAuthMethod] = useState('password');
  const [form, setForm] = useState({ email: '', password: '', name: '', confirmPassword: '' });
  const [ssoIssuer, setSsoIssuer] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const clearMessages = () => setError('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages(); setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const body = mode === 'register'
        ? { email: form.email, name: form.name, password: form.password, confirmPassword: form.confirmPassword }
        : { email: form.email, password: form.password };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Authentication failed');
      else onLogin(data.token, data.user);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const res = await fetch(`/api/auth/google/url?redirectUri=${encodeURIComponent(redirectUri)}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Google sign-in is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'google');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      window.location.href = data.url;
    } catch { setError('Failed to initialize Google sign-in'); }
    setLoading(false);
  };

  const handleSsoLogin = async () => {
    clearMessages();
    if (!ssoIssuer && !ssoEmail) { setError('Enter your work email or SSO provider URL'); return; }
    setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const params = new URLSearchParams({ redirectUri });
      if (ssoIssuer) params.set('issuerUrl', ssoIssuer);
      if (ssoEmail) params.set('email', ssoEmail);
      const res = await fetch(`/api/auth/sso/url?${params}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'SSO is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'sso');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      sessionStorage.setItem('sso_issuer', ssoIssuer);
      window.location.href = data.url;
    } catch { setError('Failed to initialize SSO sign-in'); }
    setLoading(false);
  };

  const handleDemoLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const res = await fetch('/api/auth/demo', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Demo login failed');
      else onLogin(data.token, data.user);
    } catch { setError('Demo login failed. Make sure the database is seeded.'); }
    setLoading(false);
  };

  const methodTab = (id, label, icon) => (
    <button key={id} onClick={() => { setAuthMethod(id); clearMessages(); }}
      className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${authMethod === id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
      <span className="mr-1.5">{icon}</span>{label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Forgot Password panel */}
          {mode === 'forgot' ? (
            <ForgotPasswordPanel onBack={() => { setMode('login'); clearMessages(); }} />
          ) : (
            <>
              {/* Sign In / Create Account tabs */}
              <div className="flex border-b border-gray-100">
                {[['login', 'Sign In'], ['register', 'Create Account']].map(([m, label]) => (
                  <button key={m} onClick={() => { setMode(m); clearMessages(); if (m === 'register') setAuthMethod('password'); }}
                    className={`flex-1 py-4 text-sm font-semibold transition-colors ${mode === m ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50/40' : 'text-gray-400 hover:text-gray-600'}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-8">
                {/* Auth method tabs (Sign In only) */}
                {mode === 'login' && (
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                    {methodTab('password', 'Password', '🔑')}
                    {methodTab('google', 'Google', '🔵')}
                    {methodTab('sso', 'SSO', '🏢')}
                  </div>
                )}

                {error && <AuthAlert type="error" message={error} />}

                {/* PASSWORD FORM */}
                {(authMethod === 'password' || mode === 'register') && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                      <AuthInput label="Full Name" value={form.name} onChange={e => set('name', e.target.value)}
                        placeholder="John Smith" required autoComplete="name" />
                    )}
                    <AuthInput label="Work Email" type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="you@company.com" required autoComplete="email" />
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-gray-600">Password</label>
                        {mode === 'login' && (
                          <button type="button" onClick={() => { setMode('forgot'); clearMessages(); }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <input type="password" required value={form.password} onChange={e => set('password', e.target.value)}
                        placeholder={mode === 'register' ? 'At least 8 characters' : '••••••••'}
                        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                    {mode === 'register' && (
                      <AuthInput label="Confirm Password" type="password" value={form.confirmPassword}
                        onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                        required autoComplete="new-password" />
                    )}
                    <button type="submit" disabled={loading}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Please wait…' : mode === 'register' ? 'Create Account' : 'Sign In'}
                    </button>
                  </form>
                )}

                {/* GOOGLE */}
                {authMethod === 'google' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in using your Google Workspace account.</p>
                    <button onClick={handleGoogleLogin} disabled={loading}
                      className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 rounded-lg py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors">
                      <svg width="18" height="18" viewBox="0 0 18 18">
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
                      </svg>
                      {loading ? 'Redirecting…' : 'Continue with Google'}
                    </button>
                  </div>
                )}

                {/* SSO */}
                {authMethod === 'sso' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in via Azure AD, Okta, Ping, or any OIDC provider.</p>
                    <AuthInput label="Work Email" type="email" value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} placeholder="you@company.com" />
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <div className="flex-1 h-px bg-gray-200" />or<div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <AuthInput label="SSO Provider URL" type="url" value={ssoIssuer} onChange={e => setSsoIssuer(e.target.value)} placeholder="https://login.microsoftonline.com/{tenant-id}/v2.0" />
                    <button onClick={handleSsoLogin} disabled={loading}
                      className="w-full bg-indigo-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Redirecting…' : 'Continue with SSO'}
                    </button>
                  </div>
                )}

                {/* Demo */}
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <button onClick={handleDemoLogin} disabled={loading}
                    className="w-full py-2.5 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-200 transition-colors">
                    {loading ? 'Loading…' : '⚡ Continue with Demo Account'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-blue-200 text-xs mt-6 opacity-70">ORBIT Enterprise © {new Date().getFullYear()}</p>
      </div>
    </div>
  );
};

// ---- Change Password Modal ----
const ChangePasswordModal = ({ authToken, onClose }) => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to change password');
      else setSuccess(true);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={16} className="text-blue-600" /> Change Password
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {success ? (
          <div className="p-6 text-center space-y-3">
            <div className="text-3xl">✅</div>
            <p className="font-medium text-gray-900">Password changed successfully</p>
            <button onClick={onClose} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <AuthAlert type="error" message={error} />}
            <AuthInput label="Current Password" type="password" value={form.currentPassword}
              onChange={e => set('currentPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="current-password" />
            <AuthInput label="New Password" type="password" value={form.newPassword}
              onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
              required autoComplete="new-password" />
            <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="new-password" />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
                {loading ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// AUDIT TRAIL VIEW
// ============================================================================

const ACTION_BADGE = {
  CREATE:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Create' },
  UPDATE:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Update' },
  DELETE:  { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Delete' },
  LOGIN:   { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Login' },
  LOGOUT:  { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Logout' },
  VIEW:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'View' },
  READ:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'Read' },
  EXPORT:  { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Export' },
  IMPORT:  { bg: 'bg-teal-100',   text: 'text-teal-800',   label: 'Import' },
  APPROVE: { bg: 'bg-emerald-100',text: 'text-emerald-800',label: 'Approve' },
  REJECT:  { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reject' },
};

const MODULE_BADGE = {
  plantrack:    { bg: 'bg-indigo-100',  text: 'text-indigo-800' },
  devops:       { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  quality:      { bg: 'bg-pink-100',    text: 'text-pink-800' },
  timeboard:    { bg: 'bg-orange-100',  text: 'text-orange-800' },
  leaveboard:   { bg: 'bg-teal-100',    text: 'text-teal-800' },
  system:       { bg: 'bg-gray-100',    text: 'text-gray-700' },
  dashboard:    { bg: 'bg-violet-100',  text: 'text-violet-800' },
  dataops:      { bg: 'bg-cyan-100',    text: 'text-cyan-800' },
  learntrack:   { bg: 'bg-rose-100',    text: 'text-rose-800' },
  quicktrack:   { bg: 'bg-yellow-100',  text: 'text-yellow-800' },
  vendorevents: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800' },
};

const AuditActionBadge = ({ action }) => {
  const a = (action || '').toUpperCase();
  const style = ACTION_BADGE[a] || { bg: 'bg-yellow-100', text: 'text-yellow-800', label: action };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label || action}
    </span>
  );
};

const AuditModuleBadge = ({ module }) => {
  const key = (module || '').toLowerCase();
  const style = MODULE_BADGE[key] || { bg: 'bg-blue-100', text: 'text-blue-800' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text} capitalize`}>
      {module || 'system'}
    </span>
  );
};

const AuditTrailView = ({ logs, stats, filters, onFilterChange, onLoadMore, hasMore, loading }) => {
  const [localFilters, setLocalFilters] = useState({ ...filters });

  const handleFilter = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    onFilterChange({ ...localFilters, offset: 0 });
  };

  const resetFilters = () => {
    const empty = { module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' };
    setLocalFilters(empty);
    onFilterChange({ ...empty, offset: 0 });
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ts; }
  };

  const maxModuleCount = stats?.byModule?.length ? Math.max(...stats.byModule.map(m => m.count)) : 1;

  const knownModules = ['plantrack', 'devops', 'quality', 'timeboard', 'leaveboard', 'meetingboard', 'dataops', 'learntrack', 'quicktrack', 'vendorevents', 'system', 'dashboard'];
  const knownActions = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'VIEW', 'READ', 'EXPORT', 'IMPORT', 'APPROVE', 'REJECT'];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={24} className="text-blue-600" /> Audit Trail
          </h1>
          <p className="text-gray-500 text-sm mt-1">Comprehensive system-wide activity logging and monitoring</p>
        </div>
        <button onClick={applyFilters} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Events', value: (stats?.total || 0).toLocaleString(), icon: <Activity size={18} className="text-blue-500" />, color: 'border-blue-200' },
          { label: "Today's Events", value: (stats?.today || 0).toLocaleString(), icon: <Clock size={18} className="text-green-500" />, color: 'border-green-200' },
          { label: 'Unique Users', value: (stats?.uniqueUsers || 0).toLocaleString(), icon: <Users size={18} className="text-purple-500" />, color: 'border-purple-200' },
          { label: 'Last 7 Days', value: (stats?.lastWeek || 0).toLocaleString(), icon: <BarChart3 size={18} className="text-orange-500" />, color: 'border-orange-200' },
        ].map(card => (
          <div key={card.label} className={`bg-white rounded-lg shadow-sm border-l-4 ${card.color} p-4`}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 font-medium">{card.label}</p>
              {card.icon}
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Module Breakdown + Action Breakdown */}
      {stats && (stats.byModule?.length > 0 || stats.byAction?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* By Module */}
          {stats.byModule?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Module</h3>
              <div className="space-y-2">
                {stats.byModule.map(m => (
                  <div key={m.module} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate capitalize">{m.module || 'system'}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.round((m.count / maxModuleCount) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-8 text-right">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* By Action */}
          {stats.byAction?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Action</h3>
              <div className="flex flex-wrap gap-2">
                {stats.byAction.map(a => (
                  <div key={a.action_type} className="flex items-center gap-1">
                    <AuditActionBadge action={a.action_type} />
                    <span className="text-xs text-gray-500">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={localFilters.search || ''}
              onChange={e => handleFilter('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              className="w-full pl-7 pr-2 py-2 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <select
            value={localFilters.module || ''}
            onChange={e => handleFilter('module', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Modules</option>
            {knownModules.map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
          </select>
          <select
            value={localFilters.action || ''}
            onChange={e => handleFilter('action', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Actions</option>
            {knownActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            type="date"
            value={localFilters.dateFrom || ''}
            onChange={e => handleFilter('dateFrom', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="From date"
          />
          <input
            type="date"
            value={localFilters.dateTo || ''}
            onChange={e => handleFilter('dateTo', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="To date"
          />
          <div className="flex gap-2">
            <button onClick={applyFilters} className="flex-1 bg-blue-600 text-white rounded px-3 py-2 text-xs hover:bg-blue-700">Apply</button>
            <button onClick={resetFilters} className="flex-1 bg-gray-100 text-gray-600 rounded px-3 py-2 text-xs hover:bg-gray-200">Reset</button>
          </div>
        </div>
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No audit events found matching your filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-36">Timestamp</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">User</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Action</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Module</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Entity</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Details</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-24">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap font-mono text-xs">
                        {formatTime(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{log.user_name || '—'}</div>
                        {log.user_id && <div className="text-gray-400 text-xs">ID: {log.user_id}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <AuditActionBadge action={log.action_type} />
                      </td>
                      <td className="px-4 py-3">
                        <AuditModuleBadge module={log.module} />
                      </td>
                      <td className="px-4 py-3">
                        {log.entity_name && <div className="font-medium text-gray-700">{log.entity_name}</div>}
                        {log.entity_type && <div className="text-gray-400">{log.entity_type}{log.entity_id ? ` #${log.entity_id}` : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={log.details || ''}>
                        {log.details || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 font-mono whitespace-nowrap">
                        {log.ip_address && log.ip_address !== 'unknown' ? log.ip_address : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  onClick={onLoadMore}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 font-medium"
                >
                  Load More Events
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---- Setup View (first-run admin creation wizard) ----
const SetupView = ({ onSetupComplete }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ orgName: '', name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName: form.orgName, name: form.name, email: form.email, password: form.password, confirmPassword: form.confirmPassword })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Setup failed'); }
      else {
        localStorage.setItem('orbit_token', data.token);
        localStorage.setItem('orbit_user', JSON.stringify(data.user));
        onSetupComplete(data.token, data.user);
      }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🚀</span>
          </div>
          <h1 className="text-2xl font-bold">Welcome to ORBIT</h1>
          <p className="text-blue-100 mt-1 text-sm">Let's set up your workspace</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>1</div>
            <div className={`h-1 w-8 rounded ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>2</div>
          </div>
        </div>
        <div className="p-8">
          {step === 1 ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Organization Setup</h2>
              <p className="text-gray-500 text-sm mb-6">Enter your organization name to get started.</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                <input type="text" value={form.orgName} onChange={e => setForm(f => ({ ...f, orgName: e.target.value }))}
                  placeholder="e.g. Acme Corp" autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
              <button onClick={() => { if (!form.orgName.trim()) return; setStep(2); }} disabled={!form.orgName.trim()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Continue
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Create Admin Account</h2>
              <p className="text-gray-500 text-sm mb-6">This will be the primary administrator account.</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus
                    placeholder="John Doe" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                    placeholder="admin@company.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
                    placeholder="Minimum 8 characters" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} required
                    placeholder="Repeat password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium">Back</button>
                <button type="submit" disabled={loading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? 'Creating...' : 'Create Admin Account & Launch ORBIT'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Role Permissions Section ----
const ALL_MODULES_LIST = [
  { id: 'devops',         name: 'DevOps' },
  { id: 'dataops',        name: 'DataOps' },
  { id: 'plantrack',      name: 'PlanTrack' },
  { id: 'quality',        name: 'Quality' },
  { id: 'quicktrack',     name: 'QuickTrack' },
  { id: 'learntrack',     name: 'LearnTrack' },
  { id: 'timeboard',      name: 'TimeBoard' },
  { id: 'leaveboard',     name: 'LeaveBoard' },
  { id: 'meetingboard',   name: 'MeetingBoard' },
  { id: 'knowledgeboard', name: 'KnowledgeBoard' },
  { id: 'incidentboard',  name: 'IncidentBoard' },
  { id: 'automationboard', name: 'AutomationBoard' },
  { id: 'assetboard',      name: 'AssetBoard' },
  { id: 'requestboard',   name: 'RequestBoard' },
  { id: 'vendorevents',   name: 'Vendor Events' },
  { id: 'leaderboard',    name: 'LeaderBoard' },
];

const ROLE_DISPLAY_NAMES = {
  admin:           'Admin',
  developer:       'Developer',
  dataops:         'DataOps',
  project:         'Project Manager',
  qa:              'QA',
  qa_lead:         'QA Lead',
  devops_manager:  'DevOps Manager',
  dataops_manager: 'DataOps Manager',
  manager:         'Manager',
  marketing:       'Marketing',
};

const RolePermissionsSection = ({ authToken }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  // drafts: { [roleId]: Set of moduleIds }
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await fetch('/api/settings/roles', { headers: { Authorization: `Bearer ${authToken}` } });
    if (res.ok) {
      const data = await res.json();
      const rolesData = data.roles || [];
      setRoles(rolesData);
      const init = {};
      for (const r of rolesData) init[r.id] = new Set(r.modules || []);
      setDrafts(init);
      if (rolesData.length > 0 && !selectedRole) setSelectedRole(rolesData[0].id);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleModule = (roleId, moduleId) => {
    setDrafts(d => {
      const current = new Set(d[roleId] || []);
      if (current.has(moduleId)) current.delete(moduleId); else current.add(moduleId);
      return { ...d, [roleId]: current };
    });
    setSaved(false);
  };

  const save = async () => {
    if (!selectedRole) return;
    const role = roles.find(r => r.id === selectedRole);
    if (role?.name === 'admin') return; // admin always unrestricted
    setSaving(true); setSaved(false); setError('');
    const moduleIds = Array.from(drafts[selectedRole] || []);
    const res = await fetch(`/api/settings/roles/${selectedRole}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ moduleIds }),
    });
    setSaving(false);
    if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
    else { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to save'); }
  };

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-sm text-gray-500">Loading permissions…</div>;

  const activeRole = roles.find(r => r.id === selectedRole);
  const isAdmin = activeRole?.name === 'admin';
  const activeModules = drafts[selectedRole] || new Set();

  return (
    <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-1">
          <Shield className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Role Permissions</h2>
        </div>
        <p className="text-sm text-gray-500">
          Select a role to configure which modules its members can access. Changes take effect on next login.
        </p>
      </div>

      <div className="flex min-h-[340px]">
        {/* Left: role list */}
        <div className="w-52 border-r border-gray-100 flex-shrink-0">
          {roles.map(role => (
            <button
              key={role.id}
              onClick={() => { setSelectedRole(role.id); setSaved(false); setError(''); }}
              className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors border-b border-gray-50 ${
                selectedRole === role.id
                  ? 'bg-indigo-50 text-indigo-700 border-l-4 border-l-indigo-500'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {ROLE_DISPLAY_NAMES[role.name] || role.name}
              {role.name === 'admin' && (
                <span className="ml-2 text-xs text-indigo-400 font-normal">(all access)</span>
              )}
            </button>
          ))}
        </div>

        {/* Right: module toggles */}
        <div className="flex-1 px-6 py-5">
          {!activeRole ? (
            <p className="text-sm text-gray-400 italic">Select a role to configure.</p>
          ) : isAdmin ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-2 py-10">
              <Shield className="w-8 h-8 text-indigo-300" />
              <p className="text-sm font-medium text-indigo-500">Admin role has unrestricted access to all modules.</p>
              <p className="text-xs text-gray-400">Module restrictions cannot be applied to the Admin role.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {ALL_MODULES_LIST.map(mod => {
                  const enabled = activeModules.has(mod.id);
                  return (
                    <button
                      key={mod.id}
                      onClick={() => toggleModule(selectedRole, mod.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                        enabled
                          ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center ${
                        enabled ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                      }`}>
                        {enabled && <span className="text-white text-[10px] leading-none font-bold">✓</span>}
                      </div>
                      <span className="text-sm font-medium">{mod.name}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
                </button>
                {error && <p className="text-sm text-red-500">{error}</p>}
                {saved && !saving && <p className="text-sm text-green-600">Permissions updated successfully.</p>}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          All granted modules give <strong>full access</strong> (create, edit, delete). Fine-grained permission levels are not used in this release.
        </p>
      </div>
    </div>
  );
};

const TeamManagementSection = ({ authToken }) => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [addMemberTeamId, setAddMemberTeamId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [teamForm, setTeamForm] = useState({ teamName: '', description: '', department: '', color: '#667eea', module: '' });
  const [editingTeam, setEditingTeam] = useState(null); // { id, teamName, description, department, color, module }

  const COLORS = ['#667eea', '#48bb78', '#ed8936', '#f56565', '#4299e1', '#9f7aea', '#38b2ac', '#e53e3e'];

  const load = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const teamsData = await teamsRes.json();
      const usersData = await usersRes.json();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/teams', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(teamForm)
      });
      if (res.ok) {
        setShowAddTeam(false);
        setTeamForm({ teamName: '', description: '', department: '', color: '#667eea', module: '' });
        load();
      }
    } catch { }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team?')) return;
    try {
      await fetch(`/api/teams/${teamId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      setActionMsg('Team deleted');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await fetch(`/api/teams/${editingTeam.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          teamName: editingTeam.teamName, description: editingTeam.description,
          department: editingTeam.department, color: editingTeam.color, module: editingTeam.module
        })
      });
      setEditingTeam(null);
      setActionMsg('Team updated');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserId) return;
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId: parseInt(selectedUserId) })
      });
      setSelectedUserId('');
      setAddMemberTeamId(null);
      load();
    } catch { }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { }
  };

  const getMemberNames = (team) => {
    if (!team.members) return [];
    try { return typeof team.members === 'string' ? JSON.parse(team.members) : team.members; }
    catch { return []; }
  };

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading teams...</div>;

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Team Management</h2>
        </div>
        <button onClick={() => setShowAddTeam(!showAddTeam)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Team
        </button>
      </div>

      {actionMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{actionMsg}</div>
      )}

      {showAddTeam && (
        <form onSubmit={handleAddTeam} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-3">Create New Team</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input type="text" placeholder="Team Name *" required value={teamForm.teamName}
              onChange={e => setTeamForm(f => ({ ...f, teamName: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
            <input type="text" placeholder="Department" value={teamForm.department}
              onChange={e => setTeamForm(f => ({ ...f, department: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
          </div>
          <input type="text" placeholder="Description" value={teamForm.description}
            onChange={e => setTeamForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-3" />
          <select value={teamForm.module} onChange={e => setTeamForm(f => ({ ...f, module: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-3">
            <option value="">— Module (for EOD reports) —</option>
            <option value="devops">DevOps</option>
            <option value="dataops">DataOps</option>
            <option value="quality">Quality</option>
            <option value="plantrack">PlanTrack</option>
            <option value="quicktrack">QuickTrack</option>
          </select>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-600">Color:</span>
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setTeamForm(f => ({ ...f, color: c }))}
                className={`w-6 h-6 rounded-full transition-transform ${teamForm.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Create Team</button>
            <button type="button" onClick={() => setShowAddTeam(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg text-sm font-medium hover:bg-gray-300">Cancel</button>
          </div>
        </form>
      )}

      {teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No teams yet. Create your first team above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => {
            const members = getMemberNames(team);
            const isExpanded = expandedTeam === team.id;
            return (
              <div key={team.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color || '#667eea' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{team.team_name}</span>
                      {team.department && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{team.department}</span>}
                      {team.module && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded capitalize">{team.module}</span>}
                      <span className="text-xs text-gray-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                    </div>
                    {team.description && <p className="text-sm text-gray-500 truncate">{team.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); setEditingTeam({ id: team.id, teamName: team.team_name, description: team.description || '', department: team.department || '', color: team.color || '#667eea', module: team.module || '' }); setExpandedTeam(team.id); }}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit team">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete team "${team.name}"?`, () => handleDeleteTeam(team.id)); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    {editingTeam?.id === team.id && (
                      <form onSubmit={handleUpdateTeam} className="mb-4 p-3 bg-white rounded-lg border border-indigo-200">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Edit Team</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                          <input type="text" required placeholder="Team Name *" value={editingTeam.teamName}
                            onChange={e => setEditingTeam(t => ({ ...t, teamName: e.target.value }))}
                            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                          <input type="text" placeholder="Department" value={editingTeam.department}
                            onChange={e => setEditingTeam(t => ({ ...t, department: e.target.value }))}
                            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                        </div>
                        <input type="text" placeholder="Description" value={editingTeam.description}
                          onChange={e => setEditingTeam(t => ({ ...t, description: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-2" />
                        <select value={editingTeam.module} onChange={e => setEditingTeam(t => ({ ...t, module: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-2">
                          <option value="">— Module (for EOD reports) —</option>
                          <option value="devops">DevOps</option>
                          <option value="dataops">DataOps</option>
                          <option value="quality">Quality</option>
                          <option value="plantrack">PlanTrack</option>
                          <option value="quicktrack">QuickTrack</option>
                        </select>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-600">Color:</span>
                          {COLORS.map(c => (
                            <button key={c} type="button" onClick={() => setEditingTeam(t => ({ ...t, color: c }))}
                              className={`w-5 h-5 rounded-full transition-transform ${editingTeam.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                              style={{ backgroundColor: c }} />
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">Save</button>
                          <button type="button" onClick={() => setEditingTeam(null)} className="px-3 py-1.5 text-gray-600 bg-gray-100 rounded text-sm hover:bg-gray-200">Cancel</button>
                        </div>
                      </form>
                    )}
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Members</h4>
                    {members.length === 0 ? (
                      <p className="text-sm text-gray-400 mb-2">No members yet.</p>
                    ) : (
                      <div className="space-y-1 mb-2">
                        {members.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
                                {(m.user_name || m.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{m.user_name || m.email}</span>
                                {m.member_role === 'lead' && (
                                  <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Lead</span>
                                )}
                              </div>
                            </div>
                            <button onClick={() => handleRemoveMember(team.id, m.user_id)} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {addMemberTeamId === team.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
                          <option value="">Select user...</option>
                          {users.filter(u => !members.some(m => m.user_id === u.id)).map(u => (
                            <option key={u.id} value={u.id}>{u.user_name || u.email}</option>
                          ))}
                        </select>
                        <button onClick={() => handleAddMember(team.id)} disabled={!selectedUserId}
                          className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">Add</button>
                        <button onClick={() => { setAddMemberTeamId(null); setSelectedUserId(''); }}
                          className="px-3 py-2 text-gray-600 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddMemberTeamId(team.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add Member
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- User Management Section (admin only) ----
const UserManagementSection = ({ authToken }) => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', role: 'user', password: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [assigningUserId, setAssigningUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, teamsRes] = await Promise.all([
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const usersData = await usersRes.json();
      const teamsData = await teamsRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch { setUsers([]); setTeams([]); }
    setLoading(false);
  };

  const getUserTeams = (userId) =>
    teams.filter(t => (t.members || []).some(m => m.user_id === userId));

  const getUnassignedTeams = (userId) =>
    teams.filter(t => !(t.members || []).some(m => m.user_id === userId));

  const handleAssignTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId })
      });
      setAssigningUserId(null);
      load();
    } catch { /* ignore */ }
  };

  const handleRemoveFromTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddResult(null);
    try {
      const res = await fetch('/api/users', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (!res.ok) setAddResult({ type: 'error', message: data.error || 'Failed to create user' });
      else {
        setAddResult({ type: 'success', message: 'User created.', resetUrl: data.resetUrl });
        setAddForm({ email: '', name: '', role: 'user', password: '' });
        load();
      }
    } catch { setAddResult({ type: 'error', message: 'Network error' }); }
    setAddLoading(false);
  };

  const handleToggleActive = async (u) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ isActive: !u.is_active })
      });
      setActionMsg(`User ${u.is_active ? 'deactivated' : 'activated'} successfully`);
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleRoleChange = async (u, newRole) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ role: newRole })
      });
      setActionMsg('Role updated');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleAdminReset = async (u) => {
    try {
      const res = await fetch(`/api/users/${u.id}/reset-password`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.resetUrl) {
        setActionMsg(`Reset link: ${data.resetUrl}`);
      } else {
        setActionMsg('Reset link generated and sent');
        setTimeout(() => setActionMsg(''), 4000);
      }
    } catch { /* ignore */ }
  };

  const fmt = (ts) => ts ? new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleDateString() : 'Never';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users size={20} className="text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
        </div>
        <button onClick={() => { setShowAddUser(v => !v); setAddResult(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          + Add User
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-4">Manage user accounts, roles, and password resets for your organization.</p>

      {actionMsg && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 break-all">{actionMsg}</div>
      )}

      {showAddUser && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-3 text-sm">Add New User</h3>
          {addResult && (
            <div className={`mb-3 p-2 rounded text-xs ${addResult.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {addResult.message}
              {addResult.resetUrl && <div className="mt-1 font-mono break-all">Invite URL: {addResult.resetUrl}</div>}
            </div>
          )}
          <form onSubmit={handleAddUser} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required placeholder="Full Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <input required type="email" placeholder="Email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="developer">Developer</option>
              <option value="dataops">DataOps</option>
              <option value="project">Project Team</option>
              <option value="qa">Quality Assurance</option>
              <option value="qa_lead">QA Lead</option>
              <option value="devops_manager">DevOps Manager</option>
              <option value="manager">Manager</option>
            </select>
            <input type="password" placeholder="Password (leave blank for invite link)" value={addForm.password}
              onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={addLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60">
                {addLoading ? 'Creating…' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowAddUser(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading users…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Role</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Teams</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Last Login</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => {
                const userTeams = getUserTeams(u.id);
                const unassigned = getUnassignedTeams(u.id);
                const isAssigning = assigningUserId === u.id;
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{u.user_name}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={u.user_role} onChange={e => handleRoleChange(u, e.target.value)}
                        className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="developer">Developer (DevOps)</option>
                        <option value="dataops">DataOps</option>
                        <option value="project">Project Team</option>
                        <option value="qa">Quality Assurance</option>
                        <option value="qa_lead">QA Lead</option>
                        <option value="devops_manager">DevOps Manager</option>
                        <option value="manager">Manager</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 min-w-[180px]">
                        {/* Current team pills */}
                        {userTeams.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {userTeams.map(t => (
                              <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: t.color || '#667eea' }}>
                                {t.team_name}
                                <button onClick={() => handleRemoveFromTeam(u.id, t.id)}
                                  className="hover:opacity-70 ml-0.5 leading-none" title="Remove from team">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No team assigned</span>
                        )}
                        {/* Assign team control */}
                        {teams.length === 0 ? null : isAssigning ? (
                          <div className="flex items-center gap-1.5">
                            <select autoFocus defaultValue=""
                              onChange={e => { if (e.target.value) handleAssignTeam(u.id, parseInt(e.target.value)); }}
                              className="border border-indigo-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
                              <option value="" disabled>Select a team…</option>
                              {unassigned.map(t => (
                                <option key={t.id} value={t.id}>{t.team_name}</option>
                              ))}
                            </select>
                            <button onClick={() => setAssigningUserId(null)}
                              className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded hover:bg-gray-200">
                              Cancel
                            </button>
                          </div>
                        ) : unassigned.length > 0 ? (
                          <button onClick={() => setAssigningUserId(u.id)}
                            className="w-fit inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 border border-indigo-200 text-xs font-medium text-indigo-600 hover:bg-indigo-100 hover:border-indigo-400">
                            <Plus className="w-3.5 h-3.5" /> Assign Team
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleAdminReset(u)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          Reset PW
                        </button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleToggleActive(u)}
                          className={`text-xs font-medium ${u.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-sm">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettingsView = ({ onClearData, user, authToken }) => {
  const moduleOptions = [
    { key: 'plantrack', label: 'PlanTrack (Projects, Milestones, Tasks)' },
    { key: 'quicktrack', label: 'QuickTrack (Quick Tasks)' },
    { key: 'dataops', label: 'DataOps (Pipelines, Reports)' },
    { key: 'qms', label: 'Quality / QMS (Requirements, Test Cases, Bugs)' },
    { key: 'timeboard', label: 'TimeBoard (Timesheets, Entries)' },
    { key: 'leaveboard', label: 'LeaveBoard (Leave Requests, Balances)' },
    { key: 'learntrack', label: 'LearnTrack (Courses, Skills, Badges)' },
    { key: 'vendors', label: 'Vendors (Vendors, Events)' }
  ];

  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);

  const allSelected = selected.length === moduleOptions.length;

  const toggleModule = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : moduleOptions.map(m => m.key));
  };

  const handleClear = async () => {
    setClearing(true);
    setResult(null);
    try {
      const modules = allSelected ? ['all'] : selected;
      const res = await onClearData(modules);
      if (res?.success) {
        setResult({ type: 'success', message: `Cleared ${res.tablesCleared} table(s) successfully. You can now enter your real data.` });
        setSelected([]);
      } else {
        setResult({ type: 'error', message: res?.error || 'Failed to clear data.' });
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again.' });
    }
    setClearing(false);
    setConfirmOpen(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your system data and configurations.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Clear test/demo data from selected modules so you can replace it with real data.
          This deletes all records from the selected modules but keeps the database structure intact.
        </p>

        {result && (
          <div className={`mb-4 p-4 rounded-lg ${result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {result.message}
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 text-red-600 rounded" />
            <span className="font-semibold text-gray-900">Select All Modules</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {moduleOptions.map(mod => (
              <label key={mod.key} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(mod.key)} onChange={() => toggleModule(mod.key)} className="w-4 h-4 text-red-600 rounded" />
                <span className="text-sm text-gray-700">{mod.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={selected.length === 0 || clearing}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Clear Selected Data
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Data Deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              This will permanently delete all data from <strong>{selected.length}</strong> module(s):
            </p>
            <ul className="text-sm text-gray-700 mb-4 ml-4 list-disc">
              {selected.map(key => (
                <li key={key}>{moduleOptions.find(m => m.key === key)?.label}</li>
              ))}
            </ul>
            <p className="text-red-600 text-sm font-medium mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmOpen(false)} disabled={clearing} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleClear} disabled={clearing} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {clearing ? 'Clearing...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management — admin only */}
      {user?.role === 'admin' && (
        <div className="mt-6">
          <UserManagementSection authToken={authToken} />
        </div>
      )}

      {/* Role Permissions — admin only */}
      {user?.role === 'admin' && (
        <RolePermissionsSection authToken={authToken} />
      )}

      {/* Team Management — admin only */}
      {user?.role === 'admin' && (
        <TeamManagementSection authToken={authToken} />
      )}
    </div>
  );
};

// ============================================================================
// CONTROLLER (Application Logic)
// ============================================================================

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [burnCharts, setBurnCharts] = useState(null);
  const [modules, setModules] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('weekly');
  const [devOpsData, setDevOpsData] = useState(null);
  const [devOpsViewMode, setDevOpsViewMode] = useState('sprint');
  const [devOpsGroup, setDevOpsGroup] = useState('all');
  const [showAddDevOpsTask, setShowAddDevOpsTask] = useState(false);
  const [showAddDataOpsTask, setShowAddDataOpsTask] = useState(false);
  const [selectedDevOpsTask, setSelectedDevOpsTask] = useState(null);
  const [selectedBug, setSelectedBug] = useState(null);
  const [planTrackData, setPlanTrackData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddSprint, setShowAddSprint] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showSprintsView, setShowSprintsView] = useState(false);
  const [showMilestoneEngine, setShowMilestoneEngine] = useState(false);
  const [showPostUpdateReport, setShowPostUpdateReport] = useState(false);
  const [showSendEod, setShowSendEod] = useState(false);
  const [eodModule, setEodModule] = useState('devops');
  const [showEodSettings, setShowEodSettings] = useState(false);
  const [showQaEod, setShowQaEod] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editingMilestoneData, setEditingMilestoneData] = useState(null);
  const [dataOpsData, setDataOpsData] = useState(null);
  const [dataOpsViewMode, setDataOpsViewMode] = useState('dashboard');
  const [dataOpsKanban, setDataOpsKanban] = useState(null);
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [learnTrackData, setLearnTrackData] = useState(null);
  const [learnTrackViewMode, setLearnTrackViewMode] = useState('dashboard');
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [learnFilterSkill, setLearnFilterSkill] = useState('');
  const [learnFilterGroup, setLearnFilterGroup] = useState('');
  const [timeBoardData, setTimeBoardData] = useState(null);
  const [leaveBoardData, setLeaveBoardData] = useState(null);
  const [meetingBoardData, setMeetingBoardData] = useState(null);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [knowledgeData, setKnowledgeData] = useState(null);
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [knowledgeSelectedCategory, setKnowledgeSelectedCategory] = useState(null);
  const [knowledgeViewingArticle, setKnowledgeViewingArticle] = useState(null);
  const [knowledgeEditingArticle, setKnowledgeEditingArticle] = useState(null);
  const [knowledgeShowEditor, setKnowledgeShowEditor] = useState(false);
  const [knowledgeArticleVersions, setKnowledgeArticleVersions] = useState([]);
  const [knowledgeShowVersions, setKnowledgeShowVersions] = useState(false);
  const [knowledgeShowCategoryModal, setKnowledgeShowCategoryModal] = useState(false);
  const [incidentData, setIncidentData] = useState(null);
  const [incidentFilters, setIncidentFilters] = useState({ severity: '', status: '', team: '', search: '' });
  const [viewingIncident, setViewingIncident] = useState(null);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Use Pacific timezone for week calculation
    const pacificDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const d = new Date(pacificDateStr + 'T12:00:00');
    const dow = d.getDay();
    const diff = d.getDate() - dow + (dow === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  });
  const [showRequestLeave, setShowRequestLeave] = useState(false);
  const [showTimeEntry, setShowTimeEntry] = useState(null);
  const [quickTrackData, setQuickTrackData] = useState(null);
  const [qtFilterStatus, setQtFilterStatus] = useState('');
  const [qtFilterPriority, setQtFilterPriority] = useState('');
  const [qtFilterCategory, setQtFilterCategory] = useState('');
  const [showAddQuickTask, setShowAddQuickTask] = useState(false);
  const [editingQuickTask, setEditingQuickTask] = useState(null);
  const [vendorEventsData, setVendorEventsData] = useState(null);
  const [veFilterVendor, setVeFilterVendor] = useState('');
  const [veFilterWorkflow, setVeFilterWorkflow] = useState('');
  const [veFilterStatus, setVeFilterStatus] = useState('');
  const [qualityData, setQualityData] = useState(null);
  const [qmsView, setQmsView] = useState('pipeline'); // 'pipeline' | 'bugs'
  const [allBugsData, setAllBugsData] = useState(null);
  const [allBugsFilters, setAllBugsFilters] = useState({ projectId: '', severity: '', status: '', search: '', sort: 'created_at', order: 'desc' });
  const [viewingBugDetail, setViewingBugDetail] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [taskManageData, setTaskManageData] = useState(null);
  const [taskManageFilters, setTaskManageFilters] = useState({ source: '', status: [], priority: [], assignee: [], project: [], search: '' });
  const [qmsStage, setQmsStage] = useState('requirements');
  const [qmsProjectId, setQmsProjectId] = useState(null);
  const [showAddRequirement, setShowAddRequirement] = useState(false);
  const [addReqDefaults, setAddReqDefaults] = useState({});
  const [showAddTestCase, setShowAddTestCase] = useState(false);
  const [showReportBug, setShowReportBug] = useState(false);
  const [reportBugPrefill, setReportBugPrefill] = useState(null);
  const [editingBug, setEditingBug] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMessaging, setShowMessaging] = useState(false);
  const [msgConversations, setMsgConversations] = useState([]);
  const [msgOpenUserId, setMsgOpenUserId] = useState(null);
  const [msgOpenUserName, setMsgOpenUserName] = useState('');

  const fetchUnreadMessages = async () => {
    const tok = localStorage.getItem('orbit_token');
    if (!tok) return;
    try {
      const [countRes, convRes] = await Promise.all([
        fetch('/api/messages/unread-count', { headers: { Authorization: `Bearer ${tok}` } }),
        fetch('/api/messages/conversations', { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      if (countRes.ok) { const d = await countRes.json(); setUnreadMessages(d.unread || 0); }
      if (convRes.ok) { const d = await convRes.json(); setMsgConversations(d.conversations || []); }
    } catch { /* ignore */ }
  };

  const fetchNotifications = async () => {
    const token = localStorage.getItem('orbit_token');
    if (!token) return;
    try {
      const res = await fetch('/api/notifications', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread || 0);
      }
    } catch { /* ignore */ }
  };

  const handleMarkNotificationRead = async (id) => {
    const token = localStorage.getItem('orbit_token');
    setNotifications(n => n.map(x => x.id === id ? { ...x, isRead: 1 } : x));
    setUnreadCount(c => Math.max(0, c - 1));
    await fetch(`/api/notifications/${id}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleMarkAllRead = async () => {
    const token = localStorage.getItem('orbit_token');
    setNotifications(n => n.map(x => ({ ...x, isRead: 1 })));
    setUnreadCount(0);
    await fetch('/api/notifications/read-all', { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleDismissNotification = async (id) => {
    const token = localStorage.getItem('orbit_token');
    const notif = notifications.find(n => n.id === id);
    setNotifications(n => n.filter(x => x.id !== id));
    if (notif && !notif.isRead) setUnreadCount(c => Math.max(0, c - 1));
    await fetch(`/api/notifications/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  // Auth init: check setup status, localStorage token, handle OAuth callbacks
  useEffect(() => {
    const initAuth = async () => {
      // Check for OAuth callback (code in URL params)
      const params = new URLSearchParams(window.location.search);

      // Check for password reset token
      const rt = params.get('reset_token');
      if (rt) {
        setResetToken(rt);
        window.history.replaceState({}, document.title, '/');
        setAuthChecked(true);
        return;
      }

      // Check if first-run setup is needed (no users exist)
      try {
        const setupRes = await fetch('/api/auth/setup-status');
        const setupData = await setupRes.json();
        if (setupData.needsSetup) {
          setNeedsSetup(true);
          setAuthChecked(true);
          return;
        }
      } catch { /* ignore, proceed normally */ }

      const code = params.get('code');
      const state = params.get('state');
      if (code && state) {
        const provider = sessionStorage.getItem('oauth_provider');
        const redirectUri = sessionStorage.getItem('oauth_redirect_uri') || window.location.origin;
        sessionStorage.removeItem('oauth_provider');
        sessionStorage.removeItem('oauth_redirect_uri');
        try {
          let endpoint = '/api/auth/google/callback';
          const body = { code, redirectUri };
          if (provider === 'sso' || (state && state.startsWith('sso:'))) {
            endpoint = '/api/auth/sso/callback';
            body.issuerUrl = sessionStorage.getItem('sso_issuer') || '';
            sessionStorage.removeItem('sso_issuer');
          }
          const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('orbit_token', data.token);
            localStorage.setItem('orbit_user', JSON.stringify(data.user));
            setAuthToken(data.token);
            setUser(data.user);
            setCurrentPage('dashboard');
            window.history.replaceState({}, document.title, '/');
            setAuthChecked(true);
            return;
          }
        } catch { /* fall through to login */ }
        window.history.replaceState({}, document.title, '/');
      }

      // Restore from localStorage
      const storedToken = localStorage.getItem('orbit_token');
      if (storedToken) {
        try {
          const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${storedToken}` } });
          if (res.ok) {
            const u = await res.json();
            setAuthToken(storedToken);
            setUser(u);
            setCurrentPage('dashboard');
            setAuthChecked(true);
            return;
          }
        } catch { /* token invalid */ }
        localStorage.removeItem('orbit_token');
        localStorage.removeItem('orbit_user');
      }
      setAuthChecked(true);
    };
    initAuth();
  }, []);

  // Notification polling — every 30 seconds
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Message unread count polling — every 10 seconds
  useEffect(() => {
    if (!user) return;
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 10000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user) loadData();
  }, [currentPage, devOpsViewMode, dataOpsViewMode, currentWeekStart, veFilterVendor, veFilterWorkflow, veFilterStatus, leaderboardPeriod, user]);

  useEffect(() => {
    if (user) {
      ModulesModel.getTeamMembers().then(members => setAllTeamMembers(members));
      ModulesModel.getUserDirectory().then(users => setAllUsers(users));
      ModulesModel.getPlanTrackData().then(data => setAllProjects(data?.projects || []));
    }
  }, [user]);

  useEffect(() => {
    if (!user || currentPage !== 'vendorevents') return;
    const interval = setInterval(() => {
      ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus })
        .then(data => setVendorEventsData(data));
    }, 60000);
    return () => clearInterval(interval);
  }, [user, currentPage, veFilterVendor, veFilterWorkflow, veFilterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (currentPage === 'dashboard') {
        const [data, charts] = await Promise.all([DashboardModel.getStats(), DashboardModel.getBurnCharts()]);
        setDashboardData(data);
        setBurnCharts(charts);
      } else if (currentPage === 'modules') {
        const data = await ModulesModel.getModules();
        setModules(data);
      } else if (currentPage === 'leaderboard') {
        const data = await ModulesModel.getLeaderboard(leaderboardPeriod);
        setLeaderboardData(data);
      } else if (currentPage === 'devops') {
        const devops = await ModulesModel.getDevOpsPipeline();
        if (devOpsViewMode === 'kanban') {
          const kanban = await ModulesModel.getDevOpsKanban();
          devops.kanbanPipeline = kanban.pipeline;
        }
        setDevOpsData(devops);
      } else if (currentPage === 'plantrack') {
        const data = await ModulesModel.getPlanTrackData();
        setPlanTrackData(data);
        if (selectedProjectId) {
          const detail = await ModulesModel.getProjectDetail(selectedProjectId);
          setProjectDetail(detail);
        }
      } else if (currentPage === 'dataops') {
        const data = await ModulesModel.getDataOpsData();
        setDataOpsData(data);
      } else if (currentPage === 'learntrack') {
        const data = await ModulesModel.getLearnTrackData();
        setLearnTrackData(data);
      } else if (currentPage === 'timeboard') {
        const data = await ModulesModel.getTimeBoardData(currentWeekStart);
        setTimeBoardData(data);
      } else if (currentPage === 'leaveboard') {
        const data = await ModulesModel.getLeaveBoardData();
        setLeaveBoardData(data);
      } else if (currentPage === 'meetingboard') {
        const data = await ModulesModel.getMeetingBoardData();
        setMeetingBoardData(data);
      } else if (currentPage === 'knowledgeboard') {
        const data = await ModulesModel.getKnowledgeData({ categoryId: knowledgeSelectedCategory, search: knowledgeSearch });
        setKnowledgeData(data);
      } else if (currentPage === 'incidentboard') {
        const data = await ModulesModel.getIncidentsData(incidentFilters);
        setIncidentData(data);
      } else if (currentPage === 'quality') {
        const [data, bugsData] = await Promise.all([
          ModulesModel.getQualityData(qmsProjectId),
          ModulesModel.getAllBugs(allBugsFilters)
        ]);
        setQualityData(data);
        setAllBugsData(bugsData);
        if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
      } else if (currentPage === 'quicktrack') {
        const [data, ptData] = await Promise.all([ModulesModel.getQuickTrackData(), ModulesModel.getPlanTrackData()]);
        setQuickTrackData(data);
        setAllProjects(ptData?.projects || []);
      } else if (currentPage === 'vendorevents') {
        const data = await ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus });
        setVendorEventsData(data);
      } else if (currentPage === 'reports') {
        const data = await ModulesModel.getReportsData();
        setReportsData(data);
      } else if (currentPage === 'tasks') {
        const data = await ModulesModel.getTaskManageData(taskManageFilters);
        setTaskManageData(data);
      } else if (currentPage === 'audit') {
        setAuditLoading(true);
        const [logsData, statsData] = await Promise.all([
          ModulesModel.getAuditLogs({ ...auditFilters, offset: 0 }),
          ModulesModel.getAuditStats()
        ]);
        setAuditLogs(logsData.logs || []);
        setAuditHasMore(logsData.hasMore || false);
        setAuditOffset(0);
        setAuditStats(statsData);
        setAuditLoading(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleSelectProject = async (projectId) => {
    setSelectedProjectId(projectId);
    setLoading(true);
    try {
      const detail = await ModulesModel.getProjectDetail(projectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error loading project detail:', error);
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('orbit_token', token);
    localStorage.setItem('orbit_user', JSON.stringify(userData));
    setAuthToken(token);
    setUser(userData);
    setCurrentPage('dashboard');
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
    } catch { /* ignore */ }
    localStorage.removeItem('orbit_token');
    localStorage.removeItem('orbit_user');
    setAuthToken(null);
    setUser(null);
  };

  const handleBackToProjects = () => {
    setSelectedProjectId(null);
    setProjectDetail(null);
  };

  const handleAuditFilterChange = async (newFilters) => {
    setAuditFilters(newFilters);
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...newFilters, offset: 0 });
      setAuditLogs(logsData.logs || []);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(0);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleAuditLoadMore = async () => {
    const nextOffset = auditOffset + 50;
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...auditFilters, offset: nextOffset });
      setAuditLogs(prev => [...prev, ...(logsData.logs || [])]);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(nextOffset);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleRefreshProjectDetail = async () => {
    if (!selectedProjectId) return;
    try {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error refreshing project detail:', error);
    }
  };

  const handleCreateProject = async (formData) => {
    await ModulesModel.createProject(formData);
    setShowAddProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleCreateSprint = async (formData) => {
    await ModulesModel.createSprint(formData);
    setShowAddSprint(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleUpdateSprint = async (sprintId, data) => {
    await ModulesModel.updateSprint(sprintId, data);
    const refreshed = await ModulesModel.getPlanTrackData();
    setPlanTrackData(refreshed);
  };

  const handleUpdateProject = async (formData) => {
    await ModulesModel.updateProject(selectedProjectId, formData);
    setShowEditProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    const detail = await ModulesModel.getProjectDetail(selectedProjectId);
    setProjectDetail(detail);
  };

  // Milestone Engine handlers
  const refreshPlanTrack = async () => {
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    if (selectedProjectId) {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    }
  };

  const handleCreateMilestone = async (formData) => {
    await ModulesModel.createMilestone(formData);
    setShowAddMilestone(false);
    await refreshPlanTrack();
  };

  const handleUpdateMilestone = async (msId, data) => {
    await ModulesModel.updateMilestone(msId, data);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleDeleteMilestone = async (msId) => {
    await ModulesModel.deleteMilestone(msId);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleCompleteMilestone = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed to complete milestone');
    await refreshPlanTrack();
  };

  const handleEditMilestone = async (msId) => {
    const detail = await ModulesModel.getMilestoneDetail(msId);
    if (detail) { setEditingMilestoneId(msId); setEditingMilestoneData(detail); }
  };


  // DevOps handlers
  const handleCreateDevOpsTask = async (formData) => {
    await ModulesModel.createDevOpsTask(formData);
    setShowAddDevOpsTask(false);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleCreateDataOpsTask = async (formData) => {
    await ModulesModel.createDataOpsTask(formData);
    setShowAddDataOpsTask(false);
    await refreshDataOps();
  };

  const handleDevOpsTaskClick = async (taskId) => {
    if (String(taskId).startsWith('QT-')) return;
    // Bugs: admins/managers get full edit modal; others get view-only panel
    // QA bugs have IDs like BUG-1, BUG-3 (pure numeric suffix)
    // DevOps bug tasks have IDs like BUG-2026-947 (year-sequence) — treat as regular tasks
    if (/^BUG-\d+$/.test(String(taskId))) {
      const numericId = parseInt(String(taskId).replace('BUG-', ''), 10);
      const bug = await ModulesModel.getBug(numericId);
      if (!bug?.id) return;
      if (user?.role === 'admin' || user?.role?.endsWith('_manager') || user?.role === 'manager') {
        setEditingBug(bug);
      } else {
        setSelectedBug(bug);
      }
      return;
    }
    // Try to find the task in current data first
    let task = null;
    if (devOpsData?.pipeline) {
      for (const items of Object.values(devOpsData.pipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (devOpsData?.kanbanPipeline && !task) {
      for (const items of Object.values(devOpsData.kanbanPipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    // Fallback: fetch from API
    if (!task) {
      task = await ModulesModel.getDevOpsTask(taskId);
    }
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDevOpsKanbanDelete = async (taskId) => {
    if (!String(taskId).startsWith('QT-')) return;
    const qtId = parseInt(String(taskId).replace('QT-', ''), 10);
    await ModulesModel.deleteQuickTask(qtId);
    const devops = await ModulesModel.getDevOpsPipeline();
    const kanban = await ModulesModel.getDevOpsKanban();
    devops.kanbanPipeline = kanban.pipeline;
    setDevOpsData(devops);
  };

  const handleDevOpsStatusChange = async (taskId, newStatus, extra = {}) => {
    if (String(taskId).startsWith('QT-')) {
      const qtId = parseInt(String(taskId).replace('QT-', ''), 10);
      const qtStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'in_progress', done: 'done', completed: 'done' };
      await ModulesModel.updateQuickTask(qtId, { status: qtStatusMap[newStatus] || 'open', ...extra });
    } else if (/^BUG-\d+$/.test(String(taskId))) {
      const bugId = parseInt(String(taskId).replace('BUG-', ''), 10);
      // Sprint column → bug status: backlog→open, in_progress→in_progress, review→resolved, done→closed
      const bugStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'resolved', done: 'closed', completed: 'closed' };
      await ModulesModel.updateBug(bugId, { status: bugStatusMap[newStatus] || 'open' });
    } else {
      const taskUpdate = { status: newStatus };
      if (extra.actualMinutes) {
        taskUpdate.actualHours = parseFloat((extra.actualMinutes / 60).toFixed(2));
        taskUpdate.actualMinutes = extra.actualMinutes;
      }
      await ModulesModel.updateDevOpsTask(taskId, taskUpdate);
    }
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDevOpsTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    setSelectedDevOpsTask(null);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDeleteDevOpsTask = async (taskId) => {
    await ModulesModel.deleteDevOpsTask(taskId);
    setSelectedDevOpsTask(null);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDevOpsBugReassign = async (bugId, data) => {
    await ModulesModel.updateBug(bugId, data);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  // LearnTrack handlers
  const refreshLearnTrackData = async () => {
    const data = await ModulesModel.getLearnTrackData();
    setLearnTrackData(data);
  };

  const handleCourseClick = async (courseId) => {
    const course = await ModulesModel.getCourseDetail(courseId);
    if (course) setSelectedCourse(course);
  };

  const handleCreateCourse = async (formData) => {
    await ModulesModel.createCourse(formData);
    setShowAddCourse(false);
    await refreshLearnTrackData();
  };

  const handleUpdateCourse = async (courseId, data) => {
    await ModulesModel.updateCourse(courseId, data);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleDeleteCourse = async (courseId) => {
    await ModulesModel.deleteCourse(courseId);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleEnrollUser = async (data) => {
    await ModulesModel.enrollUser(data);
    await refreshLearnTrackData();
    if (selectedCourse) {
      const updated = await ModulesModel.getCourseDetail(selectedCourse.id);
      setSelectedCourse(updated);
    }
  };

  const handleUpdateEnrollment = async (enrollmentId, data) => {
    await ModulesModel.updateEnrollment(enrollmentId, data);
    await refreshLearnTrackData();
  };

  // TimeBoard handlers
  const refreshTimeBoardData = async () => {
    const data = await ModulesModel.getTimeBoardData(currentWeekStart);
    setTimeBoardData(data);
  };

  const handleCreateTimeEntry = async (formData) => {
    await ModulesModel.createTimesheetEntry({ ...formData, weekStart: currentWeekStart });
    setShowTimeEntry(null);
    await refreshTimeBoardData();
  };

  const handleDeleteTimeEntry = async (id) => {
    await ModulesModel.deleteTimesheetEntry(id);
    await refreshTimeBoardData();
  };

  const handleSubmitTimesheet = async (id) => {
    await ModulesModel.submitTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleApproveTimesheet = async (id) => {
    await ModulesModel.approveTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleSyncCompletedTasks = async (defaultHours = 1) => {
    const result = await ModulesModel.syncCompletedTasks(defaultHours);
    await refreshTimeBoardData();
    return result;
  };

  const handleWeekChange = (direction) => {
    const current = new Date(currentWeekStart + 'T12:00:00');
    current.setDate(current.getDate() + (direction > 0 ? 7 : -7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  // LeaveBoard handlers
  const refreshLeaveBoardData = async () => {
    const data = await ModulesModel.getLeaveBoardData();
    setLeaveBoardData(data);
  };

  const handleCreateLeaveRequest = async (formData) => {
    await ModulesModel.createLeaveRequest(formData);
    setShowRequestLeave(false);
    await refreshLeaveBoardData();
  };

  const handleUpdateLeaveRequest = async (id, data) => {
    await ModulesModel.updateLeaveRequest(id, data);
    await refreshLeaveBoardData();
  };

  // MeetingBoard handlers
  const refreshMeetingBoardData = async () => {
    const data = await ModulesModel.getMeetingBoardData();
    setMeetingBoardData(data);
  };

  const handleCreateMeeting = async (formData) => {
    await ModulesModel.createMeeting({ ...formData, hostId: user?.id, hostName: user?.name });
    setShowScheduleMeeting(false);
    await refreshMeetingBoardData();
  };

  const handleEndMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'ended' });
    await refreshMeetingBoardData();
  };

  const handleStartMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'ongoing' });
    await refreshMeetingBoardData();
  };

  const handleCancelMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'cancelled' });
    await refreshMeetingBoardData();
  };

  const handleDeleteMeeting = async (id) => {
    await ModulesModel.deleteMeeting(id);
    await refreshMeetingBoardData();
  };

  // KnowledgeBoard handlers
  const refreshKnowledgeData = async (catId, search) => {
    const data = await ModulesModel.getKnowledgeData({ categoryId: catId ?? knowledgeSelectedCategory, search: search ?? knowledgeSearch });
    setKnowledgeData(data);
  };

  const handleKnowledgeCategorySelect = async (catId) => {
    setKnowledgeSelectedCategory(catId);
    setKnowledgeViewingArticle(null);
    const data = await ModulesModel.getKnowledgeData({ categoryId: catId, search: knowledgeSearch });
    setKnowledgeData(data);
  };

  const handleKnowledgeSearch = async (q) => {
    setKnowledgeSearch(q);
    const data = await ModulesModel.getKnowledgeData({ categoryId: knowledgeSelectedCategory, search: q });
    setKnowledgeData(data);
  };

  const handleViewKnowledgeArticle = async (id) => {
    const article = await ModulesModel.getKnowledgeArticle(id);
    setKnowledgeViewingArticle(article);
    setKnowledgeShowVersions(false);
    setKnowledgeArticleVersions([]);
  };

  const handleKnowledgeCreateArticle = () => {
    setKnowledgeEditingArticle(null);
    setKnowledgeShowEditor(true);
  };

  const handleKnowledgeEditArticle = (article) => {
    setKnowledgeEditingArticle(article);
    setKnowledgeShowEditor(true);
  };

  const handleKnowledgeSaveArticle = async (data) => {
    if (knowledgeEditingArticle) {
      await ModulesModel.updateKnowledgeArticle(knowledgeEditingArticle.id, data);
    } else {
      await ModulesModel.createKnowledgeArticle({ ...data, createdBy: user?.user_name, createdById: user?.user_id || user?.id });
    }
    setKnowledgeShowEditor(false);
    setKnowledgeEditingArticle(null);
    await refreshKnowledgeData();
    if (knowledgeEditingArticle) {
      const updated = await ModulesModel.getKnowledgeArticle(knowledgeEditingArticle.id);
      setKnowledgeViewingArticle(updated);
    }
  };

  const handleKnowledgeDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    await ModulesModel.deleteKnowledgeArticle(id);
    setKnowledgeViewingArticle(null);
    await refreshKnowledgeData();
  };

  const handleViewArticleVersions = async (articleId) => {
    const versions = await ModulesModel.getArticleVersions(articleId);
    setKnowledgeArticleVersions(versions);
    setKnowledgeShowVersions(true);
  };

  const handleRestoreVersion = async (articleId, versionId) => {
    if (!window.confirm('Restore this version? The current content will be saved as a version.')) return;
    const result = await ModulesModel.restoreArticleVersion(articleId, versionId);
    setKnowledgeShowVersions(false);
    if (result?.id) setKnowledgeViewingArticle(result);
    await refreshKnowledgeData();
  };

  const handleCreateKnowledgeCategory = async (data) => {
    await ModulesModel.createKnowledgeCategory({ ...data, createdBy: user?.user_name });
    setKnowledgeShowCategoryModal(false);
    await refreshKnowledgeData();
  };

  // IncidentBoard handlers
  const refreshIncidentData = async () => {
    const data = await ModulesModel.getIncidentsData(incidentFilters);
    setIncidentData(data);
  };

  const handleIncidentFilterChange = async (newFilters) => {
    const merged = { ...incidentFilters, ...newFilters };
    setIncidentFilters(merged);
    const data = await ModulesModel.getIncidentsData(merged);
    setIncidentData(data);
  };

  const handleViewIncident = async (id) => {
    const incident = await ModulesModel.getIncident(id);
    setViewingIncident(incident);
  };

  const handleCreateIncident = async (data) => {
    await ModulesModel.createIncident({ ...data, reportedBy: user?.user_name, reportedById: user?.user_id || user?.id });
    setShowCreateIncident(false);
    await refreshIncidentData();
  };

  const handleUpdateIncident = async (id, data) => {
    const result = await ModulesModel.updateIncident(id, data);
    if (result?.success) {
      setViewingIncident(result.incident);
      await refreshIncidentData();
    }
  };

  const handleAddIncidentUpdate = async (incidentId, message) => {
    const result = await ModulesModel.addIncidentUpdate(incidentId, { update_message: message });
    if (result?.success) {
      setViewingIncident(prev => prev ? { ...prev, updates: result.updates } : prev);
    }
  };

  const handleDeleteIncident = async (id) => {
    await ModulesModel.updateIncident(id, { status: 'Closed' });
    setViewingIncident(null);
    await refreshIncidentData();
  };

  // QMS handlers
  const refreshQualityData = async () => {
    const data = await ModulesModel.getQualityData(qmsProjectId);
    setQualityData(data);
  };

  const refreshAllBugs = async (filters = allBugsFilters) => {
    const data = await ModulesModel.getAllBugs(filters);
    setAllBugsData(data);
  };

  const handleAllBugsFilterChange = async (newFilters) => {
    const merged = { ...allBugsFilters, ...newFilters };
    setAllBugsFilters(merged);
    await refreshAllBugs(merged);
  };

  const handleViewBugDetail = async (id) => {
    if (id === 'list') { setQmsView('bugs'); setViewingBugDetail(null); return; }
    const bug = await ModulesModel.getBugDetail(id);
    setViewingBugDetail(bug);
    setQmsView('bugs');
  };

  const handleBugDetailUpdate = async (id, changes) => {
    await ModulesModel.updateBug(id, changes);
    const updated = await ModulesModel.getBugDetail(id);
    setViewingBugDetail(updated);
    await refreshAllBugs();
    await refreshQualityData();
  };

  const handleBugDetailComment = async (bugId, comment) => {
    await ModulesModel.addBugComment(bugId, { userName: user?.user_name, content: comment });
    const updated = await ModulesModel.getBugDetail(bugId);
    setViewingBugDetail(updated);
  };

  const handleDeleteBugComment = async (bugId, commentId) => {
    await ModulesModel.deleteBugComment(commentId);
    const updated = await ModulesModel.getBugDetail(bugId);
    setViewingBugDetail(updated);
  };

  const handleQmsStageChange = async (stage) => {
    setQmsStage(stage);
    if (qualityData?.project?.projectId) {
      await ModulesModel.updateQaProject(qualityData.project.projectId, { currentStage: stage });
    }
  };

  const handleQmsProjectChange = async (projectId) => {
    setQmsProjectId(projectId);
    const data = await ModulesModel.getQualityData(projectId);
    setQualityData(data);
    if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
  };

  const handleClearData = async (modules) => {
    return await ModulesModel.clearData(modules);
  };

  const handleCreateRequirement = async (data) => {
    const result = await ModulesModel.createRequirement(data);
    if (result?.success === false) { alert('Failed to save requirement: ' + (result.error || 'Unknown error')); return; }
    setShowAddRequirement(false);
    await refreshQualityData();
  };

  const handleApproveRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'approved' });
    await refreshQualityData();
  };

  const handleRejectRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'rejected' });
    await refreshQualityData();
  };

  const handleEditRequirement = async (id, data) => {
    await ModulesModel.updateRequirement(id, data);
    await refreshQualityData();
  };

  const handleDeleteRequirement = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/requirements/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete requirement'); return; }
    await refreshQualityData();
  };

  const handleCreateTestCase = async (data) => {
    const result = await ModulesModel.createTestCase(data);
    if (result?.success === false) { alert('Failed to save test case: ' + (result.error || 'Unknown error')); return; }
    setShowAddTestCase(false);
    await refreshQualityData();
  };

  const handlePassTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'pass', executedBy: user?.user_name || user?.name || 'QA Tester' });
    await refreshQualityData();
  };

  const handleFailTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'fail', executedBy: user?.user_name || user?.name || 'QA Tester' });
    await refreshQualityData();
  };

  const handleEditTestCase = async (id, data) => {
    await ModulesModel.updateTestCase(id, data);
    await refreshQualityData();
  };

  const handleDeleteTestCase = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/testcases/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete test case'); return; }
    await refreshQualityData();
  };

  const handleReportBug = (tc) => {
    setReportBugPrefill(tc);
    setShowReportBug(true);
  };

  const handleCreateBug = async (data) => {
    const result = await ModulesModel.createBug({ ...data, reportedBy: user?.name });
    if (result?.success === false) { alert('Failed to save bug: ' + (result.error || 'Unknown error')); return; }
    setShowReportBug(false);
    setReportBugPrefill(null);
    await refreshQualityData();
  };

  const handleEditBug = async (bug) => {
    // Bug rows from the All Bugs list don't include steps/expected/actual — fetch full detail first
    if (bug.stepsToReproduce === undefined) {
      const full = await ModulesModel.getBugDetail(bug.id);
      setEditingBug(full || bug);
    } else {
      setEditingBug(bug);
    }
  };

  const handleUpdateBug = async (id, data) => {
    const result = await ModulesModel.updateBug(id, data);
    if (result?.success === false) { alert('Failed to update bug: ' + (result.error || 'Unknown error')); return; }
    setEditingBug(null);
    if (currentPage === 'devops') {
      const devops = await ModulesModel.getDevOpsPipeline();
      if (devOpsViewMode === 'kanban') {
        const kanban = await ModulesModel.getDevOpsKanban();
        devops.kanbanPipeline = kanban.pipeline;
      }
      setDevOpsData(devops);
    } else {
      await refreshQualityData();
      await refreshAllBugs();
    }
  };

  const handleDeleteBug = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/bugs/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete bug'); return; }
    setEditingBug(null);
    await refreshQualityData();
  };

  const handleUpdateQaProject = async (id, data) => {
    await ModulesModel.updateQaProject(id, data);
    await refreshQualityData();
  };

  // QuickTrack handlers
  const refreshQuickTrackData = async () => {
    const data = await ModulesModel.getQuickTrackData();
    setQuickTrackData(data);
  };

  const handleCreateQuickTask = async (data) => {
    await ModulesModel.createQuickTask(data);
    setShowAddQuickTask(false);
    await refreshQuickTrackData();
  };

  const handleUpdateQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    await refreshQuickTrackData();
  };

  const handleDeleteQuickTask = async (taskId) => {
    await ModulesModel.deleteQuickTask(taskId);
    await refreshQuickTrackData();
  };

  const handleEditQuickTask = (task) => setEditingQuickTask(task);

  const handleSaveQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    setEditingQuickTask(null);
    await refreshQuickTrackData();
  };

  // Vendor Events handlers
  const handleVeFilterVendor = (v) => setVeFilterVendor(v);
  const handleVeFilterWorkflow = (v) => setVeFilterWorkflow(v);
  const handleVeFilterStatus = (v) => setVeFilterStatus(v);
  const handleVeResetFilters = () => { setVeFilterVendor(''); setVeFilterWorkflow(''); setVeFilterStatus(''); };

  // Task Management handlers
  const handleTaskManageFilter = async (newFilters) => {
    const merged = { ...taskManageFilters, ...newFilters };
    setTaskManageFilters(merged);
    const data = await ModulesModel.getTaskManageData(merged);
    setTaskManageData(data);
  };
  const handleTaskManageBulk = async (tasks, updates) => {
    await ModulesModel.bulkUpdateTasks(tasks, updates);
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };
  const handleTaskManageRefresh = async () => {
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };


  // DataOps handlers
  const refreshDataOps = async () => {
    const data = await ModulesModel.getDataOpsData();
    setDataOpsData(data);
  };

  const refreshDataOpsKanban = async () => {
    const data = await ModulesModel.getDataOpsKanban();
    setDataOpsKanban(data);
  };

  const handleDataOpsViewModeChange = async (mode) => {
    setDataOpsViewMode(mode);
    if (mode === 'kanban' && !dataOpsKanban) {
      refreshDataOpsKanban();
    }
  };

  const handleDataOpsKanbanStatusChange = async (taskId, update) => {
    await ModulesModel.updateQuickTask(taskId, update);
    refreshDataOpsKanban();
  };

  const handleDataOpsKanbanDelete = async (taskId) => {
    await ModulesModel.deleteQuickTask(taskId);
    refreshDataOpsKanban();
  };

  const handleDataOpsTaskClick = async (taskId) => {
    if (String(taskId).startsWith('QT-')) return;
    let task = null;
    if (dataOpsData?.sprintBoard) {
      for (const items of Object.values(dataOpsData.sprintBoard)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (!task) task = await ModulesModel.getDevOpsTask(taskId);
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDataOpsStatusChange = async (taskId, newStatus) => {
    await ModulesModel.updateDevOpsTask(taskId, { status: newStatus });
    await refreshDataOps();
  };

  const handleSelectPipeline = async (pipelineId) => {
    const pipeline = await ModulesModel.getPipeline(pipelineId);
    if (pipeline) setSelectedPipeline(pipeline);
  };

  const handleCreatePipeline = async (formData) => {
    await ModulesModel.createPipeline(formData);
    setShowAddPipeline(false);
    await refreshDataOps();
  };

  const handleUpdatePipeline = async (pipelineId, data) => {
    await ModulesModel.updatePipeline(pipelineId, data);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleDeletePipeline = async (pipelineId) => {
    await ModulesModel.deletePipeline(pipelineId);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleTriggerPipeline = async (pipelineId) => {
    await ModulesModel.updatePipeline(pipelineId, { action: 'trigger' });
    await refreshDataOps();
  };

  const handleSelectReport = async (reportId) => {
    const report = await ModulesModel.getReport(reportId);
    await ModulesModel.updateReport(reportId, { action: 'view' });
    if (report) setSelectedReport(report);
  };

  const handleCreateReport = async (formData) => {
    await ModulesModel.createReport(formData);
    setShowAddReport(false);
    await refreshDataOps();
  };

  const handleUpdateReport = async (reportId, data) => {
    await ModulesModel.updateReport(reportId, data);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleDeleteReport = async (reportId) => {
    await ModulesModel.deleteReport(reportId);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleToggleFavorite = async (reportId, isFavorite) => {
    await ModulesModel.updateReport(reportId, { isFavorite });
    await refreshDataOps();
    if (selectedReport && selectedReport.reportId === reportId) {
      const updated = await ModulesModel.getReport(reportId);
      setSelectedReport(updated);
    }
  };

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-4xl mb-4">🛰️</div>
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      );
    }

    // Block access to pages the user's role doesn't allow
    if (!canAccessPage(user, currentPage)) {
      if (currentPage !== 'dashboard') setCurrentPage('dashboard');
      return null;
    }

    switch (currentPage) {
      case 'dashboard':
        return dashboardData ? <DashboardView data={dashboardData} burnCharts={burnCharts} onNavigate={setCurrentPage} user={user} /> : null;
      case 'modules':
        return modules.length > 0 ? <ModulesView modules={modules} onNavigate={setCurrentPage} user={user} /> : null;
      case 'plantrack':
        if (showMilestoneEngine) {
          return <MilestoneEngineView
            onBack={() => setShowMilestoneEngine(false)}
            projects={planTrackData?.projects || []}
          />;
        }
        if (showSprintsView) {
          return <SprintsManagementView
            sprints={planTrackData?.sprints || []}
            onBack={() => setShowSprintsView(false)}
            onCreateSprint={handleCreateSprint}
            onUpdateSprint={handleUpdateSprint}
          />;
        }
        if (selectedProjectId && projectDetail) {
          return <ProjectDetailView detail={projectDetail} onBack={handleBackToProjects} onRefresh={handleRefreshProjectDetail} user={user} teamMembers={allTeamMembers} onEdit={() => setShowEditProject(true)}
            onCreateMilestone={() => setShowAddMilestone(true)}
            onEditMilestone={handleEditMilestone}
            onCompleteMilestone={handleCompleteMilestone}
          />;
        }
        return planTrackData ? <PlanTrackView data={planTrackData} onSelectProject={handleSelectProject} onShowAddProject={() => setShowAddProject(true)} onShowSprints={() => setShowSprintsView(true)} onShowMilestones={() => setShowMilestoneEngine(true)} onShowPostUpdateReport={() => setShowPostUpdateReport(true)} /> : null;
      case 'devops':
        return devOpsData ? (
          <DevOpsModule
            data={devOpsData}
            viewMode={devOpsViewMode}
            group={devOpsGroup}
            onViewModeChange={async (mode) => {
              setDevOpsViewMode(mode);
              if (mode === 'kanban') {
                const devops = await ModulesModel.getDevOpsPipeline();
                const kanban = await ModulesModel.getDevOpsKanban();
                devops.kanbanPipeline = kanban?.pipeline || {};
                setDevOpsData(devops);
              }
            }}
            onGroupChange={setDevOpsGroup}
            onAddTask={() => setShowAddDevOpsTask(true)}
            onTaskClick={handleDevOpsTaskClick}
            onStatusChange={handleDevOpsStatusChange}
            onSendEod={() => { setEodModule('devops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role}
            onDelete={handleDevOpsKanbanDelete}
          />
        ) : null;
      case 'dataops':
        return dataOpsData ? (
          <DataOpsModule
            data={dataOpsData}
            viewMode={dataOpsViewMode}
            onViewModeChange={handleDataOpsViewModeChange}
            onAddPipeline={() => setShowAddPipeline(true)}
            onAddReport={() => setShowAddReport(true)}
            onPipelineClick={handleSelectPipeline}
            onReportClick={handleSelectReport}
            onTriggerPipeline={handleTriggerPipeline}
            onToggleFavorite={handleToggleFavorite}
            onTaskClick={handleDataOpsTaskClick}
            onStatusChange={handleDataOpsStatusChange}
            onAddTask={() => setShowAddDataOpsTask(true)}
            onSendEod={() => { setEodModule('dataops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role}
            kanban={dataOpsKanban}
            onKanbanStatusChange={handleDataOpsKanbanStatusChange}
            onKanbanDelete={handleDataOpsKanbanDelete}
          />
        ) : null;
      case 'learntrack':
        return learnTrackData ? (
          <LearnTrackModule
            data={learnTrackData}
            viewMode={learnTrackViewMode}
            onViewModeChange={setLearnTrackViewMode}
            onAddCourse={() => setShowAddCourse(true)}
            onCourseClick={handleCourseClick}
            onEnroll={handleEnrollUser}
            onUpdateProgress={handleUpdateEnrollment}
            filterSkill={learnFilterSkill}
            filterGroup={learnFilterGroup}
            onFilterSkill={setLearnFilterSkill}
            onFilterGroup={setLearnFilterGroup}
          />
        ) : null;
      case 'timeboard':
        return timeBoardData ? (
          <TimeBoardModule
            data={timeBoardData}
            weekStart={currentWeekStart}
            user={user}
            onWeekChange={handleWeekChange}
            onSubmitTimesheet={handleSubmitTimesheet}
            onApproveTimesheet={handleApproveTimesheet}
          />
        ) : null;
      case 'leaveboard':
        return leaveBoardData ? (
          <LeaveBoardModule
            data={leaveBoardData}
            onRequestLeave={() => setShowRequestLeave(true)}
            onApproveRequest={(id) => handleUpdateLeaveRequest(id, { status: 'approved', approvedBy: user.name })}
            onRejectRequest={(id) => handleUpdateLeaveRequest(id, { status: 'rejected' })}
          />
        ) : null;
      case 'meetingboard':
        return (
          <MeetingBoardModule
            data={meetingBoardData}
            user={user}
            onSchedule={() => setShowScheduleMeeting(true)}
            onStart={handleStartMeeting}
            onEnd={handleEndMeeting}
            onCancel={handleCancelMeeting}
            onDelete={handleDeleteMeeting}
          />
        );
      case 'knowledgeboard':
        return (
          <KnowledgeBoardModule
            data={knowledgeData}
            user={user}
            search={knowledgeSearch}
            selectedCategory={knowledgeSelectedCategory}
            viewingArticle={knowledgeViewingArticle}
            showEditor={knowledgeShowEditor}
            editingArticle={knowledgeEditingArticle}
            showVersions={knowledgeShowVersions}
            articleVersions={knowledgeArticleVersions}
            showCategoryModal={knowledgeShowCategoryModal}
            onSearch={handleKnowledgeSearch}
            onSelectCategory={handleKnowledgeCategorySelect}
            onViewArticle={handleViewKnowledgeArticle}
            onCreateArticle={handleKnowledgeCreateArticle}
            onEditArticle={handleKnowledgeEditArticle}
            onSaveArticle={handleKnowledgeSaveArticle}
            onDeleteArticle={handleKnowledgeDeleteArticle}
            onCloseEditor={() => { setKnowledgeShowEditor(false); setKnowledgeEditingArticle(null); }}
            onViewVersions={handleViewArticleVersions}
            onRestoreVersion={handleRestoreVersion}
            onCloseVersions={() => setKnowledgeShowVersions(false)}
            onShowCategoryModal={() => setKnowledgeShowCategoryModal(true)}
            onCreateCategory={handleCreateKnowledgeCategory}
            onCloseCategoryModal={() => setKnowledgeShowCategoryModal(false)}
            onBackToList={() => setKnowledgeViewingArticle(null)}
          />
        );
      case 'assetboard':
        return <AssetBoardModule user={user} />;
      case 'requestboard':
        return <RequestBoardModule user={user} />;
      case 'automationboard':
        return <AutomationBoardModule user={user} />;
      case 'incidentboard':
        return (
          <IncidentBoardModule
            data={incidentData}
            user={user}
            filters={incidentFilters}
            viewingIncident={viewingIncident}
            showCreate={showCreateIncident}
            onFilterChange={handleIncidentFilterChange}
            onViewIncident={handleViewIncident}
            onCreateIncident={handleCreateIncident}
            onUpdateIncident={handleUpdateIncident}
            onAddUpdate={handleAddIncidentUpdate}
            onDeleteIncident={handleDeleteIncident}
            onBack={() => setViewingIncident(null)}
            onShowCreate={() => setShowCreateIncident(true)}
            onCloseCreate={() => setShowCreateIncident(false)}
          />
        );
      case 'quality':
        return qualityData ? (
          <QmsModule
            data={qualityData}
            stage={qmsStage}
            onStageChange={handleQmsStageChange}
            onApproveReq={handleApproveRequirement}
            onRejectReq={handleRejectRequirement}
            onAddReq={(defaults = {}) => { setAddReqDefaults(defaults); setShowAddRequirement(true); }}
            onEditReq={handleEditRequirement}
            onDeleteReq={handleDeleteRequirement}
            onRefreshReq={refreshQualityData}
            onUpdateProject={handleUpdateQaProject}
            onAddTestCase={() => setShowAddTestCase(true)}
            onEditTestCase={handleEditTestCase}
            onDeleteTestCase={handleDeleteTestCase}
            onPassTC={handlePassTestCase}
            onFailTC={handleFailTestCase}
            onReportBug={handleReportBug}
            onEditBug={handleEditBug}
            onProjectChange={handleQmsProjectChange}
            onSendEod={() => setShowQaEod(true)}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role || user?.user_role}
            user={user}
            qmsView={qmsView}
            onQmsViewChange={(v) => { setQmsView(v); if (v === 'bugs') { setViewingBugDetail(null); refreshAllBugs(); } }}
            allBugsData={allBugsData}
            allBugsFilters={allBugsFilters}
            viewingBugDetail={viewingBugDetail}
            onAllBugsFilterChange={handleAllBugsFilterChange}
            onViewBugDetail={handleViewBugDetail}
            onBugDetailUpdate={handleBugDetailUpdate}
            onBugDetailComment={handleBugDetailComment}
            onDeleteBugComment={handleDeleteBugComment}
            onBackFromBugDetail={() => setViewingBugDetail(null)}
            onReportBugFromList={() => { setShowReportBug(true); }}
          />
        ) : null;
      case 'quicktrack':
        return quickTrackData ? (
          <QuickTrackModule
            data={quickTrackData}
            filterStatus={qtFilterStatus}
            filterPriority={qtFilterPriority}
            filterCategory={qtFilterCategory}
            onFilterStatus={setQtFilterStatus}
            onFilterPriority={setQtFilterPriority}
            onFilterCategory={setQtFilterCategory}
            onResetFilters={() => { setQtFilterStatus(''); setQtFilterPriority(''); setQtFilterCategory(''); }}
            onAddTask={() => setShowAddQuickTask(true)}
            onStatusChange={handleUpdateQuickTask}
            onDelete={handleDeleteQuickTask}
            onEdit={handleEditQuickTask}
            projects={allProjects}
            user={user}
            teamMembers={allTeamMembers}
          />
        ) : null;
      case 'vendorevents':
        return vendorEventsData ? (
          <VendorEventsModule
            data={vendorEventsData}
            filterVendor={veFilterVendor}
            filterWorkflow={veFilterWorkflow}
            filterStatus={veFilterStatus}
            onFilterVendor={handleVeFilterVendor}
            onFilterWorkflow={handleVeFilterWorkflow}
            onFilterStatus={handleVeFilterStatus}
            onResetFilters={handleVeResetFilters}
          />
        ) : null;
      case 'tasks':
        return <TaskManagementView
          data={taskManageData}
          filters={taskManageFilters}
          onFilterChange={handleTaskManageFilter}
          onBulkUpdate={handleTaskManageBulk}
          onRefresh={handleTaskManageRefresh}
        />;
      case 'reports':
        return <ReportsView data={reportsData} />;
      case 'audit':
        return <AuditTrailView
          logs={auditLogs}
          stats={auditStats}
          filters={auditFilters}
          onFilterChange={handleAuditFilterChange}
          onLoadMore={handleAuditLoadMore}
          hasMore={auditHasMore}
          loading={auditLoading}
        />;
      case 'leaderboard':
        return <LeaderBoardModule data={leaderboardData || { devops: [], dataops: [] }} period={leaderboardPeriod} onPeriodChange={setLeaderboardPeriod} />;
      case 'settings':
        return <SettingsView onClearData={handleClearData} user={user} authToken={authToken} />;
      default:
        return <DashboardView data={dashboardData} burnCharts={burnCharts} onNavigate={setCurrentPage} />;
    }
  };

  // Auth guard: show loading spinner, login page, or main app
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-5xl mb-4">🛰️</div>
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (resetToken) {
    return <ResetPasswordView token={resetToken} onDone={() => setResetToken(null)} />;
  }

  if (needsSetup) {
    return <SetupView onSetupComplete={(token, userData) => { setNeedsSetup(false); handleLogin(token, userData); }} />;
  }

  if (!user) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConfirmDialog />
      <Header
        user={user} onMenuClick={() => setMenuOpen(true)} onLogout={handleLogout} onChangePassword={() => setShowChangePassword(true)}
        notifications={notifications} unreadCount={unreadCount} onMarkRead={handleMarkNotificationRead} onMarkAllRead={handleMarkAllRead} onDismiss={handleDismissNotification}
        unreadMessages={unreadMessages} msgConversations={msgConversations}
        onOpenMessage={(uid, uname) => { setMsgOpenUserId(uid); setMsgOpenUserName(uname); setShowMessaging(true); }}
        onToggleMessages={() => { setMsgOpenUserId(null); setMsgOpenUserName(''); setShowMessaging(s => !s); }}
      />
      {showMessaging && (
        <MessagingPanel
          user={user}
          onClose={() => { setShowMessaging(false); setMsgOpenUserId(null); setMsgOpenUserName(''); fetchUnreadMessages(); }}
          onUnreadChange={setUnreadMessages}
          initialUserId={msgOpenUserId}
          initialUserName={msgOpenUserName}
        />
      )}
      <Navigation
        currentPage={currentPage}
        onNavigate={(page) => { setCurrentPage(page); setSelectedProjectId(null); setProjectDetail(null); setShowSprintsView(false); setShowMilestoneEngine(false); }}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
      />
      <main>
        {renderPage()}
      </main>
      {showAddProject && <AddProjectModal onClose={() => setShowAddProject(false)} onSubmit={handleCreateProject} />}
      {showPostUpdateReport && <PostUpdateReportModal onClose={() => setShowPostUpdateReport(false)} />}
      {showSendEod && <SendEodModal module={eodModule} onClose={() => setShowSendEod(false)} />}
      {showQaEod && <SendQaEodModal onClose={() => setShowQaEod(false)} />}
      {showEodSettings && <EodSettingsModal onClose={() => setShowEodSettings(false)} />}
      {showAddSprint && <AddSprintModal onClose={() => setShowAddSprint(false)} onSubmit={handleCreateSprint} />}
      {showEditProject && projectDetail && <EditProjectModal
        project={projectDetail.project}
        sprints={projectDetail.allSprints || planTrackData?.sprints || []}
        onClose={() => setShowEditProject(false)}
        onSubmit={handleUpdateProject}
      />}
      {showAddDevOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDevOpsTask(false)} onSubmit={handleCreateDevOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'devops')} user={user} allUsers={allUsers} projects={allProjects} />}
      {showAddDataOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDataOpsTask(false)} onSubmit={handleCreateDataOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'dataops')} user={user} allUsers={allUsers} projects={allProjects} />}
      {selectedDevOpsTask && <TaskDetailPanel task={selectedDevOpsTask} onClose={() => setSelectedDevOpsTask(null)} onUpdate={handleDevOpsTaskUpdate} onDelete={handleDeleteDevOpsTask} user={user} teamMembers={allTeamMembers} allUsers={allUsers} projects={allProjects} />}
      {selectedBug && <BugDetailPanel bug={selectedBug} onClose={() => setSelectedBug(null)} user={user} readOnly={true} teamMembers={allTeamMembers} onUpdate={handleDevOpsBugReassign} />}
      {showTimeEntry && <TimeEntryModal date={showTimeEntry.date} category={showTimeEntry.category} onClose={() => setShowTimeEntry(null)} onSubmit={handleCreateTimeEntry} />}
      {showScheduleMeeting && <ScheduleMeetingModal users={allUsers || []} onClose={() => setShowScheduleMeeting(false)} onSubmit={handleCreateMeeting} />}
      {showRequestLeave && <RequestLeaveModal balance={leaveBoardData?.balances} onClose={() => setShowRequestLeave(false)} onSubmit={handleCreateLeaveRequest} />}
      {showAddPipeline && <AddPipelineModal onClose={() => setShowAddPipeline(false)} onSubmit={handleCreatePipeline} />}
      {showAddReport && <AddReportModal onClose={() => setShowAddReport(false)} onSubmit={handleCreateReport} />}
      {selectedPipeline && <PipelineDetailModal pipeline={selectedPipeline} onClose={() => setSelectedPipeline(null)} onUpdate={handleUpdatePipeline} onDelete={handleDeletePipeline} onTrigger={handleTriggerPipeline} />}
      {selectedReport && <ReportDetailModal report={selectedReport} onClose={() => setSelectedReport(null)} onUpdate={handleUpdateReport} onDelete={handleDeleteReport} onToggleFavorite={handleToggleFavorite} />}
      {showAddCourse && <AddCourseModal onClose={() => setShowAddCourse(false)} onSubmit={handleCreateCourse} skills={learnTrackData?.skills} />}
      {selectedCourse && <CourseDetailModal course={selectedCourse} onClose={() => setSelectedCourse(null)} onUpdate={handleUpdateCourse} onDelete={handleDeleteCourse} onEnroll={handleEnrollUser} />}
      {showAddQuickTask && <AddQuickTaskModal onClose={() => setShowAddQuickTask(false)} onSubmit={handleCreateQuickTask} projects={allProjects} teamMembers={allTeamMembers} user={user} />}
      {editingQuickTask && <EditQuickTaskModal task={editingQuickTask} onClose={() => setEditingQuickTask(null)} onSubmit={handleSaveQuickTask} projects={allProjects} user={user} teamMembers={allTeamMembers} />}
      {showAddRequirement && <AddRequirementModal onClose={() => { setShowAddRequirement(false); setAddReqDefaults({}); }} onSubmit={handleCreateRequirement} projectId={qualityData?.project?.projectId} existingCategories={[...new Set((qualityData?.requirements || []).filter(r => !r.parentRequirementId).map(r => r.parentCategory).filter(Boolean))]} requirements={qualityData?.requirements || []} defaultValues={addReqDefaults} />}
      {showAddTestCase && <AddTestCaseModal onClose={() => setShowAddTestCase(false)} onSubmit={handleCreateTestCase} projectId={qualityData?.project?.projectId} requirements={qualityData?.requirements || []} />}
      {showReportBug && <ReportBugModal onClose={() => { setShowReportBug(false); setReportBugPrefill(null); }} onSubmit={handleCreateBug} projectId={qualityData?.project?.projectId} testCases={qualityData?.testCases} prefillTc={reportBugPrefill} />}
      {editingBug && <EditBugModal bug={editingBug} onClose={() => setEditingBug(null)} onSave={handleUpdateBug} onDelete={handleDeleteBug} user={user} allUsers={allUsers} teamMembers={allTeamMembers} allProjects={allProjects} />}
      {showChangePassword && <ChangePasswordModal authToken={authToken} onClose={() => setShowChangePassword(false)} />}
      {showAddMilestone && <AddMilestoneModal
        onClose={() => setShowAddMilestone(false)}
        onSubmit={handleCreateMilestone}
        projectId={selectedProjectId}
        projects={planTrackData?.projects || []}
      />}
      {editingMilestoneData && <EditMilestoneModal
        milestone={editingMilestoneData}
        allMilestones={planTrackData?.milestones || []}
        onClose={() => { setEditingMilestoneId(null); setEditingMilestoneData(null); }}
        onSubmit={(data) => handleUpdateMilestone(editingMilestoneId, data)}
        onDelete={handleDeleteMilestone}
      />}
      <footer className="text-center py-8 text-gray-600 text-sm">
        ORBIT System v1.0 | React + Cloudflare Pages/Workers MVC Architecture
      </footer>
    </div>
  );
};

export default App;